﻿<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { CommerceService } from '@/services/commerce.services';
import { CategoryService } from '@/services/category.services';
import { StoreSettingsService } from '@/services/store-settings.services';
import axiosInstance from '@/services/interceptor.services';
import type { IProduct, IApiResponseProduct } from '@/types/product.types';
import { buildStorefrontUrl } from '@/utils/storefrontUrl';
import { TranslationService } from '@/services/translation.services';
import { organizationIdCommerce, lastCommerceId } from '@/refs/commerce.refs';
import {
  isGlobalSucess,
  GlobalSucessMessage,
  isGlobalError,
  GlobalErrorMessage,
} from '@/refs/common.refs';
import CommerceAddOptionGroup from '@/components/commerces/CommerceAddOptionGroup.vue';
import CommerceAddonGroupDrawer from '@/components/commerces/CommerceAddonGroupDrawer.vue';
import CommerceLegalTab from '@/components/commerces/CommerceLegalTab.vue';
import CommerceContactsTab from '@/components/commerces/CommerceContactsTab.vue';
import CommercePaymentsTab from '@/components/commerces/CommercePaymentsTab.vue';
import CommerceCampaignsTab from '@/components/commerces/campaigns/CommerceCampaignsTab.vue';
import CommerceModuleSettings from '@/components/commerces/CommerceModuleSettings.vue';
import { useUserStore } from '@/store/user.store';
import { userId as globalUserId } from '@/refs/user.refs';
import { getDateFormat } from '@/utils/formatDate';

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);

type PrimaryTab =
  | 'catalogue'
  | 'informations'
  | 'contact'
  | 'legal'
  | 'modules'
  | 'horaires'
  | 'medias'
  | 'users'
  | 'paiement'
  | 'menusEvenements';
type CatalogueTab =
  | 'produits'
  | 'assignation'
  | 'collections'
  | 'recommandations'
  | 'options'
  | 'promotions';

const _initTab = (route.query.tab as PrimaryTab) ?? 'catalogue';
const _initAction = route.query.action as string | undefined;
const activeTab = ref<PrimaryTab>(
  [
    'catalogue',
    'informations',
    'contact',
    'legal',
    'modules',
    'horaires',
    'medias',
    'users',
    'paiement',
    'menusEvenements',
  ].includes(_initTab)
    ? _initTab
    : 'catalogue',
);
const _initCatTab = route.query.catTab as CatalogueTab | undefined;
const activeCatTab = ref<CatalogueTab>(
  (
    [
      'produits',
      'assignation',
      'collections',
      'recommandations',
      'options',
      'promotions',
    ] as const
  ).includes(_initCatTab as any)
    ? (_initCatTab as CatalogueTab)
    : 'produits',
);
const catalogueView = ref<'grid' | 'list'>(
  window.innerWidth < 768 ? 'grid' : 'list',
);
const focusedCatId = ref<string | null>(null);

// Lien vers la fiche produit qui mémorise l'onglet courant, pour que le
// retour depuis ProductDetailPage/EditProductPage restaure exactement le
// même onglet plutôt que de retomber sur "catalogue / produits" par défaut.
function productLink(pid: string, edit = false) {
  const params = new URLSearchParams({
    from: id.value,
    tab: activeTab.value,
    catTab: activeCatTab.value,
  });
  return `/products/${pid}${edit ? '/edit' : ''}?${params}`;
}

function goToAssign(catId: string) {
  focusedCatId.value = catId;
  activeCatTab.value = 'assignation';
  catSearch.value = '';
  const idx = catSearchFiltered.value.findIndex((c) => c.id === catId);
  if (idx >= 0) {
    catPage.value = Math.floor(idx / catPageSize.value) + 1;
  }
  nextTick(() => {
    const el = document.querySelector(
      `[data-cat-id="${catId}"]`,
    ) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('cdp__assign-col-card--focus');
      setTimeout(
        () => el.classList.remove('cdp__assign-col-card--focus'),
        1800,
      );
    }
  });
}

const productSearch = ref('');

const PRIMARY_TABS = computed<
  { key: PrimaryTab; label: string; icon: string }[]
>(() => [
  {
    key: 'catalogue',
    label: t('commerceDetail.primaryTabs.catalogue'),
    icon: 'pi-box',
  },
  {
    key: 'informations',
    label: t('commerceDetail.primaryTabs.informations'),
    icon: 'pi-info-circle',
  },
  {
    key: 'contact',
    label: t('commerceDetail.primaryTabs.contact'),
    icon: 'pi-address-book',
  },
  {
    key: 'legal',
    label: t('commerceDetail.primaryTabs.legal'),
    icon: 'pi-file-edit',
  },
  {
    key: 'modules',
    label: t('commerceDetail.primaryTabs.modules'),
    icon: 'pi-th-large',
  },
  {
    key: 'horaires',
    label: t('commerceDetail.primaryTabs.horaires'),
    icon: 'pi-clock',
  },
  {
    key: 'medias',
    label: t('commerceDetail.primaryTabs.medias'),
    icon: 'pi-images',
  },
  {
    key: 'users',
    label: t('commerceDetail.primaryTabs.users'),
    icon: 'pi-users',
  },
  {
    key: 'paiement',
    label: t('commerceDetail.primaryTabs.paiement'),
    icon: 'pi-wallet',
  },
  {
    key: 'menusEvenements',
    label: t('commerceDetail.primaryTabs.menusEvenements'),
    icon: 'pi-megaphone',
  },
]);

const CATALOGUE_TABS = computed<{ key: CatalogueTab; label: string }[]>(() => [
  { key: 'produits', label: t('commerceDetail.catalogueTabs.produits') },
  { key: 'assignation', label: t('commerceDetail.catalogueTabs.assignation') },
  { key: 'collections', label: t('commerceDetail.catalogueTabs.collections') },
  {
    key: 'recommandations',
    label: t('commerceDetail.catalogueTabs.recommandations'),
  },
  { key: 'options', label: t('commerceDetail.catalogueTabs.options') },
  { key: 'promotions', label: t('commerceDetail.catalogueTabs.promotions') },
]);

// ── Groupes d'options ─────────────────────────────────────────────────────
const showAddGroup = ref(false);
const optsSearch = ref('');

// Fetch tous les extras de chaque produit du commerce (query activée seulement sur l'onglet options)
const optsEnabled = computed(
  () => activeCatTab.value === 'options' && !!id.value,
);

const { data: allProductsForOpts, isFetching: optsFetching } = useQuery({
  queryKey: computed(() => ['commerce-products-for-opts', id.value]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: id.value,
      pagination: 'false',
    });
    const r = await axiosInstance.get<IApiResponseProduct>(
      `products/search?${params}`,
    );
    const list: IProduct[] =
      (r.data as any)?.data?.data ?? (r.data as any)?.data ?? [];
    return list;
  },
  enabled: optsEnabled,
  staleTime: 10 * 60_000,
  gcTime: 30 * 60_000,
});

// Source principale de la liste des groupes — un seul appel, rapide, plutôt
// que le Promise.all par produit utilisé avant que cet endpoint de recherche
// dédié ne soit confirmé disponible.
const { data: extrasSearchResult, isFetching: extrasSearchFetching } = useQuery(
  {
    queryKey: computed(() => ['commerce-extras-search', id.value]),
    queryFn: async () => {
      const params = new URLSearchParams({ pagination: 'false' });
      params.append('storeId', id.value);
      const r = await axiosInstance.get(`extras/search?${params.toString()}`);
      return ((r.data as any)?.data ?? []) as any[];
    },
    enabled: optsEnabled,
  },
);
const extrasFetching = extrasSearchFetching;

// Source secondaire, en arrière-plan : uniquement pour enrichir chaque
// groupe avec ses "Produits liés" (extras/search ne renvoie pas ce lien).
// Ne bloque pas l'affichage de la liste principale, qui vient déjà de
// extrasSearchResult ci-dessus.
const { data: extrasMap } = useQuery({
  queryKey: computed(() => ['commerce-extras', id.value]),
  queryFn: async () => {
    const prods = allProductsForOpts.value ?? [];
    if (!prods.length) return {} as Record<string, any[]>;
    const results = await Promise.all(
      prods.map(async (p) => {
        try {
          const r = await axiosInstance.get(`product/${p.id}/extras`);
          const extras: any[] = (r.data as any)?.data ?? (r.data as any) ?? [];
          return { productId: p.id, productName: p.designation, extras };
        } catch {
          return { productId: p.id, productName: p.designation, extras: [] };
        }
      }),
    );
    const map: Record<
      string,
      { productId: string; productName: string; extras: any[] }[]
    > = {};
    for (const row of results) {
      for (const ex of row.extras) {
        const key =
          ex.id ??
          ex._id ??
          JSON.stringify({ title: ex.title, chooseType: ex.chooseType });
        if (!map[key]) map[key] = [];
        map[key].push({
          productId: row.productId,
          productName: row.productName,
          extras: row.extras,
        });
      }
    }
    return map;
  },
  enabled: computed(
    () => optsEnabled.value && !!allProductsForOpts.value?.length,
  ),
  staleTime: 10 * 60_000,
  gcTime: 30 * 60_000,
});

// Liste dédupliquée des groupes, avec leurs produits liés dès que la source
// secondaire (extrasMap) a résolu — vide entretemps, sans bloquer le reste.
interface IOptGroup {
  id: string;
  title: string;
  chooseType: string;
  extraType: string;
  required: boolean;
  minQty: number;
  maxQty: number;
  exactQty: number | null;
  options: { id: string; content: string; price: number }[];
  linkedProducts: { id: string; name: string }[];
  raw: any;
}

const linkedProductsByGroupId = computed(() => {
  const map = extrasMap.value ?? {};
  const result: Record<string, { id: string; name: string }[]> = {};
  for (const [key, rows] of Object.entries(map)) {
    const ex = rows[0]?.extras.find(
      (e: any) =>
        (e.id ?? e._id) === key ||
        JSON.stringify({ title: e.title, chooseType: e.chooseType }) === key,
    );
    const groupId = ex?.id ?? ex?._id ?? key;
    result[groupId] = rows.map((r) => ({
      id: r.productId,
      name: r.productName,
    }));
  }
  return result;
});

const optGroups = computed<IOptGroup[]>(() => {
  const items = extrasSearchResult.value ?? [];
  const groups: IOptGroup[] = [];
  for (const ex of items) {
    if (!ex.id) continue;
    const label =
      typeof ex.title === 'object'
        ? (ex.title.fr ??
          ex.title.en ??
          String(Object.values(ex.title)[0] ?? ''))
        : (ex.title ?? '');
    groups.push({
      id: ex.id,
      title: label,
      chooseType: ex.chooseType ?? ex.choiceType ?? 'unique',
      extraType: ex.extraType ?? '',
      required: !!ex.required,
      minQty: ex.minQty ?? 0,
      maxQty: ex.maxQty ?? 1,
      exactQty: ex.exactQty ?? null,
      options: (ex.option ?? ex.options ?? []).map((o: any) => ({
        id: o.id ?? o._id ?? '',
        content:
          typeof o.content === 'object'
            ? (o.content.fr ?? o.content.en ?? '')
            : (o.content ?? ''),
        price: parseFloat(o.price ?? '0'),
      })),
      linkedProducts: linkedProductsByGroupId.value[ex.id] ?? [],
      raw: ex,
    });
  }
  return groups;
});

const filteredOptGroups = computed(() => {
  const q = optsSearch.value.trim().toLowerCase();
  if (!q) return optGroups.value;
  return optGroups.value.filter((g) => g.title.toLowerCase().includes(q));
});

const optsPageSize = ref(20);
const optsPage = ref(1);
const optsTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredOptGroups.value.length / optsPageSize.value)),
);
const pagedOptGroups = computed(() => {
  const start = (optsPage.value - 1) * optsPageSize.value;
  return filteredOptGroups.value.slice(start, start + optsPageSize.value);
});
watch([filteredOptGroups, optsPageSize], () => {
  optsPage.value = 1;
});

function optsGoTo(p: number) {
  optsPage.value = Math.max(1, Math.min(p, optsTotalPages.value));
}

const optsLoading = computed(() => optsFetching.value || extrasFetching.value);

// ── Promotions ────────────────────────────────────────────────────────────
type PromoTrigger =
  'automatic' | 'code_required' | 'first_time_customer' | 'customer_segment';
type PromoType =
  | 'percentage'
  | 'fixed_amount'
  | 'buy_one_get_one'
  | 'quantity_discount'
  | 'bundle_discount';
type PromoStatus = 'draft' | 'active' | 'paused' | 'expired' | 'exhausted';

interface IPromotion {
  id: string;
  name: string;
  description?: string;
  type: PromoType;
  trigger: PromoTrigger;
  status: PromoStatus;
  value: number;
  code?: string;
  startDate?: string;
  endDate?: string;
  usageLimit?: number;
  usageLimitPerCustomer?: number;
  minimumOrderAmount?: number;
  stackable?: boolean;
  priority?: number;
  products?: any[];
  categories?: any[];
}

const promoEnabled = computed(
  () => activeCatTab.value === 'promotions' && !!id.value,
);
const promoFilter = ref<'all' | 'upsell' | 'code'>('all');
function goToNewPromo() {
  router.push(`/promotions/new?commerceId=${id.value}`);
}

const {
  data: promotionsResp,
  isFetching: promoFetching,
  refetch: refetchPromos,
} = useQuery({
  queryKey: computed(() => ['commerce-promotions', id.value]),
  queryFn: async () => {
    const params = new URLSearchParams({ pagination: 'false' });
    params.append('restaurantId', id.value);
    const r = await axiosInstance.get(`promotions/search?${params}`);
    const d = r.data as any;
    const list = d?.data?.data ?? d?.data ?? d ?? [];
    return (Array.isArray(list) ? list : []) as IPromotion[];
  },
  enabled: promoEnabled,
  staleTime: 30_000,
});

const promotions = computed<IPromotion[]>(() => promotionsResp.value ?? []);

const upsellPromos = computed(() =>
  promotions.value.filter(
    (p) => p.trigger === 'automatic' || p.trigger === 'first_time_customer',
  ),
);
const codePromos = computed(() =>
  promotions.value.filter((p) => p.trigger === 'code_required'),
);

const promoKpis = computed(() => ({
  active: promotions.value.filter((p) => p.status === 'active').length,
  codes: codePromos.value.length,
  upsells: upsellPromos.value.length,
}));

const TYPE_LABELS = computed<Record<PromoType, string>>(() => ({
  percentage: t('commerceDetail.typeLabels.percentage'),
  fixed_amount: t('commerceDetail.typeLabels.fixed_amount'),
  buy_one_get_one: t('commerceDetail.typeLabels.buy_one_get_one'),
  quantity_discount: t('commerceDetail.typeLabels.quantity_discount'),
  bundle_discount: t('commerceDetail.typeLabels.bundle_discount'),
}));

const STATUS_CLASS: Record<PromoStatus, string> = {
  active: 'promo__status--active',
  draft: 'promo__status--draft',
  paused: 'promo__status--paused',
  expired: 'promo__status--muted',
  exhausted: 'promo__status--muted',
};

async function togglePromoStatus(p: IPromotion) {
  const next: PromoStatus = p.status === 'active' ? 'paused' : 'active';
  try {
    await axiosInstance.put(`promotions/update/${p.id}`, {
      name: p.name,
      status: next,
      trigger: p.trigger,
      type: p.type,
      value: p.value,
    });
    refetchPromos();
  } catch {}
}

async function deletePromo(p: IPromotion) {
  if (!confirm(t('commerceDetail.confirmDeleteProduct', { name: p.name })))
    return;
  try {
    await axiosInstance.delete(`promotions/delete/${p.id}`);
    refetchPromos();
  } catch {}
}

// ── Recommandations ───────────────────────────────────────────────────────
const recSearch = ref('');
const recProcessing = ref(new Set<string>());
const recConfirmProd = ref<any>(null);

const recEnabled = computed(
  () => activeCatTab.value === 'recommandations' && !!id.value,
);

const {
  data: recProductsResp,
  isFetching: recFetching,
  refetch: refetchRecProds,
} = useQuery({
  queryKey: computed(() => ['rec-products', id.value]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: id.value,
      pagination: 'false',
      sortBy: 'recommanded',
      sortOrder: 'DESC',
    });
    const r = await axiosInstance.get(`products/search?${params}`);
    const d = r.data as any;
    return (d?.data?.data ?? d?.data ?? []) as any[];
  },
  enabled: recEnabled,
  staleTime: 30_000,
});

const { data: recListResp, refetch: refetchRecList } = useQuery({
  queryKey: computed(() => ['rec-list', id.value]),
  queryFn: async () => {
    const params = new URLSearchParams({ restaurantId: id.value });
    const r = await axiosInstance.get(
      `products/search-recommendation?${params}`,
    );
    const d = r.data as any;
    return (d?.data ?? []) as any[];
  },
  enabled: recEnabled,
  staleTime: 30_000,
});

const recList = computed<any[]>(() => recListResp.value ?? []);
const recProducts = computed<any[]>(() => {
  const all = recProductsResp.value ?? [];
  const recommendedIds = new Set(
    recList.value.map((r: any) => r?.product?.id).filter(Boolean),
  );
  return all.map((p: any) => ({
    ...p,
    isRecommanded: recommendedIds.has(p.id),
  }));
});

const recPageSize = ref(20);
const recPage = ref(1);

const filteredRecProds = computed(() => {
  const q = recSearch.value.trim().toLowerCase();
  const list = recProducts.value.filter(
    (p) => !q || p.designation?.toLowerCase().includes(q),
  );
  // Produits recommandés toujours en tête, triés par date de modification
  // la plus récente d'abord (le dernier produit recommandé/modifié en premier).
  return [...list].sort((a, b) => {
    if (a.isRecommanded !== b.isRecommanded) return a.isRecommanded ? -1 : 1;
    const aDate = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
    const bDate = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
    return bDate - aDate;
  });
});

const recTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredRecProds.value.length / recPageSize.value)),
);
const pagedRecProds = computed(() => {
  const start = (recPage.value - 1) * recPageSize.value;
  return filteredRecProds.value.slice(start, start + recPageSize.value);
});

watch([filteredRecProds, recPageSize], () => {
  recPage.value = 1;
});

function recGoTo(p: number) {
  recPage.value = Math.max(1, Math.min(p, recTotalPages.value));
}

function recClickPageItem(item: { type: string; n: number }) {
  if (item.type === 'page') recGoTo(item.n);
}

const recPageItems = computed(() => {
  const total = recTotalPages.value;
  const cur = recPage.value;
  const items: (
    { type: 'page'; n: number } | { type: 'ellipsis'; n: number }
  )[] = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || Math.abs(i - cur) <= 1) {
      items.push({ type: 'page', n: i });
    } else if (items[items.length - 1]?.type !== 'ellipsis') {
      items.push({ type: 'ellipsis', n: i });
    }
  }
  return items;
});

const recCount = computed(
  () => recProducts.value.filter((p) => p.isRecommanded).length,
);

function closeRecConfirm() {
  recConfirmProd.value = null;
}

async function confirmUnrecommend() {
  if (!recConfirmProd.value) return;
  await toggleRecommend(recConfirmProd.value);
  closeRecConfirm();
}

function handleRecToggleClick(p: any) {
  if (p.isRecommanded) {
    recConfirmProd.value = p;
  } else {
    toggleRecommend(p);
  }
}

async function toggleRecommend(p: any) {
  if (recProcessing.value.has(p.id)) return;
  recProcessing.value = new Set([...recProcessing.value, p.id]);
  try {
    if (!p.isRecommanded) {
      await axiosInstance.put('products/recommend', {
        productId: [p.id],
        restaurantId: id.value,
        targetProductId: p.id,
      });
    } else {
      const rec = recList.value.find((r: any) => r?.product?.id === p.id);
      if (rec?.id) {
        await axiosInstance.delete('products/unrecommend', {
          data: { recommendationId: [rec.id] },
        });
      }
    }
    await Promise.all([refetchRecProds(), refetchRecList()]);
  } finally {
    const s = new Set(recProcessing.value);
    s.delete(p.id);
    recProcessing.value = s;
  }
}

function onGroupSaved() {
  queryClient.invalidateQueries({ queryKey: ['commerce-extras', id.value] });
  queryClient.invalidateQueries({
    queryKey: ['commerce-extras-search', id.value],
  });
  queryClient.invalidateQueries({
    queryKey: ['commerce-products-for-opts', id.value],
  });
}

// Édition d'un groupe d'options existant (drawer dédié, plus de navigation
// vers un produit lié — un groupe peut être partagé par plusieurs produits).
const editingGroup = ref<IOptGroup | null>(null);
const editGroupDrawerOpen = ref(false);
function openGroupEdit(grp: IOptGroup) {
  editingGroup.value = grp;
  editGroupDrawerOpen.value = true;
}
function onGroupDeleted() {
  editingGroup.value = null;
}

const {
  data: store,
  isPending,
  isError,
} = useQuery({
  queryKey: computed(() => ['commerce-detail', id.value]),
  queryFn: () =>
    CommerceService.getStoreById(id.value).then((r) => {
      const d = r.data as any;
      return d?.data ?? d;
    }),
  enabled: computed(() => !!id.value),
});

const isActive = computed(() => store.value?.isActive !== false);

// ── Tab Modules : état réel on/off, lu/écrit via restaurants/:id/settings ─────
// (CommerceModuleSettings.vue le confirme déjà en commentaire : "le on/off,
// déjà géré par la carte" — mais avant ce fix, seule la carte CNC lisait une
// donnée réelle, et encore, le mauvais champ (store.isActive, le statut
// global du commerce, pas modules.click_and_collect) ; les 3 autres cartes
// étaient codées en dur.)
const MODULE_KEYS = [
  'modules.click_and_collect',
  'modules.pds_display',
  'modules.pos_register',
  'modules.kiosk',
] as const;
type TModuleKey = (typeof MODULE_KEYS)[number];

const { data: moduleSettingsData, refetch: refetchModuleSettings } = useQuery({
  queryKey: computed(() => ['commerce-module-settings', id.value]),
  queryFn: () =>
    StoreSettingsService.getStoreSettings(id.value).then((r) => {
      const d = r.data as any;
      return d?.data ?? d;
    }),
  enabled: computed(() => !!id.value && activeTab.value === 'modules'),
});

const moduleValues = computed<Record<TModuleKey, boolean>>(() => {
  const categories = moduleSettingsData.value?.categories ?? [];
  const modulesCategory = categories.find((c: any) => c.category === 'modules');
  const entries: any[] = modulesCategory?.settings ?? [];
  const map = new Map(entries.map((e) => [e.key, e.value]));
  return {
    'modules.click_and_collect': !!map.get('modules.click_and_collect'),
    'modules.pds_display': !!map.get('modules.pds_display'),
    'modules.pos_register': !!map.get('modules.pos_register'),
    'modules.kiosk': !!map.get('modules.kiosk'),
  };
});

const MODULE_LABEL_KEYS: Record<TModuleKey, string> = {
  'modules.click_and_collect': 'commerceDetail.modulesCnc',
  'modules.pds_display': 'commerceDetail.modulesPds',
  'modules.pos_register': 'commerceDetail.modulesCaisse',
  'modules.kiosk': 'commerceDetail.modulesKiosk',
};

const togglingModuleKey = ref<TModuleKey | null>(null);
async function toggleModule(key: TModuleKey) {
  if (!id.value || togglingModuleKey.value) return;
  const nextValue = !moduleValues.value[key];
  togglingModuleKey.value = key;
  try {
    await StoreSettingsService.updateStoreSettings(id.value, {
      [key]: nextValue,
    });
    await refetchModuleSettings();
    (GlobalSucessMessage as any).value = t(
      nextValue
        ? 'commerceDetail.moduleActivated'
        : 'commerceDetail.moduleDeactivated',
      { module: t(MODULE_LABEL_KEYS[key]) },
    );
    isGlobalSucess.value = true;
    setTimeout(() => {
      isGlobalSucess.value = false;
    }, 3000);
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    isGlobalError.value = true;
    GlobalErrorMessage.value = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.moduleUpdateError'));
    setTimeout(() => {
      isGlobalError.value = false;
    }, 3000);
  } finally {
    togglingModuleKey.value = null;
  }
}

// ── Tab Modules : paramétrage par module (accordion) ──────────────────────────
const openModuleSettingsKey = ref<string | null>(null);
function openModuleSettings(key: string) {
  openModuleSettingsKey.value = key;
}
function closeModuleSettings() {
  openModuleSettingsKey.value = null;
  moduleSubViewTitle.value = null;
}

// Sous-vue plein écran ouverte à l'intérieur de CommerceModuleSettings
// (créneaux de collecte, modification & annulation) — remplace le titre du
// fil d'Ariane au lieu d'empiler un second header sous "Paramètres — X".
const moduleSubViewTitle = ref<string | null>(null);
const moduleSettingsRef = ref<InstanceType<
  typeof CommerceModuleSettings
> | null>(null);
function handleModuleBack() {
  if (moduleSubViewTitle.value) {
    moduleSettingsRef.value?.closeSubView();
    return;
  }
  closeModuleSettings();
}

// ── Checklist "Slug / URL" : scroll + halo temporaire vers la section réelle
// (onglet Modules), pour montrer où configurer sans laisser l'utilisateur
// chercher lui-même. ─────────────────────────────────────────────────────────
const slugSectionRef = ref<HTMLElement | null>(null);
const highlightSlugSection = ref(false);
async function goToSlugSection() {
  activeTab.value = 'modules';
  openModuleSettingsKey.value = null;
  await nextTick();
  slugSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  highlightSlugSection.value = true;
  setTimeout(() => {
    highlightSlugSection.value = false;
  }, 2000);
}
// t() interprète les points comme des séparateurs de niveau : une clé JSON
// contenant elle-même des points (ex. "modules.click_and_collect") ne peut
// pas être résolue via un chemin i18n imbriqué, d'où ce mapping direct.
const MODULE_SETTINGS_TITLE_KEYS: Record<string, string> = {
  'modules.click_and_collect': 'commerceDetail.moduleSettingsTitleCnc',
  'modules.pds_display': 'commerceDetail.moduleSettingsTitlePds',
  'modules.pos_register': 'commerceDetail.moduleSettingsTitlePos',
  'modules.kiosk': 'commerceDetail.moduleSettingsTitleKiosk',
};
const moduleSettingsBaseTitle = computed(() =>
  openModuleSettingsKey.value
    ? t(
        MODULE_SETTINGS_TITLE_KEYS[openModuleSettingsKey.value] ??
          'commerceDetail.moduleSettingsTitle',
      )
    : '',
);
const openModuleSettingsTitle = computed(
  () => moduleSubViewTitle.value ?? moduleSettingsBaseTitle.value,
);

// Query dédiée (même queryKey que CommerceContactsTab.vue, onglet Contact) —
// pour que le résumé affiché ici dans Informations reste synchronisé sans
// dépendre d'un refetch de commerce-detail, qui n'inclut pas les contacts à
// jour après une modification faite dans l'onglet Contact.
const { data: contactsResp } = useQuery({
  queryKey: computed(() => ['store-contacts', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/${id.value}/contacts/search`,
    );
    const d = r.data as any;
    return (d?.data ?? d ?? []) as any[];
  },
  enabled: computed(() => !!id.value),
});
const contacts = computed<any[]>(() => contactsResp.value ?? []);
const slugs = computed<any[]>(() => store.value?.slugs ?? []);
const logoUrl = computed(
  () => store.value?.logo?.info?.secureUrl ?? store.value?.logoUrl ?? null,
);
const primarySlug = computed(
  () =>
    slugs.value.find((s: any) => s.isPrimary)?.slug ??
    slugs.value[0]?.slug ??
    null,
);

/* ── Tab Modules : slug ──────────────────────────────────────────────────── */
const modSlug = ref('');
const modSlugChecking = ref(false);
const modSlugSaving = ref(false);
const modSlugStatus = ref<'idle' | 'available' | 'taken'>('idle');
const modSlugSaved = ref(false);
const modSlugSuggestions = ref<string[]>([]);

watch(
  primarySlug,
  (v) => {
    // Ne réinitialise pas si l'utilisateur est en train d'éditer ou vient de sauvegarder
    if (!modSlugSaved.value && !modSlugChecking.value && !modSlugSaving.value) {
      modSlug.value = v ?? '';
    }
  },
  { immediate: true },
);

// Debounce : sans lui, chaque caractère tapé envoie une requête et fait
// apparaître/disparaître les suggestions, ce qui fait bouger l'interface
// pendant la frappe.
let modSlugDebounce: ReturnType<typeof setTimeout> | null = null;
watch(modSlug, (val) => {
  modSlugStatus.value = 'idle';
  modSlugSaved.value = false;
  modSlugSuggestions.value = [];
  if (modSlugDebounce) clearTimeout(modSlugDebounce);
  if (!val || val.trim().length < 2) return;
  modSlugDebounce = setTimeout(async () => {
    try {
      const r = await axiosInstance.get(
        `restaurants/slugs/candidates?text=${encodeURIComponent(val.trim())}&count=5`,
      );
      modSlugSuggestions.value =
        r.data?.data?.data?.candidates ??
        r.data?.data?.candidates ??
        r.data?.candidates ??
        [];
    } catch {
      /* silencieux */
    }
  }, 400);
});

async function checkAndSaveSlug() {
  const slug = modSlug.value.trim();
  if (!slug || modSlugChecking.value || modSlugSaving.value) return;

  // Si déjà le slug actuel → rien à faire
  if (slug === primarySlug.value) return;

  modSlugChecking.value = true;
  modSlugStatus.value = 'idle';
  modSlugSuggestions.value = [];
  try {
    const r = await axiosInstance.get(
      `restaurants/slugs/is-avalaible?slug=${encodeURIComponent(slug)}`,
    );
    const available = r.data?.data?.isAvalaible ?? r.data?.isAvalaible ?? false;

    if (!available) {
      modSlugStatus.value = 'taken';
      // suggestions
      try {
        const s = await axiosInstance.get(
          `restaurants/slugs/candidates?text=${encodeURIComponent(slug)}&count=5`,
        );
        modSlugSuggestions.value =
          s.data?.data?.candidates ?? s.data?.candidates ?? [];
      } catch {
        /* silencieux */
      }
      return;
    }

    // Disponible → mise à jour directe
    modSlugStatus.value = 'available';
    modSlugSaving.value = true;
    const existingSlug =
      slugs.value.find((s: any) => s.isPrimary) ?? slugs.value[0];
    if (existingSlug?.id) {
      await axiosInstance.put(`restaurants/slugs/update/${existingSlug.id}`, {
        slug,
        setPrimary: true,
      });
    } else {
      await axiosInstance.post('restaurants/slugs/add', {
        slug,
        restaurantId: id.value,
        setPrimary: true,
      });
    }
    modSlugSaved.value = true;
    // Mise à jour immédiate du cache → storeUrl + "Voir en ligne" + "Ouvrir" réagissent sans refetch
    queryClient.setQueryData(['commerce-detail', id.value], (old: any) => {
      if (!old) return old;
      const prevSlugs: any[] = old?.slugs ?? [];
      const updatedSlugs = prevSlugs.map((s: any) =>
        s.isPrimary ? { ...s, slug } : s,
      );
      if (!updatedSlugs.some((s: any) => s.isPrimary)) {
        updatedSlugs.push({ slug, isPrimary: true, restaurantId: id.value });
      }
      return { ...old, slugs: updatedSlugs };
    });
  } catch (e: any) {
    modSlugStatus.value = 'idle';
  } finally {
    modSlugChecking.value = false;
    modSlugSaving.value = false;
  }
}

function pickSlugSuggestion(s: string) {
  modSlug.value = s;
  modSlugStatus.value = 'idle';
  modSlugSuggestions.value = [];
  checkAndSaveSlug();
}

const storeUrl = computed(() => {
  if (!primarySlug.value) return null;
  return buildStorefrontUrl(primarySlug.value);
});

/* Sync org ID + dernier commerce visité */
watch(
  () => store.value,
  (s) => {
    if (!s) return;
    const orgId = s.organizationId ?? s.organization?.id ?? null;
    if (orgId) organizationIdCommerce.value = orgId;
    if (id.value) lastCommerceId.value = id.value;
  },
  { immediate: true },
);

/* ── Tab Médias ───────────────────────────────────────────────────────────── */
type FeaturedRole =
  'onboarding_image' | 'banner' | 'story' | 'product_highlight';

const FEATURED_ROLES = computed<
  { role: FeaturedRole; label: string; icon: string }[]
>(() => [
  {
    role: 'banner',
    label: t('commerceDetail.featuredRoles.banner'),
    icon: 'pi-th-large',
  },
]);

const medEnabled = computed(() => activeTab.value === 'medias' && !!id.value);

function extractFeaturedList(raw: any): any[] {
  const d = raw?.data ?? raw;
  return d?.featuredImages ?? d?.data ?? (Array.isArray(d) ? d : []);
}

const {
  data: mediaBanners,
  isFetching: mediaBannersFetching,
  refetch: refetchBanners,
} = useQuery({
  queryKey: computed(() => ['media-banners', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/banner/get-by-store/${id.value}`,
    );
    return extractFeaturedList(r.data);
  },
  enabled: medEnabled,
  staleTime: 30_000,
});
const {
  data: mediaOnboarding,
  isFetching: mediaOnboardingFetching,
  refetch: refetchOnboarding,
} = useQuery({
  queryKey: computed(() => ['media-onboarding', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/onboarding_image/get-by-store/${id.value}`,
    );
    return extractFeaturedList(r.data);
  },
  enabled: medEnabled,
  staleTime: 30_000,
});
const {
  data: mediaStory,
  isFetching: mediaStoryFetching,
  refetch: refetchStory,
} = useQuery({
  queryKey: computed(() => ['media-story', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/story/get-by-store/${id.value}`,
    );
    return extractFeaturedList(r.data);
  },
  enabled: medEnabled,
  staleTime: 30_000,
});
const {
  data: mediaHighlight,
  isFetching: mediaHighlightFetching,
  refetch: refetchHighlight,
} = useQuery({
  queryKey: computed(() => ['media-highlight', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/product_highlight/get-by-store/${id.value}`,
    );
    return extractFeaturedList(r.data);
  },
  enabled: medEnabled,
  staleTime: 30_000,
});

// Le backend ne filtre pas encore par thème sur get-by-store (il renvoie
// tous les logos du commerce, peu importe x-app-theme) — on filtre donc
// côté client sur metadata.theme pour séparer correctement light/dark.
function filterLogosByTheme(list: any[], theme: 'light' | 'dark'): any[] {
  return list.filter((item) => item?.metadata?.theme === theme);
}

const {
  data: mediaLogoDark,
  isFetching: mediaLogoDarkFetching,
  refetch: refetchLogoDark,
} = useQuery({
  queryKey: computed(() => ['media-logo-dark', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/logo/get-by-store/${id.value}`,
      { headers: { 'x-app-theme': 'dark' } },
    );
    return filterLogosByTheme(extractFeaturedList(r.data), 'dark');
  },
  enabled: medEnabled,
  staleTime: 30_000,
});
const {
  data: mediaLogoLight,
  isFetching: mediaLogoLightFetching,
  refetch: refetchLogoLight,
} = useQuery({
  queryKey: computed(() => ['media-logo-light', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/featured-images/logo/get-by-store/${id.value}`,
      { headers: { 'x-app-theme': 'light' } },
    );
    return filterLogosByTheme(extractFeaturedList(r.data), 'light');
  },
  enabled: medEnabled,
  staleTime: 30_000,
});

const banners = computed<any[]>(() => mediaBanners.value ?? []);
const onboardings = computed<any[]>(() => mediaOnboarding.value ?? []);
const stories = computed<any[]>(() => mediaStory.value ?? []);
const highlights = computed<any[]>(() => mediaHighlight.value ?? []);
const logoDarkItem = computed(() => mediaLogoDark.value?.[0] ?? null);
const logoDarkUrl = computed(() =>
  logoDarkItem.value ? mediaUrl(logoDarkItem.value) : null,
);
const logoLightItem = computed(() => mediaLogoLight.value?.[0] ?? null);
const logoLightUrl = computed(() =>
  logoLightItem.value ? mediaUrl(logoLightItem.value) : null,
);

function itemsForRole(role: FeaturedRole): any[] {
  if (role === 'banner') return banners.value;
  if (role === 'onboarding_image') return onboardings.value;
  if (role === 'story') return stories.value;
  return highlights.value;
}
function isFetchingRole(role: FeaturedRole): boolean {
  if (role === 'banner') return mediaBannersFetching.value;
  if (role === 'onboarding_image') return mediaOnboardingFetching.value;
  if (role === 'story') return mediaStoryFetching.value;
  return mediaHighlightFetching.value;
}
function refetchRole(role: FeaturedRole) {
  if (role === 'banner') refetchBanners();
  else if (role === 'onboarding_image') refetchOnboarding();
  else if (role === 'story') refetchStory();
  else refetchHighlight();
}

function mediaUrl(item: any): string {
  return (
    item?.imageUrl ??
    item?.media?.info?.secureUrl ??
    item?.media?.info?.url ??
    ''
  );
}

const medUploading = ref(false);
const medUploadError = ref<string | null>(null);
const medDeleting = ref<string | null>(null);
const confirmDeleteId = ref<string | null>(null);

function askDeleteFeatured(item: any) {
  const iid = item.id ?? item.mediaId;
  confirmDeleteId.value = confirmDeleteId.value === iid ? null : iid;
}
function cancelDeleteFeatured() {
  confirmDeleteId.value = null;
}
const medLogoUploading = ref(false);

/* ── Formulaire ajout bannière inline ──────────────────────────────────── */
interface IFeaturedForm {
  file: File | null;
  previewSrc: string | null;
  displayOrder: number;
  isActive: boolean;
  startDate: string;
  endDate: string;
  metaTitle: string;
  metaDescription: string;
  metaSlideDuration: string;
  metaSkipAllowed: boolean;
}

const showAddForm = ref(false);
const featuredForm = ref<IFeaturedForm>({
  file: null,
  previewSrc: null,
  displayOrder: 1,
  isActive: true,
  startDate: '',
  endDate: '',
  metaTitle: '',
  metaDescription: '',
  metaSlideDuration: '5000',
  metaSkipAllowed: true,
});

function resetFeaturedForm() {
  featuredForm.value = {
    file: null,
    previewSrc: null,
    displayOrder: 1,
    isActive: true,
    startDate: '',
    endDate: '',
    metaTitle: '',
    metaDescription: '',
    metaSlideDuration: '5000',
    metaSkipAllowed: true,
  };
}

function toggleAddForm(role: FeaturedRole) {
  showAddForm.value = !showAddForm.value;
  if (showAddForm.value) {
    featuredForm.value.displayOrder = itemsForRole(role).length + 1;
  } else {
    resetFeaturedForm();
  }
  medUploadError.value = null;
}

function onFeaturedFilePick(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  featuredForm.value.file = file;
  featuredForm.value.previewSrc = URL.createObjectURL(file);
}

function openFeaturedFilePicker() {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'image/*';
  inp.onchange = onFeaturedFilePick as any;
  inp.click();
}

function toggleFeaturedFormActive() {
  featuredForm.value.isActive = !featuredForm.value.isActive;
}

async function uploadFileToStorage(
  file: File,
  designation: string,
): Promise<string | null> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('designation', designation);
  fd.append('restaurantId', id.value);
  const r = await axiosInstance.post('files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const d = r.data as any;
  return d?.data?.id ?? d?.id ?? null;
}

async function uploadFeatured(role: FeaturedRole) {
  if (!featuredForm.value.file || medUploading.value) return;
  medUploading.value = true;
  medUploadError.value = null;
  try {
    const f = featuredForm.value;
    const fd = new FormData();
    fd.append('restaurantId', id.value);
    fd.append('displayOrder', String(f.displayOrder));
    fd.append('isActive', String(f.isActive));
    if (f.startDate)
      fd.append('startDate', new Date(f.startDate).toISOString());
    if (f.endDate) fd.append('endDate', new Date(f.endDate).toISOString());
    if (f.metaTitle) fd.append('metadata[title]', f.metaTitle);
    if (f.metaDescription)
      fd.append('metadata[description]', f.metaDescription);
    if (f.metaSlideDuration)
      fd.append('metadata[slideDuration]', f.metaSlideDuration);
    fd.append('metadata[skipAllowed]', String(f.metaSkipAllowed));
    if (storeUrl.value) fd.append('metadata[redirectUrl]', storeUrl.value);
    fd.append('file', f.file);
    await axiosInstance.post(`restaurants/featured-images/${role}/add`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    showAddForm.value = false;
    resetFeaturedForm();
    refetchRole(role);
  } catch (e: any) {
    const msgs: string[] = e?.response?.data?.message ?? [];
    medUploadError.value =
      Array.isArray(msgs) && msgs.length
        ? msgs.join(' · ')
        : t('commerceDetail.imageUploadError');
  } finally {
    medUploading.value = false;
  }
}

async function deleteFeatured(role: FeaturedRole, item: any) {
  if (medDeleting.value) return;
  const mediaId = item.mediaId ?? item.id;
  medDeleting.value = mediaId;
  try {
    await axiosInstance.delete(
      `restaurants/featured-images/${role}/delete/${mediaId}`,
    );
    confirmDeleteId.value = null;
    refetchRole(role);
  } catch {
    /* silencieux */
  } finally {
    medDeleting.value = null;
  }
}

/* ── Édition featured image ─────────────────────────────────────────────── */
interface IFeaturedEdit {
  role: FeaturedRole;
  mediaId: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}
const featuredEditId = ref<string | null>(null); // id de l'item en cours d'édition
const featuredEdit = ref<IFeaturedEdit | null>(null);
const featuredEditSaving = ref(false);
const featuredEditError = ref<string | null>(null);

function openFeaturedEdit(role: FeaturedRole, item: any) {
  const iid = item.id ?? item.mediaId;
  if (featuredEditId.value === iid) {
    featuredEditId.value = null;
    featuredEdit.value = null;
    return;
  }
  featuredEditId.value = iid;
  featuredEdit.value = {
    role,
    mediaId: item.mediaId ?? item.id,
    title: item.metadata?.title ?? '',
    description: item.metadata?.description ?? '',
    startDate: item.startDate ? item.startDate.slice(0, 16) : '',
    endDate: item.endDate ? item.endDate.slice(0, 16) : '',
    isActive: !!item.isActive,
  };
  featuredEditError.value = null;
}

function closeFeaturedEdit() {
  featuredEditId.value = null;
  featuredEdit.value = null;
}

function toggleFeaturedEditActive() {
  if (featuredEdit.value)
    featuredEdit.value.isActive = !featuredEdit.value.isActive;
}

async function saveFeaturedEdit() {
  if (!featuredEdit.value || featuredEditSaving.value) return;
  featuredEditSaving.value = true;
  featuredEditError.value = null;
  try {
    const { role, mediaId: mId } = featuredEdit.value;
    await axiosInstance.put(
      `restaurants/featured-images/${role}/update/${mId}`,
      {
        isActive: featuredEdit.value.isActive,
        startDate: featuredEdit.value.startDate || undefined,
        endDate: featuredEdit.value.endDate || undefined,
        metadata: {
          title: featuredEdit.value.title,
          description: featuredEdit.value.description,
        },
      },
    );
    featuredEditId.value = null;
    featuredEdit.value = null;
    refetchRole(role);
  } catch (e: any) {
    const msgs: string[] = e?.response?.data?.message ?? [];
    featuredEditError.value =
      Array.isArray(msgs) && msgs.length
        ? msgs.join(' ')
        : 'Une erreur est survenue.';
  } finally {
    featuredEditSaving.value = false;
  }
}

/* ── Réordonnement featured images ─────────────────────────────────────── */
const medReordering = ref(false);

async function moveFeatured(
  role: FeaturedRole,
  items: any[],
  fromIdx: number,
  dir: 1 | -1,
) {
  const toIdx = fromIdx + dir;
  if (toIdx < 0 || toIdx >= items.length || medReordering.value) return;
  medReordering.value = true;
  try {
    const reordered = [...items];
    const tmp = reordered[fromIdx];
    reordered[fromIdx] = reordered[toIdx];
    reordered[toIdx] = tmp;
    const data = reordered.map((item, i) => ({
      mediaId: item.mediaId ?? item.id,
      displayOrder: i + 1,
    }));
    await axiosInstance.put(
      `restaurants/featured-images/${role}/set-order/${id.value}`,
      { data },
    );
    role === 'banner' ? refetchBanners() : refetchOnboarding();
  } catch {
    /* silencieux */
  } finally {
    medReordering.value = false;
  }
}

/* ── Copier URL ─────────────────────────────────────────────────────────── */
const copiedUrl = ref<string | null>(null);
async function copyUrl(url: string) {
  try {
    await navigator.clipboard.writeText(url);
    copiedUrl.value = url;
    setTimeout(() => {
      if (copiedUrl.value === url) copiedUrl.value = null;
    }, 2000);
  } catch {
    /* silencieux */
  }
}

/* ── Section Fichiers ─────────────────────────────────────────────────── */
const filesEnabled = computed(() => activeTab.value === 'medias' && !!id.value);
const filesPage = ref(1);
const filesPageSize = ref<12 | 24 | 48>(24);
const FILES_PAGE_SIZE_OPTIONS = [12, 24, 48] as const;
const filesSearch = ref('');
let filesDebTimer = 0;
const filesQ = ref('');

function onFilesSearchInput() {
  clearTimeout(filesDebTimer);
  filesDebTimer = window.setTimeout(() => {
    filesQ.value = filesSearch.value;
    filesPage.value = 1;
  }, 350);
}

function setFilesPageSize(size: 12 | 24 | 48) {
  filesPageSize.value = size;
  filesPage.value = 1;
}

const {
  data: filesData,
  isFetching: filesFetching,
  refetch: refetchFiles,
} = useQuery({
  queryKey: computed(() => [
    'commerce-files',
    id.value,
    filesPage.value,
    filesPageSize.value,
    filesQ.value,
  ]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: id.value,
      pagination: 'true',
      page: String(filesPage.value),
      pageSize: String(filesPageSize.value),
    });
    if (filesQ.value) params.set('q', filesQ.value);
    const r = await axiosInstance.get(`files/search?${params}`);
    const d = r.data as any;
    return {
      items: (d?.data?.data ?? d?.data ?? d?.items ?? []) as any[],
      total: d?.data?.total ?? d?.total ?? 0,
    };
  },
  enabled: filesEnabled,
  staleTime: 30_000,
});

const filesList = computed<any[]>(() => filesData.value?.items ?? []);
const filesTotal = computed(() => filesData.value?.total ?? 0);
const filesTotalPages = computed(() =>
  Math.max(1, Math.ceil(filesTotal.value / filesPageSize.value)),
);

const fileUploading = ref(false);
async function uploadRawFile(file: File) {
  fileUploading.value = true;
  try {
    await uploadFileToStorage(file, file.name);
    refetchFiles();
  } catch {
    /* silencieux */
  } finally {
    fileUploading.value = false;
  }
}

const deletingFileId = ref<string | null>(null);
async function deleteFile(file: any) {
  if (deletingFileId.value) return;
  const fileId = file.id ?? file._id;
  deletingFileId.value = fileId;
  try {
    await axiosInstance.delete(`files/delete/${fileId}`);
    refetchFiles();
  } catch {
    /* silencieux */
  } finally {
    deletingFileId.value = null;
  }
}

function filePreviewUrl(file: any): string {
  return (
    file?.info?.secureUrl ??
    file?.info?.url ??
    file?.secureUrl ??
    file?.url ??
    ''
  );
}

function isImage(file: any): boolean {
  const url = filePreviewUrl(file);
  return /\.(jpg|jpeg|png|gif|webp|svg|avif)(\?|$)/i.test(url);
}

function fileExtLabel(file: any): string {
  const url = filePreviewUrl(file) || file?.name || '';
  const m = url.match(/\.([a-z0-9]+)(\?|$)/i);
  return m ? m[1].toUpperCase() : '—';
}

function fileIcon(file: any): string {
  const ext = fileExtLabel(file).toLowerCase();
  if (['pdf'].includes(ext)) return 'pi pi-file-pdf';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'pi pi-file-excel';
  if (['doc', 'docx'].includes(ext)) return 'pi pi-file-word';
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'pi pi-video';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'pi pi-volume-up';
  if (['zip', 'rar', 'tar', 'gz'].includes(ext)) return 'pi pi-box';
  return 'pi pi-file';
}

async function uploadLogo(file: File) {
  medLogoUploading.value = true;
  try {
    const logoId = await uploadFileToStorage(file, 'Logo');
    if (logoId) {
      await axiosInstance.put(`restaurants/update/${id.value}`, { logoId });
      queryClient.invalidateQueries({
        queryKey: ['commerce-detail', id.value],
      });
    }
  } catch {
    /* silencieux */
  } finally {
    medLogoUploading.value = false;
  }
}

const medLogoDarkUploading = ref(false);
async function uploadLogoDark(file: File) {
  medLogoDarkUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('restaurantId', id.value);
    fd.append('displayOrder', '1');
    fd.append('isActive', 'true');
    fd.append('metadata[theme]', 'dark');
    fd.append('file', file);
    if (logoDarkItem.value) {
      const mediaId =
        logoDarkItem.value.mediaId ??
        logoDarkItem.value.media?.id ??
        logoDarkItem.value.id;
      await axiosInstance.delete(
        `restaurants/featured-images/logo/delete/${mediaId}`,
        { headers: { 'x-app-theme': 'dark' } },
      );
    }
    await axiosInstance.post('restaurants/featured-images/logo/add', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-app-theme': 'dark',
      },
    });
    refetchLogoDark();
  } catch {
    /* silencieux */
  } finally {
    medLogoDarkUploading.value = false;
  }
}

const medLogoLightUploading = ref(false);
async function uploadLogoLight(file: File) {
  medLogoLightUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('restaurantId', id.value);
    fd.append('displayOrder', '1');
    fd.append('isActive', 'true');
    fd.append('metadata[theme]', 'light');
    fd.append('file', file);
    if (logoLightItem.value) {
      const mediaId =
        logoLightItem.value.mediaId ??
        logoLightItem.value.media?.id ??
        logoLightItem.value.id;
      await axiosInstance.delete(
        `restaurants/featured-images/logo/delete/${mediaId}`,
        { headers: { 'x-app-theme': 'light' } },
      );
    }
    await axiosInstance.post('restaurants/featured-images/logo/add', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-app-theme': 'light',
      },
    });
    refetchLogoLight();
  } catch {
    /* silencieux */
  } finally {
    medLogoLightUploading.value = false;
  }
}

function pickFile(accept: string, cb: (f: File) => void) {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = accept;
  inp.onchange = () => {
    if (inp.files?.[0]) cb(inp.files[0]);
  };
  inp.click();
}

function visitStore() {
  if (!storeUrl.value) return;
  window.open(storeUrl.value, '_blank', 'noopener');
}

function openOrShare() {
  const url = storeUrl.value;
  if (!url) return;
  if (navigator.share) {
    navigator.share({ title: store.value?.designation ?? '', url });
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

const togglingActive = ref(false);
async function toggleActive() {
  if (togglingActive.value) return;
  togglingActive.value = true;
  try {
    await axiosInstance.put(`restaurants/update/${id.value}`, {
      isActive: !isActive.value,
    });
    queryClient.invalidateQueries({ queryKey: ['commerce-detail', id.value] });
    queryClient.invalidateQueries({ queryKey: ['commerces-page'] });
    queryClient.invalidateQueries({ queryKey: ['GetRestaurants'] });
  } catch {
    /* silencieux */
  } finally {
    togglingActive.value = false;
  }
}

const POSITION_LABELS = computed<Record<string, string>>(() => ({
  legal_representative: t('commerceDetail.positions.legal_representative'),
  ceo: t('commerceDetail.positions.ceo'),
  cfo: t('commerceDetail.positions.cfo'),
  cto: t('commerceDetail.positions.cto'),
  manager: t('commerceDetail.positions.manager'),
  accountant: t('commerceDetail.positions.accountant'),
  technical_contact: t('commerceDetail.positions.technical_contact'),
  billing_contact: t('commerceDetail.positions.billing_contact'),
  support_contact: t('commerceDetail.positions.support_contact'),
}));

function fmtDate(d: string | null | undefined) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtTime(t: string | null | undefined) {
  if (!t) return '–';
  return t.slice(0, 5); // HH:mm
}

/* ── Tab Horaires ─────────────────────────────────────────────────────────── */
const WEEKDAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
const WEEKDAY_LABELS = computed<Record<string, string>>(() => ({
  monday: t('days.monday'),
  tuesday: t('days.tuesday'),
  wednesday: t('days.wednesday'),
  thursday: t('days.thursday'),
  friday: t('days.friday'),
  saturday: t('days.saturday'),
  sunday: t('days.sunday'),
}));

const horEnabled = computed(() => activeTab.value === 'horaires' && !!id.value);

// Cet onglet n'affiche/n'édite QUE les horaires d'ouverture physiques du
// commerce — jamais ceux du module Click & Collect (réglés séparément dans
// Paramètres — Click & Collect > Créneaux de collecte).
const HOR_TARGET = 'store' as const;

const {
  data: horData,
  isFetching: horFetching,
  refetch: refetchHor,
} = useQuery({
  queryKey: computed(() => ['commerce-schedules', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/${id.value}/schedules/search?pagination=false&target=${HOR_TARGET}`,
    );
    const d = r.data as any;
    return (d?.data?.data ?? d?.data ?? d ?? []) as any[];
  },
  enabled: horEnabled,
  staleTime: 30_000,
});

const horSchedules = computed<any[]>(() => horData.value ?? []);

const regularByDay = computed(() => {
  const map: Record<string, any[]> = {};
  for (const day of WEEKDAY_ORDER) map[day] = [];
  for (const s of horSchedules.value) {
    if (s.type === 'regular' && s.weekday && map[s.weekday]) {
      map[s.weekday].push(s);
    }
  }
  return map;
});

const specialSchedules = computed(() =>
  horSchedules.value
    .filter((s) => s.type === 'special')
    .sort((a, b) => (a.day ?? '').localeCompare(b.day ?? '')),
);

const openDaysCount = computed(
  () => WEEKDAY_ORDER.filter((d) => regularByDay.value[d]?.length > 0).length,
);

const horJournal = computed(() => {
  const entries: {
    id: string;
    label: string;
    time: string;
    dotClass: string;
  }[] = [];
  for (const s of horSchedules.value) {
    const updatedAt = s.updatedAt ?? s.createdAt ?? null;
    if (!updatedAt) continue;
    const date = new Date(updatedAt);
    const time =
      date.toLocaleDateString('fr-BE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }) +
      ' · ' +
      date.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
    let label = '';
    let dotClass = 'cdp__hor-journal-dot--blue';
    if (s.type === 'regular') {
      label = `${WEEKDAY_LABELS.value[s.weekday] ?? s.weekday} → ${fmtTime(s.openTime)}–${fmtTime(s.closeTime)}`;
      dotClass = 'cdp__hor-journal-dot--blue';
    } else {
      label = `${t('commerceDetail.specialBadge')} — ${fmtDate(s.day)} ${fmtTime(s.openTime)}–${fmtTime(s.closeTime)}`;
      dotClass = 'cdp__hor-journal-dot--purple';
    }
    entries.push({ id: s.id, label, time, dotClass });
  }
  return entries.slice(0, 8);
});

/* ── Édition inline des horaires ─────────────────────────────────────────── */
type WeekdayKey = (typeof WEEKDAY_ORDER)[number];

interface ISpecialSlot {
  open: string;
  close: string;
}

const horEditDay = ref<WeekdayKey | null>(null);
// Plusieurs plages par jour (ex. 09:00–12:00 puis 12:00–17:00 pour une
// coupure méridienne) — même modèle que specialFormSlots ci-dessous.
const horEditSlots = ref<ISpecialSlot[]>([{ open: '08:00', close: '19:00' }]);
const horEditClosed = ref(false);
const horSaving = ref(false);

function startEditDay(day: WeekdayKey) {
  horEditDay.value = day;
  const slots = regularByDay.value[day] ?? [];
  horEditClosed.value = slots.length === 0;
  horEditSlots.value = slots.length
    ? slots.map((s: any) => ({
        open: s.openTime?.slice(0, 5) ?? '08:00',
        close: s.closeTime?.slice(0, 5) ?? '19:00',
      }))
    : [{ open: '08:00', close: '19:00' }];
}

function addHorEditSlot() {
  horEditSlots.value.push({ open: '08:00', close: '19:00' });
}

function removeHorEditSlot(i: number) {
  horEditSlots.value.splice(i, 1);
}

/* ── Jours exceptionnels ─────────────────────────────────────────────────── */

const showSpecialForm = ref(false);
const specialFormDay = ref('');
const specialFormSlots = ref<ISpecialSlot[]>([
  { open: '08:00', close: '19:00' },
]);
const specialSaving = ref(false);
const specialFormError = ref<string | null>(null);
const deletingSpecialId = ref<string | null>(null);

function toggleSpecialForm() {
  showSpecialForm.value = !showSpecialForm.value;
  if (showSpecialForm.value) {
    specialFormDay.value = '';
    specialFormSlots.value = [{ open: '08:00', close: '19:00' }];
    specialFormError.value = null;
  }
}

function addSpecialSlot() {
  specialFormSlots.value.push({ open: '08:00', close: '19:00' });
}

function removeSpecialSlot(i: number) {
  specialFormSlots.value.splice(i, 1);
}

async function saveSpecial() {
  if (!specialFormDay.value || specialSaving.value) return;
  specialSaving.value = true;
  specialFormError.value = null;
  try {
    const schedules = specialFormSlots.value.map((s) => ({
      type: 'special',
      day: specialFormDay.value,
      open: `${s.open}:00`,
      close: `${s.close}:00`,
      target: HOR_TARGET,
    }));
    await axiosInstance.post(`restaurants/${id.value}/schedules/add`, {
      schedules,
    });
    showSpecialForm.value = false;
    refetchHor();
  } catch (e: any) {
    const msgs: string[] = e?.response?.data?.message ?? [];
    specialFormError.value =
      Array.isArray(msgs) && msgs.length
        ? msgs.join(' ')
        : 'Une erreur est survenue, veuillez réessayer.';
  } finally {
    specialSaving.value = false;
  }
}

async function deleteSpecial(slot: any) {
  if (deletingSpecialId.value) return;
  deletingSpecialId.value = slot.id;
  try {
    await axiosInstance.delete(
      `restaurants/${id.value}/schedules/delete/${slot.id}`,
    );
    refetchHor();
  } catch {
    /* silencieux */
  } finally {
    deletingSpecialId.value = null;
  }
}

function clickDayRow(day: WeekdayKey) {
  if (horEditDay.value !== day) startEditDay(day);
}

function cancelEditDay() {
  horEditDay.value = null;
}

async function saveEditDay() {
  if (!horEditDay.value || horSaving.value) return;
  horSaving.value = true;
  try {
    const body: Record<string, unknown> = { target: HOR_TARGET };
    for (const d of WEEKDAY_ORDER) {
      if (d === horEditDay.value) {
        body[d] = horEditClosed.value
          ? []
          : horEditSlots.value.map((s) => ({
              open: `${s.open}:00`,
              close: `${s.close}:00`,
            }));
      } else {
        const slots = regularByDay.value[d] ?? [];
        body[d] = slots.map((s: any) => ({
          open: s.openTime ?? s.open,
          close: s.closeTime ?? s.close,
        }));
      }
    }
    await axiosInstance.put(
      `restaurants/${id.value}/schedules/weekday-schedule`,
      body,
    );
    horEditDay.value = null;
    refetchHor();
  } catch {
    /* silencieux */
  } finally {
    horSaving.value = false;
  }
}

function initials(c: any) {
  if (c.firstName || c.lastName)
    return (
      ((c.firstName?.[0] ?? '') + (c.lastName?.[0] ?? '')).toUpperCase() || '–'
    );
  const full = c.names ?? c.name ?? c.email ?? '';
  if (full)
    return (
      full
        .trim()
        .split(/\s+/)
        .map((w: string) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || '–'
    );
  return '–';
}

function contactFullName(c: any) {
  if (c.firstName || c.lastName)
    return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
  return c.names ?? c.name ?? c.email ?? '—';
}

/* ── Catalogue : pagination + filtres API ───────────────────────────────── */
const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const prodPage = ref(1);
const prodPageSize = ref<10 | 20 | 50>(20);
let debProdTimer = 0;
const prodQ = ref(''); // debounced

// Filtres
const prodSortBy = ref<
  'createdAt' | 'price' | 'name' | 'newer' | 'favorite' | 'category'
>('createdAt');
const prodSortOrder = ref<'ASC' | 'DESC'>('DESC');
const prodCategoryIds = ref<string[]>([]);
const prodRecommended = ref<boolean | null>(null);
const prodFavorite = ref<boolean | null>(null);
const showFilters = ref(false);

const SORT_OPTIONS = computed(
  () =>
    [
      { value: 'createdAt', label: t('commerceDetail.sortOptions.createdAt') },
      { value: 'newer', label: t('commerceDetail.sortOptions.newer') },
      { value: 'price', label: t('commerceDetail.sortOptions.price') },
      { value: 'name', label: t('commerceDetail.sortOptions.name') },
      { value: 'category', label: t('commerceDetail.sortOptions.category') },
      { value: 'favorite', label: t('commerceDetail.sortOptions.favorite') },
    ] as const,
);

watch(productSearch, (v) => {
  clearTimeout(debProdTimer);
  debProdTimer = setTimeout(() => {
    prodQ.value = v.trim();
    prodPage.value = 1;
  }, 350) as any;
});

const activeFiltersCount = computed(() => {
  let n = 0;
  if (prodSortBy.value !== 'createdAt' || prodSortOrder.value !== 'DESC') n++;
  if (prodCategoryIds.value.length) n++;
  if (prodRecommended.value !== null) n++;
  if (prodFavorite.value !== null) n++;
  return n;
});

function resetFilters() {
  prodSortBy.value = 'createdAt';
  prodSortOrder.value = 'DESC';
  prodCategoryIds.value = [];
  prodRecommended.value = null;
  prodFavorite.value = null;
  prodPage.value = 1;
}

function toggleCategoryFilter(catId: string) {
  const idx = prodCategoryIds.value.indexOf(catId);
  if (idx === -1) prodCategoryIds.value = [...prodCategoryIds.value, catId];
  else prodCategoryIds.value = prodCategoryIds.value.filter((c) => c !== catId);
  prodPage.value = 1;
}

const prodQueryKey = computed(() => [
  'commerce-products',
  id.value,
  prodPage.value,
  prodPageSize.value,
  prodQ.value,
  prodSortBy.value,
  prodSortOrder.value,
  prodCategoryIds.value.join(','),
  prodRecommended.value,
  prodFavorite.value,
]);

const { data: productsResp, isPending: loadingProducts } = useQuery({
  queryKey: prodQueryKey,
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: id.value,
      pagination: 'true',
      page: String(prodPage.value),
      pageSize: String(prodPageSize.value),
      sortBy: prodSortBy.value,
      sortOrder: prodSortOrder.value,
    });
    if (prodQ.value) params.append('designation', prodQ.value);
    for (const cid of prodCategoryIds.value) params.append('categoryIds', cid);
    if (prodRecommended.value !== null)
      params.append('recommended', String(prodRecommended.value));
    if (prodFavorite.value !== null)
      params.append('favorite', String(prodFavorite.value));
    const r = await axiosInstance.get<IApiResponseProduct>(
      `products/search?${params}`,
    );
    return r.data;
  },
  enabled: computed(() => !!id.value),
  staleTime: 30_000,
  placeholderData: (prev: any) => prev,
});

const rawProducts = computed<IProduct[]>(() => {
  const r = productsResp.value as any;
  return r?.data?.data ?? r?.data ?? [];
});
const totalProds = computed(() => {
  const r = productsResp.value as any;
  return r?.data?.total ?? r?.total ?? 0;
});
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalProds.value / prodPageSize.value)),
);

// Liste complète (non paginée) des produits du commerce — sert à la fois à
// l'onglet Assignation et au calcul de categoriesMap ci-dessous, pour ne pas
// dépendre de la pagination du sous-tab Produits.
const { data: assignProductsResp, isPending: loadingAssignProducts } = useQuery(
  {
    queryKey: computed(() => ['commerce-products-assign-all', id.value]),
    queryFn: async () => {
      const params = new URLSearchParams({
        restau: id.value,
        pagination: 'false',
      });
      const r = await axiosInstance.get<IApiResponseProduct>(
        `products/search?${params}`,
      );
      return r.data;
    },
    enabled: computed(() => !!id.value),
    staleTime: 30_000,
  },
);
const assignAllProducts = computed<IProduct[]>(() => {
  const r = assignProductsResp.value as any;
  return r?.data?.data ?? r?.data ?? [];
});

// ── Toutes les catégories du commerce (y compris celles sans produit) ─────────
const { data: storeCategoriesResp, refetch: refetchStoreCategories } = useQuery(
  {
    queryKey: computed(() => ['commerce-categories-all', id.value]),
    queryFn: async () => {
      const r = await CategoryService.getCategoriesByStore(id.value);
      return r.data;
    },
    enabled: computed(() => !!id.value),
    staleTime: 60_000,
  },
);
const storeCategories = computed<any[]>(() => {
  const r = storeCategoriesResp.value as any;
  return r?.data?.data ?? r?.data ?? [];
});

// Catégories réellement affichées dans les onglets Produits/Assignation :
// on part de la liste COMPLÈTE des catégories du store (API dédiée), pas des
// produits paginés — sinon les catégories vides ou hors-page disparaissaient.
const categoriesMap = computed(() => {
  const map = new Map<
    string,
    { id: string; label: string; products: IProduct[] }
  >();
  for (const cat of storeCategories.value) {
    const catLabel = cat.designation ?? cat.name ?? cat.id;
    map.set(cat.id, { id: cat.id, label: catLabel, products: [] });
  }
  let hasUncategorized = false;
  for (const p of assignAllProducts.value.length
    ? assignAllProducts.value
    : rawProducts.value) {
    if (p.categories?.length) {
      for (const pc of p.categories) {
        const cat = (pc as any).category ?? pc;
        const catId = cat.id ?? (pc as any).id;
        const catLabel = cat.designation ?? cat.name ?? catId;
        if (!map.has(catId))
          map.set(catId, { id: catId, label: catLabel, products: [] });
        map.get(catId)!.products.push(p);
      }
    } else {
      hasUncategorized = true;
      if (!map.has('__none'))
        map.set('__none', {
          id: '__none',
          label: t('commerceDetail.noCategory'),
          products: [],
        });
      map.get('__none')!.products.push(p);
    }
  }
  if (!hasUncategorized) map.delete('__none');
  return [...map.values()];
});

// ── Collections disponibles pour ce commerce précis (storeIds) — dédié à
// l'onglet Collections, indépendant de categoriesMap (store-scoped via les
// produits déjà chargés) utilisé par Assignation.
const { data: orgCategoriesResp, refetch: refetchOrgCategories } = useQuery({
  queryKey: computed(() => ['commerce-org-categories', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `products/categories/search?pagination=false&storeIds=${id.value}`,
    );
    return r.data as any;
  },
  enabled: computed(() => !!id.value),
  staleTime: 60_000,
});
interface IOrgCategory {
  id: string;
  label: string;
  modules: string[];
  products: IProduct[];
}
const orgCategoriesMap = computed<IOrgCategory[]>(() => {
  const cats: any[] = orgCategoriesResp.value?.data ?? [];
  const productsByCatId = new Map<string, IProduct[]>();
  for (const p of assignAllProducts.value.length
    ? assignAllProducts.value
    : rawProducts.value) {
    for (const pc of p.categories ?? []) {
      const cat = (pc as any).category ?? pc;
      const catId = cat.id ?? (pc as any).id;
      if (!catId) continue;
      if (!productsByCatId.has(catId)) productsByCatId.set(catId, []);
      productsByCatId.get(catId)!.push(p);
    }
  }
  return cats.map((cat: any) => ({
    id: cat.id,
    label: cat.designationAlternatives?.fr ?? cat.designation ?? cat.id,
    modules: Array.isArray(cat.modules) ? cat.modules : [],
    products: productsByCatId.get(cat.id) ?? [],
  }));
});

// ── Collections — ordre drag & drop ──────────────────────────────────────────
// L'ordre serveur (position, piloté par PUT products/categories/move/{id})
// est la source de vérité : on adopte directement l'ordre renvoyé par
// l'API plutôt que de préserver un ordre local, sinon un déplacement
// persisté avec succès ne se reflèterait jamais après le refetch.
const colOrder = ref<string[]>([]);
watch(
  orgCategoriesMap,
  (cats) => {
    colOrder.value = cats.map((c) => c.id);
  },
  { immediate: true },
);

const orderedCollections = computed(() => {
  const map = Object.fromEntries(orgCategoriesMap.value.map((c) => [c.id, c]));
  return colOrder.value.filter((id) => map[id]).map((id) => map[id]);
});

// Recherche + pagination du sous-onglet Collections, indépendantes du reste
// (drag & drop conservé sur la liste complète, seule l'affichage est paginé).
const colSearch = ref('');
const colPage = ref(1);
const COL_PAGE_SIZES = [10, 20, 50] as const;
const colPageSize = ref<(typeof COL_PAGE_SIZES)[number]>(20);
watch([colSearch, colPageSize], () => {
  colPage.value = 1;
});
const colSearchFiltered = computed(() => {
  const q = colSearch.value.trim().toLowerCase();
  if (!q) return orderedCollections.value;
  return orderedCollections.value.filter((c) =>
    (typeof c.label === 'object' ? ((c.label as any).fr ?? '') : c.label)
      .toLowerCase()
      .includes(q),
  );
});
const colTotalPages = computed(() =>
  Math.max(1, Math.ceil(colSearchFiltered.value.length / colPageSize.value)),
);
const colVisiblePages = computed(() => {
  const total = colTotalPages.value;
  const cur = colPage.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});
const paginatedCollections = computed(() => {
  const start = (colPage.value - 1) * colPageSize.value;
  return colSearchFiltered.value.slice(start, start + colPageSize.value);
});

// ── Modules (canaux de vente) où une collection s'affiche ───────────────────
const MODULE_OPTIONS = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  { id: 'kiosk', icon: 'pi-tablet', labelKey: 'commerceDetail.modulesKiosk' },
] as const;
function moduleOption(id: string) {
  return MODULE_OPTIONS.find((m) => m.id === id);
}

// ── Création d'une nouvelle collection (organisation de ce commerce) ────────
const showNewColForm = ref(false);
const newColLang = ref<'fr' | 'en' | 'nl'>('fr');
const newColFr = ref('');
const newColEn = ref('');
const newColNl = ref('');
const newColTranslating = ref(false);
const newColDescLang = ref<'fr' | 'en' | 'nl'>('fr');
const newColDescFr = ref('');
const newColDescEn = ref('');
const newColDescNl = ref('');
const newColDescTranslating = ref(false);
const newColModules = ref<string[]>([]);
const newColSaving = ref(false);
const newColError = ref<string | null>(null);

function toggleNewColModule(id: string) {
  const idx = newColModules.value.indexOf(id);
  if (idx >= 0) newColModules.value.splice(idx, 1);
  else newColModules.value.push(id);
}
function toggleEditColModule(id: string) {
  const idx = editColModules.value.indexOf(id);
  if (idx >= 0) editColModules.value.splice(idx, 1);
  else editColModules.value.push(id);
}

function openNewColForm() {
  showNewColForm.value = true;
  newColLang.value = 'fr';
  newColFr.value = '';
  newColEn.value = '';
  newColNl.value = '';
  newColDescLang.value = 'fr';
  newColDescFr.value = '';
  newColDescEn.value = '';
  newColDescNl.value = '';
  newColModules.value = [];
  newColError.value = null;
}
function closeNewColForm() {
  showNewColForm.value = false;
}
async function onNewColBlur(lang: 'fr' | 'en' | 'nl') {
  if (newColLang.value !== lang) return;
  const text = (
    lang === 'fr'
      ? newColFr.value
      : lang === 'en'
        ? newColEn.value
        : newColNl.value
  ).trim();
  if (!text || newColTranslating.value) return;
  newColTranslating.value = true;
  try {
    const translated = await TranslationService.translateText(text, lang);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== lang)
      .forEach((l) => {
        if (l === 'fr') newColFr.value = translated.fr;
        if (l === 'en') newColEn.value = translated.en;
        if (l === 'nl') newColNl.value = translated.nl;
      });
  } finally {
    newColTranslating.value = false;
  }
}
async function onNewColDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (newColDescLang.value !== lang) return;
  const text = (
    lang === 'fr'
      ? newColDescFr.value
      : lang === 'en'
        ? newColDescEn.value
        : newColDescNl.value
  ).trim();
  if (!text || newColDescTranslating.value) return;
  newColDescTranslating.value = true;
  try {
    const translated = await TranslationService.translateText(text, lang);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== lang)
      .forEach((l) => {
        if (l === 'fr') newColDescFr.value = translated.fr;
        if (l === 'en') newColDescEn.value = translated.en;
        if (l === 'nl') newColDescNl.value = translated.nl;
      });
  } finally {
    newColDescTranslating.value = false;
  }
}
async function confirmNewCol() {
  if (!newColFr.value.trim() || newColSaving.value) return;
  newColSaving.value = true;
  newColError.value = null;
  try {
    await axiosInstance.post('products/categories/create', {
      designation: {
        fr: newColFr.value.trim(),
        en: newColEn.value.trim(),
        nl: newColNl.value.trim(),
      },
      description: {
        fr: newColDescFr.value.trim() || newColFr.value.trim(),
        en: newColDescEn.value.trim() || newColEn.value.trim(),
        nl: newColDescNl.value.trim() || newColNl.value.trim(),
      },
      restaurantId: id.value,
      modules: newColModules.value,
    });
    await refetchOrgCategories();
    queryClient.invalidateQueries({
      queryKey: ['commerce-store-stats', id.value],
    });
    showNewColForm.value = false;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    newColError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    newColSaving.value = false;
  }
}

// ── Édition inline du nom d'une collection existante ──────────────────────────
const editingColId = ref<string | null>(null);
const editColLang = ref<'fr' | 'en' | 'nl'>('fr');
const editColFr = ref('');
const editColEn = ref('');
const editColNl = ref('');
const editColTranslating = ref(false);
const editColModules = ref<string[]>([]);
const editColSaving = ref(false);
const editColError = ref<string | null>(null);
const deletingColId = ref<string | null>(null);

async function startEditCol(cat: {
  id: string;
  label: any;
  modules?: string[];
}) {
  editingColId.value = cat.id;
  editColLang.value = 'fr';
  const d = cat.label;
  editColFr.value = typeof d === 'string' ? d : (d?.fr ?? '');
  editColEn.value = typeof d === 'string' ? '' : (d?.en ?? '');
  editColNl.value = typeof d === 'string' ? '' : (d?.nl ?? '');
  editColModules.value = Array.isArray(cat.modules) ? [...cat.modules] : [];
  editColError.value = null;

  // Pré-remplissage auto des langues manquantes à partir de la première
  // langue non vide trouvée, sans attendre un blur de l'utilisateur.
  const values: Record<'fr' | 'en' | 'nl', string> = {
    fr: editColFr.value,
    en: editColEn.value,
    nl: editColNl.value,
  };
  const source = (['fr', 'en', 'nl'] as const).find((l) => values[l].trim());
  if (!source) return;
  const missing = (['fr', 'en', 'nl'] as const).filter(
    (l) => l !== source && !values[l].trim(),
  );
  if (!missing.length) return;
  const thisEdit = cat.id;
  editColTranslating.value = true;
  try {
    const translated = await TranslationService.translateText(
      values[source],
      source,
    );
    if (editingColId.value !== thisEdit) return;
    missing.forEach((l) => {
      if (l === 'fr') editColFr.value = translated.fr;
      if (l === 'en') editColEn.value = translated.en;
      if (l === 'nl') editColNl.value = translated.nl;
    });
  } finally {
    if (editingColId.value === thisEdit) editColTranslating.value = false;
  }
}
function cancelEditCol() {
  editingColId.value = null;
}

async function onEditColBlur(lang: 'fr' | 'en' | 'nl') {
  if (editColLang.value !== lang) return;
  const text = (
    lang === 'fr'
      ? editColFr.value
      : lang === 'en'
        ? editColEn.value
        : editColNl.value
  ).trim();
  if (!text || editColTranslating.value) return;
  editColTranslating.value = true;
  try {
    const translated = await TranslationService.translateText(text, lang);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== lang)
      .forEach((l) => {
        if (l === 'fr') editColFr.value = translated.fr;
        if (l === 'en') editColEn.value = translated.en;
        if (l === 'nl') editColNl.value = translated.nl;
      });
  } finally {
    editColTranslating.value = false;
  }
}

async function confirmEditCol() {
  if (!editingColId.value || !editColFr.value.trim() || editColSaving.value)
    return;
  editColSaving.value = true;
  editColError.value = null;
  try {
    await axiosInstance.put(
      `products/categories/update/${editingColId.value}`,
      {
        designation: {
          fr: editColFr.value.trim(),
          en: editColEn.value.trim(),
          nl: editColNl.value.trim(),
        },
        modules: editColModules.value,
      },
    );
    await Promise.all([refetchOrgCategories(), refetchStoreCategories()]);
    queryClient.invalidateQueries({
      queryKey: ['commerce-store-stats', id.value],
    });
    editingColId.value = null;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    editColError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    editColSaving.value = false;
  }
}

async function deleteCol(catId: string) {
  if (deletingColId.value) return;
  deletingColId.value = catId;
  try {
    await axiosInstance.delete(`products/categories/delete/${catId}`);
    if (editingColId.value === catId) editingColId.value = null;
    await Promise.all([refetchOrgCategories(), refetchStoreCategories()]);
    queryClient.invalidateQueries({
      queryKey: ['commerce-store-stats', id.value],
    });
  } catch {
    /* 409 si la catégorie ne peut pas être supprimée (ex: encore des produits) */
  } finally {
    deletingColId.value = null;
  }
}

let colDragSrc: string | null = null;
const colDragOver = ref<string | null>(null);

function onColDragStart(id: string) {
  colDragSrc = id;
}
function onColDragEnd() {
  colDragSrc = null;
  colDragOver.value = null;
}
function onColDragEnter(id: string) {
  if (colDragSrc && colDragSrc !== id) colDragOver.value = id;
}
// L'ordre des collections est un réglage serveur partagé par tout
// l'admin (onglets Produit, Assignation, Collections) — persisté via
// PUT products/categories/move/{id} pour que les autres pages n'affichent
// pas un ordre périmé tant qu'elles ne rechargent pas séparément.
async function moveCategoryOnServer(catId: string, afterId: string | null) {
  try {
    // afterId/beforeId sont mutuellement exclusifs — on ne pilote l'ordre
    // que via afterId (null = première position).
    await axiosInstance.put(`products/categories/move/${catId}`, {
      afterId,
    });
    // Refetch pour que Produit/Assignation/Collections repartent du même
    // ordre serveur — colOrder ne fait ensuite que reprendre cet ordre au
    // prochain watch(orgCategoriesMap). assignCatOrder est vidé pour forcer
    // sa reconstruction complète : il fige normalement l'ordre entre deux
    // assignations de produits (pour éviter un saut visuel), mais un
    // déplacement volontaire dans Collections doit primer sur ce figement.
    assignCatOrder.value = [];
    await refetchOrgCategories();
    // L'onglet Produits filtre/affiche par catégorie : les chips de filtre
    // viennent de categoriesMap, lui-même basé sur storeCategories (query
    // commerce-categories-all, séparée de orgCategoriesResp) — sans ce
    // refetch, l'ordre des chips resterait périmé jusqu'à un rechargement.
    await refetchStoreCategories();
    queryClient.invalidateQueries({ queryKey: ['commerce-products'] });
    queryClient.invalidateQueries({ queryKey: ['commerce-products-all'] });
  } catch {
    // Requête échouée : on annule le réordonnancement local pour ne pas
    // laisser croire que le nouvel ordre est enregistré côté serveur.
    assignCatOrder.value = [];
    await refetchOrgCategories();
  }
}

function onColDrop(targetId: string) {
  if (!colDragSrc || colDragSrc === targetId) return;
  const arr = [...colOrder.value];
  const from = arr.indexOf(colDragSrc);
  const to = arr.indexOf(targetId);
  if (from === -1 || to === -1) return;
  arr.splice(from, 1);
  arr.splice(to, 0, colDragSrc);
  colOrder.value = arr;
  colDragOver.value = null;

  const afterId = to > 0 ? arr[to - 1] : null;
  moveCategoryOnServer(colDragSrc, afterId);
}

const allProducts = computed(() => rawProducts.value);
const totalVisible = computed(
  () => allProducts.value.filter((p) => !p.disabledAt).length,
);

// ── KPIs catalogue — totaux fiables via l'API dashboard stats (indépendants
// de la pagination des produits affichés à l'écran) ────────────────────────
const { data: storeStatsResp } = useQuery({
  queryKey: computed(() => ['commerce-store-stats', id.value]),
  queryFn: async () => {
    const r = await CommerceService.getStoreStats({ storeIds: [id.value] });
    return r.data;
  },
  enabled: computed(() => !!id.value),
  staleTime: 60_000,
});
const storeStatsTotals = computed(
  () => (storeStatsResp.value as any)?.data?.totals ?? null,
);
// Le snapshot serveur (restaurants/stats) n'est pas recalculé en temps réel
// (même limitation que kpiCategoryCount plus bas) : on privilégie donc le
// total réel de la query produits paginée (toujours à jour côté client après
// une suppression/ajout), et on ne retombe sur le snapshot qu'avant son
// premier chargement.
const kpiProductCount = computed(
  () => totalProds.value ?? storeStatsTotals.value?.snapshot?.productCount ?? 0,
);
const kpiActiveProductCount = computed(
  () =>
    storeStatsTotals.value?.snapshot?.activeProductCount ?? totalVisible.value,
);
const kpiHiddenProductCount = computed(() =>
  Math.max(0, kpiProductCount.value - kpiActiveProductCount.value),
);
// Le snapshot serveur (restaurants/stats) n'est pas recalculé en temps réel :
// on privilégie donc orgCategoriesMap (toujours à jour côté client) et on ne
// retombe sur le snapshot qu'avant son premier chargement.
const kpiCategoryCount = computed(
  () =>
    orgCategoriesResp.value?.data?.length ??
    storeStatsTotals.value?.snapshot?.categoryCount ??
    categoriesMap.value.length,
);
const kpiAverageBasket = computed(
  () => storeStatsTotals.value?.windowed?.averageBasket ?? 0,
);
const kpiPaidOrderCount = computed(
  () => storeStatsTotals.value?.windowed?.paidOrderCount ?? 0,
);
const filteredCategories = computed(() => {
  const q = productSearch.value.trim().toLowerCase();
  if (!q) return categoriesMap.value;
  return categoriesMap.value
    .map((cat) => ({
      ...cat,
      products: cat.products.filter((p) =>
        p.designation.toLowerCase().includes(q),
      ),
    }))
    .filter((cat) => cat.products.length > 0);
});

function goToProdPage(p: number) {
  prodPage.value = Math.max(1, Math.min(p, totalPages.value));
}
function onPageSizeChange() {
  prodPage.value = 1;
}

const exporting = ref(false);
async function exportProducts() {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const params = new URLSearchParams({
      restau: id.value,
      pagination: 'false',
    });
    const r = await axiosInstance.get(`products/search?${params}`);
    const raw = r.data as any;
    const list: any[] = raw?.data?.data ?? raw?.data ?? [];

    const headers = [
      'ID',
      `${t('commerceDetail.colName')} FR`,
      `${t('commerceDetail.colName')} EN`,
      `${t('commerceDetail.colName')} NL`,
      t('commerceDetail.colPrice'),
      t('commerceDetail.catalogueKpis.avgPrice'),
      t('commerceDetail.colType'),
      t('commerceDetail.colStatus'),
      t('commerceDetail.recommended'),
      t('commerceDetail.assignProducts'),
      t('commerceDetail.assignTo'),
      t('commerceDetail.colName'),
      t('commerceDetail.categoryFilter'),
      t('commerceDetail.createdAt'),
    ];
    const rows = list.map((p) => {
      const des =
        typeof p.designation === 'object'
          ? p.designation
          : { fr: p.designation };
      const cats = (p.categories ?? [])
        .map((c: any) => (c?.category ?? c)?.designation ?? '')
        .filter(Boolean)
        .join(' | ');
      const status = p.disabledAt
        ? t('commerceDetail.inactiveStatus')
        : t('commerceDetail.activeStatus');
      return [
        p.id ?? '',
        des?.fr ?? '',
        des?.en ?? '',
        des?.nl ?? '',
        p.price ?? '',
        p.priceUnit ?? '',
        p.type ?? '',
        status,
        p.isRecommanded
          ? t('commerceDetail.recommended')
          : t('commerceDetail.no'),
        p.packageUnit ?? '',
        p.packageQty ?? '',
        p.preparationTime ?? '',
        cats,
        p.createdAt ? new Date(p.createdAt).toLocaleDateString('fr-BE') : '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',');
    });

    const csv = [headers.map((h) => `"${h}"`).join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `produits-${store.value?.designation ?? id.value}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    /* silencieux */
  } finally {
    exporting.value = false;
  }
}

function fmtPrice(n: number | string) {
  const v = typeof n === 'string' ? parseFloat(n) : n;
  return isNaN(v) ? '—' : v.toFixed(2).replace('.', ',') + ' €';
}
function productImgUrl(p: IProduct) {
  return (p.image as any)?.info?.secureUrl ?? (p.image as any)?.url ?? null;
}
function isProductActive(p: IProduct) {
  return !p.disabledAt;
}

// ── Chargement des images produits (skeleton pendant le fetch, fallback si erreur) ──
const productImgLoaded = ref<Set<string>>(new Set());
const productImgFailed = ref<Set<string>>(new Set());
function onProductImgLoad(productId: string) {
  productImgLoaded.value.add(productId);
  productImgLoaded.value = new Set(productImgLoaded.value);
}
function onProductImgError(productId: string) {
  productImgFailed.value.add(productId);
  productImgFailed.value = new Set(productImgFailed.value);
}
function showProductImg(p: IProduct) {
  return !!productImgUrl(p) && !productImgFailed.value.has(p.id);
}
function productImgPending(p: IProduct) {
  return showProductImg(p) && !productImgLoaded.value.has(p.id);
}

/* ── Checklist configuration ─────────────────────────────────────────────── */
const configItems = computed(() => {
  const s = store.value;
  return [
    {
      key: 'image',
      label: t('commerceDetail.configItems.banner'),
      desc: t('commerceDetail.configItems.bannerDesc'),
      icon: 'pi-image',
      done: !!(s?.logo?.info?.secureUrl ?? s?.logoUrl),
      urgent: false,
      tab: 'medias',
    },
    {
      key: 'slug',
      label: t('commerceDetail.configItems.slug'),
      desc: t('commerceDetail.configItems.slugDesc'),
      icon: 'pi-link',
      done: !!slugs.value.length,
      urgent: true,
      tab: 'modules',
    },
    {
      key: 'contact',
      label: t('commerceDetail.configItems.contact'),
      desc: t('commerceDetail.configItems.contactDesc'),
      icon: 'pi-user',
      done: contacts.value.length > 0,
      urgent: false,
      tab: 'informations',
    },
    {
      key: 'horaires',
      label: t('commerceDetail.configItems.horaires'),
      desc: t('commerceDetail.configItems.horairesDesc'),
      icon: 'pi-clock',
      done: !!s?.schedules?.length,
      urgent: false,
      tab: 'horaires',
    },
    {
      key: 'catalogue',
      label: t('commerceDetail.configItems.catalogue'),
      desc: t('commerceDetail.configItems.catalogueDesc'),
      icon: 'pi-box',
      done: allProducts.value.filter((p) => !p.disabledAt).length > 0,
      urgent: false,
      tab: 'catalogue',
    },
  ];
});
const configDone = computed(
  () => configItems.value.filter((i) => i.done).length,
);
const configTotal = computed(() => configItems.value.length);
const configPct = computed(() =>
  Math.round((configDone.value / configTotal.value) * 100),
);

/* Pages numérotées pour la barre de pagination */
const prodPageNums = computed(() => {
  const total = totalPages.value;
  const cur = prodPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (cur > 3) pages.push('…');
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++)
    pages.push(i);
  if (cur < total - 2) pages.push('…');
  pages.push(total);
  return pages;
});

/* ── Assignation drag & drop ────────────────────────────────────────────── */
// Liste indépendante de la pagination du sous-tab Produits : l'assignation
// doit pouvoir cibler TOUS les produits du commerce, pas seulement la page
// affichée dans l'onglet Produits.
const assignSearch = ref('');
const assignPage = ref(1);
const ASSIGN_PAGE_SIZES = [10, 20, 50, 100] as const;
const assignPageSize = ref<(typeof ASSIGN_PAGE_SIZES)[number]>(20);
watch([assignSearch, assignPageSize], () => {
  assignPage.value = 1;
});

// Colonne de droite (catégories) : recherche + pagination indépendantes,
// pour ne pas tout afficher d'un bloc quand il y a beaucoup de catégories.
const catSearch = ref('');
const catPage = ref(1);
const CAT_PAGE_SIZES = [5, 10, 20, 50] as const;
const catPageSize = ref<(typeof CAT_PAGE_SIZES)[number]>(10);
watch([catSearch, catPageSize], () => {
  catPage.value = 1;
});
// Ordre d'affichage figé (catégories avec produits d'abord au moment du
// premier chargement / de chaque nouvelle recherche), pour que la liste ne
// saute pas visuellement dès qu'on assigne un produit à une catégorie vide
// en plein milieu d'une série d'assignations.
const assignCatOrder = ref<string[]>([]);
watch(
  [orgCategoriesMap, catSearch],
  ([cats]) => {
    const realIds = new Set(cats.map((c) => c.id));
    const kept = assignCatOrder.value.filter((id) => realIds.has(id));
    const known = new Set(kept);
    const added = cats
      .filter((c) => !known.has(c.id))
      .sort((a, b) => {
        const aEmpty = a.products.length === 0 ? 1 : 0;
        const bEmpty = b.products.length === 0 ? 1 : 0;
        return aEmpty - bEmpty;
      })
      .map((c) => c.id);
    assignCatOrder.value = [...kept, ...added];
  },
  { immediate: true },
);

const catSearchFiltered = computed(() => {
  const q = catSearch.value.trim().toLowerCase();
  const list = q
    ? orgCategoriesMap.value.filter((c) =>
        (typeof c.label === 'object' ? ((c.label as any).fr ?? '') : c.label)
          .toLowerCase()
          .includes(q),
      )
    : orgCategoriesMap.value;
  const map = new Map(list.map((c) => [c.id, c]));
  const ordered = assignCatOrder.value
    .filter((cid) => map.has(cid))
    .map((cid) => map.get(cid)!);
  // Filet de sécurité : une catégorie présente dans `list` mais pas encore
  // dans l'ordre figé (ne devrait pas arriver grâce au watch ci-dessus).
  const missing = list.filter((c) => !assignCatOrder.value.includes(c.id));
  return [...ordered, ...missing];
});

const catTotalPages = computed(() =>
  Math.max(1, Math.ceil(catSearchFiltered.value.length / catPageSize.value)),
);
const catVisiblePages = computed(() => {
  const total = catTotalPages.value;
  const cur = catPage.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});
const paginatedCategories = computed(() => {
  const start = (catPage.value - 1) * catPageSize.value;
  return catSearchFiltered.value.slice(start, start + catPageSize.value);
});

const dragOverCat = ref<string | null>(null);

let draggedProduct: any = null;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Modale + retrait via API
const confirmRemove = ref<{ catId: string; pid: string } | null>(null);
const removingKey = ref<string | null>(null);

function askRemoveAssign(catId: string, pid: string) {
  confirmRemove.value = { catId, pid };
}
function cancelRemove() {
  confirmRemove.value = null;
}

async function doRemoveAssign() {
  if (!confirmRemove.value || removingKey.value) return;
  const catId = confirmRemove.value.catId;
  const pid = confirmRemove.value.pid;
  if (!UUID_RE.test(catId)) {
    assignedMap.value[catId] = (assignedMap.value[catId] ?? []).filter(
      (i) => i !== pid,
    );
    confirmRemove.value = null;
    return;
  }
  removingKey.value = `${catId}:${pid}`;
  try {
    await axiosInstance.put(
      `products/update/${pid}`,
      { removeFromCategories: [catId] },
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    assignedMap.value[catId] = (assignedMap.value[catId] ?? []).filter(
      (i) => i !== pid,
    );
    queryClient.invalidateQueries({
      queryKey: ['commerce-products', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-all', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-assign-all', id.value],
    });
    confirmRemove.value = null;
    (GlobalSucessMessage as any).value = t(
      'commerceDetail.successRemoveProduct',
    );
    isGlobalSucess.value = true;
    setTimeout(() => {
      isGlobalSucess.value = false;
    }, 3000);
  } catch {
    (GlobalErrorMessage as any).value = t('commerceDetail.productRemoveError');
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 3000);
    confirmRemove.value = null;
  } finally {
    removingKey.value = null;
  }
}

// Picker : clic sur un produit → ouvre un menu de sélection de catégorie
const pickerPid = ref<string | null>(null);
function togglePicker(pid: string) {
  pickerPid.value = pickerPid.value === pid ? null : pid;
}
function closePicker() {
  pickerPid.value = null;
}
async function toggleCatForProduct(catId: string, pid: string) {
  if (!UUID_RE.test(catId) || !UUID_RE.test(pid)) return;
  const list = assignedMap.value[catId] ?? [];
  const isAssigned = list.includes(pid);
  try {
    await axiosInstance.put(
      `products/update/${pid}`,
      isAssigned
        ? { removeFromCategories: [catId] }
        : { addInCategories: [catId] },
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    assignedMap.value[catId] = isAssigned
      ? list.filter((i) => i !== pid)
      : [...list, pid];
    queryClient.invalidateQueries({
      queryKey: ['commerce-products', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-all', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-assign-all', id.value],
    });
    (GlobalSucessMessage as any).value = isAssigned
      ? t('commerceDetail.successRemoveProduct')
      : t('commerceDetail.successAssignProduct');
    isGlobalSucess.value = true;
    setTimeout(() => {
      isGlobalSucess.value = false;
    }, 3000);
  } catch {
    (GlobalErrorMessage as any).value = t('commerceDetail.assignError');
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 3000);
  }
}
function isPidInCat(catId: string, pid: string) {
  return (assignedMap.value[catId] ?? []).includes(pid);
}

// Map catId → productIds assignés
const assignedMap = ref<Record<string, string[]>>({});

// Synchronise depuis orgCategoriesMap (toutes les collections de l'organisation,
// y compris celles sans produit sur ce commerce) à chaque rechargement des
// produits/catégories. Source de vérité serveur à chaque fois : les mutations
// (toggleCatForProduct, doRemoveAssign) invalident déjà les queries concernées,
// donc ce watch doit refléter le nouvel état plutôt que garder l'ancien tableau
// (sinon une catégorie déjà vue une fois ne se remet jamais à jour ensuite).
watch(
  orgCategoriesMap,
  (cats) => {
    const m: Record<string, string[]> = {};
    for (const cat of cats) {
      m[cat.id] = cat.products.map((p: any) => p.id);
    }
    assignedMap.value = m;
  },
  { immediate: true },
);

const assignSearchFilteredProducts = computed(() => {
  const q = assignSearch.value.trim().toLowerCase();
  if (!q) return assignAllProducts.value;
  return assignAllProducts.value.filter((p: any) =>
    (typeof p.designation === 'string'
      ? p.designation
      : (p.designation?.fr ?? '')
    )
      .toLowerCase()
      .includes(q),
  );
});
const assignTotalPages = computed(() =>
  Math.max(
    1,
    Math.ceil(assignSearchFilteredProducts.value.length / assignPageSize.value),
  ),
);
const assignVisiblePages = computed(() => {
  const total = assignTotalPages.value;
  const cur = assignPage.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});
const assignFilteredProducts = computed(() => {
  const start = (assignPage.value - 1) * assignPageSize.value;
  return assignSearchFilteredProducts.value.slice(
    start,
    start + assignPageSize.value,
  );
});

function isAssigned(pid: string) {
  return Object.values(assignedMap.value).some((ids) => ids.includes(pid));
}

function productName(pid: string) {
  const p = assignAllProducts.value.find((p: any) => p.id === pid);
  if (!p) return pid;
  return typeof p.designation === 'string'
    ? p.designation
    : ((p.designation as any)?.fr ?? pid);
}

function onDragStart(e: DragEvent, p: any) {
  draggedProduct = p;
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
}

async function onDrop(_e: DragEvent, catId: string) {
  dragOverCat.value = null;
  if (!draggedProduct) return;
  const pid = draggedProduct.id;
  draggedProduct = null;
  if (!UUID_RE.test(pid)) return;

  // Drop sur "Sans catégorie" → retirer le produit de toutes ses catégories réelles
  if (catId === '__none') {
    const realCatIds = Object.keys(assignedMap.value).filter(
      (k) => UUID_RE.test(k) && (assignedMap.value[k] ?? []).includes(pid),
    );
    if (!realCatIds.length) return;
    try {
      await axiosInstance.put(
        `products/update/${pid}`,
        { removeFromCategories: realCatIds },
        { headers: { 'x-client-app': 'admin-back-office' } },
      );
      for (const k of realCatIds) {
        assignedMap.value[k] = (assignedMap.value[k] ?? []).filter(
          (i) => i !== pid,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['commerce-products', id.value],
      });
      queryClient.invalidateQueries({
        queryKey: ['commerce-products-all', id.value],
      });
      queryClient.invalidateQueries({
        queryKey: ['commerce-products-assign-all', id.value],
      });
      (GlobalSucessMessage as any).value = t(
        'commerceDetail.successRemoveFromCat',
      );
      isGlobalSucess.value = true;
      setTimeout(() => {
        isGlobalSucess.value = false;
      }, 3000);
    } catch {
      (GlobalErrorMessage as any).value = t('commerceDetail.removeCatError');
      isGlobalError.value = true;
      setTimeout(() => {
        isGlobalError.value = false;
      }, 3000);
    }
    return;
  }

  if (!UUID_RE.test(catId)) return;
  if ((assignedMap.value[catId] ?? []).includes(pid)) return;
  try {
    await axiosInstance.put(
      `products/update/${pid}`,
      { addInCategories: [catId] },
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    if (!assignedMap.value[catId]) assignedMap.value[catId] = [];
    assignedMap.value[catId] = [...assignedMap.value[catId], pid];
    queryClient.invalidateQueries({
      queryKey: ['commerce-products', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-all', id.value],
    });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-assign-all', id.value],
    });
    (GlobalSucessMessage as any).value = t(
      'commerceDetail.successAssignProduct',
    );
    isGlobalSucess.value = true;
    setTimeout(() => {
      isGlobalSucess.value = false;
    }, 3000);
  } catch {
    (GlobalErrorMessage as any).value = t('commerceDetail.assignError2');
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 3000);
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB UTILISATEURS & RÔLES
// ══════════════════════════════════════════════════════════════════════════════
const userStore = useUserStore();

// ── Roles (globaux — restaurantId: null retourné par l'API) ──────────────────
const {
  data: rolesData,
  isLoading: isLoadingRoles,
  refetch: refetchRoles,
} = useQuery({
  queryKey: ['cdp-roles'],
  queryFn: () => userStore.searchRolesGestion(),
  enabled: computed(() => activeTab.value === 'users'),
  staleTime: 60_000,
});
const roles = computed<any[]>(() => {
  const d = rolesData.value as any;
  return d?.data?.data ?? d?.data ?? (Array.isArray(d) ? d : []);
});
// Rôles exclus de la liste des utilisateurs et du choix de rôle à l'assignation
// (admin système, gestionnaire d'organisation, guest et customer gérés ailleurs).
const EXCLUDED_STAFF_ROLES = [
  'admin_system',
  'admin-system',
  'organization_manager',
  'organisation_manager',
  'guest',
  'customer',
];
const assignableRoles = computed(() =>
  roles.value.filter(
    (r: any) => !EXCLUDED_STAFF_ROLES.includes((r.code ?? '').toLowerCase()),
  ),
);
const assignableRoleCodes = computed(() =>
  assignableRoles.value.map((r: any) => r.code).filter(Boolean),
);

// ── Permissions disponibles ───────────────────────────────────────────────────
const { data: permsData } = useQuery({
  queryKey: ['cdp-permissions'],
  queryFn: async () => {
    const r = await axiosInstance.get('roles/permissions');
    return r.data;
  },
  enabled: computed(() => activeTab.value === 'users'),
  staleTime: 5 * 60_000,
});
const allPerms = computed<string[]>(() => {
  const d = (permsData.value as any)?.data ?? permsData.value;
  if (Array.isArray(d)) return d;
  return [];
});

// ── Utilisateurs du commerce ──────────────────────────────────────────────────
// Sous-onglets du tab "Utilisateurs & rôles"
type UsrSubTab = 'management' | 'cnc';
const activeUsrTab = ref<UsrSubTab>('management');
const USR_SUB_TABS = computed<{ key: UsrSubTab; label: string }[]>(() => [
  { key: 'management', label: t('commerceDetail.usrSubTabs.management') },
  { key: 'cnc', label: t('commerceDetail.usrSubTabs.cnc') },
]);

// Panneau détail inline (liste ↔ détail) — indépendant par sous-onglet
const selectedManagerId = ref<string | null>(null);
const selectedCncId = ref<string | null>(null);

const usersSearch = ref('');
const usersRoleFilter = ref('');
const usersPage = ref(1);
watch([usersSearch, usersRoleFilter], () => {
  usersPage.value = 1;
});

// Select de rôle personnalisé (recherchable) pour le filtre utilisateurs
const usersRoleDropdownOpen = ref(false);
const usersRoleSearch = ref('');
const filteredUsersRoleOptions = computed(() =>
  assignableRoles.value.filter((r: any) =>
    (r.label ?? r.code ?? '')
      .toLowerCase()
      .includes(usersRoleSearch.value.toLowerCase()),
  ),
);
const selectedUsersRoleLabel = computed(() => {
  if (!usersRoleFilter.value) return '';
  const r = assignableRoles.value.find(
    (r: any) => r.code === usersRoleFilter.value,
  );
  return r?.label ?? r?.code ?? '';
});
function selectUsersRoleFilter(code: string) {
  usersRoleFilter.value = code;
  usersRoleDropdownOpen.value = false;
}
function clearUsersRoleFilter() {
  usersRoleFilter.value = '';
  usersRoleDropdownOpen.value = false;
}

const {
  data: usersData,
  isLoading: isLoadingUsers,
  refetch: refetchUsers,
} = useQuery({
  queryKey: computed(() => [
    'cdp-users',
    id.value,
    usersRoleFilter.value,
    usersSearch.value,
    usersPage.value,
    assignableRoleCodes.value.join(','),
  ]),
  queryFn: () =>
    userStore.searchUsersGestion({
      role: usersRoleFilter.value || assignableRoleCodes.value,
      q: usersSearch.value || undefined,
      restaurantId: id.value,
      page: usersPage.value,
      pageSize: 9,
    }),
  enabled: computed(
    () =>
      activeTab.value === 'users' &&
      !!id.value &&
      (!!usersRoleFilter.value || assignableRoleCodes.value.length > 0),
  ),
});
const staffUsers = computed<any[]>(() => (usersData.value as any)?.data ?? []);
const staffTotal = computed<number>(() => (usersData.value as any)?.total ?? 0);
const staffTotalPages = computed(() =>
  Math.max(1, Math.ceil(staffTotal.value / 9)),
);

// ── Utilisateurs Click & Collect (CNC) du commerce ────────────────────────────
const cncSearch = ref('');
const cncPage = ref(1);
const CNC_PAGE_SIZE = 9;
const cncSortBy = ref<
  'created' | 'updated' | 'order_count' | 'latest_order_date'
>('created');
const cncSortOrder = ref<'ASC' | 'DESC'>('DESC');
watch([cncSearch, cncSortBy, cncSortOrder], () => {
  cncPage.value = 1;
});
function toggleCncSortOrder() {
  cncSortOrder.value = cncSortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

const { data: cncData, isLoading: isLoadingCnc } = useQuery({
  queryKey: computed(() => [
    'cdp-cnc-users',
    id.value,
    cncSearch.value,
    cncPage.value,
    cncSortBy.value,
    cncSortOrder.value,
  ]),
  queryFn: async () => {
    const qs = new URLSearchParams();
    qs.append('pagination', 'true');
    qs.append('page', String(cncPage.value));
    qs.append('pageSize', String(CNC_PAGE_SIZE));
    qs.append('sortBy', cncSortBy.value);
    qs.append('sortOrder', cncSortOrder.value);
    if (cncSearch.value.trim()) qs.append('q', cncSearch.value.trim());
    qs.append('restaurantId', id.value);
    const res = await axiosInstance.get<{ data: any[]; total: number }>(
      `customers/search?${qs.toString()}`,
    );
    return res.data;
  },
  enabled: computed(
    () =>
      activeTab.value === 'users' && activeUsrTab.value === 'cnc' && !!id.value,
  ),
});
const cncUsers = computed<any[]>(() => (cncData.value as any)?.data ?? []);
const cncTotal = computed<number>(() => (cncData.value as any)?.total ?? 0);
const cncTotalPages = computed(() =>
  Math.max(1, Math.ceil(cncTotal.value / CNC_PAGE_SIZE)),
);
function cncDisplayName(u: any): string | null {
  const full = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  return full || null;
}
function cncIsAnonymous(u: any) {
  return !cncDisplayName(u) && !u.email && !u.phone;
}
function cncInitials(u: any): string {
  const name = cncDisplayName(u);
  if (name) {
    const parts = name.split(' ');
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return u.email ? u.email.slice(0, 2).toUpperCase() : '??';
}

const selectedCncUser = computed(
  () => cncUsers.value.find((u: any) => u.id === selectedCncId.value) ?? null,
);
function openCncDetail(u: any) {
  selectedCncId.value = u.id;
}
function closeCncDetail() {
  selectedCncId.value = null;
}

// ── Helpers couleur/initiales ─────────────────────────────────────────────────
const USER_COLORS = [
  '#F68402',
  '#8B5CF6',
  '#06B6D4',
  '#EC4899',
  '#10B981',
  '#EF4444',
];
function userInitials(u: any) {
  const f = (u.firstname ?? '').trim();
  const l = (u.lastname ?? '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  return (u.email ?? '??').slice(0, 2).toUpperCase();
}
function userColor(userId: string) {
  return USER_COLORS[userId.charCodeAt(0) % USER_COLORS.length];
}
function userIsActive(u: any) {
  return !u.disabledAt && !u.lockedAt;
}
function userPrimaryRole(u: any): string {
  const rolesList = u.roles ?? [];
  const visible =
    rolesList.find(
      (r: any) => !EXCLUDED_STAFF_ROLES.includes((r.code ?? '').toLowerCase()),
    ) ?? rolesList[0];
  return visible?.label ?? visible?.code ?? '–';
}

// ── Panneau Créer utilisateur ─────────────────────────────────────────────────
const showCreateUser = ref(false);
const createUserErr = ref('');
const createUserLoading = ref(false);
const newUser = ref({
  email: '',
  password: '',
  firstname: '',
  lastname: '',
  roleIds: [] as string[],
});

function openCreateUser() {
  showCreateUser.value = true;
  createUserErr.value = '';
  newUser.value = {
    email: '',
    password: '',
    firstname: '',
    lastname: '',
    roleIds: [],
  };
}
function closeCreateUser() {
  showCreateUser.value = false;
  createUserErr.value = '';
}

// Auto-open create-user form when navigated with ?action=createUser
if (_initAction === 'createUser') {
  nextTick(() => openCreateUser());
}

// Un utilisateur peut avoir plusieurs rôles simultanément — la sélection est
// donc un tableau (checkboxes), pas une valeur unique (radio).
function toggleRoleId(list: string[], roleId: string): string[] {
  return list.includes(roleId)
    ? list.filter((id) => id !== roleId)
    : [...list, roleId];
}

async function saveNewUser() {
  if (!newUser.value.email.trim() || !newUser.value.password.trim()) return;
  createUserLoading.value = true;
  createUserErr.value = '';
  try {
    const res = await userStore.createUser({
      email: newUser.value.email.trim(),
      password: newUser.value.password,
      restaurantId: id.value,
    } as any);
    const createdId: string =
      res?.data?.data?.user?.id ?? res?.data?.data?.id ?? res?.data?.id ?? '';
    if (createdId && newUser.value.roleIds.length) {
      await userStore.assignUserRolesById(
        createdId,
        newUser.value.roleIds.map((roleId) => ({
          roleId,
          restaurantId: id.value,
        })),
      );
    }
    await queryClient.invalidateQueries({ queryKey: ['cdp-users', id.value] });
    showCreateUser.value = false;
    showUsrToast(
      'success',
      t('commerceDetail.userCreated', { email: newUser.value.email }),
    );
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    const txt = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.userCreateError'));
    createUserErr.value = txt;
    showUsrToast('error', txt);
  } finally {
    createUserLoading.value = false;
  }
}

// ── Panneau détail inline gestionnaire (liste ↔ détail) ───────────────────────
function openManagerDetail(u: any) {
  selectedManagerId.value = u.id;
}
function closeManagerDetail() {
  selectedManagerId.value = null;
}

// ── Édition inline utilisateur ────────────────────────────────────────────────
const editUserId = ref<string | null>(null);
const editUser = ref<{
  roleIds: string[];
  initialRoleIds: string[];
  password: string;
} | null>(null);
const editUserErr = ref('');
const editUserLoading = ref(false);

// Upload photo (formulaire d'édition gestionnaire)
// editUserId garantit qu'un seul <input ref="..."> de la boucle v-for est
// monté à la fois, mais Vue collecte quand même toutes les refs partageant le
// même nom dans une boucle sous forme de tableau : une ref string classique
// ici deviendrait un HTMLInputElement[], d'où le .click() qui échouait.
let editUserPhotoInputEl: HTMLInputElement | null = null;
function setEditUserPhotoInput(el: unknown) {
  editUserPhotoInputEl = (el as HTMLInputElement | null) ?? null;
}
const isUploadingUserPhoto = ref(false);
const editUserPhotoErr = ref('');
function triggerEditUserPhotoPicker() {
  editUserPhotoInputEl?.click();
}
async function onEditUserPhotoSelected(e: Event, u: any) {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (!file || !u?.id) return;
  isUploadingUserPhoto.value = true;
  editUserPhotoErr.value = '';
  try {
    await userStore.updateUserPhotoById(
      u.id,
      file,
      [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email,
      id.value,
    );
    await queryClient.invalidateQueries({ queryKey: ['cdp-users', id.value] });
    showUsrToast('success', t('commerceDetail.photoUpdated'));
  } catch (err: any) {
    const msg = err?.response?.data?.message;
    editUserPhotoErr.value = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.photoUpdateError'));
    showUsrToast('error', editUserPhotoErr.value);
  } finally {
    isUploadingUserPhoto.value = false;
  }
}

function openEditUser(u: any) {
  if (editUserId.value === u.id) {
    editUserId.value = null;
    editUser.value = null;
    return;
  }
  editUserId.value = u.id;
  const rolesList = u.roles ?? [];
  const visibleRoleIds = rolesList
    .filter(
      (r: any) => !EXCLUDED_STAFF_ROLES.includes((r.code ?? '').toLowerCase()),
    )
    .map((r: any) => r.id)
    .filter(Boolean);
  editUser.value = {
    roleIds: [...visibleRoleIds],
    initialRoleIds: visibleRoleIds,
    password: '',
  };
  editUserErr.value = '';
  editUserPhotoErr.value = '';
}
function closeEditUser() {
  editUserId.value = null;
  editUser.value = null;
  editUserErr.value = '';
  editUserPhotoErr.value = '';
}

async function saveEditUser(u: any) {
  if (!editUser.value) return;
  editUserLoading.value = true;
  editUserErr.value = '';
  try {
    const { roleIds, initialRoleIds } = editUser.value;
    const addedRoleIds = roleIds.filter((rid) => !initialRoleIds.includes(rid));
    const removedRoleIds = initialRoleIds.filter(
      (rid) => !roleIds.includes(rid),
    );

    if (addedRoleIds.length) {
      await userStore.assignUserRolesById(
        u.id,
        addedRoleIds.map((roleId) => ({ roleId, restaurantId: id.value })),
      );
    }
    if (removedRoleIds.length) {
      // removeUserRoles cible userId.value (ref global partagé) plutôt qu'un
      // paramètre — même pattern que staffPage.vue, on le positionne juste
      // avant l'appel.
      globalUserId.value = u.id;
      await userStore.removeUserRoles({
        removals: removedRoleIds.map((roleId) => ({
          roleId,
          restaurantId: id.value,
        })),
      });
    }
    if (editUser.value.password.trim()) {
      await userStore.updateUserPasswordById(
        u.id,
        editUser.value.password.trim(),
      );
    }
    await queryClient.invalidateQueries({ queryKey: ['cdp-users', id.value] });
    closeEditUser();
    showUsrToast(
      'success',
      t('commerceDetail.userUpdated', { email: u.email }),
    );
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    const txt = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.userUpdateError'));
    editUserErr.value = txt;
    showUsrToast('error', txt);
  } finally {
    editUserLoading.value = false;
  }
}

// ── Suppression inline utilisateur ───────────────────────────────────────────
const confirmDeleteUserId = ref<string | null>(null);
const deletingUserId = ref<string | null>(null);
function askDeleteUser(u: any) {
  confirmDeleteUserId.value = confirmDeleteUserId.value === u.id ? null : u.id;
}
function cancelDeleteUser() {
  confirmDeleteUserId.value = null;
}
async function doDeleteUser(u: any) {
  deletingUserId.value = u.id;
  try {
    await axiosInstance.delete(`auth/delete/${u.id}`);
    await queryClient.invalidateQueries({ queryKey: ['cdp-users', id.value] });
  } catch {
    /* silently ignore */
  }
  confirmDeleteUserId.value = null;
  deletingUserId.value = null;
}

// ── Gestion des rôles ─────────────────────────────────────────────────────────
const showCreateRole = ref(false);
const createRoleErr = ref('');
const createRoleLoading = ref(false);
const newRole = ref({
  code: '',
  label: '',
  enabled: true,
  permissions: [] as string[],
});

function openCreateRole() {
  showCreateRole.value = true;
  createRoleErr.value = '';
  newRole.value = { code: '', label: '', enabled: true, permissions: [] };
}
function closeCreateRole() {
  showCreateRole.value = false;
  createRoleErr.value = '';
}
function toggleNewRolePerm(perm: string) {
  const idx = newRole.value.permissions.indexOf(perm);
  if (idx === -1) newRole.value.permissions.push(perm);
  else newRole.value.permissions.splice(idx, 1);
}

async function saveNewRole() {
  if (!newRole.value.code.trim() || !newRole.value.label.trim()) return;
  createRoleLoading.value = true;
  createRoleErr.value = '';
  try {
    await axiosInstance.post('roles/create', {
      code: newRole.value.code.trim(),
      label: newRole.value.label.trim(),
      restaurantId: id.value,
      enabled: newRole.value.enabled,
      permissions: newRole.value.permissions,
    });
    await queryClient.invalidateQueries({ queryKey: ['cdp-roles'] });
    showCreateRole.value = false;
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    createRoleErr.value = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.roleCreateError'));
  } finally {
    createRoleLoading.value = false;
  }
}

const editRoleId = ref<string | null>(null);
const editRole = ref<{
  label: string;
  enabled: boolean;
  permissions: string[];
} | null>(null);
const editRoleErr = ref('');
const editRoleLoading = ref(false);
const confirmDeleteRoleId = ref<string | null>(null);
const deletingRoleId = ref<string | null>(null);

function openEditRole(r: any) {
  if (editRoleId.value === r.id) {
    editRoleId.value = null;
    editRole.value = null;
    return;
  }
  editRoleId.value = r.id;
  editRole.value = {
    label: r.label ?? r.designation ?? '',
    enabled: r.enabled ?? r.status === 'active',
    permissions: Array.isArray(r.permissions) ? [...r.permissions] : [],
  };
  editRoleErr.value = '';
  confirmDeleteRoleId.value = null;
}
function closeEditRole() {
  editRoleId.value = null;
  editRole.value = null;
  editRoleErr.value = '';
}
function toggleEditRolePerm(perm: string) {
  if (!editRole.value) return;
  const idx = editRole.value.permissions.indexOf(perm);
  if (idx === -1) editRole.value.permissions.push(perm);
  else editRole.value.permissions.splice(idx, 1);
}

async function saveEditRole(r: any) {
  if (!editRole.value) return;
  editRoleLoading.value = true;
  editRoleErr.value = '';
  try {
    await axiosInstance.put(`roles/update/${r.id}`, {
      designation: editRole.value.label,
      status: editRole.value.enabled ? 'active' : 'inactive',
      permissions: editRole.value.permissions,
    });
    await queryClient.invalidateQueries({ queryKey: ['cdp-roles'] });
    closeEditRole();
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    editRoleErr.value = Array.isArray(msg)
      ? msg[0]
      : (msg ?? t('commerceDetail.roleUpdateError'));
  } finally {
    editRoleLoading.value = false;
  }
}

function askDeleteRole(r: any) {
  confirmDeleteRoleId.value = confirmDeleteRoleId.value === r.id ? null : r.id;
  editRoleId.value = null;
  editRole.value = null;
}
function cancelDeleteRole() {
  confirmDeleteRoleId.value = null;
}
async function doDeleteRole(r: any) {
  deletingRoleId.value = r.id;
  try {
    await axiosInstance.delete(`roles/delete/${r.id}`);
    await queryClient.invalidateQueries({ queryKey: ['cdp-roles'] });
  } catch {
    /* silently ignore */
  }
  confirmDeleteRoleId.value = null;
  deletingRoleId.value = null;
}

// ── Générateur de mot de passe ───────────────────────────────────────────────
const showNewPwd = ref(false);
const showEditPwd = ref(false);

function generatePassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const spec = '!@#$%&*-_=+?';
  const all = upper + lower + digits + spec;
  const arr = new Uint8Array(14);
  crypto.getRandomValues(arr);
  let pwd =
    upper[arr[0] % upper.length] +
    lower[arr[1] % lower.length] +
    digits[arr[2] % digits.length] +
    spec[arr[3] % spec.length];
  for (let i = 4; i < 14; i++) pwd += all[arr[i] % all.length];
  return pwd
    .split('')
    .sort(() => (crypto.getRandomValues(new Uint8Array(1))[0] > 127 ? 1 : -1))
    .join('');
}

function genNewUserPwd() {
  newUser.value.password = generatePassword();
  showNewPwd.value = true;
}
function genEditUserPwd() {
  editUser.value!.password = generatePassword();
  showEditPwd.value = true;
}

// ── Sous-onglet users/roles ──────────────────────────────────────────────────
// ── Toast succès/erreur gestionnaires ────────────────────────────────────────
const usrToast = ref<{ type: 'success' | 'error'; msg: string } | null>(null);
let usrToastTimer: ReturnType<typeof setTimeout> | null = null;
function showUsrToast(type: 'success' | 'error', msg: string) {
  if (usrToastTimer) clearTimeout(usrToastTimer);
  usrToast.value = { type, msg };
  usrToastTimer = setTimeout(() => {
    usrToast.value = null;
  }, 4000);
}
</script>

<template>
  <div>
    <!-- Modale retrait produit -->
    <Teleport to="body">
      <div
        v-if="confirmRemove"
        class="cdp__confirm-overlay"
        @click.self="cancelRemove"
      >
        <div class="cdp__confirm-modal">
          <div class="cdp__confirm-ico">
            <i class="pi pi-exclamation-triangle" />
          </div>
          <h4 class="cdp__confirm-title">
            {{ t('commerceDetail.removeProduct') }}
          </h4>
          <p class="cdp__confirm-desc">
            {{ t('commerceDetail.removeProductBody') }}
            <strong>{{ productName(confirmRemove.pid) }}</strong>
            {{ t('commerceDetail.removeProductFrom') }}
          </p>
          <div class="cdp__confirm-actions">
            <button class="cdp__btn cdp__btn--ghost" @click="cancelRemove">
              {{ t('commerceDetail.btnCancel') }}
            </button>
            <button
              class="cdp__btn cdp__btn--danger"
              :disabled="!!removingKey"
              @click="doRemoveAssign"
            >
              <span
                v-if="removingKey"
                style="
                  display: inline-block;
                  width: 0.85rem;
                  height: 0.85rem;
                  border: 2px solid rgba(255, 255, 255, 0.3);
                  border-top-color: #fff;
                  border-radius: 50%;
                  animation: cdp-spin 0.7s linear infinite;
                "
              />
              <i v-else class="pi pi-trash" />
              {{
                removingKey
                  ? t('commerceDetail.btnSaving')
                  : t('commerceDetail.removeRecBtn')
              }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <div class="cdp">
      <!-- ── Breadcrumb ── -->
      <nav class="cdp__bc">
        <button class="cdp__bc-link" @click="router.push('/commerces')">
          {{ t('commerceDetail.breadcrumbCommerces') }}
        </button>
        <template v-if="store">
          <i class="pi pi-angle-right cdp__bc-sep" />
          <span class="cdp__bc-cur">{{ store.designation }}</span>
        </template>
      </nav>

      <!-- ── Skeleton ── -->
      <div v-if="isPending" class="cdp__skeletons">
        <div class="cdp__sk cdp__sk--hero" />
        <div class="cdp__sk cdp__sk--banner" />
        <div class="cdp__sk cdp__sk--tabs" />
        <div class="cdp__sk cdp__sk--body" />
      </div>

      <!-- ── Erreur ── -->
      <div v-else-if="isError" class="cdp__error">
        <i class="pi pi-exclamation-circle" />
        <p>{{ t('commerceDetail.loadError') }}</p>
        <button
          class="cdp__btn cdp__btn--ghost"
          @click="router.push('/commerces')"
        >
          <i class="pi pi-arrow-left" /> {{ t('commerceDetail.btnBack') }}
        </button>
      </div>

      <template v-else-if="store">
        <!-- ══ Hero ═════════════════════════════════════════════════════════════ -->
        <div class="cdp__hero">
          <div class="cdp__hero-left">
            <div class="cdp__logo-wrap">
              <img v-if="logoUrl" :src="logoUrl" class="cdp__logo" alt="" />
              <div v-else class="cdp__logo-ph"><i class="pi pi-shop" /></div>
            </div>
            <div class="cdp__hero-info">
              <div class="cdp__hero-title-row">
                <h1 class="cdp__title">{{ store.designation }}</h1>
                <span
                  class="cdp__open-badge"
                  :class="
                    isActive
                      ? 'cdp__open-badge--open'
                      : 'cdp__open-badge--closed'
                  "
                >
                  <span
                    class="cdp__open-dot"
                    :class="{ 'cdp__open-dot--pulse': isActive }"
                  />
                  {{
                    isActive
                      ? t('commerceDetail.open2')
                      : t('commerceDetail.closed2')
                  }}
                </span>
              </div>
              <p class="cdp__hero-sub">
                <template v-if="store.address">
                  <i class="pi pi-map-marker cdp__hero-pin" />
                  {{ store.address }}
                </template>
              </p>
              <p
                v-if="store.organization?.name"
                class="cdp__hero-sub cdp__hero-org"
                @click="router.push(`/clients/${store.organization.id}`)"
              >
                <i class="pi pi-building cdp__hero-pin" />
                {{ store.organization.name }}
              </p>
            </div>
          </div>
          <div class="cdp__hero-actions">
            <button
              class="cdp__btn cdp__btn--outline"
              :disabled="!storeUrl"
              @click="visitStore()"
            >
              <i class="pi pi-external-link" />
              {{ t('commerceDetail.viewOnline') }}
            </button>
            <button
              class="cdp__btn cdp__btn--outline"
              :disabled="!storeUrl"
              @click="openOrShare()"
            >
              <i class="pi pi-share-alt" />
              {{ t('commerceDetail.share') }}
            </button>
            <button
              class="cdp__btn"
              :class="
                isActive ? 'cdp__btn--danger-soft' : 'cdp__btn--success-soft'
              "
              :disabled="togglingActive"
              @click="toggleActive"
            >
              <span v-if="togglingActive" class="cdp__spinner" />
              <i v-else class="pi" :class="isActive ? 'pi-pause' : 'pi-play'" />
              {{
                togglingActive
                  ? '…'
                  : isActive
                    ? t('commerceDetail.pause')
                    : t('commerceDetail.activate')
              }}
            </button>
            <button
              class="cdp__btn cdp__btn--primary cdp__btn--icon"
              @click="router.push(`/commerces/${id}/edit`)"
            >
              <i class="pi pi-pencil" />
            </button>
          </div>
        </div>

        <!-- ══ Bannière configuration (masquée à 100%) ═══════════════════════════ -->
        <div v-if="configDone < configTotal" class="cdp__banner">
          <div class="cdp__banner-body">
            <div class="cdp__banner-ring-wrap">
              <svg class="cdp__banner-svg" viewBox="0 0 56 56">
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  fill="transparent"
                  stroke-width="4"
                  class="cdp__ring-bg"
                />
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  fill="transparent"
                  stroke-width="4"
                  stroke-dasharray="150"
                  :stroke-dashoffset="150 - (150 * configPct) / 100"
                  class="cdp__ring-fill"
                />
              </svg>
              <span class="cdp__banner-pct">{{ configPct }}%</span>
            </div>
            <div class="cdp__banner-text">
              <h3 class="cdp__banner-title">
                <template v-if="configDone === configTotal">
                  {{ t('commerceDetail.allConfigured') }}
                </template>
                <template v-else>
                  {{ configTotal - configDone }}
                  {{
                    configTotal - configDone > 1
                      ? t('commerceDetail.configPoints')
                      : t('commerceDetail.configPoint')
                  }}
                  <template
                    v-if="
                      configItems.filter((i) => !i.done && i.urgent).length > 0
                    "
                  >
                    ·
                    <span class="cdp__banner-accent"
                      >{{
                        configItems.filter((i) => !i.done && i.urgent).length
                      }}
                      {{
                        configItems.filter((i) => !i.done && i.urgent).length >
                        1
                          ? t('commerceDetail.configPriorities')
                          : t('commerceDetail.configPriority')
                      }}</span
                    >
                  </template>
                </template>
              </h3>
              <p class="cdp__banner-sub">
                <template v-if="configDone === configTotal">
                  {{ t('commerceDetail.configComplete') }}
                </template>
                <template v-else>
                  {{ t('commerceDetail.configPct', { pct: configPct }) }}
                  <template
                    v-if="configItems.filter((i) => !i.done).length > 0"
                  >
                    —
                    {{
                      configItems
                        .filter((i) => !i.done)
                        .map((i) => i.label)
                        .join(', ')
                    }}.
                  </template>
                </template>
              </p>
            </div>
          </div>
          <button
            class="cdp__btn cdp__btn--primary cdp__btn--shadow"
            @click="activeTab = 'informations'"
          >
            {{
              configDone === configTotal
                ? t('commerceDetail.btnEditConfig')
                : t('commerceDetail.btnFinalizeConfig')
            }}
          </button>
        </div>

        <!-- ══ Onglets principaux ══════════════════════════════════════════════ -->
        <div class="cdp__tabs-bar">
          <button
            v-for="tab in PRIMARY_TABS"
            :key="tab.key"
            class="cdp__tab"
            :class="{ 'cdp__tab--active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            <i class="pi" :class="tab.icon" />
            {{ tab.label }}
          </button>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : CATALOGUE
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-if="activeTab === 'catalogue'" class="cdp__catalogue">
          <!-- Sous-onglets -->
          <div class="cdp__subtabs">
            <button
              v-for="st in CATALOGUE_TABS"
              :key="st.key"
              class="cdp__subtab"
              :class="{ 'cdp__subtab--active': activeCatTab === st.key }"
              @click="activeCatTab = st.key"
            >
              {{ st.label }}
            </button>
          </div>

          <!-- KPI row -->
          <div class="cdp__kpis">
            <div class="cdp__kpi">
              <p class="cdp__kpi-label">
                {{ t('commerceDetail.kpiCatalogue') }}
              </p>
              <div class="cdp__kpi-val-row">
                <span class="cdp__kpi-num">{{ kpiProductCount }}</span>
                <span class="cdp__kpi-unit">{{
                  t('commerceDetail.kpiProducts')
                }}</span>
              </div>
            </div>
            <div class="cdp__kpi">
              <p class="cdp__kpi-label">
                {{ t('commerceDetail.catalogueKpis.status') }}
              </p>
              <div class="cdp__kpi-val-row">
                <span class="cdp__kpi-num cdp__kpi-num--success">{{
                  kpiActiveProductCount
                }}</span>
                <span class="cdp__kpi-unit">{{
                  t('commerceDetail.catalogueKpis.visible')
                }}</span>
              </div>
            </div>
            <div class="cdp__kpi">
              <p class="cdp__kpi-label">
                {{ t('commerceDetail.inactiveStatus') }}
              </p>
              <div class="cdp__kpi-val-row">
                <span class="cdp__kpi-num">{{ kpiHiddenProductCount }}</span>
                <span class="cdp__kpi-unit">{{
                  t('commerceDetail.catalogueKpis.hidden')
                }}</span>
              </div>
            </div>
            <div class="cdp__kpi">
              <p class="cdp__kpi-label">
                {{ t('commerceDetail.catalogueKpis.structure') }}
              </p>
              <div class="cdp__kpi-val-row">
                <span class="cdp__kpi-num">{{ kpiCategoryCount }}</span>
                <span class="cdp__kpi-unit">{{
                  t('commerceDetail.catalogueKpis.categories')
                }}</span>
              </div>
            </div>
            <div class="cdp__kpi">
              <p class="cdp__kpi-label">
                {{ t('commerceDetail.catalogueKpis.avgPrice') }}
              </p>
              <div class="cdp__kpi-val-row">
                <span class="cdp__kpi-num cdp__kpi-num--primary">{{
                  fmtPrice(kpiAverageBasket)
                }}</span>
                <span class="cdp__kpi-unit"
                  >{{ kpiPaidOrderCount }}
                  {{ t('commerceDetail.catalogueKpis.paidOrders') }}</span
                >
              </div>
            </div>
          </div>

          <!-- Contenu sous-onglet Produits -->
          <template v-if="activeCatTab === 'produits'">
            <!-- Barre de contrôle -->
            <div class="cdp__cat-bar">
              <div class="cdp__cat-bar-left">
                <div class="cdp__search-wrap">
                  <i class="pi pi-search cdp__search-ico" />
                  <input
                    v-model="productSearch"
                    class="cdp__search-input"
                    :placeholder="t('commerceDetail.prodSearch')"
                  />
                </div>
                <!-- Tri rapide -->
                <div class="cdp__sort-group">
                  <div class="cdp__select-wrap">
                    <select
                      v-model="prodSortBy"
                      class="cdp__sort-sel"
                      @change="prodPage = 1"
                    >
                      <option
                        v-for="o in SORT_OPTIONS"
                        :key="o.value"
                        :value="o.value"
                      >
                        {{ o.label }}
                      </option>
                    </select>
                    <i class="pi pi-chevron-down cdp__sort-ico" />
                  </div>
                  <button
                    class="cdp__sort-dir-btn"
                    :title="
                      prodSortOrder === 'ASC'
                        ? t('commerceDetail.ascending')
                        : t('commerceDetail.descending')
                    "
                    @click="
                      prodSortOrder = prodSortOrder === 'ASC' ? 'DESC' : 'ASC';
                      prodPage = 1;
                    "
                  >
                    <i
                      class="pi"
                      :class="
                        prodSortOrder === 'ASC'
                          ? 'pi-sort-amount-up-alt'
                          : 'pi-sort-amount-down'
                      "
                    />
                  </button>
                </div>
                <!-- Bouton filtres -->
                <button
                  class="cdp__btn cdp__btn--outline cdp__filter-btn"
                  :class="{
                    'cdp__filter-btn--active':
                      showFilters || activeFiltersCount > 0,
                  }"
                  @click="showFilters = !showFilters"
                >
                  <i class="pi pi-filter" />
                  Filtres
                  <span
                    v-if="activeFiltersCount > 0"
                    class="cdp__filter-count"
                    >{{ activeFiltersCount }}</span
                  >
                </button>
                <div class="cdp__view-toggle">
                  <button
                    class="cdp__view-btn"
                    :class="{
                      'cdp__view-btn--active': catalogueView === 'grid',
                    }"
                    @click="catalogueView = 'grid'"
                  >
                    Grille
                  </button>
                  <button
                    class="cdp__view-btn"
                    :class="{
                      'cdp__view-btn--active': catalogueView === 'list',
                    }"
                    @click="catalogueView = 'list'"
                  >
                    Tableau
                  </button>
                </div>
              </div>
              <div class="cdp__cat-bar-right">
                <button
                  class="cdp__btn cdp__btn--outline"
                  :disabled="exporting"
                  @click="exportProducts"
                >
                  <span v-if="exporting" class="cdp__spinner" />
                  <i v-else class="pi pi-upload" />
                  {{
                    exporting
                      ? t('commerceDetail.exporting')
                      : t('commerceDetail.exportBtn')
                  }}
                </button>
                <button
                  class="cdp__btn cdp__btn--primary cdp__btn--shadow"
                  @click="
                    router.push({
                      path: '/products/new',
                      query: { storeId: id },
                    })
                  "
                >
                  <i class="pi pi-plus" />
                  {{ t('commerceDetail.btnAddProduct') }}
                </button>
              </div>
            </div>

            <!-- Panneau filtres avancés -->
            <div v-if="showFilters" class="cdp__filters-panel">
              <div class="cdp__filters-row">
                <!-- Catégories -->
                <div class="cdp__filter-group">
                  <span class="cdp__filter-lbl"
                    ><i class="pi pi-tag" />
                    {{ t('commerceDetail.filterCategories') }}</span
                  >
                  <div class="cdp__filter-chips">
                    <button
                      v-for="cat in categoriesMap"
                      :key="cat.id"
                      class="cdp__fchip"
                      :class="{
                        'cdp__fchip--active': prodCategoryIds.includes(cat.id),
                      }"
                      @click="toggleCategoryFilter(cat.id)"
                    >
                      {{
                        typeof cat.label === 'object'
                          ? ((cat.label as any).fr ??
                            (cat.label as any).en ??
                            cat.id)
                          : cat.label
                      }}
                      <span class="cdp__fchip-count">{{
                        cat.products.length
                      }}</span>
                    </button>
                  </div>
                </div>

                <!-- Statut produit -->
                <div class="cdp__filter-group">
                  <span class="cdp__filter-lbl"
                    ><i class="pi pi-star" />
                    {{ t('commerceDetail.filterAttributes') }}</span
                  >
                  <div class="cdp__filter-chips">
                    <button
                      class="cdp__fchip"
                      :class="{
                        'cdp__fchip--active': prodRecommended === true,
                      }"
                      @click="
                        prodRecommended =
                          prodRecommended === true ? null : true;
                        prodPage = 1;
                      "
                    >
                      <i class="pi pi-thumbs-up" /> Recommandés
                    </button>
                    <button
                      class="cdp__fchip"
                      :class="{ 'cdp__fchip--active': prodFavorite === true }"
                      @click="
                        prodFavorite = prodFavorite === true ? null : true;
                        prodPage = 1;
                      "
                    >
                      <i class="pi pi-heart" /> Favoris
                    </button>
                  </div>
                </div>
              </div>

              <!-- Reset -->
              <div v-if="activeFiltersCount > 0" class="cdp__filters-footer">
                <button
                  class="cdp__btn cdp__btn--ghost cdp__btn--sm"
                  @click="resetFilters"
                >
                  <i class="pi pi-times" /> Réinitialiser les filtres
                </button>
              </div>
            </div>

            <!-- Catégories + produits -->
            <div v-if="loadingProducts" class="cdp__prod-loading">
              <div class="cdp__sk cdp__sk--body" style="height: 16rem" />
            </div>
            <div v-else-if="rawProducts.length === 0" class="cdp__empty">
              <i class="pi pi-box cdp__empty-ico" />
              <p>{{ t('commerceDetail.emptyProducts') }}</p>
            </div>
            <div v-else class="cdp__categories">
              <section
                v-for="cat in filteredCategories"
                :key="cat.id"
                class="cdp__cat-section"
              >
                <div class="cdp__cat-head">
                  <h3 class="cdp__cat-title">
                    <span class="cdp__cat-icon-wrap"
                      ><i class="pi pi-tag"
                    /></span>
                    {{ cat.label }}
                    <span class="cdp__cat-count">{{
                      cat.products.length
                    }}</span>
                  </h3>
                </div>

                <!-- Grille -->
                <div v-if="catalogueView === 'grid'" class="cdp__product-grid">
                  <div
                    v-for="p in cat.products"
                    :key="p.id"
                    class="cdp__pcard cdp__pcard--clickable"
                    @click="router.push(productLink(p.id))"
                  >
                    <!-- Image -->
                    <div class="cdp__pcard-img">
                      <div
                        v-if="productImgPending(p)"
                        class="cdp__pcard-img-skeleton"
                      />
                      <img
                        v-if="showProductImg(p)"
                        v-show="productImgLoaded.has(p.id)"
                        :src="productImgUrl(p)!"
                        class="cdp__pcard-img-real"
                        alt=""
                        @load="onProductImgLoad(p.id)"
                        @error="onProductImgError(p.id)"
                      />
                      <div
                        v-else-if="!productImgPending(p)"
                        class="cdp__pcard-img-ph"
                      >
                        <i class="pi pi-image" />
                      </div>
                      <span
                        class="cdp__pcard-badge"
                        :class="
                          isProductActive(p)
                            ? 'cdp__pcard-badge--active'
                            : 'cdp__pcard-badge--off'
                        "
                      >
                        {{
                          isProductActive(p)
                            ? t('commerceDetail.activeStatus')
                            : t('commerceDetail.inactiveStatus')
                        }}
                      </span>
                    </div>
                    <!-- Corps -->
                    <div class="cdp__pcard-body">
                      <div class="cdp__pcard-type-row">
                        <span class="cdp__pcard-type-chip">
                          {{
                            (p as any).type === 'plat'
                              ? t('commerceDetail.typeProduct')
                              : (p as any).type === 'food&extra'
                                ? t('commerceDetail.typeProductExtra')
                                : ((p as any).type ??
                                  t('commerceDetail.typeProduct'))
                          }}
                        </span>
                      </div>
                      <h4 class="cdp__pcard-name">
                        {{ p.designation }}
                      </h4>
                      <p class="cdp__pcard-desc">
                        {{ (p as any).description || '—' }}
                      </p>
                      <p class="cdp__pcard-price">{{ fmtPrice(p.price) }}</p>
                      <!-- Actions -->
                      <div class="cdp__pcard-foot">
                        <button
                          class="cdp__pcard-action-btn cdp__pcard-action-btn--eye"
                          @click.stop="router.push(productLink(p.id))"
                        >
                          <i class="pi pi-eye" /> Détail
                        </button>
                        <button
                          class="cdp__pcard-action-btn cdp__pcard-action-btn--edit"
                          @click.stop="router.push(productLink(p.id, true))"
                        >
                          <i class="pi pi-pencil" />
                          {{ t('commerceDetail.btnEdit') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tableau -->
                <div v-else class="cdp__product-table-wrap">
                  <div class="cdp__product-table">
                    <div class="cdp__ptable-head">
                      <span class="cdp__ptable-th cdp__ptc--num">#</span>
                      <span class="cdp__ptable-th cdp__ptc--img">{{
                        t('commerceDetail.colImage')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--name">{{
                        t('commerceDetail.colName')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--desc">{{
                        t('commerceDetail.colDesc')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--price">{{
                        t('commerceDetail.colPrice')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--type">{{
                        t('commerceDetail.colType')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--status">{{
                        t('commerceDetail.colStatus')
                      }}</span>
                      <span class="cdp__ptable-th cdp__ptc--actions">{{
                        t('commerceDetail.colActions')
                      }}</span>
                    </div>
                    <div
                      v-for="(p, pi) in cat.products"
                      :key="p.id"
                      class="cdp__ptable-row cdp__ptable-row--clickable"
                      @click="router.push(productLink(p.id))"
                    >
                      <span
                        class="cdp__ptable-cell cdp__ptc--num"
                        style="
                          color: var(--nxt-on-surface-muted);
                          font-size: 0.8rem;
                        "
                        >{{ pi + 1 }}</span
                      >
                      <span class="cdp__ptc--img">
                        <div class="cdp__ptable-img-wrap">
                          <div
                            v-if="productImgPending(p)"
                            class="cdp__ptable-img-skeleton"
                          />
                          <img
                            v-if="showProductImg(p)"
                            v-show="productImgLoaded.has(p.id)"
                            :src="productImgUrl(p)!"
                            class="cdp__ptable-img"
                            alt=""
                            @load="onProductImgLoad(p.id)"
                            @error="onProductImgError(p.id)"
                          />
                          <i
                            v-else-if="!productImgPending(p)"
                            class="pi pi-image cdp__ptable-img-ph"
                          />
                        </div>
                      </span>
                      <span class="cdp__ptable-name cdp__ptc--name">{{
                        p.designation
                      }}</span>
                      <span
                        class="cdp__ptable-cell cdp__ptc--desc"
                        style="
                          font-size: 0.8rem;
                          color: var(--nxt-on-surface-muted);
                        "
                        >{{ (p as any).description || '—' }}</span
                      >
                      <span
                        class="cdp__ptable-cell cdp__ptc--price cdp__ptable-price"
                        style="font-weight: 700"
                        >{{ fmtPrice(p.price) }}</span
                      >
                      <span
                        class="cdp__ptable-cell cdp__ptc--type"
                        style="
                          font-size: 0.8rem;
                          color: var(--nxt-on-surface-muted);
                        "
                        >{{
                          (p as any).type === 'plat'
                            ? t('commerceDetail.typeProduct')
                            : (p as any).type === 'food&extra'
                              ? t('commerceDetail.typeProductExtra')
                              : ((p as any).type ?? '—')
                        }}</span
                      >
                      <span class="cdp__ptable-cell cdp__ptc--status">
                        <span
                          class="cdp__pcard-badge cdp__pcard-badge--inline"
                          :class="
                            isProductActive(p)
                              ? 'cdp__pcard-badge--active'
                              : 'cdp__pcard-badge--off'
                          "
                          >{{
                            isProductActive(p)
                              ? t('commerceDetail.activeStatus')
                              : t('commerceDetail.inactiveStatus')
                          }}</span
                        >
                      </span>
                      <span
                        class="cdp__ptable-cell cdp__ptc--actions"
                        style="
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          gap: 6px;
                        "
                      >
                        <button
                          class="cdp__pcard-edit"
                          :title="t('commerceDetail.btnEdit')"
                          @click.stop="router.push(productLink(p.id, true))"
                        >
                          <i class="pi pi-pencil" />
                        </button>
                        <button
                          class="cdp__pcard-edit"
                          :title="t('commerceDetail.detail')"
                          style="background: var(--nxt-surface-low, #f5f5f5)"
                          @click.stop="router.push(productLink(p.id))"
                        >
                          <i class="pi pi-eye" />
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <div
                v-if="
                  filteredCategories.length === 0 &&
                  !loadingProducts &&
                  allProducts.length > 0
                "
                class="cdp__empty"
              >
                <i class="pi pi-search cdp__empty-ico" />
                <p>{{ t('commerceDetail.emptyProductsSearch') }}</p>
              </div>
            </div>

            <!-- ── Barre pagination ── -->
            <div v-if="totalProds > 0" class="cdp__prod-pag">
              <div class="cdp__prod-pag-left">
                <span class="cdp__prod-pag-info"
                  >{{ totalProds }}
                  {{
                    totalProds > 1
                      ? t('commerceDetail.pagInfoPlural')
                      : t('commerceDetail.pagInfo')
                  }}
                  {{ prodPage }} / {{ totalPages }}</span
                >
                <select
                  class="cdp__pagesize-sel"
                  v-model="prodPageSize"
                  @change="onPageSizeChange"
                >
                  <option v-for="s in PAGE_SIZE_OPTIONS" :key="s" :value="s">
                    {{ s }} {{ t('commerceDetail.perPage') }}
                  </option>
                </select>
              </div>
              <div class="cdp__prod-pag-btns">
                <button
                  class="cdp__pag-btn"
                  :disabled="prodPage === 1"
                  @click="goToProdPage(1)"
                >
                  <i class="pi pi-angle-double-left" />
                </button>
                <button
                  class="cdp__pag-btn"
                  :disabled="prodPage === 1"
                  @click="goToProdPage(prodPage - 1)"
                >
                  <i class="pi pi-angle-left" />
                </button>
                <template v-for="n in prodPageNums" :key="n">
                  <span v-if="n === '…'" class="cdp__pag-ellipsis">…</span>
                  <button
                    v-else
                    class="cdp__pag-btn"
                    :class="{ 'cdp__pag-btn--active': n === prodPage }"
                    @click="goToProdPage(n as number)"
                  >
                    {{ n }}
                  </button>
                </template>
                <button
                  class="cdp__pag-btn"
                  :disabled="prodPage === totalPages"
                  @click="goToProdPage(prodPage + 1)"
                >
                  <i class="pi pi-angle-right" />
                </button>
                <button
                  class="cdp__pag-btn"
                  :disabled="prodPage === totalPages"
                  @click="goToProdPage(totalPages)"
                >
                  <i class="pi pi-angle-double-right" />
                </button>
              </div>
            </div>
          </template>

          <!-- ══ Sous-onglet : Assignation ══════════════════════════════════════ -->
          <template v-else-if="activeCatTab === 'assignation'">
            <div class="cdp__assign">
              <!-- Colonnes -->
              <div class="cdp__assign-layout">
                <!-- Gauche : liste produits -->
                <div class="cdp__assign-left">
                  <div class="cdp__assign-card">
                    <div class="cdp__assign-card-hd">
                      <i
                        class="pi pi-box"
                        style="font-size: 1.1rem; color: var(--nxt-primary)"
                      />
                      <span class="cdp__assign-card-title">{{
                        t('commerceDetail.kpiProducts')
                      }}</span>
                      <span class="cdp__assign-count">{{
                        assignAllProducts.length
                      }}</span>
                    </div>
                    <div class="cdp__assign-search-wrap">
                      <i class="pi pi-search cdp__assign-search-ico" />
                      <input
                        v-model="assignSearch"
                        class="cdp__assign-search"
                        :placeholder="t('commerceDetail.assignSearch')"
                      />
                    </div>
                    <div
                      v-if="loadingAssignProducts"
                      class="cdp__assign-loading"
                    >
                      <span class="cdp__spinner" />
                    </div>
                    <div
                      v-else
                      class="cdp__assign-prod-list"
                      @click.self="closePicker"
                    >
                      <div
                        v-for="p in assignFilteredProducts"
                        :key="p.id"
                        class="cdp__assign-prod"
                        :class="{
                          'cdp__assign-prod--assigned': isAssigned(p.id),
                          'cdp__assign-prod--active': pickerPid === p.id,
                        }"
                        style="position: relative; cursor: grab"
                        draggable="true"
                        @dragstart="onDragStart($event, p)"
                        @click="togglePicker(p.id)"
                      >
                        <div class="cdp__assign-prod-img">
                          <div
                            v-if="productImgPending(p)"
                            class="cdp__assign-prod-img-skeleton"
                          />
                          <img
                            v-if="showProductImg(p)"
                            v-show="productImgLoaded.has(p.id)"
                            :src="productImgUrl(p)!"
                            alt=""
                            @load="onProductImgLoad(p.id)"
                            @error="onProductImgError(p.id)"
                          />
                          <i
                            v-else-if="!productImgPending(p)"
                            class="pi pi-image"
                          />
                        </div>
                        <div class="cdp__assign-prod-info">
                          <span class="cdp__assign-prod-name">{{
                            p.designation
                          }}</span>
                          <span class="cdp__assign-prod-price">{{
                            fmtPrice(p.price)
                          }}</span>
                        </div>
                        <div class="cdp__assign-prod-right">
                          <span v-if="isAssigned(p.id)" class="cdp__assign-pill"
                            ><i class="pi pi-check"
                          /></span>
                          <i class="pi pi-bars cdp__assign-drag-ico" />
                        </div>

                        <!-- Picker catégories -->
                        <div
                          v-if="pickerPid === p.id"
                          class="cdp__assign-picker"
                          @click.stop
                        >
                          <div class="cdp__assign-picker-title">
                            Assigner à…
                          </div>
                          <label
                            v-for="cat in orgCategoriesMap"
                            :key="cat.id"
                            class="cdp__assign-picker-row"
                          >
                            <input
                              type="checkbox"
                              :checked="isPidInCat(cat.id, p.id)"
                              @change="toggleCatForProduct(cat.id, p.id)"
                            />
                            <span>{{
                              typeof cat.label === 'object'
                                ? ((cat.label as any).fr ?? cat.id)
                                : cat.label
                            }}</span>
                          </label>
                          <div
                            v-if="!orgCategoriesMap.length"
                            style="
                              font-size: 0.75rem;
                              color: #aaa;
                              padding: 0.25rem 0;
                            "
                          >
                            {{ t('commerceDetail.noCategory') }}
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="assignFilteredProducts.length === 0"
                        class="cdp__assign-empty"
                      >
                        <i class="pi pi-search" />
                        {{ t('commerceDetail.emptyProductsSearch') }}
                      </div>
                    </div>

                    <!-- Pagination + taille de page, indépendantes du sous-onglet Produits -->
                    <div class="cdp__assign-pagination-bar">
                      <select
                        v-model.number="assignPageSize"
                        class="cdp__sort-sel"
                      >
                        <option
                          v-for="ps in ASSIGN_PAGE_SIZES"
                          :key="ps"
                          :value="ps"
                        >
                          {{ ps }} {{ t('commerceDetail.perPage') }}
                        </option>
                      </select>
                      <div
                        v-if="assignTotalPages > 1"
                        class="cdp__prod-pag-btns"
                      >
                        <button
                          class="cdp__pag-btn"
                          :disabled="assignPage === 1"
                          @click="assignPage = 1"
                        >
                          <i class="pi pi-angle-double-left" />
                        </button>
                        <button
                          class="cdp__pag-btn"
                          :disabled="assignPage === 1"
                          @click="assignPage--"
                        >
                          <i class="pi pi-angle-left" />
                        </button>
                        <button
                          v-for="n in assignVisiblePages"
                          :key="n"
                          class="cdp__pag-btn"
                          :class="{ 'cdp__pag-btn--active': n === assignPage }"
                          @click="assignPage = n"
                        >
                          {{ n }}
                        </button>
                        <button
                          class="cdp__pag-btn"
                          :disabled="assignPage === assignTotalPages"
                          @click="assignPage++"
                        >
                          <i class="pi pi-angle-right" />
                        </button>
                        <button
                          class="cdp__pag-btn"
                          :disabled="assignPage === assignTotalPages"
                          @click="assignPage = assignTotalPages"
                        >
                          <i class="pi pi-angle-double-right" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Droite : collections -->
                <div class="cdp__assign-right-wrap">
                  <div class="cdp__assign-card-hd">
                    <i
                      class="pi pi-tag"
                      style="font-size: 1.1rem; color: var(--nxt-primary)"
                    />
                    <span class="cdp__assign-card-title">{{
                      t('commerceDetail.catalogueKpis.categories')
                    }}</span>
                    <span class="cdp__assign-count">{{
                      orgCategoriesMap.length
                    }}</span>
                  </div>
                  <div class="cdp__assign-search-wrap">
                    <i class="pi pi-search cdp__assign-search-ico" />
                    <input
                      v-model="catSearch"
                      class="cdp__assign-search"
                      :placeholder="t('commerceDetail.assignSearch')"
                    />
                  </div>
                  <div class="cdp__assign-right">
                    <div
                      v-for="cat in paginatedCategories"
                      :key="cat.id"
                      class="cdp__assign-col-card"
                      :class="{
                        'cdp__assign-col-card--over': dragOverCat === cat.id,
                      }"
                      :data-cat-id="cat.id"
                      @dragover.prevent="dragOverCat = cat.id"
                      @dragleave="dragOverCat = null"
                      @drop="onDrop($event, cat.id)"
                    >
                      <div class="cdp__assign-col-hd">
                        <div
                          class="cdp__assign-col-ico"
                          :class="{
                            'cdp__assign-col-ico--none': cat.id === '__none',
                          }"
                        >
                          <i
                            :class="
                              cat.id === '__none' ? 'pi pi-inbox' : 'pi pi-tag'
                            "
                          />
                        </div>
                        <div class="cdp__assign-col-info">
                          <span class="cdp__assign-col-name">{{
                            typeof cat.label === 'object'
                              ? ((cat.label as any).fr ?? cat.id)
                              : cat.label
                          }}</span>
                          <span class="cdp__assign-col-count">{{
                            t(
                              'commerceDetail.productCount',
                              { n: assignedMap[cat.id]?.length ?? 0 },
                              assignedMap[cat.id]?.length ?? 0,
                            )
                          }}</span>
                        </div>
                      </div>
                      <div
                        class="cdp__assign-dropzone"
                        :class="{
                          'cdp__assign-dropzone--over': dragOverCat === cat.id,
                        }"
                      >
                        <div
                          v-if="!assignedMap[cat.id]?.length"
                          class="cdp__assign-dropzone-empty"
                        >
                          <i class="pi pi-arrow-down" />
                          <span>{{ t('commerceDetail.dropHere') }}</span>
                        </div>
                        <div v-else class="cdp__assign-chips">
                          <div
                            v-for="pid in assignedMap[cat.id]"
                            :key="pid"
                            class="cdp__assign-chip"
                          >
                            <span>{{ productName(pid) }}</span>
                            <button
                              v-if="cat.id !== '__none'"
                              class="cdp__assign-chip-rm"
                              :disabled="removingKey === `${cat.id}:${pid}`"
                              @click="askRemoveAssign(cat.id, pid)"
                            >
                              <span
                                v-if="removingKey === `${cat.id}:${pid}`"
                                class="cdp__spinner cdp__spinner--xs"
                              />
                              <i v-else class="pi pi-times" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="paginatedCategories.length === 0"
                      class="cdp__assign-no-cats"
                    >
                      <i class="pi pi-folder-open" />
                      <p>{{ t('commerceDetail.emptyCategories') }}</p>
                    </div>
                  </div>

                  <!-- Pagination + taille de page, indépendantes du reste -->
                  <div class="cdp__assign-pagination-bar">
                    <select v-model.number="catPageSize" class="cdp__sort-sel">
                      <option
                        v-for="ps in CAT_PAGE_SIZES"
                        :key="ps"
                        :value="ps"
                      >
                        {{ ps }} {{ t('commerceDetail.perPage') }}
                      </option>
                    </select>
                    <div v-if="catTotalPages > 1" class="cdp__prod-pag-btns">
                      <button
                        class="cdp__pag-btn"
                        :disabled="catPage === 1"
                        @click="catPage = 1"
                      >
                        <i class="pi pi-angle-double-left" />
                      </button>
                      <button
                        class="cdp__pag-btn"
                        :disabled="catPage === 1"
                        @click="catPage--"
                      >
                        <i class="pi pi-angle-left" />
                      </button>
                      <button
                        v-for="n in catVisiblePages"
                        :key="n"
                        class="cdp__pag-btn"
                        :class="{ 'cdp__pag-btn--active': n === catPage }"
                        @click="catPage = n"
                      >
                        {{ n }}
                      </button>
                      <button
                        class="cdp__pag-btn"
                        :disabled="catPage === catTotalPages"
                        @click="catPage++"
                      >
                        <i class="pi pi-angle-right" />
                      </button>
                      <button
                        class="cdp__pag-btn"
                        :disabled="catPage === catTotalPages"
                        @click="catPage = catTotalPages"
                      >
                        <i class="pi pi-angle-double-right" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="cdp__assign-footer">
                <span class="cdp__assign-footer-info"
                  ><i class="pi pi-info-circle" />
                  {{ t('commerceDetail.assignHint') }}</span
                >
              </div>
            </div>
          </template>

          <!-- ══ Sous-onglet : Collections ════════════════════════════════════ -->
          <template v-else-if="activeCatTab === 'collections'">
            <div class="cdp__cols">
              <!-- Barre recherche + taille de page -->
              <div class="cdp__cols-toolbar">
                <div class="cdp__search-wrap">
                  <i class="pi pi-search cdp__search-ico" />
                  <input
                    v-model="colSearch"
                    class="cdp__search-input"
                    :placeholder="t('commerceDetail.assignSearch')"
                  />
                </div>
                <select v-model.number="colPageSize" class="cdp__sort-sel">
                  <option v-for="ps in COL_PAGE_SIZES" :key="ps" :value="ps">
                    {{ ps }} {{ t('commerceDetail.perPage') }}
                  </option>
                </select>
                <span class="cdp__assign-count">{{
                  colSearchFiltered.length
                }}</span>
                <button
                  class="cdp__cols-new-btn cdp__cols-new-btn--toolbar"
                  :disabled="showNewColForm"
                  @click="openNewColForm()"
                >
                  <i class="pi pi-plus" />
                  {{ t('commerceDetail.newCollection') }}
                </button>
              </div>

              <!-- Formulaire nouvelle collection -->
              <div v-if="showNewColForm" class="cdp__ncol">
                <div class="cdp__ncol-hd">
                  <div class="cdp__ncol-hd-ico">
                    <i class="pi pi-folder-plus" />
                  </div>
                  <span class="cdp__ncol-title">{{
                    t('commerceDetail.newCollection')
                  }}</span>
                  <div class="cdp__ncol-lang-toggle">
                    <button
                      v-for="lang in ['fr', 'en', 'nl'] as const"
                      :key="lang"
                      type="button"
                      class="cdp__ncol-lang-btn"
                      :class="{
                        'cdp__ncol-lang-btn--active': newColLang === lang,
                      }"
                      @click="newColLang = lang"
                    >
                      {{ lang.toUpperCase() }}
                    </button>
                  </div>
                </div>
                <div class="cdp__ncol-row">
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColLang === 'fr',
                        }"
                        >FR</span
                      >
                      Français
                      <span class="cdp__ncol-req">*</span>
                    </label>
                    <input
                      v-model="newColFr"
                      class="cdp__ncol-input"
                      :class="{
                        'cdp__ncol-input--active': newColLang === 'fr',
                      }"
                      type="text"
                      placeholder="Nom en français…"
                      @blur="onNewColBlur('fr')"
                    />
                  </div>
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColLang === 'en',
                        }"
                        >EN</span
                      >
                      English
                      <i
                        v-if="newColTranslating && newColLang !== 'en'"
                        class="pi pi-spin pi-spinner cdp__ncol-spin"
                      />
                    </label>
                    <input
                      v-model="newColEn"
                      class="cdp__ncol-input"
                      :class="{
                        'cdp__ncol-input--active': newColLang === 'en',
                      }"
                      type="text"
                      placeholder="Name in English…"
                      @blur="onNewColBlur('en')"
                    />
                  </div>
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColLang === 'nl',
                        }"
                        >NL</span
                      >
                      Nederlands
                      <i
                        v-if="newColTranslating && newColLang !== 'nl'"
                        class="pi pi-spin pi-spinner cdp__ncol-spin"
                      />
                    </label>
                    <input
                      v-model="newColNl"
                      class="cdp__ncol-input"
                      :class="{
                        'cdp__ncol-input--active': newColLang === 'nl',
                      }"
                      type="text"
                      placeholder="Naam…"
                      @blur="onNewColBlur('nl')"
                    />
                  </div>
                </div>

                <div class="cdp__ncol-hd" style="margin-top: 0.25rem">
                  <span class="cdp__ncol-title" style="font-size: 0.8125rem">{{
                    t('allCollections.newDescLabel')
                  }}</span>
                  <div class="cdp__ncol-lang-toggle">
                    <button
                      v-for="lang in ['fr', 'en', 'nl'] as const"
                      :key="lang"
                      type="button"
                      class="cdp__ncol-lang-btn"
                      :class="{
                        'cdp__ncol-lang-btn--active': newColDescLang === lang,
                      }"
                      @click="newColDescLang = lang"
                    >
                      {{ lang.toUpperCase() }}
                    </button>
                  </div>
                </div>
                <div class="cdp__ncol-row">
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColDescLang === 'fr',
                        }"
                        >FR</span
                      >
                      Français
                    </label>
                    <textarea
                      v-model="newColDescFr"
                      class="cdp__ncol-input cdp__ncol-textarea"
                      :class="{
                        'cdp__ncol-input--active': newColDescLang === 'fr',
                      }"
                      rows="2"
                      placeholder="Description en français…"
                      @blur="onNewColDescBlur('fr')"
                    />
                  </div>
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColDescLang === 'en',
                        }"
                        >EN</span
                      >
                      English
                      <i
                        v-if="newColDescTranslating && newColDescLang !== 'en'"
                        class="pi pi-spin pi-spinner cdp__ncol-spin"
                      />
                    </label>
                    <textarea
                      v-model="newColDescEn"
                      class="cdp__ncol-input cdp__ncol-textarea"
                      :class="{
                        'cdp__ncol-input--active': newColDescLang === 'en',
                      }"
                      rows="2"
                      placeholder="Description in English…"
                      @blur="onNewColDescBlur('en')"
                    />
                  </div>
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">
                      <span
                        class="cdp__ncol-badge"
                        :class="{
                          'cdp__ncol-badge--active': newColDescLang === 'nl',
                        }"
                        >NL</span
                      >
                      Nederlands
                      <i
                        v-if="newColDescTranslating && newColDescLang !== 'nl'"
                        class="pi pi-spin pi-spinner cdp__ncol-spin"
                      />
                    </label>
                    <textarea
                      v-model="newColDescNl"
                      class="cdp__ncol-input cdp__ncol-textarea"
                      :class="{
                        'cdp__ncol-input--active': newColDescLang === 'nl',
                      }"
                      rows="2"
                      placeholder="Beschrijving…"
                      @blur="onNewColDescBlur('nl')"
                    />
                  </div>
                </div>

                <div class="cdp__ncol-field">
                  <label class="cdp__ncol-label">{{
                    t('allCollections.modulesLabel')
                  }}</label>
                  <div class="cdp__ncol-modules">
                    <button
                      v-for="m in MODULE_OPTIONS"
                      :key="m.id"
                      type="button"
                      class="cdp__ncol-module-chip"
                      :class="{
                        'cdp__ncol-module-chip--on': newColModules.includes(
                          m.id,
                        ),
                      }"
                      @click="toggleNewColModule(m.id)"
                    >
                      <i :class="['pi', m.icon]" />
                      {{ t(m.labelKey) }}
                    </button>
                  </div>
                </div>

                <div v-if="newColError" class="cdp__ncol-error">
                  <i class="pi pi-exclamation-circle" /> {{ newColError }}
                </div>
                <div class="cdp__ncol-actions">
                  <button
                    class="cdp__ncol-btn cdp__ncol-btn--ghost"
                    :disabled="newColSaving"
                    @click="closeNewColForm()"
                  >
                    {{ t('product.cancel') }}
                  </button>
                  <button
                    class="cdp__ncol-btn cdp__ncol-btn--primary"
                    :disabled="newColSaving || !newColFr.trim()"
                    @click="confirmNewCol()"
                  >
                    <i v-if="newColSaving" class="pi pi-spin pi-spinner" />
                    <i v-else class="pi pi-check" />
                    {{
                      newColSaving ? t('product.saving') : t('product.create')
                    }}
                  </button>
                </div>
              </div>
              <!-- Table header -->
              <div class="cdp__cols-thead">
                <span></span>
                <span class="cdp__cols-th">{{
                  t('commerceDetail.colCollection')
                }}</span>
                <span class="cdp__cols-th">{{
                  t('commerceDetail.colFeatured')
                }}</span>
                <span class="cdp__cols-th">{{
                  t('commerceDetail.colChannels')
                }}</span>
                <span class="cdp__cols-th">{{
                  t('commerceDetail.colProducts')
                }}</span>
                <span class="cdp__cols-th cdp__cols-th--r">{{
                  t('commerceDetail.colActions')
                }}</span>
              </div>

              <!-- Rows -->
              <div
                v-if="paginatedCollections.length === 0"
                class="cdp__cols-empty"
              >
                <i class="pi pi-folder-open" />
                <p>{{ t('commerceDetail.emptyCollections') }}</p>
              </div>

              <template v-for="cat in paginatedCollections" :key="cat.id">
                <!-- Formulaire édition inline -->
                <div
                  v-if="editingColId === cat.id"
                  class="cdp__ncol cdp__ncol--row"
                >
                  <div class="cdp__ncol-hd">
                    <div class="cdp__ncol-hd-ico">
                      <i class="pi pi-pencil" />
                    </div>
                    <span class="cdp__ncol-title">{{
                      t('commerceDetail.editCollection')
                    }}</span>
                    <div class="cdp__ncol-lang-toggle">
                      <button
                        v-for="lang in ['fr', 'en', 'nl'] as const"
                        :key="lang"
                        type="button"
                        class="cdp__ncol-lang-btn"
                        :class="{
                          'cdp__ncol-lang-btn--active': editColLang === lang,
                        }"
                        @click="editColLang = lang"
                      >
                        {{ lang.toUpperCase() }}
                      </button>
                    </div>
                  </div>
                  <div class="cdp__ncol-row">
                    <div class="cdp__ncol-field">
                      <label class="cdp__ncol-label">
                        <span
                          class="cdp__ncol-badge"
                          :class="{
                            'cdp__ncol-badge--active': editColLang === 'fr',
                          }"
                          >FR</span
                        >
                        Français
                        <span class="cdp__ncol-req">*</span>
                      </label>
                      <input
                        v-model="editColFr"
                        class="cdp__ncol-input"
                        :class="{
                          'cdp__ncol-input--active': editColLang === 'fr',
                        }"
                        type="text"
                        @blur="onEditColBlur('fr')"
                        @keyup.enter="confirmEditCol()"
                        @keyup.escape="cancelEditCol()"
                      />
                    </div>
                    <div class="cdp__ncol-field">
                      <label class="cdp__ncol-label">
                        <span
                          class="cdp__ncol-badge"
                          :class="{
                            'cdp__ncol-badge--active': editColLang === 'en',
                          }"
                          >EN</span
                        >
                        English
                        <i
                          v-if="editColTranslating && editColLang !== 'en'"
                          class="pi pi-spin pi-spinner cdp__ncol-spin"
                        />
                      </label>
                      <input
                        v-model="editColEn"
                        class="cdp__ncol-input"
                        :class="{
                          'cdp__ncol-input--active': editColLang === 'en',
                        }"
                        type="text"
                        @blur="onEditColBlur('en')"
                        @keyup.enter="confirmEditCol()"
                        @keyup.escape="cancelEditCol()"
                      />
                    </div>
                    <div class="cdp__ncol-field">
                      <label class="cdp__ncol-label">
                        <span
                          class="cdp__ncol-badge"
                          :class="{
                            'cdp__ncol-badge--active': editColLang === 'nl',
                          }"
                          >NL</span
                        >
                        Nederlands
                        <i
                          v-if="editColTranslating && editColLang !== 'nl'"
                          class="pi pi-spin pi-spinner cdp__ncol-spin"
                        />
                      </label>
                      <input
                        v-model="editColNl"
                        class="cdp__ncol-input"
                        :class="{
                          'cdp__ncol-input--active': editColLang === 'nl',
                        }"
                        type="text"
                        @blur="onEditColBlur('nl')"
                        @keyup.enter="confirmEditCol()"
                        @keyup.escape="cancelEditCol()"
                      />
                    </div>
                  </div>
                  <div class="cdp__ncol-field">
                    <label class="cdp__ncol-label">{{
                      t('allCollections.modulesLabel')
                    }}</label>
                    <div class="cdp__ncol-modules">
                      <button
                        v-for="m in MODULE_OPTIONS"
                        :key="m.id"
                        type="button"
                        class="cdp__ncol-module-chip"
                        :class="{
                          'cdp__ncol-module-chip--on': editColModules.includes(
                            m.id,
                          ),
                        }"
                        @click="toggleEditColModule(m.id)"
                      >
                        <i :class="['pi', m.icon]" />
                        {{ t(m.labelKey) }}
                      </button>
                    </div>
                  </div>

                  <div v-if="editColError" class="cdp__ncol-error">
                    <i class="pi pi-exclamation-circle" /> {{ editColError }}
                  </div>
                  <div class="cdp__ncol-actions">
                    <button
                      class="cdp__ncol-btn cdp__ncol-btn--ghost"
                      :disabled="editColSaving"
                      @click="cancelEditCol()"
                    >
                      {{ t('product.cancel') }}
                    </button>
                    <button
                      class="cdp__ncol-btn cdp__ncol-btn--primary"
                      :disabled="editColSaving || !editColFr.trim()"
                      @click="confirmEditCol()"
                    >
                      <i v-if="editColSaving" class="pi pi-spin pi-spinner" />
                      <i v-else class="pi pi-check" />
                      {{
                        editColSaving ? t('product.saving') : t('product.save')
                      }}
                    </button>
                  </div>
                </div>

                <!-- Row normale -->
                <div
                  v-else
                  class="cdp__cols-row"
                  :class="{
                    'cdp__cols-row--drag-over': colDragOver === cat.id,
                  }"
                  draggable="true"
                  @dragstart.stop="onColDragStart(cat.id)"
                  @dragend.stop="onColDragEnd"
                  @dragenter.prevent="onColDragEnter(cat.id)"
                  @dragover.prevent
                  @drop.prevent="onColDrop(cat.id)"
                >
                  <!-- Handle -->
                  <div
                    class="cdp__cols-handle"
                    :title="t('commerceDetail.reorder')"
                  >
                    <i class="pi pi-bars" />
                  </div>

                  <!-- Icône + nom (cliquable pour éditer) -->
                  <button
                    type="button"
                    class="cdp__cols-cell cdp__cols-cell--name cdp__cols-cell--clickable"
                    :title="t('commerceDetail.clickToEdit')"
                    @click="startEditCol(cat)"
                  >
                    <div class="cdp__cols-ico">
                      <i class="pi pi-tag" />
                    </div>
                    <div class="cdp__cols-info">
                      <span class="cdp__cols-name">{{
                        typeof cat.label === 'object'
                          ? ((cat.label as any).fr ?? cat.id)
                          : cat.label
                      }}</span>
                      <span class="cdp__cols-id">{{ cat.id }}</span>
                    </div>
                    <i class="pi pi-pencil cdp__cols-edit-hint" />
                  </button>

                  <!-- Mise en avant -->
                  <div class="cdp__cols-cell">
                    <span class="cdp__cols-badge cdp__cols-badge--std">
                      <i class="pi pi-minus-circle" />
                      {{ t('commerceDetail.standard') }}
                    </span>
                  </div>

                  <!-- Canaux diffusés -->
                  <div class="cdp__cols-cell cdp__cols-cell--channels">
                    <span
                      v-if="!cat.modules.length"
                      class="cdp__cols-channel cdp__cols-channel--all"
                    >
                      <i class="pi pi-globe" />
                      {{ t('commerceDetail.allChannels') }}
                    </span>
                    <span
                      v-for="mid in cat.modules"
                      :key="mid"
                      class="cdp__cols-channel"
                    >
                      <i :class="['pi', moduleOption(mid)?.icon]" />
                      {{
                        moduleOption(mid) ? t(moduleOption(mid)!.labelKey) : mid
                      }}
                    </span>
                  </div>

                  <!-- Nb produits -->
                  <div class="cdp__cols-cell">
                    <span
                      class="cdp__cols-count-badge"
                      :class="{
                        'cdp__cols-count-badge--empty':
                          cat.products.length === 0,
                      }"
                      :title="
                        cat.products.length === 0
                          ? t('commerceDetail.emptyCollectionHint')
                          : undefined
                      "
                    >
                      <i
                        v-if="cat.products.length === 0"
                        class="pi pi-exclamation-triangle"
                      />
                      {{
                        cat.products.length === 0
                          ? t('commerceDetail.emptyCollectionBadge')
                          : cat.products.length
                      }}
                    </span>
                  </div>

                  <!-- Actions -->
                  <div class="cdp__cols-cell cdp__cols-cell--actions">
                    <button
                      class="cdp__cols-btn cdp__cols-btn--assign"
                      @click="goToAssign(cat.id)"
                    >
                      <i class="pi pi-th-large" />
                      {{ t('commerceDetail.manageProducts') }}
                    </button>
                    <button
                      class="cdp__cols-icon-btn cdp__cols-icon-btn--danger"
                      :disabled="deletingColId === cat.id"
                      :title="t('product.delete')"
                      @click="deleteCol(cat.id)"
                    >
                      <i
                        v-if="deletingColId === cat.id"
                        class="pi pi-spin pi-spinner"
                      />
                      <i v-else class="pi pi-trash" />
                    </button>
                  </div>
                </div>
              </template>

              <!-- Pagination -->
              <div v-if="colTotalPages > 1" class="cdp__prod-pag-btns">
                <button
                  class="cdp__pag-btn"
                  :disabled="colPage === 1"
                  @click="colPage = 1"
                >
                  <i class="pi pi-angle-double-left" />
                </button>
                <button
                  class="cdp__pag-btn"
                  :disabled="colPage === 1"
                  @click="colPage--"
                >
                  <i class="pi pi-angle-left" />
                </button>
                <button
                  v-for="n in colVisiblePages"
                  :key="n"
                  class="cdp__pag-btn"
                  :class="{ 'cdp__pag-btn--active': n === colPage }"
                  @click="colPage = n"
                >
                  {{ n }}
                </button>
                <button
                  class="cdp__pag-btn"
                  :disabled="colPage === colTotalPages"
                  @click="colPage++"
                >
                  <i class="pi pi-angle-right" />
                </button>
                <button
                  class="cdp__pag-btn"
                  :disabled="colPage === colTotalPages"
                  @click="colPage = colTotalPages"
                >
                  <i class="pi pi-angle-double-right" />
                </button>
              </div>

              <!-- Stats du bas -->
              <div class="cdp__cols-stats">
                <!-- Tendance -->
                <div class="cdp__cols-stat">
                  <div class="cdp__cols-stat-hd">
                    <div class="cdp__cols-stat-ico cdp__cols-stat-ico--orange">
                      <i class="pi pi-chart-line" />
                    </div>
                    <span
                      class="cdp__cols-stat-badge cdp__cols-stat-badge--green"
                      >+24%</span
                    >
                  </div>
                  <span class="cdp__cols-stat-label">{{
                    t('commerceDetail.viewTrend')
                  }}</span>
                  <span class="cdp__cols-stat-val">{{
                    [...orgCategoriesMap].sort(
                      (a, b) => b.products.length - a.products.length,
                    )[0]?.label ?? '—'
                  }}</span>
                  <span class="cdp__cols-stat-desc">{{
                    t('commerceDetail.viewTrendDesc')
                  }}</span>
                </div>

                <!-- Statut terminaux -->
                <div class="cdp__cols-stat">
                  <div class="cdp__cols-stat-hd">
                    <div class="cdp__cols-stat-ico cdp__cols-stat-ico--blue">
                      <i class="pi pi-sync" />
                    </div>
                    <span class="cdp__cols-stat-sync"
                      ><span class="cdp__cols-stat-dot" />{{
                        t('commerceDetail.synced')
                      }}</span
                    >
                  </div>
                  <span class="cdp__cols-stat-label">{{
                    t('commerceDetail.terminalStatus')
                  }}</span>
                  <span class="cdp__cols-stat-val"
                    >{{ orgCategoriesMap.length }}
                    /
                    {{ orgCategoriesMap.length }}
                    {{ t('commerceDetail.activeStatus') }}</span
                  >
                  <span class="cdp__cols-stat-desc">{{
                    t('commerceDetail.lastUpdated')
                  }}</span>
                </div>

                <!-- Optimisation IA -->
                <div class="cdp__cols-stat cdp__cols-stat--cta">
                  <div class="cdp__cols-stat-hd">
                    <div class="cdp__cols-stat-ico cdp__cols-stat-ico--white">
                      <i class="pi pi-star-fill" />
                    </div>
                  </div>
                  <span
                    class="cdp__cols-stat-label cdp__cols-stat-label--light"
                    >{{ t('commerceDetail.aiOptimization') }}</span
                  >
                  <span class="cdp__cols-stat-val cdp__cols-stat-val--light">{{
                    t('commerceDetail.aiOptimizationVal')
                  }}</span>
                  <span
                    class="cdp__cols-stat-desc cdp__cols-stat-desc--light"
                    >{{ t('commerceDetail.aiOptimizationDesc') }}</span
                  >
                  <i class="pi pi-arrow-right cdp__cols-stat-arrow" />
                </div>
              </div>
            </div>
          </template>

          <!-- ══ Sous-onglet : Recommandations ════════════════════════════════ -->
          <template v-else-if="activeCatTab === 'recommandations'">
            <div class="rec__wrap">
              <!-- Header -->
              <div class="rec__toolbar">
                <div class="rec__search-wrap">
                  <i class="pi pi-search rec__search-ico" />
                  <input
                    v-model="recSearch"
                    class="rec__search"
                    :placeholder="t('commerceDetail.prodSearch')"
                  />
                </div>
                <div class="rec__kpi">
                  <i class="pi pi-thumbs-up" />
                  <span>{{
                    t(
                      'commerceDetail.recommendedCount',
                      { n: recCount, s: recCount > 1 ? 's' : '' },
                      recCount,
                    )
                  }}</span>
                </div>
                <select v-model="recPageSize" class="rec__pagesize">
                  <option :value="10">10 / page</option>
                  <option :value="20">20 / page</option>
                  <option :value="50">50 / page</option>
                </select>
              </div>

              <!-- Skeleton -->
              <div v-if="recFetching" class="rec__list">
                <div v-for="n in 8" :key="n" class="rec__row rec__row--skel">
                  <div
                    class="cdp__skel"
                    style="
                      width: 2.5rem;
                      height: 2.5rem;
                      border-radius: 0.625rem;
                      flex-shrink: 0;
                    "
                  />
                  <div
                    style="
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                      gap: 0.375rem;
                    "
                  >
                    <div
                      class="cdp__skel"
                      style="height: 0.875rem; width: 50%"
                    />
                    <div
                      class="cdp__skel"
                      style="height: 0.75rem; width: 25%"
                    />
                  </div>
                  <div
                    class="cdp__skel"
                    style="width: 5rem; height: 2rem; border-radius: 0.5rem"
                  />
                </div>
              </div>

              <!-- Liste -->
              <div v-else class="rec__list">
                <div v-if="filteredRecProds.length === 0" class="rec__empty">
                  <i class="pi pi-box" />
                  <p>{{ t('commerceDetail.emptyProductsSearch') }}</p>
                </div>
                <div
                  v-for="p in pagedRecProds"
                  :key="p.id"
                  class="rec__row"
                  :class="{ 'rec__row--on': p.isRecommanded }"
                >
                  <!-- Image -->
                  <div class="rec__img-wrap">
                    <img
                      v-if="p.image?.info?.secureUrl"
                      :src="p.image.info.secureUrl"
                      class="rec__img"
                      loading="lazy"
                      decoding="async"
                      alt=""
                    />
                    <i v-else class="pi pi-box rec__img-ico" />
                  </div>

                  <!-- Info -->
                  <div class="rec__info">
                    <span class="rec__name">{{ p.designation }}</span>
                    <span class="rec__price"
                      >{{ parseFloat(p.price ?? '0').toFixed(2) }} €</span
                    >
                  </div>

                  <!-- Badge recommandé -->
                  <span v-if="p.isRecommanded" class="rec__badge">
                    <i class="pi pi-thumbs-up" /> Recommandé
                  </span>

                  <!-- Toggle -->
                  <button
                    class="rec__btn"
                    :class="p.isRecommanded ? 'rec__btn--off' : 'rec__btn--on'"
                    :disabled="recProcessing.has(p.id)"
                    @click="handleRecToggleClick(p)"
                  >
                    <i
                      v-if="recProcessing.has(p.id)"
                      class="pi pi-spinner pi-spin"
                    />
                    <i
                      v-else
                      :class="
                        p.isRecommanded
                          ? 'pi pi-thumbs-down'
                          : 'pi pi-thumbs-up'
                      "
                    />
                    <span v-if="!recProcessing.has(p.id)">{{
                      p.isRecommanded
                        ? t('commerceDetail.remove')
                        : t('commerceDetail.recommend')
                    }}</span>
                  </button>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="!recFetching && recTotalPages > 1" class="rec__pag">
                <button
                  class="rec__pag-btn"
                  :disabled="recPage === 1"
                  @click="recGoTo(recPage - 1)"
                >
                  <i class="pi pi-chevron-left" />
                </button>
                <span
                  v-for="item in recPageItems"
                  :key="item.n"
                  class="rec__pag-btn"
                  :class="{
                    'rec__pag-btn--active':
                      item.type === 'page' && item.n === recPage,
                    'rec__pag-ellipsis': item.type === 'ellipsis',
                  }"
                  @click="recClickPageItem(item)"
                  >{{ item.type === 'ellipsis' ? '…' : item.n }}</span
                >
                <button
                  class="rec__pag-btn"
                  :disabled="recPage === recTotalPages"
                  @click="recGoTo(recPage + 1)"
                >
                  <i class="pi pi-chevron-right" />
                </button>
                <span class="rec__pag-info">{{
                  t(
                    'commerceDetail.productCount',
                    { n: filteredRecProds.length },
                    filteredRecProds.length,
                  )
                }}</span>
              </div>
            </div>
          </template>

          <!-- ══ Sous-onglet : Groupes d'options ═══════════════════════════════ -->
          <template v-else-if="activeCatTab === 'options'">
            <!-- Toolbar -->
            <div class="cdp__opts-toolbar">
              <div class="cdp__opts-search-wrap">
                <i class="pi pi-search cdp__opts-search-ico" />
                <input
                  v-model="optsSearch"
                  class="cdp__opts-search"
                  :placeholder="t('commerceDetail.searchGroup')"
                />
              </div>
              <div class="cdp__opts-toolbar-right">
                <button
                  class="cdp__btn cdp__btn--primary cdp__btn--icon-left"
                  @click="showAddGroup = true"
                >
                  <i class="pi pi-plus" /> {{ t('commerceDetail.newGroup') }}
                </button>
              </div>
            </div>

            <!-- Skeleton -->
            <div v-if="optsLoading" class="cdp__opts-table-wrap">
              <table class="cdp__opts-table">
                <thead>
                  <tr>
                    <th>{{ t('commerceDetail.colGroup') }}</th>
                    <th>{{ t('commerceDetail.colOptions') }}</th>
                    <th class="cdp__opts-th--center">
                      {{ t('commerceDetail.colChoice') }}
                    </th>
                    <th class="cdp__opts-th--center">
                      {{ t('commerceDetail.colType') }}
                    </th>
                    <th>{{ t('commerceDetail.colPrice') }}</th>
                    <th>{{ t('commerceDetail.colProdLinked') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="n in 8"
                    :key="n"
                    class="cdp__opts-row cdp__opts-row--skel"
                  >
                    <td><div class="cdp__skel cdp__skel--name" /></td>
                    <td><div class="cdp__skel cdp__skel--chips" /></td>
                    <td class="cdp__opts-td--center">
                      <div class="cdp__skel cdp__skel--badge" />
                    </td>
                    <td class="cdp__opts-td--center">
                      <div class="cdp__skel cdp__skel--badge" />
                    </td>
                    <td><div class="cdp__skel cdp__skel--badge" /></td>
                    <td><div class="cdp__skel cdp__skel--prod" /></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Table -->
            <div v-else class="cdp__opts-table-wrap">
              <table class="cdp__opts-table">
                <thead>
                  <tr>
                    <th>{{ t('commerceDetail.colGroup') }}</th>
                    <th>{{ t('commerceDetail.colOptions') }}</th>
                    <th class="cdp__opts-th--center">
                      {{ t('commerceDetail.colChoice') }}
                    </th>
                    <th class="cdp__opts-th--center">
                      {{ t('commerceDetail.colType') }}
                    </th>
                    <th>{{ t('commerceDetail.colPrice') }}</th>
                    <th>{{ t('commerceDetail.colProdLinked') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredOptGroups.length === 0">
                    <td colspan="6" class="cdp__opts-empty-row">
                      <i class="pi pi-sliders-h" />
                      <p>{{ t('commerceDetail.emptyOptions') }}</p>
                    </td>
                  </tr>
                  <tr
                    v-for="grp in pagedOptGroups"
                    :key="grp.id"
                    class="cdp__opts-row"
                    style="cursor: pointer"
                    @click="openGroupEdit(grp)"
                  >
                    <td>
                      <div class="cdp__opts-name-cell">
                        <span class="cdp__opts-name">{{
                          grp.title || '—'
                        }}</span>
                        <span class="cdp__opts-id">{{ grp.id }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="cdp__opts-chips">
                        <span
                          v-for="opt in grp.options.slice(0, 3)"
                          :key="opt.content"
                          class="cdp__opts-chip"
                          >{{ opt.content }}</span
                        >
                        <span
                          v-if="grp.options.length > 3"
                          class="cdp__opts-chip-more"
                          >+{{ grp.options.length - 3 }}</span
                        >
                      </div>
                    </td>
                    <td class="cdp__opts-td--center">
                      <span class="cdp__opts-badge cdp__opts-badge--neutral">
                        {{
                          grp.chooseType === 'multiple'
                            ? t('commerceDetail.multiple')
                            : t('commerceDetail.unique')
                        }}
                      </span>
                    </td>
                    <td class="cdp__opts-td--center">
                      <span
                        class="cdp__opts-badge"
                        :class="
                          grp.required
                            ? 'cdp__opts-badge--primary'
                            : 'cdp__opts-badge--muted'
                        "
                      >
                        {{
                          grp.required
                            ? t('commerceDetail.required')
                            : t('commerceDetail.optional')
                        }}
                      </span>
                    </td>
                    <td>
                      <span
                        v-if="grp.options.some((o) => o.price > 0)"
                        class="cdp__opts-badge cdp__opts-badge--success"
                        >{{ t('commerceDetail.paid') }}</span
                      >
                      <span v-else class="cdp__opts-price-free">{{
                        t('commerceDetail.free')
                      }}</span>
                    </td>
                    <td>
                      <div class="cdp__opts-products">
                        <span
                          v-for="p in grp.linkedProducts.slice(0, 3)"
                          :key="p.id"
                          class="cdp__opts-product cdp__opts-product--link"
                          @click.stop="router.push(productLink(p.id))"
                          >{{ p.name }}</span
                        >
                        <span
                          v-if="grp.linkedProducts.length > 3"
                          class="cdp__opts-chip-more"
                          >+{{ grp.linkedProducts.length - 3 }}</span
                        >
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="cdp__opts-pagination">
                <div class="cdp__opts-pag-left">
                  <span class="cdp__opts-pag-info">
                    {{ filteredOptGroups.length }} groupe{{
                      filteredOptGroups.length > 1 ? 's' : ''
                    }}
                    d'options
                  </span>
                  <select v-model="optsPageSize" class="cdp__opts-pag-size">
                    <option :value="10">10 / page</option>
                    <option :value="20">20 / page</option>
                    <option :value="50">50 / page</option>
                  </select>
                </div>
                <div class="cdp__opts-pag-btns">
                  <button
                    class="cdp__opts-pag-btn"
                    :disabled="optsPage === 1"
                    @click="optsGoTo(1)"
                  >
                    <i class="pi pi-angle-double-left" />
                  </button>
                  <button
                    class="cdp__opts-pag-btn"
                    :disabled="optsPage === 1"
                    @click="optsGoTo(optsPage - 1)"
                  >
                    <i class="pi pi-chevron-left" />
                  </button>
                  <template v-for="p in optsTotalPages" :key="p">
                    <button
                      v-if="
                        p === 1 ||
                        p === optsTotalPages ||
                        Math.abs(p - optsPage) <= 1
                      "
                      class="cdp__opts-pag-btn"
                      :class="{ 'cdp__opts-pag-btn--active': p === optsPage }"
                      @click="optsGoTo(p)"
                    >
                      {{ p }}
                    </button>
                    <span
                      v-else-if="p === optsPage - 2 || p === optsPage + 2"
                      class="cdp__opts-pag-ellipsis"
                      >…</span
                    >
                  </template>
                  <button
                    class="cdp__opts-pag-btn"
                    :disabled="optsPage === optsTotalPages"
                    @click="optsGoTo(optsPage + 1)"
                  >
                    <i class="pi pi-chevron-right" />
                  </button>
                  <button
                    class="cdp__opts-pag-btn"
                    :disabled="optsPage === optsTotalPages"
                    @click="optsGoTo(optsTotalPages)"
                  >
                    <i class="pi pi-angle-double-right" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Slide-over création -->
            <CommerceAddOptionGroup
              v-if="showAddGroup"
              :products="allProductsForOpts ?? []"
              :commerce-id="id"
              @close="showAddGroup = false"
              @saved="onGroupSaved"
            />

            <!-- Drawer édition -->
            <CommerceAddonGroupDrawer
              v-model="editGroupDrawerOpen"
              :group="editingGroup"
              :all-products="allProductsForOpts ?? []"
              @saved="onGroupSaved"
              @deleted="
                onGroupSaved();
                onGroupDeleted();
              "
            />
          </template>

          <!-- ══ Sous-onglet : Promotions ══════════════════════════════════════ -->
          <template v-else-if="activeCatTab === 'promotions'">
            <div class="promo__wrap">
              <!-- KPIs -->
              <div class="promo__kpis">
                <div class="promo__kpi">
                  <span class="promo__kpi-val">{{ promoKpis.active }}</span>
                  <span class="promo__kpi-lbl">{{
                    t('commerceDetail.promoActive')
                  }}</span>
                </div>
                <div class="promo__kpi promo__kpi--green">
                  <span class="promo__kpi-val">{{ promoKpis.upsells }}</span>
                  <span class="promo__kpi-lbl">{{
                    t('commerceDetail.promoAdditional')
                  }}</span>
                </div>
                <div class="promo__kpi">
                  <span class="promo__kpi-val">{{ promoKpis.codes }}</span>
                  <span class="promo__kpi-lbl">{{
                    t('commerceDetail.promoCodes')
                  }}</span>
                </div>
              </div>

              <!-- Filtres + bouton -->
              <div class="promo__toolbar">
                <div class="promo__filters">
                  <button
                    class="promo__filter"
                    :class="{ 'promo__filter--on': promoFilter === 'all' }"
                    @click="promoFilter = 'all'"
                  >
                    {{ t('commerceDetail.promoAll') }}
                  </button>
                  <button
                    class="promo__filter"
                    :class="{ 'promo__filter--on': promoFilter === 'upsell' }"
                    @click="promoFilter = 'upsell'"
                  >
                    <i class="pi pi-arrow-up-right" />
                    {{ t('commerceDetail.promoAddSales') }}
                  </button>
                  <button
                    class="promo__filter"
                    :class="{ 'promo__filter--on': promoFilter === 'code' }"
                    @click="promoFilter = 'code'"
                  >
                    <i class="pi pi-ticket" />
                    {{ t('commerceDetail.promoCodes') }}
                  </button>
                </div>
                <button class="promo__add-btn" @click="goToNewPromo">
                  <i class="pi pi-plus" /> {{ t('commerceDetail.newPromo') }}
                </button>
              </div>

              <!-- Loading skeleton -->
              <div v-if="promoFetching" class="promo__list">
                <div
                  v-for="n in 4"
                  :key="n"
                  class="promo__card promo__card--skel"
                >
                  <div
                    class="cdp__skel"
                    style="height: 1.25rem; width: 40%; margin-bottom: 0.5rem"
                  />
                  <div class="cdp__skel" style="height: 0.875rem; width: 65%" />
                </div>
              </div>

              <!-- Vide -->
              <div v-else-if="promotions.length === 0" class="promo__empty">
                <div class="promo__empty-ico-wrap">
                  <i class="pi pi-percentage" />
                </div>
                <p class="promo__empty-title">
                  {{ t('commerceDetail.emptyPromos') }}
                </p>
                <p class="promo__empty-sub">
                  {{ t('commerceDetail.noPromoDesc') }}
                </p>
                <button class="promo__add-btn" @click="goToNewPromo">
                  <i class="pi pi-plus" /> {{ t('commerceDetail.createPromo') }}
                </button>
              </div>

              <!-- Liste -->
              <div v-else class="promo__list">
                <!-- Section Ventes add. -->
                <template
                  v-if="promoFilter !== 'code' && upsellPromos.length > 0"
                >
                  <p class="promo__section-title">
                    <i class="pi pi-arrow-up-right" />
                    {{ t('commerceDetail.promoAdditional') }}
                  </p>
                  <div
                    v-for="p in upsellPromos"
                    :key="p.id"
                    class="promo__card"
                    :class="{ 'promo__card--inactive': p.status !== 'active' }"
                  >
                    <div class="promo__card-left">
                      <div
                        class="promo__card-ico"
                        :class="
                          p.status === 'active'
                            ? 'promo__card-ico--green'
                            : 'promo__card-ico--grey'
                        "
                      >
                        <i class="pi pi-arrow-up-right" />
                      </div>
                      <div class="promo__card-info">
                        <div class="promo__card-name-row">
                          <span class="promo__card-name">{{ p.name }}</span>
                          <span
                            class="promo__status"
                            :class="STATUS_CLASS[p.status]"
                            >{{ p.status }}</span
                          >
                        </div>
                        <span class="promo__card-desc">{{
                          p.description || TYPE_LABELS[p.type]
                        }}</span>
                        <div class="promo__card-tags">
                          <span class="promo__tag">{{
                            TYPE_LABELS[p.type]
                          }}</span>
                          <span class="promo__tag promo__tag--val">
                            {{
                              p.type === 'percentage'
                                ? `−${p.value} %`
                                : `−${p.value} €`
                            }}
                          </span>
                          <span
                            v-if="p.minimumOrderAmount"
                            class="promo__tag"
                            >{{
                              t('commerceDetail.promoMin', {
                                amount: p.minimumOrderAmount,
                              })
                            }}</span
                          >
                        </div>
                      </div>
                    </div>
                    <div class="promo__card-actions">
                      <label
                        class="promo__toggle-wrap"
                        :title="
                          p.status === 'active'
                            ? t('commerceDetail.pause')
                            : t('commerceDetail.activate')
                        "
                      >
                        <input
                          type="checkbox"
                          class="promo__toggle"
                          :checked="p.status === 'active'"
                          @change="togglePromoStatus(p)"
                        />
                      </label>
                      <button
                        class="promo__icon-btn"
                        :title="t('commerceDetail.btnDelete')"
                        @click="deletePromo(p)"
                      >
                        <i class="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Section Codes promo -->
                <template
                  v-if="promoFilter !== 'upsell' && codePromos.length > 0"
                >
                  <p class="promo__section-title">
                    <i class="pi pi-ticket" /> Codes promo
                  </p>
                  <div
                    v-for="p in codePromos"
                    :key="p.id"
                    class="promo__card"
                    :class="{ 'promo__card--inactive': p.status !== 'active' }"
                  >
                    <div class="promo__card-left">
                      <div
                        class="promo__card-ico"
                        :class="
                          p.status === 'active'
                            ? 'promo__card-ico--purple'
                            : 'promo__card-ico--grey'
                        "
                      >
                        <i class="pi pi-ticket" />
                      </div>
                      <div class="promo__card-info">
                        <div class="promo__card-name-row">
                          <span class="promo__card-name">{{ p.name }}</span>
                          <span
                            class="promo__status"
                            :class="STATUS_CLASS[p.status]"
                            >{{ p.status }}</span
                          >
                        </div>
                        <div class="promo__card-tags">
                          <code class="promo__code-chip">{{
                            p.code || '—'
                          }}</code>
                          <span class="promo__tag">{{
                            TYPE_LABELS[p.type]
                          }}</span>
                          <span class="promo__tag promo__tag--val">
                            {{
                              p.type === 'percentage'
                                ? `−${p.value} %`
                                : `−${p.value} €`
                            }}
                          </span>
                          <span v-if="p.usageLimit" class="promo__tag">{{
                            t('commerceDetail.promoLimit', {
                              limit: p.usageLimit,
                            })
                          }}</span>
                          <span
                            v-if="p.minimumOrderAmount"
                            class="promo__tag"
                            >{{
                              t('commerceDetail.promoMin', {
                                amount: p.minimumOrderAmount,
                              })
                            }}</span
                          >
                        </div>
                      </div>
                    </div>
                    <div class="promo__card-actions">
                      <label
                        class="promo__toggle-wrap"
                        :title="
                          p.status === 'active'
                            ? t('commerceDetail.pause')
                            : t('commerceDetail.activate')
                        "
                      >
                        <input
                          type="checkbox"
                          class="promo__toggle"
                          :checked="p.status === 'active'"
                          @change="togglePromoStatus(p)"
                        />
                      </label>
                      <button
                        class="promo__icon-btn"
                        :title="t('commerceDetail.btnDelete')"
                        @click="deletePromo(p)"
                      >
                        <i class="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>

          <!-- Placeholder autres sous-onglets -->
          <div v-else class="cdp__empty">
            <i class="pi pi-box cdp__empty-ico" />
            <p>
              {{ CATALOGUE_TABS.find((t) => t.key === activeCatTab)?.label }} —
              {{ t('commerceDetail.comingSoon') }}
            </p>
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : INFORMATIONS
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'informations'" class="cdp__info">
          <div class="cdp__info-cols">
            <!-- ── Colonne gauche ── -->
            <div class="cdp__info-left">
              <!-- Coordonnées -->
              <div class="cdp__icard">
                <div class="cdp__icard-hd">
                  <i class="pi pi-id-card cdp__icard-ico" />
                  <span>{{ t('commerceDetail.infoTitle') }}</span>
                </div>
                <div class="cdp__ifields">
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoDesignation')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.designation ?? '—'
                    }}</span>
                  </div>
                  <div class="cdp__ifield cdp__ifield--full">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoAddress')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.address ?? '—'
                    }}</span>
                  </div>
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoCode')
                    }}</span>
                    <code class="cdp__icode">{{ store.orderCode ?? '—' }}</code>
                  </div>
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoCurrency')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.currencyCode ?? store.currency ?? '—'
                    }}</span>
                  </div>
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoTimezone')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.timezone ?? '—'
                    }}</span>
                  </div>
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.infoState')
                    }}</span>
                    <span
                      class="cdp__ifield-status"
                      :class="
                        isActive
                          ? 'cdp__ifield-status--on'
                          : 'cdp__ifield-status--off'
                      "
                    >
                      <span class="cdp__ifield-dot" />
                      {{
                        isActive
                          ? t('commerceDetail.infoEnabled')
                          : t('commerceDetail.infoDisabled')
                      }}
                    </span>
                  </div>
                  <template v-if="primarySlug">
                    <div class="cdp__ifield">
                      <span class="cdp__ifield-lbl">{{
                        t('commerceDetail.infoSlug')
                      }}</span>
                      <code class="cdp__icode">{{ primarySlug }}</code>
                    </div>
                  </template>
                  <template v-if="store.marketPayShopId">
                    <div class="cdp__ifield">
                      <span class="cdp__ifield-lbl">{{
                        t('commerceDetail.infoShopId')
                      }}</span>
                      <code class="cdp__icode">{{
                        store.marketPayShopId
                      }}</code>
                    </div>
                  </template>
                  <template v-if="store.preparationTimeBuffer != null">
                    <div class="cdp__ifield">
                      <span class="cdp__ifield-lbl">{{
                        t('commerceDetail.infoBuffer')
                      }}</span>
                      <span class="cdp__ifield-val"
                        >{{ store.preparationTimeBuffer }}
                        {{ t('commerceDetail.infoMin') }}</span
                      >
                    </div>
                  </template>
                </div>
              </div>

              <!-- Contacts -->
              <div class="cdp__icard">
                <div class="cdp__icard-hd">
                  <i class="pi pi-users cdp__icard-ico" />
                  <span>{{ t('commerceDetail.infoContacts') }}</span>
                </div>
                <div v-if="contacts.length === 0" class="cdp__iempty">
                  {{ t('commerceDetail.emptyContacts') }}
                </div>
                <div v-else class="cdp__icontacts">
                  <div v-for="c in contacts" :key="c.id" class="cdp__icontact">
                    <div class="cdp__iav">{{ initials(c) }}</div>
                    <div class="cdp__icontact-body">
                      <div class="cdp__icontact-name-row">
                        <span class="cdp__icontact-name">{{
                          contactFullName(c)
                        }}</span>
                        <span
                          v-if="c.isPrimary"
                          class="cdp__ibadge cdp__ibadge--primary"
                          >{{ t('commerceDetail.primaryContact') }}</span
                        >
                        <span
                          v-if="c.position"
                          class="cdp__ibadge cdp__ibadge--role"
                          >{{ POSITION_LABELS[c.position] ?? c.position }}</span
                        >
                      </div>
                      <div class="cdp__icontact-links">
                        <a
                          v-if="c.email"
                          :href="`mailto:${c.email}`"
                          class="cdp__icontact-link"
                        >
                          <i class="pi pi-envelope" /> {{ c.email }}
                        </a>
                        <a
                          v-if="c.phone"
                          :href="`tel:${c.phone}`"
                          class="cdp__icontact-link"
                        >
                          <i class="pi pi-phone" /> {{ c.phone }}
                        </a>
                      </div>
                    </div>
                    <button
                      v-if="false"
                      class="cdp__iedit-btn"
                      @click="activeTab = 'contact'"
                    >
                      <i class="pi pi-pencil" />
                    </button>
                  </div>
                  <!-- Contact principal mis en avant -->
                  <template v-if="contacts.find((c) => c.isPrimary)">
                    <div class="cdp__ifield-divider" />
                    <div class="cdp__icontact-ref">
                      <div class="cdp__icontact-ref-lbl">
                        {{ t('commerceDetail.primaryContact') }}
                      </div>
                      <div
                        class="cdp__icontact-ref-row"
                        @click="activeTab = 'contact'"
                      >
                        <div class="cdp__iav cdp__iav--sm">
                          {{ initials(contacts.find((c) => c.isPrimary)) }}
                        </div>
                        <div class="cdp__icontact-ref-info">
                          <span class="cdp__icontact-ref-name">{{
                            contactFullName(contacts.find((c) => c.isPrimary))
                          }}</span>
                          <span class="cdp__icontact-ref-sub">
                            {{
                              [
                                contacts.find((c) => c.isPrimary)?.email,
                                contacts.find((c) => c.isPrimary)?.phone,
                              ]
                                .filter(Boolean)
                                .join(' · ')
                            }}
                          </span>
                        </div>
                        <i class="pi pi-arrow-right cdp__icontact-ref-arrow" />
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- ── Colonne droite ── -->
            <div class="cdp__info-right">
              <!-- Checklist -->
              <div class="cdp__icard">
                <div class="cdp__icard-hd">
                  <i class="pi pi-chart-bar cdp__icard-ico" />
                  <span>{{ t('commerceDetail.toFinalizeSec') }}</span>
                  <span class="cdp__ichecklist-ratio"
                    >{{ configDone }}/{{ configTotal }}</span
                  >
                </div>
                <div class="cdp__ichecklist-body">
                  <!-- Barre progression -->
                  <div class="cdp__iprog-wrap">
                    <div class="cdp__iprog-top">
                      <span class="cdp__iprog-pct">{{ configPct }}%</span>
                      <span class="cdp__iprog-lbl">{{
                        t('commerceDetail.totalProgress')
                      }}</span>
                    </div>
                    <div class="cdp__iprog-bar">
                      <div
                        class="cdp__iprog-fill"
                        :style="{ width: configPct + '%' }"
                      />
                    </div>
                  </div>
                  <!-- Items à faire -->
                  <div class="cdp__ichecklist-items">
                    <div
                      v-for="item in configItems.filter((i) => !i.done)"
                      :key="item.key"
                      class="cdp__icheck-todo"
                    >
                      <div
                        class="cdp__icheck-todo-icon"
                        :class="
                          item.urgent ? 'cdp__icheck-todo-icon--urgent' : ''
                        "
                      >
                        <i class="pi" :class="item.icon" />
                      </div>
                      <div class="cdp__icheck-todo-body">
                        <span class="cdp__icheck-todo-title">{{
                          item.label
                        }}</span>
                        <span class="cdp__icheck-todo-sub">{{
                          item.desc
                        }}</span>
                      </div>
                      <button
                        class="cdp__icheck-todo-btn"
                        @click="
                          item.key === 'slug'
                            ? goToSlugSection()
                            : item.key === 'catalogue'
                              ? router.push(`/products/new?storeId=${id}`)
                              : (activeTab = item.tab as any)
                        "
                      >
                        {{ t('commerceDetail.complete') }}
                      </button>
                    </div>
                    <!-- Items faits -->
                    <div
                      v-for="item in configItems.filter((i) => i.done)"
                      :key="item.key"
                      class="cdp__icheck-done"
                    >
                      <i class="pi pi-check-circle cdp__icheck-done-ico" />
                      <span>{{ item.label }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats rapides -->
              <div class="cdp__istats">
                <div class="cdp__istat">
                  <span class="cdp__istat-num">{{
                    totalProds || allProducts.length
                  }}</span>
                  <span class="cdp__istat-lbl">{{
                    t('commerceDetail.catalogueKpis.products')
                  }}</span>
                </div>
                <div class="cdp__istat">
                  <span class="cdp__istat-num">{{ categoriesMap.length }}</span>
                  <span class="cdp__istat-lbl">{{
                    t('commerceDetail.catalogueKpis.categories')
                  }}</span>
                </div>
              </div>

              <!-- Dupliquer -->
              <div
                class="cdp__iduplicate"
                @click="router.push(`/commerces/${id}/edit`)"
              >
                <div class="cdp__iduplicate-icon"><i class="pi pi-copy" /></div>
                <h4 class="cdp__iduplicate-title">
                  {{ t('commerceDetail.duplicateStore') }}
                </h4>
                <p class="cdp__iduplicate-sub">
                  {{ t('commerceDetail.duplicateDesc') }}
                </p>
              </div>

              <!-- Dates -->
              <div class="cdp__icard">
                <div class="cdp__icard-hd">
                  <i class="pi pi-calendar cdp__icard-ico" />
                  <span>{{ t('commerceDetail.infoDates') }}</span>
                </div>
                <div class="cdp__ifields">
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.createdAt')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.createdAt ? fmtDate(store.createdAt) : '—'
                    }}</span>
                  </div>
                  <div class="cdp__ifield">
                    <span class="cdp__ifield-lbl">{{
                      t('commerceDetail.updatedAt')
                    }}</span>
                    <span class="cdp__ifield-val">{{
                      store.updatedAt ? fmtDate(store.updatedAt) : '—'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : MODULES
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'modules'" class="cdp__mod">
          <template v-if="!openModuleSettingsKey">
            <!-- Bannière séquence -->
            <div class="cdp__mod-banner">
              <div class="cdp__mod-banner-ico">
                <i class="pi pi-info-circle" />
              </div>
              <div>
                <p class="cdp__mod-banner-title">
                  {{ t('commerceDetail.modulesSeqTitle') }}
                </p>
                <p class="cdp__mod-banner-sub">
                  {{ t('commerceDetail.modulesSeqDesc') }}
                </p>
              </div>
            </div>

            <!-- Bento grid 4 modules -->
            <div class="cdp__mod-grid">
              <!-- Click & Collect -->
              <div
                class="cdp__mod-card"
                :class="
                  moduleValues['modules.click_and_collect']
                    ? 'cdp__mod-card--on'
                    : 'cdp__mod-card--off'
                "
              >
                <div class="cdp__mod-card-top">
                  <div
                    class="cdp__mod-card-ico"
                    :class="
                      moduleValues['modules.click_and_collect']
                        ? 'cdp__mod-card-ico--on'
                        : ''
                    "
                  >
                    <i class="pi pi-shopping-cart" />
                  </div>
                  <span
                    class="cdp__mod-badge"
                    :class="
                      moduleValues['modules.click_and_collect']
                        ? 'cdp__mod-badge--on'
                        : 'cdp__mod-badge--off'
                    "
                  >
                    {{
                      moduleValues['modules.click_and_collect']
                        ? t('commerceDetail.activate2')
                        : t('commerceDetail.deactivate2')
                    }}
                  </span>
                  <button
                    class="cdp__mod-settings-btn"
                    :title="t('commerceDetail.moduleSettingsTitle')"
                    @click.stop="
                      openModuleSettings('modules.click_and_collect')
                    "
                  >
                    <i class="pi pi-cog" />
                  </button>
                </div>
                <p class="cdp__mod-card-title">
                  {{ t('commerceDetail.modulesCnc') }}
                </p>
                <p class="cdp__mod-card-desc">
                  {{ t('commerceDetail.modulesCncDesc') }}
                </p>
                <div class="cdp__mod-card-ft">
                  <a
                    v-if="storeUrl"
                    :href="storeUrl"
                    target="_blank"
                    class="cdp__mod-link"
                  >
                    <i class="pi pi-external-link" />
                    {{ t('commerceDetail.open3') }}
                  </a>
                  <span v-else class="cdp__mod-link cdp__mod-link--dim">{{
                    t('commerceDetail.noSlug')
                  }}</span>
                  <button
                    type="button"
                    class="cdp__mod-toggle"
                    :class="{
                      'cdp__mod-toggle--on':
                        moduleValues['modules.click_and_collect'],
                      'cdp__mod-toggle--pending':
                        togglingModuleKey === 'modules.click_and_collect',
                    }"
                    :disabled="!!togglingModuleKey"
                    @click.stop="toggleModule('modules.click_and_collect')"
                  >
                    <div class="cdp__mod-toggle-thumb" />
                  </button>
                </div>
              </div>

              <!-- PDS Display -->
              <div
                class="cdp__mod-card"
                :class="
                  moduleValues['modules.pds_display']
                    ? 'cdp__mod-card--on'
                    : 'cdp__mod-card--off'
                "
              >
                <div class="cdp__mod-card-top">
                  <div
                    class="cdp__mod-card-ico"
                    :class="
                      moduleValues['modules.pds_display']
                        ? 'cdp__mod-card-ico--on'
                        : ''
                    "
                  >
                    <i class="pi pi-desktop" />
                  </div>
                  <span
                    class="cdp__mod-badge"
                    :class="
                      moduleValues['modules.pds_display']
                        ? 'cdp__mod-badge--on'
                        : 'cdp__mod-badge--off'
                    "
                  >
                    {{
                      moduleValues['modules.pds_display']
                        ? t('commerceDetail.activate2')
                        : t('commerceDetail.deactivate2')
                    }}
                  </span>
                  <button
                    class="cdp__mod-settings-btn"
                    :title="t('commerceDetail.moduleSettingsTitle')"
                    @click.stop="openModuleSettings('modules.pds_display')"
                  >
                    <i class="pi pi-cog" />
                  </button>
                </div>
                <p class="cdp__mod-card-title">
                  {{ t('commerceDetail.modulesPds') }}
                </p>
                <p class="cdp__mod-card-desc">
                  {{ t('commerceDetail.modulesPdsDesc') }}
                </p>
                <div class="cdp__mod-card-ft">
                  <button
                    type="button"
                    class="cdp__mod-toggle"
                    :class="{
                      'cdp__mod-toggle--on':
                        moduleValues['modules.pds_display'],
                      'cdp__mod-toggle--pending':
                        togglingModuleKey === 'modules.pds_display',
                    }"
                    :disabled="!!togglingModuleKey"
                    @click.stop="toggleModule('modules.pds_display')"
                  >
                    <div class="cdp__mod-toggle-thumb" />
                  </button>
                </div>
              </div>

              <!-- Système de Caisse -->
              <div
                class="cdp__mod-card"
                :class="
                  moduleValues['modules.pos_register']
                    ? 'cdp__mod-card--on'
                    : 'cdp__mod-card--off'
                "
              >
                <div class="cdp__mod-card-top">
                  <div
                    class="cdp__mod-card-ico"
                    :class="
                      moduleValues['modules.pos_register']
                        ? 'cdp__mod-card-ico--on'
                        : ''
                    "
                  >
                    <i class="pi pi-calculator" />
                  </div>
                  <span
                    class="cdp__mod-badge"
                    :class="
                      moduleValues['modules.pos_register']
                        ? 'cdp__mod-badge--on'
                        : 'cdp__mod-badge--off'
                    "
                  >
                    {{
                      moduleValues['modules.pos_register']
                        ? t('commerceDetail.activate2')
                        : t('commerceDetail.deactivate2')
                    }}
                  </span>
                  <button
                    class="cdp__mod-settings-btn"
                    :title="t('commerceDetail.moduleSettingsTitle')"
                    @click.stop="openModuleSettings('modules.pos_register')"
                  >
                    <i class="pi pi-cog" />
                  </button>
                </div>
                <p class="cdp__mod-card-title">
                  {{ t('commerceDetail.modulesCaisse') }}
                </p>
                <p class="cdp__mod-card-desc">
                  {{ t('commerceDetail.modulesCaisseDesc') }}
                </p>
                <div class="cdp__mod-card-ft">
                  <button
                    type="button"
                    class="cdp__mod-toggle"
                    :class="{
                      'cdp__mod-toggle--on':
                        moduleValues['modules.pos_register'],
                      'cdp__mod-toggle--pending':
                        togglingModuleKey === 'modules.pos_register',
                    }"
                    :disabled="!!togglingModuleKey"
                    @click.stop="toggleModule('modules.pos_register')"
                  >
                    <div class="cdp__mod-toggle-thumb" />
                  </button>
                </div>
              </div>

              <!-- Kiosque -->
              <div
                class="cdp__mod-card"
                :class="
                  moduleValues['modules.kiosk']
                    ? 'cdp__mod-card--on'
                    : 'cdp__mod-card--disabled'
                "
              >
                <div class="cdp__mod-card-top">
                  <div
                    class="cdp__mod-card-ico"
                    :class="
                      moduleValues['modules.kiosk']
                        ? 'cdp__mod-card-ico--on'
                        : ''
                    "
                  >
                    <i class="pi pi-tablet" />
                  </div>
                  <span
                    class="cdp__mod-badge"
                    :class="
                      moduleValues['modules.kiosk']
                        ? 'cdp__mod-badge--on'
                        : 'cdp__mod-badge--off'
                    "
                  >
                    {{
                      moduleValues['modules.kiosk']
                        ? t('commerceDetail.activate2')
                        : t('commerceDetail.deactivate2')
                    }}
                  </span>
                  <button
                    class="cdp__mod-settings-btn"
                    :title="t('commerceDetail.moduleSettingsTitle')"
                    @click.stop="openModuleSettings('modules.kiosk')"
                  >
                    <i class="pi pi-cog" />
                  </button>
                </div>
                <p class="cdp__mod-card-title">
                  {{ t('commerceDetail.modulesKiosk') }}
                </p>
                <p class="cdp__mod-card-desc">
                  {{ t('commerceDetail.modulesKioskDesc') }}
                </p>
                <div class="cdp__mod-card-ft">
                  <button
                    type="button"
                    class="cdp__mod-toggle"
                    :class="{
                      'cdp__mod-toggle--on': moduleValues['modules.kiosk'],
                      'cdp__mod-toggle--pending':
                        togglingModuleKey === 'modules.kiosk',
                    }"
                    :disabled="!!togglingModuleKey"
                    @click.stop="toggleModule('modules.kiosk')"
                  >
                    <div class="cdp__mod-toggle-thumb" />
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Panneau de paramétrage du module sélectionné (accordion) -->
          <template v-else>
            <div class="cdp__mod-settings-header">
              <button class="cdp__mod-back" @click="handleModuleBack">
                <i class="pi pi-arrow-left" />
                {{
                  moduleSubViewTitle
                    ? moduleSettingsBaseTitle
                    : t('commerceDetail.moduleSettingsBack')
                }}
              </button>
              <h3 class="cdp__mod-settings-title">
                {{ openModuleSettingsTitle }}
              </h3>
            </div>
            <CommerceModuleSettings
              ref="moduleSettingsRef"
              :store-id="id"
              :module-key="openModuleSettingsKey"
              :store-name="store?.designation"
              :logo-url="logoUrl"
              @update:sub-view-title="moduleSubViewTitle = $event"
            />
          </template>

          <!-- Slug de la boutique + numérotation : masqués quand un panneau
               de paramétrage de module est ouvert. -->
          <template v-if="!openModuleSettingsKey">
            <div
              ref="slugSectionRef"
              class="cdp__mod-section"
              :class="{ 'cdp__mod-section--highlight': highlightSlugSection }"
            >
              <div class="cdp__mod-section-hd">
                <i class="pi pi-globe cdp__mod-section-ico" />
                <h3 class="cdp__mod-section-title">
                  {{ t('commerceDetail.slugTitle') }}
                </h3>
              </div>
              <p class="cdp__mod-section-desc">
                {{ t('commerceDetail.slugInfo') }}
              </p>
              <div
                class="cdp__mod-slug-row"
                :class="{
                  'cdp__mod-slug-row--ok': modSlugStatus === 'available',
                  'cdp__mod-slug-row--err': modSlugStatus === 'taken',
                }"
              >
                <span class="cdp__mod-slug-prefix">https://</span>
                <input
                  v-model="modSlug"
                  class="cdp__mod-slug-input"
                  placeholder="mon-commerce"
                />
                <span class="cdp__mod-slug-suffix">.nextep.be</span>
                <button
                  class="cdp__mod-slug-btn"
                  :class="{
                    'cdp__mod-slug-btn--ok': modSlugStatus === 'available',
                    'cdp__mod-slug-btn--err': modSlugStatus === 'taken',
                  }"
                  :disabled="
                    modSlugChecking || modSlugSaving || modSlug === primarySlug
                  "
                  @click="checkAndSaveSlug()"
                >
                  <i
                    v-if="modSlugChecking || modSlugSaving"
                    class="pi pi-spinner pi-spin"
                  />
                  <i
                    v-else-if="modSlugStatus === 'available'"
                    class="pi pi-check"
                  />
                  <i
                    v-else-if="modSlugStatus === 'taken'"
                    class="pi pi-times"
                  />
                  <i v-else class="pi pi-search" />
                  {{
                    modSlugChecking
                      ? t('commerceDetail.slugVerifying')
                      : modSlugSaving
                        ? t('commerceDetail.slugSaving')
                        : modSlugStatus === 'available'
                          ? t('commerceDetail.slugAvailable')
                          : modSlugStatus === 'taken'
                            ? t('commerceDetail.slugUnavailable')
                            : t('commerceDetail.slugCheck')
                  }}
                </button>
              </div>
              <!-- Feedback — hauteur réservée pour ne pas pousser le reste
                   du formulaire pendant la frappe -->
              <div class="cdp__mod-slug-feedback">
                <p v-show="modSlugSaved" class="cdp__mod-slug-ok">
                  <i class="pi pi-check-circle" />
                  {{ t('commerceDetail.slugSuccess') }}
                </p>
                <p v-show="modSlugStatus === 'taken'" class="cdp__mod-slug-err">
                  <i class="pi pi-times-circle" />
                  {{ t('commerceDetail.slugTaken') }}
                </p>
                <!-- Suggestions -->
                <div
                  v-show="modSlugSuggestions.length"
                  class="cdp__mod-slug-suggestions"
                >
                  <span
                    v-for="s in modSlugSuggestions"
                    :key="s"
                    class="cdp__mod-slug-chip"
                    @click="pickSlugSuggestion(s)"
                    >{{ s }}</span
                  >
                </div>
                <p
                  v-show="modSlug === primarySlug && !modSlugSaved"
                  class="cdp__mod-slug-hint"
                >
                  {{ t('commerceDetail.slugCurrent') }}
                </p>
              </div>
            </div>

            <!-- Numérotation des commandes -->
            <div class="cdp__mod-section cdp__mod-section--row">
              <div class="cdp__mod-num-ico"><i class="pi pi-list" /></div>
              <div class="cdp__mod-num-body">
                <h3 class="cdp__mod-section-title">
                  {{ t('commerceDetail.orderNumTitle') }}
                </h3>
                <div class="cdp__mod-num-row">
                  <code class="cdp__icode">{{ store.orderCode ?? '—' }}</code>
                  <p class="cdp__mod-num-desc">
                    {{ t('commerceDetail.orderNumDesc') }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : HORAIRES
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'horaires'" class="cdp__hor">
          <div class="cdp__hor-layout">
            <!-- ── Colonne principale (2/3) ── -->
            <div class="cdp__hor-col-main">
              <!-- Horaires d'ouverture -->
              <div class="cdp__hor-section">
                <div class="cdp__hor-section-hd">
                  <div>
                    <h3 class="cdp__hor-section-title">
                      {{ t('commerceDetail.schedulesTitle') }}
                    </h3>
                    <p class="cdp__hor-section-sub">
                      {{ t('commerceDetail.schedulesHint') }}
                    </p>
                  </div>
                </div>

                <!-- Skeleton -->
                <div v-if="horFetching" class="cdp__hor-days">
                  <div v-for="n in 7" :key="n" class="cdp__hor-day-row">
                    <div
                      class="cdp__skel"
                      style="
                        width: 5.5rem;
                        height: 0.875rem;
                        border-radius: 0.25rem;
                        flex-shrink: 0;
                      "
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 7rem;
                        height: 0.875rem;
                        border-radius: 0.25rem;
                        flex: 1;
                      "
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 4rem;
                        height: 1.5rem;
                        border-radius: 9999px;
                        flex-shrink: 0;
                      "
                    />
                  </div>
                </div>

                <!-- Jours -->
                <div v-else class="cdp__hor-days">
                  <div
                    v-for="day in WEEKDAY_ORDER"
                    :key="day"
                    class="cdp__hor-day-row"
                    :class="[
                      regularByDay[day]?.length ? '' : 'cdp__hor-day-row--off',
                      horEditDay !== day
                        ? 'cdp__hor-day-row--clickable'
                        : 'cdp__hor-day-row--editing',
                    ]"
                    @click="clickDayRow(day)"
                  >
                    <!-- Vue normale -->
                    <span v-if="horEditDay !== day" class="cdp__hor-day-name">{{
                      WEEKDAY_LABELS[day]
                    }}</span>
                    <span
                      v-if="horEditDay !== day && !regularByDay[day]?.length"
                      class="cdp__hor-day-empty"
                      >{{ t('commerceDetail.closed2') }}</span
                    >
                    <span
                      v-if="horEditDay !== day && regularByDay[day]?.length"
                      class="cdp__hor-time"
                    >
                      <template
                        v-for="(slot, si) in regularByDay[day]"
                        :key="si"
                        ><span v-if="si > 0" class="cdp__hor-time-sep">·</span
                        >{{ fmtTime(slot.openTime) }} –
                        {{ fmtTime(slot.closeTime) }}</template
                      >
                    </span>
                    <span
                      v-if="horEditDay !== day"
                      class="cdp__hor-pill"
                      :class="
                        regularByDay[day]?.length
                          ? 'cdp__hor-pill--open'
                          : 'cdp__hor-pill--closed'
                      "
                    >
                      {{
                        regularByDay[day]?.length
                          ? t('commerceDetail.open2')
                          : t('commerceDetail.closed2')
                      }}
                    </span>
                    <i
                      v-if="horEditDay !== day"
                      class="pi pi-pencil cdp__hor-edit-ico"
                    />

                    <!-- Vue édition inline -->
                    <span
                      v-if="horEditDay === day"
                      class="cdp__hor-day-name cdp__hor-day-name--editing"
                      >{{ WEEKDAY_LABELS[day] }}</span
                    >
                    <div
                      v-if="horEditDay === day"
                      class="cdp__hor-edit-row"
                      @click.stop
                    >
                      <label class="cdp__hor-edit-toggle">
                        <input
                          type="checkbox"
                          :checked="horEditClosed"
                          @change="horEditClosed = !horEditClosed"
                        />
                        <span>{{ t('commerceDetail.closed2') }}</span>
                      </label>
                      <template v-if="!horEditClosed">
                        <div
                          v-for="(slot, si) in horEditSlots"
                          :key="si"
                          class="cdp__hor-edit-slot-row"
                        >
                          <input
                            class="cdp__hor-edit-time"
                            type="time"
                            v-model="slot.open"
                          />
                          <span class="cdp__hor-edit-sep">–</span>
                          <input
                            class="cdp__hor-edit-time"
                            type="time"
                            v-model="slot.close"
                          />
                          <button
                            v-if="horEditSlots.length > 1"
                            class="cdp__hor-del-btn"
                            style="opacity: 1"
                            @click.stop="removeHorEditSlot(si)"
                          >
                            <i class="pi pi-times" />
                          </button>
                        </div>
                        <button
                          class="cdp__hor-sf-add-slot"
                          @click.stop="addHorEditSlot()"
                        >
                          <i class="pi pi-plus" />
                          {{ t('commerceDetail.btnAddSlot') }}
                        </button>
                      </template>
                      <button
                        class="cdp__hor-edit-save"
                        :disabled="horSaving"
                        @click.stop="saveEditDay()"
                      >
                        <i v-if="horSaving" class="pi pi-spin pi-spinner" />
                        <i v-else class="pi pi-check" />
                      </button>
                      <button
                        class="cdp__hor-edit-cancel"
                        @click.stop="cancelEditDay()"
                      >
                        <i class="pi pi-times" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Fermetures & ouvertures exceptionnelles -->
              <div class="cdp__hor-section">
                <div class="cdp__hor-section-hd">
                  <div>
                    <h3 class="cdp__hor-section-title">
                      {{ t('commerceDetail.exceptionalTitle') }}
                    </h3>
                    <p class="cdp__hor-section-sub">
                      {{ t('commerceDetail.specialDesc') }}
                    </p>
                  </div>
                  <button class="cdp__hor-add-btn" @click="toggleSpecialForm()">
                    <i class="pi pi-plus" />
                    {{ t('commerceDetail.btnAdd') }}
                  </button>
                </div>

                <!-- Formulaire d'ajout -->
                <div v-if="showSpecialForm" class="cdp__hor-special-form">
                  <!-- Ligne date -->
                  <div class="cdp__hor-sf-row">
                    <div class="cdp__hor-sf-field">
                      <label class="cdp__hor-sf-label">{{
                        t('commerceDetail.colDate')
                      }}</label>
                      <input
                        class="cdp__hor-sf-input"
                        type="date"
                        v-model="specialFormDay"
                      />
                    </div>
                  </div>

                  <!-- Créneaux horaires -->
                  <div class="cdp__hor-sf-slots">
                    <div class="cdp__hor-sf-slots-hd">
                      <span class="cdp__hor-sf-label">{{
                        t('commerceDetail.slotsTitle')
                      }}</span>
                      <button
                        class="cdp__hor-sf-add-slot"
                        @click="addSpecialSlot()"
                      >
                        <i class="pi pi-plus" />
                        {{ t('commerceDetail.btnAddSlot') }}
                      </button>
                    </div>
                    <div
                      v-for="(slot, si) in specialFormSlots"
                      :key="si"
                      class="cdp__hor-sf-slot-row"
                    >
                      <div class="cdp__hor-sf-slot-inputs">
                        <input
                          class="cdp__hor-sf-input cdp__hor-sf-input--time"
                          type="time"
                          v-model="slot.open"
                        />
                        <span class="cdp__hor-edit-sep">–</span>
                        <input
                          class="cdp__hor-sf-input cdp__hor-sf-input--time"
                          type="time"
                          v-model="slot.close"
                        />
                      </div>
                      <button
                        v-if="specialFormSlots.length > 1"
                        class="cdp__hor-del-btn"
                        style="opacity: 1"
                        @click="removeSpecialSlot(si)"
                      >
                        <i class="pi pi-times" />
                      </button>
                    </div>
                  </div>

                  <div v-if="specialFormError" class="cdp__hor-sf-error">
                    <i class="pi pi-exclamation-triangle" />
                    {{ specialFormError }}
                  </div>

                  <div class="cdp__hor-sf-actions">
                    <button
                      class="cdp__hor-sf-cancel"
                      @click="toggleSpecialForm()"
                    >
                      {{ t('commerceDetail.btnCancel') }}
                    </button>
                    <button
                      class="cdp__hor-sf-save"
                      :disabled="specialSaving || !specialFormDay"
                      @click="saveSpecial()"
                    >
                      <i v-if="specialSaving" class="pi pi-spin pi-spinner" />
                      <span v-else>{{ t('commerceDetail.btnSave') }}</span>
                    </button>
                  </div>
                </div>

                <!-- Skeleton -->
                <div v-if="horFetching" class="cdp__hor-days">
                  <div v-for="n in 3" :key="n" class="cdp__hor-day-row">
                    <div
                      class="cdp__skel"
                      style="
                        width: 7rem;
                        height: 0.875rem;
                        border-radius: 0.25rem;
                        flex-shrink: 0;
                      "
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 9rem;
                        height: 0.875rem;
                        border-radius: 0.25rem;
                        flex: 1;
                      "
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 4rem;
                        height: 1.5rem;
                        border-radius: 9999px;
                        flex-shrink: 0;
                      "
                    />
                  </div>
                </div>

                <!-- Vide -->
                <div
                  v-else-if="
                    !horFetching &&
                    specialSchedules.length === 0 &&
                    !showSpecialForm
                  "
                  class="cdp__hor-empty-state"
                >
                  <div class="cdp__hor-empty-ico">
                    <i class="pi pi-calendar-times" />
                  </div>
                  <p class="cdp__hor-empty-title">
                    {{ t('commerceDetail.emptyExceptional') }}
                  </p>
                  <p class="cdp__hor-empty-desc">
                    Vos clients verront vos horaires standards uniquement.
                  </p>
                </div>

                <!-- Liste -->
                <div
                  v-if="!horFetching && specialSchedules.length > 0"
                  class="cdp__hor-days"
                >
                  <div
                    v-for="slot in specialSchedules"
                    :key="slot.id"
                    class="cdp__hor-day-row cdp__hor-day-row--special"
                  >
                    <span class="cdp__hor-day-name">{{
                      fmtDate(slot.day)
                    }}</span>
                    <span
                      v-if="slot.type === 'holiday'"
                      class="cdp__hor-day-empty"
                      >{{ t('commerceDetail.closedOption') }}</span
                    >
                    <span v-else class="cdp__hor-time"
                      >{{ fmtTime(slot.openTime) }} –
                      {{ fmtTime(slot.closeTime) }}</span
                    >
                    <span
                      class="cdp__hor-pill"
                      :class="
                        slot.type === 'holiday'
                          ? 'cdp__hor-pill--closed'
                          : 'cdp__hor-pill--special'
                      "
                    >
                      {{
                        slot.type === 'holiday'
                          ? t('commerceDetail.closed2')
                          : t('commerceDetail.specialSlots')
                      }}
                    </span>
                    <button
                      class="cdp__hor-del-btn"
                      :disabled="deletingSpecialId === slot.id"
                      @click.stop="deleteSpecial(slot)"
                    >
                      <i
                        v-if="deletingSpecialId === slot.id"
                        class="pi pi-spin pi-spinner"
                      />
                      <i v-else class="pi pi-trash" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Colonne droite (1/3) ── -->
            <div class="cdp__hor-col-side">
              <!-- Mise en pause manuelle -->
              <div
                class="cdp__hor-section cdp__hor-pause"
                :class="isActive ? 'cdp__hor-pause--on' : 'cdp__hor-pause--off'"
              >
                <div class="cdp__hor-pause-hd">
                  <div class="cdp__hor-pause-hd-left">
                    <i class="pi pi-power-off" />
                    <span>{{ t('commerceDetail.pauseTitle') }}</span>
                  </div>
                </div>
                <p class="cdp__hor-pause-desc">
                  {{ t('commerceDetail.pauseDesc') }}
                </p>
                <div
                  class="cdp__hor-pause-status"
                  :class="
                    isActive
                      ? 'cdp__hor-pause-status--on'
                      : 'cdp__hor-pause-status--off'
                  "
                >
                  <span class="cdp__hor-pause-dot" />
                  {{
                    isActive
                      ? t('commerceDetail.pauseOpen')
                      : t('commerceDetail.pauseSuspended')
                  }}
                </div>
              </div>

              <!-- Résumé -->
              <div class="cdp__hor-section">
                <div class="cdp__hor-tips-hd">
                  <i class="pi pi-chart-bar" />
                  <span>{{ t('commerceDetail.summary') }}</span>
                </div>
                <div class="cdp__hor-stats">
                  <div class="cdp__hor-stat">
                    <span class="cdp__hor-stat-val">{{ openDaysCount }}</span>
                    <span class="cdp__hor-stat-lbl">{{
                      t('commerceDetail.daysPerWeek')
                    }}</span>
                  </div>
                  <div class="cdp__hor-stat">
                    <span class="cdp__hor-stat-val">{{
                      specialSchedules.length
                    }}</span>
                    <span class="cdp__hor-stat-lbl">{{
                      t('commerceDetail.specialSlots')
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Journal d'activité (masqué temporairement) -->
              <div v-if="false" class="cdp__hor-section">
                <div class="cdp__hor-tips-hd">
                  <i class="pi pi-history" />
                  <span>{{ t('commerceDetail.journalTitle') }}</span>
                </div>
                <div class="cdp__hor-journal">
                  <div v-if="horFetching" class="cdp__hor-journal-list">
                    <div v-for="n in 3" :key="n" class="cdp__hor-journal-row">
                      <div
                        class="cdp__skel"
                        style="
                          width: 0.625rem;
                          height: 0.625rem;
                          border-radius: 50%;
                          flex-shrink: 0;
                          margin-top: 0.25rem;
                        "
                      />
                      <div
                        style="
                          flex: 1;
                          display: flex;
                          flex-direction: column;
                          gap: 0.25rem;
                        "
                      >
                        <div
                          class="cdp__skel"
                          style="
                            width: 90%;
                            height: 0.75rem;
                            border-radius: 0.2rem;
                          "
                        />
                        <div
                          class="cdp__skel"
                          style="
                            width: 50%;
                            height: 0.625rem;
                            border-radius: 0.2rem;
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div v-else class="cdp__hor-journal-list">
                    <div
                      v-for="entry in horJournal"
                      :key="entry.id"
                      class="cdp__hor-journal-row"
                    >
                      <span
                        class="cdp__hor-journal-dot"
                        :class="entry.dotClass"
                      />
                      <div class="cdp__hor-journal-body">
                        <span class="cdp__hor-journal-label">{{
                          entry.label
                        }}</span>
                        <span class="cdp__hor-journal-time">{{
                          entry.time
                        }}</span>
                      </div>
                    </div>
                    <div
                      v-if="horJournal.length === 0"
                      class="cdp__hor-journal-empty"
                    >
                      {{ t('commerceDetail.emptyJournal') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : MÉDIAS
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'medias'" class="cdp__med">
          <div class="cdp__med-sections">
            <!-- ── Logo ── -->
            <section class="cdp__med-section">
              <div class="cdp__med-section-hd">
                <div class="cdp__med-section-hd-left">
                  <i class="pi pi-image" />
                  <h3 class="cdp__med-section-title">
                    Logo
                    <span class="cdp__med-count">({{ logoUrl ? 1 : 0 }})</span>
                  </h3>
                </div>
                <button
                  class="cdp__med-add-btn"
                  :disabled="medLogoUploading"
                  @click="pickFile('image/*', uploadLogo)"
                >
                  <i v-if="medLogoUploading" class="pi pi-spin pi-spinner" />
                  <i v-else class="pi pi-plus-circle" />
                  {{
                    logoUrl
                      ? t('commerceDetail.btnReplaceLogo')
                      : t('commerceDetail.btnAddLogo')
                  }}
                </button>
              </div>

              <div v-if="logoUrl" class="cdp__med-list">
                <div class="cdp__med-item">
                  <div
                    class="cdp__med-item-preview cdp__med-item-preview--logo"
                  >
                    <img :src="logoUrl" alt="Logo" />
                  </div>
                  <div class="cdp__med-item-info">
                    <span class="cdp__med-item-name">{{
                      t('commerceDetail.logoName')
                    }}</span>
                    <span
                      class="cdp__med-item-badge cdp__med-item-badge--active"
                    >
                      <span class="cdp__med-item-dot" />
                      {{ t('commerceDetail.activeStatus') }}
                    </span>
                  </div>
                  <div class="cdp__med-item-actions">
                    <button
                      class="cdp__med-icon-btn"
                      :class="{
                        'cdp__med-icon-btn--copied': copiedUrl === logoUrl,
                      }"
                      :title="
                        copiedUrl === logoUrl
                          ? t('commerceDetail.copiedUrl')
                          : t('commerceDetail.copyLink')
                      "
                      @click="copyUrl(logoUrl)"
                    >
                      <i
                        :class="
                          copiedUrl === logoUrl ? 'pi pi-check' : 'pi pi-copy'
                        "
                      />
                    </button>
                    <a
                      :href="logoUrl"
                      target="_blank"
                      rel="noopener"
                      class="cdp__med-icon-btn"
                      :title="t('commerceDetail.open3')"
                    >
                      <i class="pi pi-external-link" />
                    </a>
                    <button
                      class="cdp__med-config-btn"
                      @click="pickFile('image/*', uploadLogo)"
                    >
                      <i class="pi pi-pencil" />
                      {{ t('commerceDetail.btnReplaceLogo') }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="cdp__med-empty">
                <i class="pi pi-image" />
                <span>{{ t('commerceDetail.noLogo') }}</span>
              </div>
            </section>

            <!-- ── Logo (mode clair) ── -->
            <section class="cdp__med-section">
              <div class="cdp__med-section-hd">
                <div class="cdp__med-section-hd-left">
                  <i class="pi pi-sun" />
                  <h3 class="cdp__med-section-title">
                    {{ t('commerceDetail.logoLightTitle') }}
                    <span class="cdp__med-count"
                      >({{ logoLightUrl ? 1 : 0 }})</span
                    >
                  </h3>
                </div>
                <button
                  class="cdp__med-add-btn"
                  :disabled="medLogoLightUploading"
                  @click="pickFile('image/*', uploadLogoLight)"
                >
                  <i
                    v-if="medLogoLightUploading"
                    class="pi pi-spin pi-spinner"
                  />
                  <i v-else class="pi pi-plus-circle" />
                  {{
                    logoLightUrl
                      ? t('commerceDetail.btnReplaceLogo')
                      : t('commerceDetail.btnAddLogo')
                  }}
                </button>
              </div>

              <div v-if="mediaLogoLightFetching" class="cdp__med-empty">
                <i class="pi pi-spin pi-spinner" />
              </div>
              <div v-else-if="logoLightUrl" class="cdp__med-list">
                <div class="cdp__med-item">
                  <div
                    class="cdp__med-item-preview cdp__med-item-preview--logo cdp__med-item-preview--light"
                  >
                    <img :src="logoLightUrl" alt="Logo light" />
                  </div>
                  <div class="cdp__med-item-info">
                    <span class="cdp__med-item-name">{{
                      t('commerceDetail.logoLightName')
                    }}</span>
                    <span
                      class="cdp__med-item-badge cdp__med-item-badge--active"
                    >
                      <span class="cdp__med-item-dot" />
                      {{ t('commerceDetail.activeStatus') }}
                    </span>
                  </div>
                  <div class="cdp__med-item-actions">
                    <button
                      class="cdp__med-icon-btn"
                      :class="{
                        'cdp__med-icon-btn--copied': copiedUrl === logoLightUrl,
                      }"
                      :title="
                        copiedUrl === logoLightUrl
                          ? t('commerceDetail.copiedUrl')
                          : t('commerceDetail.copyLink')
                      "
                      @click="copyUrl(logoLightUrl)"
                    >
                      <i
                        :class="
                          copiedUrl === logoLightUrl
                            ? 'pi pi-check'
                            : 'pi pi-copy'
                        "
                      />
                    </button>
                    <a
                      :href="logoLightUrl"
                      target="_blank"
                      rel="noopener"
                      class="cdp__med-icon-btn"
                      :title="t('commerceDetail.open3')"
                    >
                      <i class="pi pi-external-link" />
                    </a>
                    <button
                      class="cdp__med-config-btn"
                      @click="pickFile('image/*', uploadLogoLight)"
                    >
                      <i class="pi pi-pencil" />
                      {{ t('commerceDetail.btnReplaceLogo') }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="cdp__med-empty">
                <i class="pi pi-sun" />
                <span>{{ t('commerceDetail.noLogoLight') }}</span>
              </div>
            </section>

            <!-- ── Logo (mode sombre) ── -->
            <section class="cdp__med-section">
              <div class="cdp__med-section-hd">
                <div class="cdp__med-section-hd-left">
                  <i class="pi pi-moon" />
                  <h3 class="cdp__med-section-title">
                    {{ t('commerceDetail.logoDarkTitle') }}
                    <span class="cdp__med-count"
                      >({{ logoDarkUrl ? 1 : 0 }})</span
                    >
                  </h3>
                </div>
                <button
                  class="cdp__med-add-btn"
                  :disabled="medLogoDarkUploading"
                  @click="pickFile('image/*', uploadLogoDark)"
                >
                  <i
                    v-if="medLogoDarkUploading"
                    class="pi pi-spin pi-spinner"
                  />
                  <i v-else class="pi pi-plus-circle" />
                  {{
                    logoDarkUrl
                      ? t('commerceDetail.btnReplaceLogo')
                      : t('commerceDetail.btnAddLogo')
                  }}
                </button>
              </div>

              <div v-if="mediaLogoDarkFetching" class="cdp__med-empty">
                <i class="pi pi-spin pi-spinner" />
              </div>
              <div v-else-if="logoDarkUrl" class="cdp__med-list">
                <div class="cdp__med-item">
                  <div
                    class="cdp__med-item-preview cdp__med-item-preview--logo cdp__med-item-preview--dark"
                  >
                    <img :src="logoDarkUrl" alt="Logo dark" />
                  </div>
                  <div class="cdp__med-item-info">
                    <span class="cdp__med-item-name">{{
                      t('commerceDetail.logoDarkName')
                    }}</span>
                    <span
                      class="cdp__med-item-badge cdp__med-item-badge--active"
                    >
                      <span class="cdp__med-item-dot" />
                      {{ t('commerceDetail.activeStatus') }}
                    </span>
                  </div>
                  <div class="cdp__med-item-actions">
                    <button
                      class="cdp__med-icon-btn"
                      :class="{
                        'cdp__med-icon-btn--copied': copiedUrl === logoDarkUrl,
                      }"
                      :title="
                        copiedUrl === logoDarkUrl
                          ? t('commerceDetail.copiedUrl')
                          : t('commerceDetail.copyLink')
                      "
                      @click="copyUrl(logoDarkUrl)"
                    >
                      <i
                        :class="
                          copiedUrl === logoDarkUrl
                            ? 'pi pi-check'
                            : 'pi pi-copy'
                        "
                      />
                    </button>
                    <a
                      :href="logoDarkUrl"
                      target="_blank"
                      rel="noopener"
                      class="cdp__med-icon-btn"
                      :title="t('commerceDetail.open3')"
                    >
                      <i class="pi pi-external-link" />
                    </a>
                    <button
                      class="cdp__med-config-btn"
                      @click="pickFile('image/*', uploadLogoDark)"
                    >
                      <i class="pi pi-pencil" />
                      {{ t('commerceDetail.btnReplaceLogo') }}
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="cdp__med-empty">
                <i class="pi pi-moon" />
                <span>{{ t('commerceDetail.noLogoDark') }}</span>
              </div>
            </section>

            <!-- Erreur upload featured -->
            <div
              v-if="medUploadError"
              class="cdp__med-form-error"
              style="margin-bottom: 0.5rem"
            >
              <i class="pi pi-exclamation-circle" /> {{ medUploadError }}
            </div>

            <!-- ── Featured images (par rôle) ── -->
            <section
              v-for="roleConf in FEATURED_ROLES"
              :key="roleConf.role"
              class="cdp__med-section"
            >
              <div class="cdp__med-section-hd">
                <div class="cdp__med-section-hd-left">
                  <i :class="`pi ${roleConf.icon}`" />
                  <h3 class="cdp__med-section-title">
                    {{ roleConf.label }}
                    <span class="cdp__med-count"
                      >({{ itemsForRole(roleConf.role).length }})</span
                    >
                  </h3>
                </div>
                <button
                  class="cdp__med-add-btn"
                  @click="toggleAddForm(roleConf.role)"
                >
                  <i
                    :class="showAddForm ? 'pi pi-times' : 'pi pi-plus-circle'"
                  />
                  {{
                    showAddForm
                      ? t('commerceDetail.btnCancel')
                      : t('commerceDetail.btnAdd')
                  }}
                </button>
              </div>

              <!-- Formulaire ajout inline -->
              <div v-if="showAddForm" class="cdp__add-form">
                <p class="cdp__add-form-title">
                  {{ t('commerceDetail.newImage') }} — {{ roleConf.label }}
                </p>

                <!-- Picker fichier -->
                <div class="cdp__add-form-row">
                  <div
                    class="cdp__add-form-preview"
                    @click="openFeaturedFilePicker()"
                  >
                    <img
                      v-if="featuredForm.previewSrc"
                      :src="featuredForm.previewSrc"
                      alt="Aperçu"
                    />
                    <div v-else class="cdp__add-form-preview-empty">
                      <i class="pi pi-image" />
                      <span>{{ t('commerceDetail.chooseImage') }}</span>
                      <span class="cdp__add-form-preview-hint">{{
                        t('commerceDetail.imageFormatHint')
                      }}</span>
                    </div>
                  </div>

                  <div class="cdp__add-form-fields">
                    <div class="cdp__med-field-row">
                      <div class="cdp__med-field">
                        <label class="cdp__med-label">{{
                          t('commerceDetail.displayOrder')
                        }}</label>
                        <input
                          class="cdp__med-input"
                          type="number"
                          min="1"
                          v-model.number="featuredForm.displayOrder"
                        />
                      </div>
                      <div
                        class="cdp__med-field"
                        style="justify-content: flex-end"
                      >
                        <label
                          class="cdp__med-toggle-label"
                          style="margin-top: 1.25rem"
                        >
                          <button
                            class="cdp__med-toggle"
                            :class="{
                              'cdp__med-toggle--on': featuredForm.isActive,
                            }"
                            @click="toggleFeaturedFormActive()"
                          >
                            <span class="cdp__med-toggle-thumb" />
                          </button>
                          <span>{{
                            featuredForm.isActive
                              ? t('commerceDetail.activeStatus')
                              : t('commerceDetail.inactiveStatus')
                          }}</span>
                        </label>
                      </div>
                    </div>
                    <div class="cdp__med-field-row">
                      <div class="cdp__med-field">
                        <label class="cdp__med-label">{{
                          t('commerceDetail.startDate')
                        }}</label>
                        <input
                          class="cdp__med-input"
                          type="datetime-local"
                          v-model="featuredForm.startDate"
                        />
                      </div>
                      <div class="cdp__med-field">
                        <label class="cdp__med-label">{{
                          t('commerceDetail.endDate')
                        }}</label>
                        <input
                          class="cdp__med-input"
                          type="datetime-local"
                          v-model="featuredForm.endDate"
                        />
                      </div>
                    </div>
                    <div class="cdp__med-field">
                      <label class="cdp__med-label">{{
                        t('commerceDetail.titleLabel')
                      }}</label>
                      <input
                        class="cdp__med-input"
                        v-model="featuredForm.metaTitle"
                        :placeholder="t('commerceDetail.overlayPlaceholder')"
                      />
                    </div>
                    <div class="cdp__med-field-row">
                      <div class="cdp__med-field">
                        <label class="cdp__med-label">{{
                          t('commerceDetail.slideDuration')
                        }}</label>
                        <input
                          class="cdp__med-input"
                          type="number"
                          min="1000"
                          max="30000"
                          step="500"
                          v-model="featuredForm.metaSlideDuration"
                          placeholder="5000"
                        />
                      </div>
                      <div
                        class="cdp__med-field"
                        style="align-items: flex-end; justify-content: flex-end"
                      >
                        <label
                          class="cdp__med-toggle-label"
                          style="margin-bottom: 0.375rem"
                        >
                          <input
                            type="checkbox"
                            v-model="featuredForm.metaSkipAllowed"
                            style="
                              width: 1rem;
                              height: 1rem;
                              accent-color: var(--nxt-primary);
                            "
                          />
                          <span>{{ t('commerceDetail.skipAllowed') }}</span>
                        </label>
                      </div>
                    </div>
                    <div class="cdp__med-field">
                      <label class="cdp__med-label">{{
                        t('commerceDetail.medDescription')
                      }}</label>
                      <textarea
                        class="cdp__med-input cdp__med-textarea"
                        v-model="featuredForm.metaDescription"
                        :placeholder="t('commerceDetail.descPlaceholder')"
                        rows="2"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="medUploadError" class="cdp__med-form-error">
                  <i class="pi pi-exclamation-circle" /> {{ medUploadError }}
                </div>

                <div class="cdp__add-form-footer">
                  <button
                    class="cdp__med-btn-ghost"
                    @click="toggleAddForm(roleConf.role)"
                  >
                    {{ t('commerceDetail.btnCancel') }}
                  </button>
                  <button
                    class="cdp__med-btn-primary"
                    :disabled="!featuredForm.file || medUploading"
                    @click="uploadFeatured(roleConf.role)"
                  >
                    <i v-if="medUploading" class="pi pi-spin pi-spinner" />
                    {{
                      medUploading
                        ? t('commerceDetail.btnAdding')
                        : t('commerceDetail.btnAdd')
                    }}
                  </button>
                </div>
              </div>

              <div v-if="isFetchingRole(roleConf.role)" class="cdp__med-list">
                <div v-for="n in 2" :key="n" class="cdp__med-item">
                  <div
                    class="cdp__skel"
                    style="
                      width: 5rem;
                      height: 5rem;
                      border-radius: 0.75rem;
                      flex-shrink: 0;
                    "
                  />
                  <div
                    style="
                      flex: 1;
                      display: flex;
                      flex-direction: column;
                      gap: 0.375rem;
                    "
                  >
                    <div
                      class="cdp__skel"
                      style="
                        width: 60%;
                        height: 0.875rem;
                        border-radius: 0.25rem;
                      "
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 4rem;
                        height: 1.375rem;
                        border-radius: 9999px;
                      "
                    />
                  </div>
                </div>
              </div>
              <div
                v-else-if="
                  itemsForRole(roleConf.role).length === 0 && !showAddForm
                "
                class="cdp__med-empty"
              >
                <i class="pi pi-photo" />
                <span>{{ t('commerceDetail.noMediaForRole') }}</span>
              </div>
              <div
                v-else-if="itemsForRole(roleConf.role).length > 0"
                class="cdp__med-list"
              >
                <template
                  v-for="(item, idx) in itemsForRole(roleConf.role)"
                  :key="item.id ?? idx"
                >
                  <!-- Ligne item -->
                  <div
                    class="cdp__med-item"
                    :class="{
                      'cdp__med-item--editing':
                        featuredEditId === (item.id ?? item.mediaId),
                    }"
                  >
                    <div class="cdp__med-item-order-btns">
                      <button
                        class="cdp__med-order-btn"
                        :disabled="idx === 0 || medReordering"
                        @click="
                          moveFeatured(
                            roleConf.role,
                            itemsForRole(roleConf.role),
                            idx,
                            -1,
                          )
                        "
                      >
                        <i class="pi pi-chevron-up" />
                      </button>
                      <span class="cdp__med-item-order-num">{{ idx + 1 }}</span>
                      <button
                        class="cdp__med-order-btn"
                        :disabled="
                          idx === itemsForRole(roleConf.role).length - 1 ||
                          medReordering
                        "
                        @click="
                          moveFeatured(
                            roleConf.role,
                            itemsForRole(roleConf.role),
                            idx,
                            1,
                          )
                        "
                      >
                        <i class="pi pi-chevron-down" />
                      </button>
                    </div>
                    <div class="cdp__med-item-preview">
                      <img
                        v-if="mediaUrl(item)"
                        :src="mediaUrl(item)"
                        :alt="item.metadata?.title ?? roleConf.label"
                      />
                      <i v-else class="pi pi-image" />
                    </div>
                    <div class="cdp__med-item-info">
                      <span class="cdp__med-item-name">{{
                        item.metadata?.title || `${roleConf.label} #${idx + 1}`
                      }}</span>
                      <p
                        v-if="item.metadata?.description"
                        class="cdp__med-item-desc"
                      >
                        {{ item.metadata.description }}
                      </p>
                      <span
                        class="cdp__med-item-badge"
                        :class="
                          item.isActive
                            ? 'cdp__med-item-badge--active'
                            : 'cdp__med-item-badge--draft'
                        "
                      >
                        <span class="cdp__med-item-dot" />
                        {{
                          item.isActive
                            ? t('commerceDetail.activeStatus')
                            : t('commerceDetail.draft')
                        }}
                      </span>
                    </div>
                    <div class="cdp__med-item-actions">
                      <button
                        v-if="mediaUrl(item)"
                        class="cdp__med-icon-btn"
                        :class="{
                          'cdp__med-icon-btn--copied':
                            copiedUrl === mediaUrl(item),
                        }"
                        :title="
                          copiedUrl === mediaUrl(item)
                            ? t('commerceDetail.copiedUrl')
                            : t('commerceDetail.copyLink')
                        "
                        @click="copyUrl(mediaUrl(item))"
                      >
                        <i
                          :class="
                            copiedUrl === mediaUrl(item)
                              ? 'pi pi-check'
                              : 'pi pi-copy'
                          "
                        />
                      </button>
                      <a
                        v-if="mediaUrl(item)"
                        :href="mediaUrl(item)"
                        target="_blank"
                        rel="noopener"
                        class="cdp__med-icon-btn"
                        :title="t('commerceDetail.preview')"
                      >
                        <i class="pi pi-external-link" />
                      </a>
                      <button
                        class="cdp__med-config-btn"
                        :class="{
                          'cdp__med-config-btn--active':
                            featuredEditId === (item.id ?? item.mediaId),
                        }"
                        @click="openFeaturedEdit(roleConf.role, item)"
                      >
                        <i class="pi pi-pencil" />
                        {{ t('commerceDetail.btnEdit') }}
                      </button>
                      <button
                        class="cdp__med-del-btn"
                        @click="askDeleteFeatured(item)"
                      >
                        <i class="pi pi-trash" />
                      </button>
                    </div>
                  </div>

                  <!-- Panneau confirmation suppression -->
                  <div
                    v-if="confirmDeleteId === (item.id ?? item.mediaId)"
                    class="cdp__inline-confirm"
                  >
                    <i
                      class="pi pi-exclamation-triangle cdp__inline-confirm-icon"
                    />
                    <span>{{ t('commerceDetail.deleteImageConfirm') }}</span>
                    <div class="cdp__inline-confirm-btns">
                      <button
                        class="cdp__med-btn-ghost cdp__inline-confirm-btn-sm"
                        @click="cancelDeleteFeatured()"
                      >
                        {{ t('commerceDetail.no') }}
                      </button>
                      <button
                        class="cdp__inline-confirm-btn-del"
                        :disabled="medDeleting === (item.id ?? item.mediaId)"
                        @click="deleteFeatured(roleConf.role, item)"
                      >
                        <i
                          v-if="medDeleting === (item.id ?? item.mediaId)"
                          class="pi pi-spin pi-spinner"
                        />
                        {{ t('commerceDetail.yesDelete') }}
                      </button>
                    </div>
                  </div>

                  <!-- Panneau édition inline -->
                  <div
                    v-if="
                      featuredEditId === (item.id ?? item.mediaId) &&
                      featuredEdit
                    "
                    class="cdp__add-form"
                  >
                    <div class="cdp__add-form-fields">
                      <div class="cdp__med-field-row">
                        <div class="cdp__med-field">
                          <label class="cdp__med-label">{{
                            t('commerceDetail.titleLabel')
                          }}</label>
                          <input
                            class="cdp__med-input"
                            v-model="featuredEdit.title"
                            :placeholder="t('commerceDetail.titlePlaceholder')"
                          />
                        </div>
                        <div
                          class="cdp__med-field"
                          style="justify-content: flex-end"
                        >
                          <label
                            class="cdp__med-toggle-label"
                            style="margin-top: 1.25rem"
                          >
                            <button
                              class="cdp__med-toggle"
                              :class="{
                                'cdp__med-toggle--on': featuredEdit.isActive,
                              }"
                              @click="toggleFeaturedEditActive()"
                            >
                              <span class="cdp__med-toggle-thumb" />
                            </button>
                            <span>{{
                              featuredEdit.isActive
                                ? t('commerceDetail.activeStatus')
                                : t('commerceDetail.inactiveStatus')
                            }}</span>
                          </label>
                        </div>
                      </div>
                      <div class="cdp__med-field">
                        <label class="cdp__med-label">{{
                          t('commerceDetail.medDescription')
                        }}</label>
                        <textarea
                          class="cdp__med-input cdp__med-textarea"
                          v-model="featuredEdit.description"
                          :placeholder="t('commerceDetail.descPlaceholder')"
                          rows="2"
                        />
                      </div>
                      <div class="cdp__med-field-row">
                        <div class="cdp__med-field">
                          <label class="cdp__med-label">{{
                            t('commerceDetail.startDate')
                          }}</label>
                          <input
                            class="cdp__med-input"
                            type="datetime-local"
                            v-model="featuredEdit.startDate"
                          />
                        </div>
                        <div class="cdp__med-field">
                          <label class="cdp__med-label">{{
                            t('commerceDetail.endDate')
                          }}</label>
                          <input
                            class="cdp__med-input"
                            type="datetime-local"
                            v-model="featuredEdit.endDate"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-if="featuredEditError" class="cdp__med-form-error">
                      <i class="pi pi-exclamation-circle" />
                      {{ featuredEditError }}
                    </div>
                    <div class="cdp__add-form-footer">
                      <button
                        class="cdp__med-btn-ghost"
                        @click="closeFeaturedEdit()"
                      >
                        {{ t('commerceDetail.btnCancel') }}
                      </button>
                      <button
                        class="cdp__med-btn-primary"
                        :disabled="featuredEditSaving"
                        @click="saveFeaturedEdit()"
                      >
                        <i
                          v-if="featuredEditSaving"
                          class="pi pi-spin pi-spinner"
                        />
                        {{ t('commerceDetail.btnSave') }}
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <!-- ── Fichiers ── -->
            <section class="cdp__med-section">
              <div class="cdp__med-section-hd">
                <div class="cdp__med-section-hd-left">
                  <i class="pi pi-folder-open" />
                  <h3 class="cdp__med-section-title">
                    {{ t('commerceDetail.filesTitle') }}
                    <span class="cdp__med-count">({{ filesTotal }})</span>
                  </h3>
                </div>
                <div class="cdp__med-files-hd-right">
                  <div class="cdp__med-files-search">
                    <i class="pi pi-search cdp__med-files-search-icon" />
                    <input
                      class="cdp__med-files-search-input"
                      v-model="filesSearch"
                      @input="onFilesSearchInput"
                      :placeholder="t('commerceDetail.fileSearch')"
                    />
                  </div>
                  <button
                    class="cdp__med-add-btn"
                    :disabled="fileUploading"
                    @click="pickFile('*/*', uploadRawFile)"
                  >
                    <i v-if="fileUploading" class="pi pi-spin pi-spinner" />
                    <i v-else class="pi pi-upload" />
                    {{ t('commerceDetail.upload') }}
                  </button>
                </div>
              </div>

              <div v-if="filesFetching" class="cdp__med-files-grid">
                <div v-for="n in 6" :key="n" class="cdp__med-file-card">
                  <div
                    class="cdp__skel"
                    style="height: 7rem; border-radius: 0.625rem 0.625rem 0 0"
                  />
                  <div
                    style="
                      padding: 0.625rem;
                      display: flex;
                      flex-direction: column;
                      gap: 0.375rem;
                    "
                  >
                    <div
                      class="cdp__skel"
                      style="height: 0.75rem; border-radius: 0.25rem"
                    />
                    <div
                      class="cdp__skel"
                      style="
                        width: 60%;
                        height: 0.625rem;
                        border-radius: 0.25rem;
                      "
                    />
                  </div>
                </div>
              </div>
              <div v-else-if="filesList.length === 0" class="cdp__med-empty">
                <i class="pi pi-folder-open" />
                <span>{{ t('commerceDetail.emptyFiles') }}</span>
              </div>
              <div v-else class="cdp__med-files-grid">
                <div
                  v-for="file in filesList"
                  :key="file.id"
                  class="cdp__med-file-card"
                >
                  <!-- Preview -->
                  <div
                    class="cdp__med-file-preview"
                    :style="
                      isImage(file) && filePreviewUrl(file)
                        ? { backgroundImage: `url('${filePreviewUrl(file)}')` }
                        : {}
                    "
                  >
                    <div v-if="!isImage(file)" class="cdp__med-file-icon">
                      <i :class="fileIcon(file)" />
                    </div>
                    <!-- Extension badge -->
                    <span class="cdp__med-file-ext">{{
                      fileExtLabel(file)
                    }}</span>
                    <!-- Hover actions overlay -->
                    <div class="cdp__med-file-overlay">
                      <button
                        class="cdp__med-overlay-btn"
                        :class="{
                          'cdp__med-overlay-btn--ok':
                            copiedUrl === filePreviewUrl(file),
                        }"
                        :title="
                          copiedUrl === filePreviewUrl(file)
                            ? t('commerceDetail.copiedUrl')
                            : t('commerceDetail.copyLink')
                        "
                        @click.stop="copyUrl(filePreviewUrl(file))"
                      >
                        <i
                          :class="
                            copiedUrl === filePreviewUrl(file)
                              ? 'pi pi-check'
                              : 'pi pi-copy'
                          "
                        />
                      </button>
                      <a
                        v-if="filePreviewUrl(file)"
                        :href="filePreviewUrl(file)"
                        target="_blank"
                        rel="noopener"
                        class="cdp__med-overlay-btn"
                        :title="t('commerceDetail.open3')"
                        @click.stop
                      >
                        <i class="pi pi-external-link" />
                      </a>
                      <button
                        class="cdp__med-overlay-btn cdp__med-overlay-btn--del"
                        :disabled="deletingFileId === file.id"
                        :title="t('common.delete')"
                        @click.stop="deleteFile(file)"
                      >
                        <i
                          v-if="deletingFileId === file.id"
                          class="pi pi-spin pi-spinner"
                        />
                        <i v-else class="pi pi-trash" />
                      </button>
                    </div>
                  </div>
                  <!-- Info -->
                  <div class="cdp__med-file-info">
                    <span
                      class="cdp__med-file-name"
                      :title="file.designation ?? file.name"
                      >{{ file.designation ?? file.name ?? '—' }}</span
                    >
                    <span v-if="file.createdAt" class="cdp__med-file-date">
                      <i class="pi pi-calendar" />
                      {{ fmtDate(file.createdAt) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Pagination fichiers -->
              <div class="cdp__med-files-pager">
                <div class="cdp__med-pager-sizes">
                  <button
                    v-for="s in FILES_PAGE_SIZE_OPTIONS"
                    :key="s"
                    class="cdp__med-pager-size-btn"
                    :class="{
                      'cdp__med-pager-size-btn--active': filesPageSize === s,
                    }"
                    @click="setFilesPageSize(s)"
                  >
                    {{ s }}
                  </button>
                </div>
                <template v-if="filesTotalPages > 1">
                  <button
                    class="cdp__med-pager-btn"
                    :disabled="filesPage === 1"
                    @click="filesPage--"
                  >
                    <i class="pi pi-chevron-left" />
                  </button>
                  <span class="cdp__med-pager-info"
                    >{{ filesPage }} / {{ filesTotalPages }}</span
                  >
                  <button
                    class="cdp__med-pager-btn"
                    :disabled="filesPage === filesTotalPages"
                    @click="filesPage++"
                  >
                    <i class="pi pi-chevron-right" />
                  </button>
                </template>
              </div>
            </section>
          </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : UTILISATEURS & RÔLES
    ═══════════════════════════════════════════════════════════════════════ -->
        <div
          v-else-if="activeTab === 'users'"
          class="cdp__usr"
          @click="usersRoleDropdownOpen = false"
        >
          <!-- Toast succès / erreur -->
          <Transition name="cdp__usr-toast-anim">
            <div
              v-if="usrToast"
              class="cdp__usr-toast"
              :class="`cdp__usr-toast--${usrToast.type}`"
            >
              <i
                class="pi"
                :class="
                  usrToast.type === 'success'
                    ? 'pi-check-circle'
                    : 'pi-exclamation-circle'
                "
              />
              <span>{{ usrToast.msg }}</span>
              <button class="cdp__usr-toast-close" @click="usrToast = null">
                <i class="pi pi-times" />
              </button>
            </div>
          </Transition>

          <!-- Sous-onglets -->
          <div class="cdp__subtabs">
            <button
              v-for="st in USR_SUB_TABS"
              :key="st.key"
              class="cdp__subtab"
              :class="{ 'cdp__subtab--active': activeUsrTab === st.key }"
              @click="activeUsrTab = st.key"
            >
              {{ st.label }}
            </button>
          </div>

          <!-- ══════════════════════════════════════════════════════════════════
           SOUS-TAB : GESTION DU COMMERCE
      ═══════════════════════════════════════════════════════════════════ -->
          <template v-if="activeUsrTab === 'management'">
            <!-- ══ Gestionnaires ════════════════════════════════════════════════ -->

            <!-- Toolbar -->
            <div
              v-if="!selectedManagerId && !editUserId"
              class="cdp__usr-toolbar"
            >
              <div class="cdp__usr-search-wrap">
                <i class="pi pi-search cdp__usr-search-ico" />
                <input
                  v-model="usersSearch"
                  type="text"
                  :placeholder="t('commerceDetail.userSearch')"
                  class="cdp__usr-search"
                />
              </div>
              <div class="cdp__usr-role-dropdown-wrap" @click.stop>
                <button
                  class="cdp__usr-role-dropdown-btn"
                  :class="{
                    'cdp__usr-role-dropdown-btn--active': usersRoleFilter,
                  }"
                  @click="usersRoleDropdownOpen = !usersRoleDropdownOpen"
                >
                  <i class="pi pi-filter" />
                  <span>{{
                    selectedUsersRoleLabel || t('commerceDetail.allRoles')
                  }}</span>
                  <i
                    v-if="usersRoleFilter"
                    class="pi pi-times"
                    @click.stop="clearUsersRoleFilter"
                  />
                  <i v-else class="pi pi-chevron-down" />
                </button>
                <Transition name="cdp__usr-drop">
                  <div
                    v-if="usersRoleDropdownOpen"
                    class="cdp__usr-role-dropdown-panel"
                  >
                    <div class="cdp__usr-role-dropdown-search-wrap">
                      <i class="pi pi-search" />
                      <input
                        v-model="usersRoleSearch"
                        class="cdp__usr-role-dropdown-search"
                        :placeholder="t('userManagement.create.searchRole')"
                        @click.stop
                      />
                    </div>
                    <div class="cdp__usr-role-dropdown-list">
                      <button
                        class="cdp__usr-role-dropdown-item"
                        :class="{
                          'cdp__usr-role-dropdown-item--active':
                            !usersRoleFilter,
                        }"
                        @click="clearUsersRoleFilter"
                      >
                        {{ t('commerceDetail.allRoles') }}
                      </button>
                      <button
                        v-for="r in filteredUsersRoleOptions"
                        :key="r.id"
                        class="cdp__usr-role-dropdown-item"
                        :class="{
                          'cdp__usr-role-dropdown-item--active':
                            usersRoleFilter === r.code,
                        }"
                        @click="selectUsersRoleFilter(r.code)"
                      >
                        {{ r.label ?? r.code }}
                      </button>
                    </div>
                  </div>
                </Transition>
              </div>
              <span class="cdp__usr-count"
                >{{ staffTotal }}
                {{
                  t(
                    staffTotal > 1
                      ? 'commerceDetail.results'
                      : 'commerceDetail.result',
                  )
                }}</span
              >
              <button
                class="cdp__btn cdp__btn--primary cdp__btn--shadow cdp__usr-add-btn"
                @click="openCreateUser"
              >
                <i class="pi pi-plus" /> {{ t('commerceDetail.btnAdd') }}
              </button>
            </div>

            <template v-if="!selectedManagerId">
              <!-- Formulaire inline créer utilisateur — design soigné -->
              <div v-if="showCreateUser" class="cdp__uf">
                <!-- Header avec avatar preview live -->
                <div class="cdp__uf-header">
                  <div
                    class="cdp__uf-avatar-preview"
                    :style="`background:${userColor((newUser.firstname || newUser.email || 'A').slice(0, 1))}`"
                  >
                    <span>{{
                      newUser.firstname && newUser.lastname
                        ? (
                            newUser.firstname[0] + newUser.lastname[0]
                          ).toUpperCase()
                        : newUser.firstname
                          ? newUser.firstname.slice(0, 2).toUpperCase()
                          : newUser.email
                            ? newUser.email.slice(0, 2).toUpperCase()
                            : '?'
                    }}</span>
                  </div>
                  <div class="cdp__uf-header-text">
                    <p class="cdp__uf-header-title">
                      {{ t('commerceDetail.newManager') }}
                    </p>
                    <p class="cdp__uf-header-sub">
                      {{ t('commerceDetail.managerDesc') }}
                    </p>
                  </div>
                  <button
                    class="cdp__uf-close"
                    @click="closeCreateUser"
                    :title="t('commerceDetail.btnCancel')"
                  >
                    <i class="pi pi-times" />
                  </button>
                </div>

                <!-- Corps -->
                <div class="cdp__uf-body">
                  <!-- Section identité -->
                  <div class="cdp__uf-section">
                    <p class="cdp__uf-section-label">
                      <i class="pi pi-user" />
                      {{ t('commerceDetail.identity') }}
                    </p>
                    <div class="cdp__uf-grid2">
                      <div class="cdp__uf-field">
                        <label class="cdp__uf-label">{{
                          t('commerceDetail.firstName')
                        }}</label>
                        <input
                          v-model="newUser.firstname"
                          type="text"
                          class="cdp__uf-input"
                          :placeholder="
                            t('commerceDetail.firstNamePlaceholder')
                          "
                          autocomplete="given-name"
                        />
                      </div>
                      <div class="cdp__uf-field">
                        <label class="cdp__uf-label">{{
                          t('commerceDetail.lastName')
                        }}</label>
                        <input
                          v-model="newUser.lastname"
                          type="text"
                          class="cdp__uf-input"
                          :placeholder="t('commerceDetail.lastNamePlaceholder')"
                          autocomplete="family-name"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Séparateur -->
                  <div class="cdp__uf-sep" />

                  <!-- Section accès -->
                  <div class="cdp__uf-section">
                    <p class="cdp__uf-section-label">
                      <i class="pi pi-lock" /> {{ t('commerceDetail.access') }}
                    </p>
                    <div class="cdp__uf-field">
                      <label class="cdp__uf-label"
                        >{{ t('commerceDetail.emailLabel') }}
                        <span class="cdp__uf-required">*</span></label
                      >
                      <input
                        v-model="newUser.email"
                        type="email"
                        class="cdp__uf-input"
                        :placeholder="t('commerceDetail.emailPlaceholder2')"
                        autocomplete="email"
                      />
                    </div>
                    <div class="cdp__uf-field" style="margin-top: 0.75rem">
                      <label class="cdp__uf-label"
                        >{{ t('commerceDetail.pwdLabel') }}
                        <span class="cdp__uf-required">*</span></label
                      >
                      <div class="cdp__uf-pwd-wrap">
                        <input
                          v-model="newUser.password"
                          :type="showNewPwd ? 'text' : 'password'"
                          class="cdp__uf-input cdp__uf-input--pwd"
                          :placeholder="t('commerceDetail.pwdPlaceholder')"
                          autocomplete="new-password"
                        />
                        <button
                          type="button"
                          class="cdp__uf-pwd-eye"
                          @click="showNewPwd = !showNewPwd"
                          :title="
                            showNewPwd
                              ? t('commerceDetail.hidePwd')
                              : t('commerceDetail.showPwd')
                          "
                        >
                          <i
                            class="pi"
                            :class="showNewPwd ? 'pi-eye-slash' : 'pi-eye'"
                          />
                        </button>
                        <button
                          type="button"
                          class="cdp__uf-pwd-gen"
                          @click="genNewUserPwd"
                          :title="t('commerceDetail.generatePwd')"
                        >
                          <i class="pi pi-refresh" />
                          <span>{{
                            t('commerceDetail.generatePwdLabel')
                          }}</span>
                        </button>
                      </div>
                      <span
                        v-if="newUser.password && showNewPwd"
                        class="cdp__uf-pwd-preview"
                        >{{ newUser.password }}</span
                      >
                    </div>
                  </div>

                  <!-- Séparateur -->
                  <div class="cdp__uf-sep" />

                  <!-- Section rôle -->
                  <div class="cdp__uf-section">
                    <p class="cdp__uf-section-label">
                      <i class="pi pi-shield" />
                      {{ t('commerceDetail.roleLabel') }}
                    </p>

                    <!-- Chargement -->
                    <div v-if="isLoadingRoles" class="cdp__uf-roles-loading">
                      <span class="cdp__spinner" />
                      {{ t('commerceDetail.loadingRoles') }}
                    </div>

                    <!-- Aucun rôle défini -->
                    <div
                      v-else-if="assignableRoles.length === 0"
                      class="cdp__uf-roles-empty"
                    >
                      <i class="pi pi-info-circle" />
                      <span>{{ t('commerceDetail.emptyRoles') }}</span>
                    </div>

                    <!-- Liste des rôles — un utilisateur peut avoir plusieurs
                         rôles simultanément, d'où des cases à cocher plutôt
                         que des boutons radio (un seul choix possible). -->
                    <div v-else class="cdp__uf-role-list">
                      <p
                        v-if="!newUser.roleIds.length"
                        class="cdp__uf-role-desc"
                      >
                        {{ t('commerceDetail.laterRole') }}
                      </p>
                      <label
                        v-for="r in assignableRoles"
                        :key="r.id"
                        class="cdp__uf-role-opt"
                        :class="{
                          'cdp__uf-role-opt--sel': newUser.roleIds.includes(
                            r.id,
                          ),
                        }"
                      >
                        <input
                          type="checkbox"
                          :checked="newUser.roleIds.includes(r.id)"
                          style="display: none"
                          @change="
                            newUser.roleIds = toggleRoleId(
                              newUser.roleIds,
                              r.id,
                            )
                          "
                        />
                        <span
                          class="cdp__uf-role-checkbox"
                          :class="{
                            'cdp__uf-role-checkbox--on':
                              newUser.roleIds.includes(r.id),
                          }"
                        >
                          <i
                            v-if="newUser.roleIds.includes(r.id)"
                            class="pi pi-check"
                          />
                        </span>
                        <span class="cdp__uf-role-name">{{ r.label }}</span>
                        <span class="cdp__uf-role-code">{{ r.code }}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Erreur -->
                <div v-if="createUserErr" class="cdp__uf-error">
                  <i class="pi pi-exclamation-circle" /> {{ createUserErr }}
                </div>

                <!-- Footer -->
                <div class="cdp__uf-footer">
                  <button
                    class="cdp__btn cdp__btn--ghost"
                    @click="closeCreateUser"
                  >
                    {{ t('commerceDetail.btnCancel') }}
                  </button>
                  <button
                    class="cdp__btn cdp__btn--primary cdp__uf-submit"
                    :disabled="
                      createUserLoading ||
                      !newUser.email.trim() ||
                      !newUser.password.trim()
                    "
                    @click="saveNewUser"
                  >
                    <span v-if="createUserLoading" class="cdp__spinner" />
                    <i v-else class="pi pi-user-plus" />
                    {{
                      createUserLoading
                        ? t('commerceDetail.creating')
                        : t('commerceDetail.createAccount')
                    }}
                  </button>
                </div>
              </div>

              <template v-if="!showCreateUser">
                <!-- Skeleton -->
                <div v-if="isLoadingUsers" class="cdp__usr-grid">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="cdp__usr-card cdp__usr-card--sk"
                  >
                    <div
                      class="cdp__sk cdp__sk--circle"
                      style="
                        width: 2.75rem;
                        height: 2.75rem;
                        border-radius: 50%;
                      "
                    />
                    <div style="flex: 1">
                      <div
                        class="cdp__sk"
                        style="
                          height: 0.875rem;
                          width: 60%;
                          margin-bottom: 0.5rem;
                        "
                      />
                      <div
                        class="cdp__sk"
                        style="height: 0.75rem; width: 40%"
                      />
                    </div>
                  </div>
                </div>

                <!-- Vide -->
                <div v-else-if="staffUsers.length === 0" class="cdp__empty">
                  <i class="pi pi-users cdp__empty-ico" />
                  <p>{{ t('commerceDetail.emptyManagers') }}</p>
                </div>

                <!-- Liste -->
                <div v-else class="cdp__usr-grid">
                  <template v-for="u in staffUsers" :key="u.id">
                    <!-- Carte utilisateur (cachée si une autre carte est en édition) -->
                    <div
                      v-if="!editUserId || editUserId === u.id"
                      class="cdp__usr-card"
                      :class="{ 'cdp__usr-card--editing': editUserId === u.id }"
                      @click="openManagerDetail(u)"
                    >
                      <img
                        v-if="u.photoUrl"
                        :src="u.photoUrl"
                        class="cdp__usr-avatar cdp__usr-avatar--img"
                        alt=""
                      />
                      <div
                        v-else
                        class="cdp__usr-avatar"
                        :style="`background:${userColor(u.id)}`"
                      >
                        {{ userInitials(u) }}
                      </div>
                      <div class="cdp__usr-info">
                        <p class="cdp__usr-name">
                          {{
                            [u.firstname, u.lastname]
                              .filter(Boolean)
                              .join(' ') || u.email
                          }}
                        </p>
                        <p class="cdp__usr-email">{{ u.email }}</p>
                        <div class="cdp__usr-meta">
                          <span class="cdp__usr-role-badge">{{
                            userPrimaryRole(u)
                          }}</span>
                          <span
                            class="cdp__usr-status"
                            :class="
                              userIsActive(u)
                                ? 'cdp__usr-status--on'
                                : 'cdp__usr-status--off'
                            "
                          >
                            {{
                              userIsActive(u)
                                ? t('commerceDetail.activeStatus')
                                : t('commerceDetail.inactiveStatus')
                            }}
                          </span>
                        </div>
                      </div>
                      <div class="cdp__usr-actions" @click.stop>
                        <button
                          class="cdp__med-config-btn"
                          :class="{
                            'cdp__med-config-btn--active': editUserId === u.id,
                          }"
                          @click="openEditUser(u)"
                        >
                          {{ t('commerceDetail.btnEdit') }}
                        </button>
                        <button
                          class="cdp__med-del-btn"
                          :disabled="deletingUserId === u.id"
                          @click="askDeleteUser(u)"
                        >
                          <i
                            v-if="deletingUserId === u.id"
                            class="pi pi-spin pi-spinner"
                          />
                          <i v-else class="pi pi-trash" />
                        </button>
                      </div>
                    </div>

                    <!-- Confirm suppression inline -->
                    <div
                      v-if="confirmDeleteUserId === u.id"
                      class="cdp__inline-confirm"
                    >
                      <i
                        class="pi pi-exclamation-triangle cdp__inline-confirm-ico"
                      />
                      <span
                        >{{ t('commerceDetail.confirmDeleteUserPre') }}
                        <strong>{{ u.email }}</strong
                        >{{ t('commerceDetail.confirmDeleteUserPost') }}</span
                      >
                      <button
                        class="cdp__btn cdp__btn--ghost cdp__btn--sm"
                        @click="cancelDeleteUser"
                      >
                        {{ t('commerceDetail.no2') }}
                      </button>
                      <button
                        class="cdp__btn cdp__btn--danger cdp__btn--sm"
                        :disabled="deletingUserId === u.id"
                        @click="doDeleteUser(u)"
                      >
                        <span
                          v-if="deletingUserId === u.id"
                          class="cdp__spinner"
                        />
                        {{ t('commerceDetail.yesDelete2') }}
                      </button>
                    </div>

                    <!-- Édition inline redesignée -->
                    <div
                      v-if="editUserId === u.id && editUser"
                      class="cdp__uf cdp__uf--edit"
                    >
                      <!-- Header compact -->
                      <div class="cdp__uf-header">
                        <img
                          v-if="u.photoUrl"
                          :src="u.photoUrl"
                          class="cdp__uf-avatar-preview cdp__uf-avatar-preview--img"
                          alt=""
                        />
                        <div
                          v-else
                          class="cdp__uf-avatar-preview"
                          :style="`background:${userColor(u.id)}`"
                        >
                          {{ userInitials(u) }}
                        </div>
                        <div class="cdp__uf-header-text">
                          <p class="cdp__uf-header-title">
                            {{ t('commerceDetail.editAccount') }}
                          </p>
                          <p class="cdp__uf-header-sub">{{ u.email }}</p>
                        </div>
                        <button
                          class="cdp__uf-close"
                          @click="closeEditUser"
                          :title="t('commerceDetail.btnCancel')"
                        >
                          <i class="pi pi-times" />
                        </button>
                      </div>

                      <div class="cdp__uf-body">
                        <!-- Photo -->
                        <div class="cdp__uf-section">
                          <p class="cdp__uf-section-label">
                            <i class="pi pi-image" />
                            {{ t('commerceDetail.photoLabel') }}
                          </p>
                          <div class="cdp__uf-photo-row">
                            <img
                              v-if="u.photoUrl"
                              :src="u.photoUrl"
                              class="cdp__uf-photo-preview"
                              alt=""
                            />
                            <div
                              v-else
                              class="cdp__uf-photo-preview cdp__uf-photo-preview--empty"
                              :style="`background:${userColor(u.id)}`"
                            >
                              {{ userInitials(u) }}
                            </div>
                            <input
                              :ref="setEditUserPhotoInput"
                              type="file"
                              accept="image/*"
                              class="cdp__uf-photo-input"
                              @change="onEditUserPhotoSelected($event, u)"
                            />
                            <button
                              type="button"
                              class="cdp__btn cdp__btn--ghost cdp__btn--sm"
                              :disabled="isUploadingUserPhoto"
                              @click="triggerEditUserPhotoPicker"
                            >
                              <span
                                v-if="isUploadingUserPhoto"
                                class="cdp__spinner"
                              />
                              <i v-else class="pi pi-upload" />
                              {{
                                isUploadingUserPhoto
                                  ? t('commerceDetail.uploadingPhoto')
                                  : t('commerceDetail.uploadPhoto')
                              }}
                            </button>
                          </div>
                          <p v-if="editUserPhotoErr" class="cdp__uf-error">
                            <i class="pi pi-exclamation-circle" />
                            {{ editUserPhotoErr }}
                          </p>
                        </div>

                        <div class="cdp__uf-sep" />

                        <!-- Rôle -->
                        <div class="cdp__uf-section">
                          <p class="cdp__uf-section-label">
                            <i class="pi pi-shield" />
                            {{ t('commerceDetail.roleLabel') }}
                          </p>

                          <div
                            v-if="isLoadingRoles"
                            class="cdp__uf-roles-loading"
                          >
                            <span class="cdp__spinner" />
                            {{ t('commerceDetail.loading') }}
                          </div>
                          <div
                            v-else-if="assignableRoles.length === 0"
                            class="cdp__uf-roles-empty"
                          >
                            <i class="pi pi-info-circle" />
                            <span>{{
                              t('commerceDetail.noRolesDefined')
                            }}</span>
                          </div>
                          <div v-else class="cdp__uf-role-list">
                            <p
                              v-if="!editUser.roleIds.length"
                              class="cdp__uf-role-desc"
                            >
                              {{ t('commerceDetail.noRole') }}
                            </p>
                            <label
                              v-for="r in assignableRoles"
                              :key="r.id"
                              class="cdp__uf-role-opt"
                              :class="{
                                'cdp__uf-role-opt--sel':
                                  editUser.roleIds.includes(r.id),
                              }"
                            >
                              <input
                                type="checkbox"
                                :checked="editUser.roleIds.includes(r.id)"
                                style="display: none"
                                @change="
                                  editUser.roleIds = toggleRoleId(
                                    editUser.roleIds,
                                    r.id,
                                  )
                                "
                              />
                              <span
                                class="cdp__uf-role-checkbox"
                                :class="{
                                  'cdp__uf-role-checkbox--on':
                                    editUser.roleIds.includes(r.id),
                                }"
                              >
                                <i
                                  v-if="editUser.roleIds.includes(r.id)"
                                  class="pi pi-check"
                                />
                              </span>
                              <span class="cdp__uf-role-name">{{
                                r.label
                              }}</span>
                              <span class="cdp__uf-role-code">{{
                                r.code
                              }}</span>
                            </label>
                          </div>
                        </div>

                        <div class="cdp__uf-sep" />

                        <!-- Mot de passe -->
                        <div class="cdp__uf-section">
                          <p class="cdp__uf-section-label">
                            <i class="pi pi-lock" />
                            {{ t('commerceDetail.security') }}
                          </p>
                          <div class="cdp__uf-field">
                            <label class="cdp__uf-label">{{
                              t('commerceDetail.newPassword')
                            }}</label>
                            <div class="cdp__uf-pwd-wrap">
                              <input
                                v-model="editUser.password"
                                :type="showEditPwd ? 'text' : 'password'"
                                class="cdp__uf-input cdp__uf-input--pwd"
                                :placeholder="t('commerceDetail.keepEmpty')"
                                autocomplete="new-password"
                              />
                              <button
                                type="button"
                                class="cdp__uf-pwd-eye"
                                @click="showEditPwd = !showEditPwd"
                                :title="
                                  showEditPwd
                                    ? t('commerceDetail.hidePwd')
                                    : t('commerceDetail.showPwd')
                                "
                              >
                                <i
                                  class="pi"
                                  :class="
                                    showEditPwd ? 'pi-eye-slash' : 'pi-eye'
                                  "
                                />
                              </button>
                              <button
                                type="button"
                                class="cdp__uf-pwd-gen"
                                @click="genEditUserPwd"
                                :title="t('commerceDetail.generatePwd')"
                              >
                                <i class="pi pi-refresh" />
                                <span>{{
                                  t('commerceDetail.generatePwdLabel')
                                }}</span>
                              </button>
                            </div>
                            <span
                              v-if="editUser.password && showEditPwd"
                              class="cdp__uf-pwd-preview"
                              >{{ editUser.password }}</span
                            >
                          </div>
                        </div>
                      </div>

                      <div v-if="editUserErr" class="cdp__uf-error">
                        <i class="pi pi-exclamation-circle" /> {{ editUserErr }}
                      </div>

                      <div class="cdp__uf-footer">
                        <button
                          class="cdp__btn cdp__btn--ghost"
                          @click="closeEditUser"
                        >
                          {{ t('commerceDetail.btnCancel') }}
                        </button>
                        <button
                          class="cdp__btn cdp__btn--primary cdp__uf-submit"
                          :disabled="editUserLoading"
                          @click="saveEditUser(u)"
                        >
                          <span v-if="editUserLoading" class="cdp__spinner" />
                          <i v-else class="pi pi-check" />
                          {{
                            editUserLoading
                              ? t('commerceDetail.btnSaving')
                              : t('commerceDetail.btnSave')
                          }}
                        </button>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Pagination -->
                <div v-if="staffTotalPages > 1" class="cdp__usr-pager">
                  <button
                    class="cdp__med-pager-btn"
                    :disabled="usersPage === 1"
                    @click="usersPage--"
                  >
                    <i class="pi pi-chevron-left" />
                  </button>
                  <span class="cdp__med-pager-info"
                    >{{ usersPage }} / {{ staffTotalPages }}</span
                  >
                  <button
                    class="cdp__med-pager-btn"
                    :disabled="usersPage === staffTotalPages"
                    @click="usersPage++"
                  >
                    <i class="pi pi-chevron-right" />
                  </button>
                </div>
              </template>
            </template>

            <!-- ══ Panneau détail inline gestionnaire ═════════════════════════ -->
            <div v-else class="cdp__usr-detail">
              <button
                class="cdp__btn cdp__btn--ghost cdp__usr-detail-back"
                @click="closeManagerDetail"
              >
                <i class="pi pi-arrow-left" />
                {{ t('commerceDetail.backToList') }}
              </button>

              <template v-for="u in staffUsers" :key="u.id">
                <div
                  v-if="u.id === selectedManagerId"
                  class="cdp__usr-detail-card"
                >
                  <div
                    v-if="editUserId !== u.id"
                    class="cdp__usr-detail-header"
                  >
                    <img
                      v-if="u.photoUrl"
                      :src="u.photoUrl"
                      class="cdp__usr-avatar cdp__usr-avatar--img cdp__usr-avatar--lg"
                      alt=""
                    />
                    <div
                      v-else
                      class="cdp__usr-avatar cdp__usr-avatar--lg"
                      :style="`background:${userColor(u.id)}`"
                    >
                      {{ userInitials(u) }}
                    </div>
                    <div class="cdp__usr-detail-header-text">
                      <p class="cdp__usr-detail-name">
                        {{
                          [u.firstname, u.lastname].filter(Boolean).join(' ') ||
                          u.email
                        }}
                      </p>
                      <p class="cdp__usr-detail-email">{{ u.email }}</p>
                      <div class="cdp__usr-meta">
                        <span class="cdp__usr-role-badge">{{
                          userPrimaryRole(u)
                        }}</span>
                        <span
                          class="cdp__usr-status"
                          :class="
                            userIsActive(u)
                              ? 'cdp__usr-status--on'
                              : 'cdp__usr-status--off'
                          "
                        >
                          {{
                            userIsActive(u)
                              ? t('commerceDetail.activeStatus')
                              : t('commerceDetail.inactiveStatus')
                          }}
                        </span>
                      </div>
                    </div>
                    <button
                      class="cdp__btn cdp__btn--primary"
                      @click="openEditUser(u)"
                    >
                      <i class="pi pi-pencil" />
                      {{ t('commerceDetail.btnEdit') }}
                    </button>
                  </div>

                  <!-- Édition inline (rôle / mot de passe / photo) réutilisée -->
                  <div
                    v-if="editUserId === u.id && editUser"
                    class="cdp__uf cdp__uf--edit"
                  >
                    <div class="cdp__uf-body">
                      <!-- Photo -->
                      <div class="cdp__uf-section">
                        <p class="cdp__uf-section-label">
                          <i class="pi pi-image" />
                          {{ t('commerceDetail.photoLabel') }}
                        </p>
                        <div class="cdp__uf-photo-row">
                          <img
                            v-if="u.photoUrl"
                            :src="u.photoUrl"
                            class="cdp__uf-photo-preview"
                            alt=""
                          />
                          <div
                            v-else
                            class="cdp__uf-photo-preview cdp__uf-photo-preview--empty"
                            :style="`background:${userColor(u.id)}`"
                          >
                            {{ userInitials(u) }}
                          </div>
                          <input
                            :ref="setEditUserPhotoInput"
                            type="file"
                            accept="image/*"
                            class="cdp__uf-photo-input"
                            @change="onEditUserPhotoSelected($event, u)"
                          />
                          <button
                            type="button"
                            class="cdp__btn cdp__btn--ghost cdp__btn--sm"
                            :disabled="isUploadingUserPhoto"
                            @click="triggerEditUserPhotoPicker"
                          >
                            <span
                              v-if="isUploadingUserPhoto"
                              class="cdp__spinner"
                            />
                            <i v-else class="pi pi-upload" />
                            {{
                              isUploadingUserPhoto
                                ? t('commerceDetail.uploadingPhoto')
                                : t('commerceDetail.uploadPhoto')
                            }}
                          </button>
                        </div>
                        <p v-if="editUserPhotoErr" class="cdp__uf-error">
                          <i class="pi pi-exclamation-circle" />
                          {{ editUserPhotoErr }}
                        </p>
                      </div>

                      <div class="cdp__uf-sep" />

                      <!-- Rôle -->
                      <div class="cdp__uf-section">
                        <p class="cdp__uf-section-label">
                          <i class="pi pi-shield" />
                          {{ t('commerceDetail.roleLabel') }}
                        </p>

                        <div
                          v-if="isLoadingRoles"
                          class="cdp__uf-roles-loading"
                        >
                          <span class="cdp__spinner" />
                          {{ t('commerceDetail.loading') }}
                        </div>
                        <div
                          v-else-if="assignableRoles.length === 0"
                          class="cdp__uf-roles-empty"
                        >
                          <i class="pi pi-info-circle" />
                          <span>{{ t('commerceDetail.noRolesDefined') }}</span>
                        </div>
                        <div v-else class="cdp__uf-role-list">
                          <p
                            v-if="!editUser.roleIds.length"
                            class="cdp__uf-role-desc"
                          >
                            {{ t('commerceDetail.noRole') }}
                          </p>
                          <label
                            v-for="r in assignableRoles"
                            :key="r.id"
                            class="cdp__uf-role-opt"
                            :class="{
                              'cdp__uf-role-opt--sel':
                                editUser.roleIds.includes(r.id),
                            }"
                          >
                            <input
                              type="checkbox"
                              :checked="editUser.roleIds.includes(r.id)"
                              style="display: none"
                              @change="
                                editUser.roleIds = toggleRoleId(
                                  editUser.roleIds,
                                  r.id,
                                )
                              "
                            />
                            <span
                              class="cdp__uf-role-checkbox"
                              :class="{
                                'cdp__uf-role-checkbox--on':
                                  editUser.roleIds.includes(r.id),
                              }"
                            >
                              <i
                                v-if="editUser.roleIds.includes(r.id)"
                                class="pi pi-check"
                              />
                            </span>
                            <span class="cdp__uf-role-name">{{ r.label }}</span>
                            <span class="cdp__uf-role-code">{{ r.code }}</span>
                          </label>
                        </div>
                      </div>

                      <div class="cdp__uf-sep" />

                      <!-- Mot de passe -->
                      <div class="cdp__uf-section">
                        <p class="cdp__uf-section-label">
                          <i class="pi pi-lock" />
                          {{ t('commerceDetail.security') }}
                        </p>
                        <div class="cdp__uf-field">
                          <label class="cdp__uf-label">{{
                            t('commerceDetail.newPassword')
                          }}</label>
                          <div class="cdp__uf-pwd-wrap">
                            <input
                              v-model="editUser.password"
                              :type="showEditPwd ? 'text' : 'password'"
                              class="cdp__uf-input cdp__uf-input--pwd"
                              :placeholder="t('commerceDetail.keepEmpty')"
                              autocomplete="new-password"
                            />
                            <button
                              type="button"
                              class="cdp__uf-pwd-eye"
                              @click="showEditPwd = !showEditPwd"
                              :title="
                                showEditPwd
                                  ? t('commerceDetail.hidePwd')
                                  : t('commerceDetail.showPwd')
                              "
                            >
                              <i
                                class="pi"
                                :class="showEditPwd ? 'pi-eye-slash' : 'pi-eye'"
                              />
                            </button>
                            <button
                              type="button"
                              class="cdp__uf-pwd-gen"
                              @click="genEditUserPwd"
                              :title="t('commerceDetail.generatePwd')"
                            >
                              <i class="pi pi-refresh" />
                              <span>{{
                                t('commerceDetail.generatePwdLabel')
                              }}</span>
                            </button>
                          </div>
                          <span
                            v-if="editUser.password && showEditPwd"
                            class="cdp__uf-pwd-preview"
                            >{{ editUser.password }}</span
                          >
                        </div>
                      </div>
                    </div>

                    <div v-if="editUserErr" class="cdp__uf-error">
                      <i class="pi pi-exclamation-circle" /> {{ editUserErr }}
                    </div>

                    <div class="cdp__uf-footer">
                      <button
                        class="cdp__btn cdp__btn--ghost"
                        @click="closeEditUser"
                      >
                        {{ t('commerceDetail.btnCancel') }}
                      </button>
                      <button
                        class="cdp__btn cdp__btn--primary cdp__uf-submit"
                        :disabled="editUserLoading"
                        @click="saveEditUser(u)"
                      >
                        <span v-if="editUserLoading" class="cdp__spinner" />
                        <i v-else class="pi pi-check" />
                        {{
                          editUserLoading
                            ? t('commerceDetail.btnSaving')
                            : t('commerceDetail.btnSave')
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>

          <!-- ══════════════════════════════════════════════════════════════════
           SOUS-TAB : UTILISATEURS CLICK & COLLECT
      ═══════════════════════════════════════════════════════════════════ -->
          <template v-else-if="activeUsrTab === 'cnc'">
            <div v-if="!selectedCncId" class="cdp__usr-toolbar">
              <div class="cdp__usr-search-wrap">
                <i class="pi pi-search cdp__usr-search-ico" />
                <input
                  v-model="cncSearch"
                  type="text"
                  :placeholder="t('commerceDetail.cncSearchPlaceholder')"
                  class="cdp__usr-search"
                />
              </div>
              <select v-model="cncSortBy" class="cdp__usr-sort-select">
                <option value="created">
                  {{ t('membresPage.filters.sortCreated') }}
                </option>
                <option value="updated">
                  {{ t('membresPage.filters.sortUpdated') }}
                </option>
                <option value="order_count">
                  {{ t('membresPage.filters.sortOrderCount') }}
                </option>
                <option value="latest_order_date">
                  {{ t('membresPage.filters.sortLatestOrder') }}
                </option>
              </select>
              <button
                class="cdp__usr-sort-btn"
                :title="t('membresPage.filters.toggleOrder')"
                @click="toggleCncSortOrder"
              >
                <i
                  :class="
                    cncSortOrder === 'DESC'
                      ? 'pi pi-sort-amount-down'
                      : 'pi pi-sort-amount-up'
                  "
                />
              </button>
              <span class="cdp__usr-count"
                >{{ cncTotal }}
                {{
                  t(
                    cncTotal > 1
                      ? 'commerceDetail.results'
                      : 'commerceDetail.result',
                  )
                }}</span
              >
            </div>

            <template v-if="!selectedCncId">
              <!-- Skeleton -->
              <div v-if="isLoadingCnc" class="cdp__usr-grid">
                <div
                  v-for="i in 6"
                  :key="i"
                  class="cdp__usr-card cdp__usr-card--sk"
                >
                  <div
                    class="cdp__sk cdp__sk--circle"
                    style="width: 2.75rem; height: 2.75rem; border-radius: 50%"
                  />
                  <div style="flex: 1">
                    <div
                      class="cdp__sk"
                      style="
                        height: 0.875rem;
                        width: 60%;
                        margin-bottom: 0.5rem;
                      "
                    />
                    <div class="cdp__sk" style="height: 0.75rem; width: 40%" />
                  </div>
                </div>
              </div>

              <!-- Vide -->
              <div v-else-if="cncUsers.length === 0" class="cdp__empty">
                <i class="pi pi-users cdp__empty-ico" />
                <p>{{ t('commerceDetail.emptyCnc') }}</p>
              </div>

              <!-- Liste -->
              <div v-else class="cdp__usr-grid">
                <div
                  v-for="u in cncUsers"
                  :key="u.id"
                  class="cdp__usr-card"
                  @click="openCncDetail(u)"
                >
                  <img
                    v-if="u.photoUrl"
                    :src="u.photoUrl"
                    class="cdp__usr-avatar cdp__usr-avatar--img"
                    alt=""
                  />
                  <div
                    v-else
                    class="cdp__usr-avatar"
                    :class="{ 'cdp__usr-avatar--anon': cncIsAnonymous(u) }"
                    :style="
                      cncIsAnonymous(u)
                        ? 'background:#64748b'
                        : `background:${userColor(u.id)}`
                    "
                  >
                    <i v-if="cncIsAnonymous(u)" class="pi pi-user" />
                    <span v-else>{{ cncInitials(u) }}</span>
                  </div>
                  <div class="cdp__usr-info">
                    <p class="cdp__usr-name">
                      {{
                        cncDisplayName(u) ?? t('membresPage.table.anonymous')
                      }}
                    </p>
                    <p v-if="u.email" class="cdp__usr-email">{{ u.email }}</p>
                    <p v-else-if="u.phone" class="cdp__usr-email">
                      {{ u.phone }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="cncTotalPages > 1" class="cdp__usr-pager">
                <button
                  class="cdp__med-pager-btn"
                  :disabled="cncPage === 1"
                  @click="cncPage--"
                >
                  <i class="pi pi-chevron-left" />
                </button>
                <span class="cdp__med-pager-info"
                  >{{ cncPage }} / {{ cncTotalPages }}</span
                >
                <button
                  class="cdp__med-pager-btn"
                  :disabled="cncPage === cncTotalPages"
                  @click="cncPage++"
                >
                  <i class="pi pi-chevron-right" />
                </button>
              </div>
            </template>

            <!-- ══ Panneau détail inline customer CNC ═══════════════════════ -->
            <div v-else-if="selectedCncUser" class="cdp__usr-detail">
              <button
                class="cdp__btn cdp__btn--ghost cdp__usr-detail-back"
                @click="closeCncDetail"
              >
                <i class="pi pi-arrow-left" />
                {{ t('commerceDetail.backToList') }}
              </button>

              <div class="cdp__usr-detail-card">
                <div class="cdp__usr-detail-header">
                  <img
                    v-if="selectedCncUser.photoUrl"
                    :src="selectedCncUser.photoUrl"
                    class="cdp__usr-avatar cdp__usr-avatar--img cdp__usr-avatar--lg"
                    alt=""
                  />
                  <div
                    v-else
                    class="cdp__usr-avatar cdp__usr-avatar--lg"
                    :class="{
                      'cdp__usr-avatar--anon': cncIsAnonymous(selectedCncUser),
                    }"
                    :style="
                      cncIsAnonymous(selectedCncUser)
                        ? 'background:#64748b'
                        : `background:${userColor(selectedCncUser.id)}`
                    "
                  >
                    <i
                      v-if="cncIsAnonymous(selectedCncUser)"
                      class="pi pi-user"
                      style="font-size: 1.5rem"
                    />
                    <span v-else>{{ cncInitials(selectedCncUser) }}</span>
                  </div>
                  <div class="cdp__usr-detail-header-text">
                    <p class="cdp__usr-detail-name">
                      {{
                        cncDisplayName(selectedCncUser) ??
                        t('membresPage.table.anonymous')
                      }}
                    </p>
                    <p
                      v-if="selectedCncUser.email"
                      class="cdp__usr-detail-email"
                    >
                      {{ selectedCncUser.email }}
                    </p>
                    <p
                      v-else-if="selectedCncUser.phone"
                      class="cdp__usr-detail-email"
                    >
                      {{ selectedCncUser.phone }}
                    </p>
                    <div class="cdp__usr-meta">
                      <span
                        class="cdp__usr-status"
                        :class="
                          !selectedCncUser.disabledAt &&
                          !selectedCncUser.lockedAt
                            ? 'cdp__usr-status--on'
                            : 'cdp__usr-status--off'
                        "
                      >
                        {{
                          !selectedCncUser.disabledAt &&
                          !selectedCncUser.lockedAt
                            ? t('commerceDetail.activeStatus')
                            : t('commerceDetail.inactiveStatus')
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="cdp__usr-detail-info-grid">
                  <div class="cdp__usr-detail-info-item">
                    <span class="cdp__usr-detail-info-label">{{
                      t('membresPage.table.createdAt')
                    }}</span>
                    <span class="cdp__usr-detail-info-value">{{
                      getDateFormat(selectedCncUser.createdAt)
                    }}</span>
                  </div>
                  <div class="cdp__usr-detail-info-item">
                    <span class="cdp__usr-detail-info-label">{{
                      t('membresPage.table.language')
                    }}</span>
                    <span class="cdp__usr-detail-info-value">{{
                      (selectedCncUser.language ?? '—').toUpperCase?.() ?? '—'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Bloc Rôles masqué temporairement — sera réactivé plus tard -->
          <template v-if="false">
            <!-- Toolbar -->
            <div class="cdp__usr-toolbar">
              <span class="cdp__usr-count"
                >{{ roles.length }}
                {{ t('commerceDetail.newRoleTitle').toLowerCase()
                }}{{ roles.length > 1 ? 's' : '' }}</span
              >
              <button
                class="cdp__btn cdp__btn--primary cdp__btn--shadow cdp__usr-add-btn"
                @click="openCreateRole"
              >
                <i class="pi pi-plus" /> {{ t('commerceDetail.newRoleTitle') }}
              </button>
            </div>

            <!-- Formulaire création de rôle — design soigné -->
            <div v-if="showCreateRole" class="cdp__uf">
              <!-- Header -->
              <div class="cdp__uf-header cdp__uf-header--role">
                <div class="cdp__uf-role-icon">
                  <i class="pi pi-shield" />
                </div>
                <div class="cdp__uf-header-text">
                  <p class="cdp__uf-header-title">
                    {{ t('commerceDetail.newRoleTitle') }}
                  </p>
                  <p class="cdp__uf-header-sub">
                    {{ t('commerceDetail.newRoleSub') }}
                  </p>
                </div>
                <button
                  class="cdp__uf-close"
                  @click="closeCreateRole"
                  :title="t('commerceDetail.btnCancel')"
                >
                  <i class="pi pi-times" />
                </button>
              </div>

              <div class="cdp__uf-body">
                <!-- Identité du rôle -->
                <div class="cdp__uf-section">
                  <p class="cdp__uf-section-label">
                    <i class="pi pi-tag" />
                    {{ t('commerceDetail.roleIdentity') }}
                  </p>
                  <div class="cdp__uf-grid2">
                    <div class="cdp__uf-field">
                      <label class="cdp__uf-label"
                        >{{ t('commerceDetail.roleCode') }}
                        <span class="cdp__uf-required">*</span></label
                      >
                      <input
                        v-model="newRole.code"
                        type="text"
                        class="cdp__uf-input cdp__uf-input--mono"
                        placeholder="store_manager"
                      />
                      <span class="cdp__uf-hint">{{
                        t('commerceDetail.roleCodeHint')
                      }}</span>
                    </div>
                    <div class="cdp__uf-field">
                      <label class="cdp__uf-label"
                        >{{ t('commerceDetail.roleLibelle') }}
                        <span class="cdp__uf-required">*</span></label
                      >
                      <input
                        v-model="newRole.label"
                        type="text"
                        class="cdp__uf-input"
                        :placeholder="
                          t('commerceDetail.roleLibellePlaceholder')
                        "
                      />
                      <span class="cdp__uf-hint">{{
                        t('commerceDetail.roleLibelleHint')
                      }}</span>
                    </div>
                  </div>

                  <!-- Statut -->
                  <div class="cdp__uf-status-row">
                    <div class="cdp__uf-status-text">
                      <span class="cdp__uf-label">{{
                        t('commerceDetail.roleStatus')
                      }}</span>
                      <span class="cdp__uf-hint">{{
                        newRole.enabled
                          ? t('commerceDetail.roleActiveDesc')
                          : t('commerceDetail.roleDisabledDesc')
                      }}</span>
                    </div>
                    <button
                      class="cdp__uf-toggle"
                      :class="{ 'cdp__uf-toggle--on': newRole.enabled }"
                      @click="newRole.enabled = !newRole.enabled"
                      :aria-label="
                        newRole.enabled
                          ? t('commerceDetail.roleToggleActive')
                          : t('commerceDetail.roleToggleInactive')
                      "
                    >
                      <span class="cdp__uf-toggle-knob" />
                      <span class="cdp__uf-toggle-label">{{
                        newRole.enabled
                          ? t('commerceDetail.activeStatus')
                          : t('commerceDetail.inactiveStatus')
                      }}</span>
                    </button>
                  </div>
                </div>

                <!-- Permissions -->
                <template v-if="allPerms.length">
                  <div class="cdp__uf-sep" />
                  <div class="cdp__uf-section">
                    <div class="cdp__uf-perm-header">
                      <p class="cdp__uf-section-label">
                        <i class="pi pi-key" />
                        {{ t('commerceDetail.permissionsLabel') }}
                      </p>
                      <span class="cdp__uf-perm-count">
                        {{ newRole.permissions.length }} / {{ allPerms.length }}
                        {{
                          newRole.permissions.length > 1
                            ? t('commerceDetail.permSelectedPlural')
                            : t('commerceDetail.permSelected')
                        }}
                      </span>
                    </div>
                    <div class="cdp__uf-perm-grid">
                      <label
                        v-for="perm in allPerms"
                        :key="perm"
                        class="cdp__uf-perm-chip"
                        :class="{
                          'cdp__uf-perm-chip--on':
                            newRole.permissions.includes(perm),
                        }"
                      >
                        <input
                          type="checkbox"
                          :checked="newRole.permissions.includes(perm)"
                          @change="toggleNewRolePerm(perm)"
                          style="display: none"
                        />
                        <svg
                          class="cdp__uf-perm-check"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            v-if="newRole.permissions.includes(perm)"
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <circle
                            v-else
                            cx="6"
                            cy="6"
                            r="4.5"
                            stroke="currentColor"
                            stroke-width="1.25"
                            opacity=".3"
                          />
                        </svg>
                        <span>{{ perm }}</span>
                      </label>
                    </div>
                  </div>
                </template>
              </div>

              <div v-if="createRoleErr" class="cdp__uf-error">
                <i class="pi pi-exclamation-circle" /> {{ createRoleErr }}
              </div>

              <div class="cdp__uf-footer">
                <button
                  class="cdp__btn cdp__btn--ghost"
                  @click="closeCreateRole"
                >
                  {{ t('commerceDetail.btnCancel') }}
                </button>
                <button
                  class="cdp__btn cdp__btn--primary cdp__uf-submit"
                  :disabled="
                    createRoleLoading ||
                    !newRole.code.trim() ||
                    !newRole.label.trim()
                  "
                  @click="saveNewRole"
                >
                  <span v-if="createRoleLoading" class="cdp__spinner" />
                  <i v-else class="pi pi-shield" />
                  {{
                    createRoleLoading
                      ? t('commerceDetail.creatingRole')
                      : t('commerceDetail.createRole')
                  }}
                </button>
              </div>
            </div>

            <!-- Skeleton -->
            <div v-if="isLoadingRoles" class="cdp__usr-roles-list">
              <div
                v-for="i in 3"
                :key="i"
                class="cdp__usr-role-row cdp__usr-role-row--sk"
              >
                <div
                  class="cdp__sk"
                  style="height: 1rem; width: 40%; margin-bottom: 0.5rem"
                />
                <div class="cdp__sk" style="height: 0.75rem; width: 25%" />
              </div>
            </div>

            <!-- Vide -->
            <div v-else-if="roles.length === 0" class="cdp__empty">
              <i class="pi pi-shield cdp__empty-ico" />
              <p>{{ t('commerceDetail.emptyRoles') }}</p>
            </div>

            <!-- Liste des rôles -->
            <div v-else class="cdp__usr-roles-list">
              <template v-for="r in roles" :key="r.id">
                <!-- Ligne rôle -->
                <div
                  class="cdp__usr-role-row"
                  :class="{ 'cdp__usr-role-row--editing': editRoleId === r.id }"
                >
                  <div class="cdp__usr-role-info">
                    <span class="cdp__usr-role-code">{{ r.code }}</span>
                    <span class="cdp__usr-role-label">{{
                      r.label ?? r.designation
                    }}</span>
                  </div>
                  <div class="cdp__usr-role-meta">
                    <span
                      class="cdp__usr-status"
                      :class="
                        r.enabled || r.status === 'active'
                          ? 'cdp__usr-status--on'
                          : 'cdp__usr-status--off'
                      "
                    >
                      {{
                        r.enabled || r.status === 'active'
                          ? t('commerceDetail.activeStatus')
                          : t('commerceDetail.inactiveStatus')
                      }}
                    </span>
                    <span
                      v-if="r.permissions?.length"
                      class="cdp__usr-perm-count"
                      >{{ r.permissions.length }} permission{{
                        r.permissions.length > 1 ? 's' : ''
                      }}</span
                    >
                  </div>
                  <div class="cdp__usr-actions">
                    <button
                      class="cdp__med-config-btn"
                      :class="{
                        'cdp__med-config-btn--active': editRoleId === r.id,
                      }"
                      @click="openEditRole(r)"
                    >
                      {{ t('commerceDetail.btnEdit') }}
                    </button>
                    <button
                      class="cdp__med-del-btn"
                      :disabled="deletingRoleId === r.id"
                      @click="askDeleteRole(r)"
                    >
                      <i
                        v-if="deletingRoleId === r.id"
                        class="pi pi-spin pi-spinner"
                      />
                      <i v-else class="pi pi-trash" />
                    </button>
                  </div>
                </div>

                <!-- Confirm suppression inline -->
                <div
                  v-if="confirmDeleteRoleId === r.id"
                  class="cdp__inline-confirm"
                >
                  <i
                    class="pi pi-exclamation-triangle cdp__inline-confirm-ico"
                  />
                  <span
                    >{{ t('commerceDetail.btnDeleteRole') }}
                    <strong>{{ r.label ?? r.code }}</strong> ?</span
                  >
                  <button
                    class="cdp__btn cdp__btn--ghost cdp__btn--sm"
                    @click="cancelDeleteRole"
                  >
                    {{ t('commerceDetail.no2') }}
                  </button>
                  <button
                    class="cdp__btn cdp__btn--danger cdp__btn--sm"
                    :disabled="deletingRoleId === r.id"
                    @click="doDeleteRole(r)"
                  >
                    <span v-if="deletingRoleId === r.id" class="cdp__spinner" />
                    {{ t('commerceDetail.yesDelete2') }}
                  </button>
                </div>

                <!-- Édition inline rôle — design soigné -->
                <div
                  v-if="editRoleId === r.id && editRole"
                  class="cdp__uf cdp__uf--edit"
                >
                  <div class="cdp__uf-header cdp__uf-header--role">
                    <div class="cdp__uf-role-icon">
                      <i class="pi pi-shield" />
                    </div>
                    <div class="cdp__uf-header-text">
                      <p class="cdp__uf-header-title">
                        {{ t('commerceDetail.modifyRoleTitle') }}
                      </p>
                      <p class="cdp__uf-header-sub cdp__uf-header-sub--mono">
                        {{ r.code }}
                      </p>
                    </div>
                    <button
                      class="cdp__uf-close"
                      @click="closeEditRole"
                      :title="t('commerceDetail.btnCancel')"
                    >
                      <i class="pi pi-times" />
                    </button>
                  </div>

                  <div class="cdp__uf-body">
                    <div class="cdp__uf-section">
                      <p class="cdp__uf-section-label">
                        <i class="pi pi-tag" />
                        {{ t('commerceDetail.roleIdentity') }}
                      </p>
                      <div class="cdp__uf-field">
                        <label class="cdp__uf-label">{{
                          t('commerceDetail.roleLibelle')
                        }}</label>
                        <input
                          v-model="editRole.label"
                          type="text"
                          class="cdp__uf-input"
                        />
                      </div>
                      <div
                        class="cdp__uf-status-row"
                        style="margin-top: 0.75rem"
                      >
                        <div class="cdp__uf-status-text">
                          <span class="cdp__uf-label">{{
                            t('commerceDetail.roleStatus')
                          }}</span>
                          <span class="cdp__uf-hint">{{
                            editRole.enabled
                              ? t('commerceDetail.roleActiveDesc')
                              : t('commerceDetail.roleDisabledDesc')
                          }}</span>
                        </div>
                        <button
                          class="cdp__uf-toggle"
                          :class="{ 'cdp__uf-toggle--on': editRole.enabled }"
                          @click="editRole.enabled = !editRole.enabled"
                        >
                          <span class="cdp__uf-toggle-knob" />
                          <span class="cdp__uf-toggle-label">{{
                            editRole.enabled
                              ? t('commerceDetail.activeStatus')
                              : t('commerceDetail.inactiveStatus')
                          }}</span>
                        </button>
                      </div>
                    </div>

                    <template v-if="allPerms.length">
                      <div class="cdp__uf-sep" />
                      <div class="cdp__uf-section">
                        <div class="cdp__uf-perm-header">
                          <p class="cdp__uf-section-label">
                            <i class="pi pi-key" />
                            {{ t('commerceDetail.permissionsLabel') }}
                          </p>
                          <span class="cdp__uf-perm-count">
                            {{ editRole.permissions.length }} /
                            {{ allPerms.length }}
                            {{
                              editRole.permissions.length > 1
                                ? t('commerceDetail.permSelectedPlural')
                                : t('commerceDetail.permSelected')
                            }}
                          </span>
                        </div>
                        <div class="cdp__uf-perm-grid">
                          <label
                            v-for="perm in allPerms"
                            :key="perm"
                            class="cdp__uf-perm-chip"
                            :class="{
                              'cdp__uf-perm-chip--on':
                                editRole.permissions.includes(perm),
                            }"
                          >
                            <input
                              type="checkbox"
                              :checked="editRole.permissions.includes(perm)"
                              @change="toggleEditRolePerm(perm)"
                              style="display: none"
                            />
                            <svg
                              class="cdp__uf-perm-check"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                v-if="editRole.permissions.includes(perm)"
                                d="M2 6l3 3 5-5"
                                stroke="currentColor"
                                stroke-width="1.75"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                              <circle
                                v-else
                                cx="6"
                                cy="6"
                                r="4.5"
                                stroke="currentColor"
                                stroke-width="1.25"
                                opacity=".3"
                              />
                            </svg>
                            <span>{{ perm }}</span>
                          </label>
                        </div>
                      </div>
                    </template>
                  </div>

                  <div v-if="editRoleErr" class="cdp__uf-error">
                    <i class="pi pi-exclamation-circle" /> {{ editRoleErr }}
                  </div>

                  <div class="cdp__uf-footer">
                    <button
                      class="cdp__btn cdp__btn--ghost"
                      @click="closeEditRole"
                    >
                      {{ t('commerceDetail.btnCancel') }}
                    </button>
                    <button
                      class="cdp__btn cdp__btn--primary cdp__uf-submit"
                      :disabled="editRoleLoading"
                      @click="saveEditRole(r)"
                    >
                      <span v-if="editRoleLoading" class="cdp__spinner" />
                      <i v-else class="pi pi-check" />
                      {{
                        editRoleLoading
                          ? t('commerceDetail.btnSaving')
                          : t('commerceDetail.btnSave')
                      }}
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : CONTACT
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'contact'" class="cdp__contact">
          <CommerceContactsTab :store-id="id" />
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : PAGES LÉGALES
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'legal'" class="cdp__legal">
          <CommerceLegalTab :store-id="id" />
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : PAIEMENT
    ═══════════════════════════════════════════════════════════════════════ -->
        <div v-else-if="activeTab === 'paiement'" class="cdp__paiement">
          <CommercePaymentsTab :store-id="id" />
        </div>

        <!-- ══════════════════════════════════════════════════════════════════════
         TAB : MENUS & ÉVÉNEMENTS
    ═══════════════════════════════════════════════════════════════════════ -->
        <div
          v-else-if="activeTab === 'menusEvenements'"
          class="cdp__menus-evenements"
        >
          <CommerceCampaignsTab :store-id="id" />
        </div>

        <!-- ══ Placeholder autres onglets ═════════════════════════════════════ -->
        <div v-else class="cdp__empty">
          <i class="pi pi-box cdp__empty-ico" />
          <p>
            {{ PRIMARY_TABS.find((t) => t.key === activeTab)?.label }} —
            {{ t('commerceDetail.comingSoon') }}
          </p>
          <button
            class="cdp__btn cdp__btn--ghost"
            @click="activeTab = 'catalogue'"
          >
            <i class="pi pi-arrow-left" />
            {{ t('commerceDetail.btnBackCatalogue') }}
          </button>
        </div>
      </template>

      <!-- Popin confirmation "Retirer recommandation" — hors chaîne v-if -->
      <Teleport to="body">
        <Transition name="cdp-fade">
          <div
            v-if="recConfirmProd"
            class="cdp__confirm-overlay"
            @click.self="closeRecConfirm()"
          >
            <div class="cdp__confirm-modal">
              <div class="cdp__confirm-ico">
                <i class="pi pi-thumbs-down" />
              </div>
              <p class="cdp__confirm-title">
                {{ t('commerceDetail.removeRecTitle') }}
              </p>
              <p class="cdp__confirm-desc">
                <strong>{{ recConfirmProd.designation }}</strong>
                {{ t('commerceDetail.removeRecDesc') }}
              </p>
              <div class="cdp__confirm-actions">
                <button
                  class="cdp__btn cdp__btn--ghost"
                  @click="closeRecConfirm()"
                >
                  {{ t('commerceDetail.btnCancel') }}
                </button>
                <button
                  class="cdp__btn cdp__btn--danger"
                  :disabled="recProcessing.has(recConfirmProd.id)"
                  @click="confirmUnrecommend()"
                >
                  <i
                    v-if="recProcessing.has(recConfirmProd.id)"
                    class="pi pi-spinner pi-spin"
                  />
                  <span v-else>{{ t('commerceDetail.removeRecBtn') }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
    <!-- /cdp -->
  </div>
  <!-- /root -->
</template>

<style scoped>
/* ── Base ─────────────────────────────────────────────────────────────────── */
.cdp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .cdp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cdp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* ── Breadcrumb ───────────────────────────────────────────────────────────── */
.cdp__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cdp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  transition: color 0.15s;
}
.cdp__bc-link:hover {
  color: var(--nxt-primary);
}
.cdp__bc-sep {
  font-size: 0.625rem;
}
.cdp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
.cdp__skeletons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: cdp-sk 1.4s infinite;
}
.cdp__sk--hero {
  height: 5rem;
}
.cdp__sk--banner {
  height: 5rem;
}
.cdp__sk--tabs {
  height: 3rem;
}
.cdp__sk--body {
  height: 22rem;
}
@keyframes cdp-sk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Erreur ───────────────────────────────────────────────────────────────── */
.cdp__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.cdp__error i {
  font-size: 2rem;
  color: var(--nxt-error);
}
.cdp__error p {
  margin: 0;
  color: var(--nxt-on-surface-muted);
}

/* ══ Hero ════════════════════════════════════════════════════════════════════ */
.cdp__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.cdp__hero-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex: 1;
  min-width: 0;
}
.cdp__logo-wrap {
  width: 3.5rem;
  height: 3.5rem;
  flex-shrink: 0;
}
.cdp__logo {
  width: 100%;
  height: 100%;
  border-radius: 0.75rem;
  object-fit: contain;
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__logo-ph {
  width: 100%;
  height: 100%;
  border-radius: 0.75rem;
  background: var(--nxt-surface-mid);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.375rem;
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__hero-info {
  min-width: 0;
}
.cdp__hero-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.cdp__title {
  margin: 0;
  font-size: 1.625rem;
  font-weight: 900;
  letter-spacing: -0.035em;
  color: var(--nxt-on-surface);
}
.cdp__hero-sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.cdp__hero-org {
  cursor: pointer;
  color: var(--nxt-primary);
  width: fit-content;
  transition: opacity 0.15s;
}
.cdp__hero-org:hover {
  text-decoration: underline;
  opacity: 0.8;
}
.cdp__hero-sep {
  opacity: 0.4;
}
.cdp__hero-pin {
  font-size: 0.875rem;
  color: var(--nxt-primary);
}

.cdp__open-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.65rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}
.cdp__open-badge--open {
  background: rgba(16, 181, 16, 0.12);
  color: #10b510;
}
.cdp__open-badge--closed {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
}
.cdp__open-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.cdp__open-dot--pulse {
  animation: cdp-pulse 2s infinite;
}
@keyframes cdp-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.cdp__hero-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* ══ Bannière ════════════════════════════════════════════════════════════════ */
.cdp__banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.cdp__banner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, transparent 55%);
  pointer-events: none;
}
.cdp__banner-body {
  display: flex;
  align-items: center;
  gap: 1.125rem;
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
}
.cdp__banner-ring-wrap {
  position: relative;
  width: 3.25rem;
  height: 3.25rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cdp__banner-svg {
  position: absolute;
  inset: 0;
  transform: rotate(-90deg);
}
.cdp__ring-bg {
  stroke: var(--nxt-outline-variant);
}
.cdp__ring-fill {
  stroke: var(--nxt-primary);
  stroke-linecap: round;
}
.cdp__banner-pct {
  font-size: 0.6875rem;
  font-weight: 800;
  color: var(--nxt-primary);
  z-index: 1;
}
.cdp__banner-text {
  flex: 1;
  min-width: 0;
}
.cdp__banner-title {
  margin: 0 0 0.2rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__banner-accent {
  color: var(--nxt-primary);
}
.cdp__banner-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* ══ Onglets principaux ══════════════════════════════════════════════════════ */
.cdp__tabs-bar {
  display: flex;
  border-bottom: 2px solid var(--nxt-outline-variant);
  overflow-x: auto;
  scrollbar-width: none;
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--nxt-card-bg, #fff);
}
.cdp__tabs-bar::-webkit-scrollbar {
  display: none;
}
.cdp__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.8rem 1rem;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.cdp__tab:hover {
  color: var(--nxt-on-surface);
  background: var(--nxt-surface-low);
}
.cdp__tab--active {
  color: var(--nxt-primary);
  border-bottom-color: var(--nxt-primary);
  font-weight: 700;
  background: none;
}
.cdp__tab .pi {
  font-size: 0.875rem;
}

/* ══ Catalogue ══════════════════════════════════════════════════════════════ */
.cdp__catalogue {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Sous-onglets */
.cdp__subtabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.cdp__subtab {
  padding: 0.45rem 1rem;
  border: none;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  background: transparent;
  color: var(--nxt-on-surface-muted);
  transition: all 0.15s;
  white-space: nowrap;
}
.cdp__subtab:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cdp__subtab--active {
  background: var(--nxt-surface-low);
  color: var(--nxt-primary);
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .cdp__subtab--active {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
  box-shadow: inset 0 0 0 1px rgba(255, 214, 10, 0.4);
}
[data-theme='dark'] .cdp__tab--active {
  color: #ffd60a;
  border-bottom-color: #ffd60a;
}
[data-theme='dark'] .cdp__banner {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__ring-bg {
  stroke: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__ring-fill {
  stroke: #ffd60a;
}
[data-theme='dark'] .cdp__banner-pct,
[data-theme='dark'] .cdp__banner-accent {
  color: #ffd60a;
}
[data-theme='dark'] .cdp__banner-title {
  color: #f1f5f9;
}
[data-theme='dark'] .cdp__banner-sub {
  color: rgba(255, 255, 255, 0.5);
}

/* KPIs */
.cdp__kpis {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}
@media (max-width: 900px) {
  .cdp__kpis {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}
.cdp__kpi {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.035);
  border-radius: 0.875rem;
  padding: 1rem 1.25rem;
}
[data-theme='dark'] .cdp__kpi {
  background: rgba(28, 30, 34, 0.72);
  border-color: rgba(255, 255, 255, 0.06);
}
.cdp__kpi--success {
}
.cdp__kpi-label {
  margin: 0 0 0.4rem;
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
}
.cdp__kpi-val-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}
.cdp__kpi-num {
  font-size: 1.625rem;
  font-weight: 900;
  color: var(--nxt-on-surface);
  letter-spacing: -0.025em;
}
.cdp__kpi-num--success {
  color: #10b510;
}
.cdp__kpi-num--primary {
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__kpi-num--primary {
  color: #ffd60a;
}
.cdp__kpi-unit {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

/* Barre catalogue */
.cdp__cat-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.cdp__cat-bar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}
.cdp__cat-bar-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cdp__search-wrap {
  position: relative;
  max-width: 22rem;
  flex: 1;
}
.cdp__search-ico {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}
.cdp__search-input {
  width: 100%;
  height: 2.75rem;
  padding: 0 1rem 0 2.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  font-size: 0.9375rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.cdp__search-input:focus {
  border-color: var(--nxt-primary);
}
.cdp__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  overflow: hidden;
  height: 2.75rem;
  flex-shrink: 0;
}
.cdp__view-btn {
  padding: 0 1rem;
  background: var(--nxt-card-bg);
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.15s;
  white-space: nowrap;
}
.cdp__view-btn:not(:last-child) {
  border-right: 1px solid var(--nxt-outline-variant);
}
.cdp__view-btn--active {
  background: var(--nxt-surface-low);
  color: var(--nxt-primary);
}

/* Sort + filtres */
.cdp__sort-group {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}
.cdp__select-wrap {
  position: relative;
}
.cdp__sort-sel {
  height: 2.75rem;
  padding: 0 2.25rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-right: none;
  border-radius: 0.625rem 0 0 0.625rem;
  background: var(--nxt-card-bg);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
  appearance: none;
  transition: border-color 0.15s;
}
.cdp__sort-sel:focus {
  border-color: var(--nxt-primary);
}
.cdp__sort-ico {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.625rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}
.cdp__sort-dir-btn {
  width: 2.75rem;
  height: 2.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0 0.625rem 0.625rem 0;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cdp__sort-dir-btn:hover {
  background: var(--nxt-surface-low);
  color: var(--nxt-primary);
}

.cdp__filter-btn {
  position: relative;
}
.cdp__filter-btn--active {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.05);
}
[data-theme='dark'] .cdp__filter-btn--active {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.125rem;
  height: 1.125rem;
  border-radius: 9999px;
  background: var(--nxt-primary);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 800;
  padding: 0 0.25rem;
}
[data-theme='dark'] .cdp__filter-count {
  background: #ffd60a;
  color: #0b1320;
}

/* Panneau filtres */
.cdp__filters-panel {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: var(--nxt-shadow-sm);
}
.cdp__filters-row {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}
.cdp__filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  min-width: 0;
}
.cdp__filter-lbl {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-muted);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.cdp__filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.cdp__fchip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.875rem;
  padding: 0 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.cdp__fchip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.05);
}
[data-theme='dark'] .cdp__fchip:hover {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__fchip--active {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__fchip--active {
  background: rgba(255, 255, 255, 0.12);
}
.cdp__fchip-count {
  min-width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  font-size: 0.625rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cdp__fchip--active .cdp__fchip-count {
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__fchip--active .cdp__fchip-count {
  background: #ffd60a;
  color: #0b1320;
}
.cdp__filters-footer {
  border-top: 1px solid var(--nxt-outline-variant);
  padding-top: 0.875rem;
  display: flex;
  justify-content: flex-end;
}

/* Catégories */
.cdp__categories {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}
.cdp__cat-section {
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
}
.cdp__cat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cdp__cat-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cdp__cat-icon-wrap {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__cat-icon-wrap {
  background: rgba(255, 255, 255, 0.1);
}
.cdp__cat-count {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--nxt-on-surface-muted);
}

/* Produits — grille */
.cdp__product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;
}

/* Card */
.cdp__pcard {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition:
    transform 0.18s,
    box-shadow 0.18s;
}
.cdp__pcard--clickable {
  cursor: pointer;
}
.cdp__pcard--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}
[data-theme='dark'] .cdp__pcard {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}
[data-theme='dark'] .cdp__pcard-name {
  color: #f1f5f9;
}
[data-theme='dark'] .cdp__pcard-type-chip {
  color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  border-color: rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cdp__pcard-price {
  color: #ffd60a;
}
.cdp__pcard:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.11);
}
[data-theme='dark'] .cdp__pcard:hover {
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.4);
}

/* Image zone */
.cdp__pcard-img {
  height: 10rem;
  position: relative;
  background: #f5f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
}
[data-theme='dark'] .cdp__pcard-img {
  background: #2a2d34;
}
.cdp__pcard-img-real {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  transition: transform 0.25s;
}
.cdp__pcard-img-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.08) 50%,
    rgba(0, 0, 0, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: cdp-shimmer 1.4s infinite;
}
[data-theme='dark'] .cdp__pcard-img-skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
}
.cdp__pcard-img:hover .cdp__pcard-img-real {
  transform: scale(1.04);
}
.cdp__pcard-img-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 2.25rem;
  color: #c0c0c8;
}
[data-theme='dark'] .cdp__pcard-img-ph {
  color: #6b7280;
}

/* Badge statut */
.cdp__pcard-badge {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  padding: 0.2rem 0.55rem;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  border-radius: 0.35rem;
  color: #fff;
}
.cdp__pcard-badge--inline {
  position: static;
}
.cdp__pcard-badge--active {
  background: #16a34a;
}
.cdp__pcard-badge--off {
  background: #9ca3af;
}

/* Corps */
.cdp__pcard-body {
  padding: 0.875rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
}
.cdp__pcard-type-row {
  display: flex;
  align-items: center;
}
.cdp__pcard-type-chip {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.18);
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
}
[data-theme='dark'] .cdp__pcard-type-chip {
  background: rgba(0, 0, 0, 0.12);
  border-color: rgba(0, 0, 0, 0.25);
}
.cdp__pcard-name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
  line-height: 1.3;
  cursor: pointer;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
[data-theme='dark'] .cdp__pcard-name {
  color: #f0f0f0;
}
.cdp__pcard-name:hover {
  color: var(--nxt-primary);
}
.cdp__pcard-desc {
  margin: 0;
  font-size: 0.78rem;
  color: #888;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
[data-theme='dark'] .cdp__pcard-desc {
  color: #9aa4b2;
}
.cdp__pcard-price {
  margin: 0.25rem 0 0;
  font-size: 1.1875rem;
  font-weight: 900;
  color: var(--nxt-primary);
  letter-spacing: -0.02em;
}

/* Footer actions */
.cdp__pcard-foot {
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
}
[data-theme='dark'] .cdp__pcard-foot {
  border-top-color: rgba(255, 255, 255, 0.07);
}
.cdp__pcard-action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.45rem 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #fafafa;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
  color: #555;
}
[data-theme='dark'] .cdp__pcard-action-btn {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.1);
  color: #aaa;
}
.cdp__pcard-action-btn--eye:hover {
  background: #f0f9ff;
  border-color: #60a5fa;
  color: #2563eb;
}
[data-theme='dark'] .cdp__pcard-action-btn--eye:hover {
  background: rgba(96, 165, 250, 0.1);
  border-color: #60a5fa;
  color: #60a5fa;
}
.cdp__pcard-action-btn--edit:hover {
  background: rgba(0, 0, 0, 0.07);
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__pcard-action-btn--edit:hover {
  background: rgba(0, 0, 0, 0.12);
}

.cdp__prod-loading {
  padding: 1rem 0;
}

/* Pagination produits */
.cdp__prod-pag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 0;
  border-top: 1px solid var(--nxt-outline-variant);
}
.cdp__prod-pag-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}
.cdp__prod-pag-info {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.cdp__pagesize-sel {
  height: 2.125rem;
  padding: 0 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
}
.cdp__prod-pag-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cdp__pag-btn {
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.45rem;
  background: var(--nxt-card-bg);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cdp__pag-btn:hover:not(:disabled) {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
}
.cdp__pag-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
.cdp__pag-btn--active {
  background: var(--nxt-primary);
  color: #fff;
  border-color: var(--nxt-primary);
  font-weight: 700;
}
[data-theme='dark'] .cdp__pag-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
.cdp__pag-ellipsis {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  padding: 0 0.25rem;
}
.cdp__ptable-img-wrap {
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.45rem;
  background: var(--nxt-surface-mid);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.cdp__ptable-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cdp__ptable-img-ph {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.4;
}
.cdp__ptable-img-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.08) 50%,
    rgba(0, 0, 0, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: cdp-shimmer 1.4s infinite;
}
[data-theme='dark'] .cdp__ptable-img-skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
}
.cdp__pcard-edit {
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.45rem;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--nxt-primary);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.cdp__pcard-edit:hover {
  background: rgba(0, 0, 0, 0.1);
}
[data-theme='dark'] .cdp__pcard-edit {
  color: #ffd60a;
}
[data-theme='dark'] .cdp__pcard-edit:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Produits — tableau */
.cdp__product-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
}
.cdp__product-table {
  display: flex;
  flex-direction: column;
  min-width: 780px;
}
.cdp__ptable-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.625rem 1rem;
  background: var(--nxt-surface-low);
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cdp__ptable-th {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}
.cdp__ptable-row--clickable {
  cursor: pointer;
}
.cdp__ptable-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  transition: background 0.12s;
}
.cdp__ptable-row:hover {
  background: var(--nxt-surface-low);
}
.cdp__ptable-row:last-child {
  border-bottom: none;
}
.cdp__ptable-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cdp__ptable-cell {
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
}
/* colonnes fixes — head + row partagent les mêmes classes */
.cdp__ptc--num {
  width: 2rem;
  flex-shrink: 0;
  text-align: center;
}
.cdp__ptc--img {
  width: 3.5rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cdp__ptc--name {
  flex: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdp__ptc--desc {
  flex: 1.5;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdp__ptc--price {
  width: 6rem;
  flex-shrink: 0;
  text-align: right;
}
.cdp__ptable-price {
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__ptable-price {
  color: #ffd60a;
}
[data-theme='dark'] .cdp__product-table-wrap {
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__ptable-head {
  background: rgba(255, 255, 255, 0.04);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__ptable-row {
  background: rgba(20, 28, 41, 0.8);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__ptable-row:hover {
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .cdp__ptable-img-wrap {
  background: rgba(255, 255, 255, 0.06);
}
.cdp__ptc--type {
  width: 7rem;
  flex-shrink: 0;
  text-align: center;
}
.cdp__ptc--status {
  width: 5rem;
  flex-shrink: 0;
  text-align: center;
}
.cdp__ptc--actions {
  width: 6rem;
  flex-shrink: 0;
  text-align: center;
}

/* ══ Informations ════════════════════════════════════════════════════════════ */
.cdp__info {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cdp__info-cols {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  align-items: start;
}
@media (min-width: 1024px) {
  .cdp__info-cols {
    grid-template-columns: 1fr 380px;
  }
}

.cdp__info-left,
.cdp__info-right {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* iCard générique */
.cdp__icard {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
[data-theme='dark'] .cdp__icard {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__icard-hd {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__icard-ico {
  font-size: 1rem;
  color: var(--nxt-primary);
}
.cdp__ichecklist-ratio {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
}

/* iFields grille 2 col */
.cdp__ifields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem 2rem;
  padding: 1.5rem;
}
.cdp__ifield {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.cdp__ifield--full {
  grid-column: span 2;
}
.cdp__ifield-lbl {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-muted);
  opacity: 0.7;
}
.cdp__ifield-val {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--nxt-on-surface);
}
.cdp__ifield-divider {
  height: 1px;
  background: rgba(221, 193, 175, 0.4);
  margin: 0.5rem 1.5rem;
}
.cdp__icode {
  font-size: 0.8125rem;
  font-family: 'Menlo', 'Consolas', monospace;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
  padding: 0.2rem 0.625rem;
  border-radius: 0.375rem;
  color: var(--nxt-primary);
  display: inline-block;
  font-weight: 700;
}
.cdp__ifield-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  font-weight: 700;
}
.cdp__ifield-status--on {
  color: #10b510;
}
.cdp__ifield-status--off {
  color: var(--nxt-on-surface-muted);
}
.cdp__ifield-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* Contacts */
.cdp__iempty {
  padding: 1.5rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  text-align: center;
}
.cdp__icontacts {
  display: flex;
  flex-direction: column;
}
.cdp__icontact {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(221, 193, 175, 0.35);
}
.cdp__icontact:last-of-type {
  border-bottom: none;
}
.cdp__iav {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.cdp__iav--sm {
  width: 2.25rem;
  height: 2.25rem;
  font-size: 0.75rem;
}
.cdp__icontact-body {
  flex: 1;
  min-width: 0;
}
.cdp__icontact-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.375rem;
}
.cdp__icontact-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__ibadge {
  font-size: 0.5625rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 0.2rem 0.5rem;
  border-radius: 0.3rem;
}
.cdp__ibadge--primary {
  background: rgba(0, 0, 0, 0.12);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.cdp__ibadge--role {
  background: rgba(86, 67, 53, 0.08);
  color: var(--nxt-on-surface-variant);
  border: 1px solid rgba(86, 67, 53, 0.12);
}
.cdp__icontact-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.cdp__icontact-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  text-decoration: none;
  transition: color 0.15s;
}
.cdp__icontact-link:hover {
  color: var(--nxt-primary);
}
.cdp__icontact-link .pi {
  font-size: 0.8125rem;
}
.cdp__iedit-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(221, 193, 175, 0.5);
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
.cdp__iedit-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  color: var(--nxt-primary);
  border-color: rgba(0, 0, 0, 0.2);
}

/* Contact référent */
.cdp__icontact-ref {
  padding: 1rem 1.5rem 1.25rem;
}
.cdp__icontact-ref-lbl {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-muted);
  margin-bottom: 0.75rem;
  display: block;
  opacity: 0.6;
}
.cdp__icontact-ref-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(221, 193, 175, 0.4);
  border-radius: 0.875rem;
  padding: 0.875rem 1rem;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.cdp__icontact-ref-row:hover {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.04);
}
.cdp__icontact-ref-info {
  flex: 1;
  min-width: 0;
}
.cdp__icontact-ref-name {
  display: block;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__icontact-ref-sub {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.cdp__icontact-ref-arrow {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}

/* Checklist */
.cdp__ichecklist-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cdp__iprog-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cdp__iprog-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.cdp__iprog-pct {
  font-size: 1.75rem;
  font-weight: 900;
  color: var(--nxt-on-surface);
  letter-spacing: -0.03em;
}
.cdp__iprog-lbl {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cdp__iprog-bar {
  height: 0.75rem;
  background: rgba(221, 193, 175, 0.3);
  border-radius: 9999px;
  overflow: hidden;
}
[data-theme='dark'] .cdp__iprog-bar {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__iprog-fill {
  height: 100%;
  background: var(--nxt-gradient-primary);
  border-radius: 9999px;
  transition: width 0.4s ease;
}
[data-theme='dark'] .cdp__iprog-fill {
  background: #ffd60a;
}
.cdp__ichecklist-items {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cdp__icheck-todo {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(221, 193, 175, 0.4);
  border-radius: 0.875rem;
  padding: 1rem;
}
[data-theme='dark'] .cdp__icheck-todo {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__icheck-todo-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.0625rem;
  background: rgba(0, 0, 0, 0.12);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.15);
}
.cdp__icheck-todo-icon--urgent {
  background: rgba(0, 0, 0, 0.18);
}
[data-theme='dark'] .cdp__icheck-todo-icon {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .cdp__icheck-todo-icon--urgent {
  background: rgba(255, 214, 10, 0.25);
}
.cdp__icheck-todo-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cdp__icheck-todo-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__icheck-todo-sub {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.cdp__icheck-todo-btn {
  height: 1.875rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-primary);
  border-radius: 0.5rem;
  background: none;
  color: var(--nxt-primary);
  font-size: 0.6875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: background 0.15s;
}
.cdp__icheck-todo-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}
[data-theme='dark'] .cdp__icheck-todo-btn {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .cdp__icheck-todo-btn:hover {
  background: rgba(255, 214, 10, 0.12);
}
.cdp__icheck-done {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: rgba(16, 181, 16, 0.06);
  border: 1px solid rgba(16, 181, 16, 0.15);
  border-radius: 0.75rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  opacity: 0.7;
}
[data-theme='dark'] .cdp__icheck-done {
  background: rgba(16, 181, 16, 0.1);
  border-color: rgba(16, 181, 16, 0.25);
}
.cdp__icheck-done-ico {
  color: #10b510;
  font-size: 1rem;
}

/* Stats rapides */
.cdp__istats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.cdp__istat {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
[data-theme='dark'] .cdp__istat {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__istat-num {
  display: block;
  font-size: 1.75rem;
  font-weight: 900;
  color: var(--nxt-primary);
  letter-spacing: -0.03em;
}
.cdp__istat-lbl {
  display: block;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-muted);
  margin-top: 0.25rem;
}

/* Dupliquer */
.cdp__iduplicate {
  border: 2px dashed var(--nxt-outline-variant);
  border-radius: 1.125rem;
  padding: 1.75rem;
  text-align: center;
  cursor: pointer;
  background: transparent;
  transition:
    border-color 0.2s,
    background 0.2s;
}
.cdp__iduplicate:hover {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.03);
}
.cdp__iduplicate-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: var(--nxt-card-bg);
  box-shadow: var(--nxt-shadow-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.25rem;
  color: var(--nxt-primary);
  transition: transform 0.2s;
}
.cdp__iduplicate:hover .cdp__iduplicate-icon {
  transform: scale(1.1);
}
.cdp__iduplicate-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0 0 0.5rem;
}
.cdp__iduplicate-sub {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  margin: 0;
  line-height: 1.5;
}

/* ══ Empty ═══════════════════════════════════════════════════════════════════ */
.cdp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.875rem;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.cdp__empty-ico {
  font-size: 2.25rem;
  color: var(--nxt-on-surface-muted);
  opacity: 0.2;
}
.cdp__empty p {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-muted);
}

/* ══ Boutons ══════════════════════════════════════════════════════════════════ */
.cdp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    background 0.15s,
    transform 0.1s;
  flex-shrink: 0;
}
.cdp__btn:active {
  transform: scale(0.97);
}
.cdp__btn--icon {
  width: 2.5rem;
  padding: 0;
  justify-content: center;
}
.cdp__btn--sm {
  height: 2.125rem;
  font-size: 0.8125rem;
  padding: 0 0.875rem;
}
.cdp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.cdp__btn--primary:hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}
.cdp__btn--shadow {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
}
.cdp__btn--outline {
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__btn--outline:hover {
  background: var(--nxt-surface-low);
}
[data-theme='dark'] .cdp__btn--outline {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.75);
  border-color: rgba(255, 255, 255, 0.12);
}
[data-theme='dark'] .cdp__btn--outline:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.cdp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.cdp__btn--danger-soft {
  background: rgba(186, 26, 26, 0.08);
  color: var(--nxt-error);
  border: 1px solid rgba(186, 26, 26, 0.2);
}
.cdp__btn--danger-soft:hover:not(:disabled) {
  background: rgba(186, 26, 26, 0.15);
}
.cdp__btn--success-soft {
  background: rgba(16, 181, 16, 0.08);
  color: var(--nxt-status-up, #10b510);
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cdp__btn--success-soft:hover:not(:disabled) {
  background: rgba(16, 181, 16, 0.15);
}
.cdp__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__spinner {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: currentColor;
  animation: cdp-spin 0.7s linear infinite;
  display: inline-block;
}
.cdp__spinner--xs {
  width: 0.65rem;
  height: 0.65rem;
  border-width: 1.5px;
}
@keyframes cdp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ══ Responsive ══════════════════════════════════════════════════════════════ */
@media (max-width: 767px) {
  /* Hero empilé */
  .cdp__hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .cdp__hero-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .cdp__hero-actions .cdp__btn--icon {
    margin-left: auto;
  }
  .cdp__title {
    font-size: 1.25rem;
  }

  /* Bannière */
  .cdp__banner {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .cdp__banner > .cdp__btn {
    width: 100%;
    justify-content: center;
  }

  /* Onglets scrollables */
  .cdp__tabs-bar {
    gap: 0;
  }
  .cdp__tab {
    padding: 0.65rem 0.75rem;
    font-size: 0.8125rem;
  }

  /* KPIs 2 colonnes sur mobile */
  .cdp__kpis {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  /* Barre catalogue empilée */
  .cdp__cat-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  .cdp__cat-bar-left {
    flex-direction: column;
    align-items: stretch;
  }
  .cdp__search-wrap {
    max-width: 100%;
  }
  .cdp__cat-bar-right {
    flex-direction: column;
    align-items: stretch;
  }
  .cdp__cat-bar-right .cdp__btn {
    width: 100%;
    justify-content: center;
  }
  .cdp__sort-group {
    flex: 1;
  }
  .cdp__sort-sel {
    flex: 1;
    width: 100%;
  }
  .cdp__filters-row {
    flex-direction: column;
    gap: 1rem;
  }

  /* Pagination empilée */
  .cdp__prod-pag {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .cdp__prod-pag-btns {
    flex-wrap: wrap;
  }

  /* Sous-onglets scrollables */
  .cdp__subtabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 0.25rem;
  }
  .cdp__subtabs::-webkit-scrollbar {
    display: none;
  }
  .cdp__subtab {
    flex-shrink: 0;
  }
}

@media (max-width: 480px) {
  .cdp__kpis {
    grid-template-columns: 1fr 1fr;
  }
  .cdp__hero-actions {
    gap: 0.375rem;
  }
  .cdp__hero-actions .cdp__btn {
    padding: 0 0.75rem;
    font-size: 0.8125rem;
  }
}

/* ══ Assignation ════════════════════════════════════════════════════════════ */
.cdp__assign {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cdp__assign-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.25rem;
  align-items: start;
}
@media (max-width: 900px) {
  .cdp__assign-layout {
    grid-template-columns: 1fr;
  }
}

/* Carte gauche */
.cdp__assign-card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
[data-theme='dark'] .cdp__assign-card {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
.cdp__assign-card-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.125rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}
[data-theme='dark'] .cdp__assign-card-hd {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
.cdp__assign-card-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
  flex: 1;
}
[data-theme='dark'] .cdp__assign-card-title {
  color: #f0f0f0;
}
.cdp__assign-count {
  font-size: 0.7rem;
  font-weight: 700;
  background: #f5f5f7;
  border: 1px solid #e8e8e8;
  color: #888;
  padding: 0.15rem 0.45rem;
  border-radius: 0.35rem;
}
[data-theme='dark'] .cdp__assign-count {
  background: #2a2d34;
  color: #aab0bb;
  border-color: rgba(255, 255, 255, 0.08);
}

/* Search */
.cdp__assign-search-wrap {
  position: relative;
  padding: 0.75rem 1rem 0.5rem;
}
.cdp__assign-search-ico {
  position: absolute;
  left: 1.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875rem;
  color: #bbb;
}
.cdp__assign-search {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  outline: none;
  background: #fafafa;
  color: #111;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
[data-theme='dark'] .cdp__assign-search {
  background: #23262d;
  border-color: rgba(255, 255, 255, 0.08);
  color: #e0e0e0;
}
.cdp__assign-search:focus {
  border-color: var(--nxt-primary);
}

/* Product list */
.cdp__assign-prod-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.5rem 0.625rem 1rem;
  max-height: 520px;
  overflow-y: auto;
}
.cdp__assign-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;
}
.cdp__assign-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.625rem 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant);
  flex-wrap: wrap;
}
.cdp__assign-prod {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid transparent;
  background: #fafafa;
  cursor: grab;
  transition:
    background 0.12s,
    border-color 0.12s,
    box-shadow 0.12s;
  user-select: none;
}
[data-theme='dark'] .cdp__assign-prod {
  background: #23262d;
}
.cdp__assign-prod:hover {
  background: #fff;
  border-color: #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}
[data-theme='dark'] .cdp__assign-prod:hover {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.1);
}
.cdp__assign-prod:active {
  cursor: grabbing;
}
.cdp__assign-prod--assigned {
  opacity: 1;
}
.cdp__assign-prod-img {
  position: relative;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cdp__assign-prod-img-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.08) 50%,
    rgba(0, 0, 0, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: cdp-shimmer 1.4s infinite;
}
[data-theme='dark'] .cdp__assign-prod-img-skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
}
[data-theme='dark'] .cdp__assign-prod-img {
  background: #333;
}
.cdp__assign-prod-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cdp__assign-prod-img .pi {
  font-size: 0.9rem;
  color: #ccc;
}
.cdp__assign-prod-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.cdp__assign-prod-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
[data-theme='dark'] .cdp__assign-prod-name {
  color: #e0e0e0;
}
.cdp__assign-prod-price {
  font-size: 0.75rem;
  color: var(--nxt-primary);
  font-weight: 700;
}
[data-theme='dark'] .cdp__assign-prod-price {
  color: #ffd60a;
}
.cdp__assign-prod-right {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}
.cdp__assign-drag-ico {
  color: #ccc;
  font-size: 0.875rem;
}
.cdp__assign-pill {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
}
.cdp__assign-empty {
  text-align: center;
  padding: 1.5rem;
  color: #bbb;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}
.cdp__assign-prod--active {
  background: #fff;
  border-color: var(--nxt-primary);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}
[data-theme='dark'] .cdp__assign-prod--active {
  background: #2a2d34;
  border-color: #ffd60a;
}

/* Picker */
.cdp__assign-picker {
  position: absolute;
  left: calc(100% + 0.5rem);
  top: 0;
  z-index: 200;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  padding: 0.625rem;
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
[data-theme='dark'] .cdp__assign-picker {
  background: #23262d;
  border-color: rgba(255, 255, 255, 0.1);
}
.cdp__assign-picker-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #aaa;
  padding: 0.125rem 0.25rem 0.375rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0.25rem;
}
[data-theme='dark'] .cdp__assign-picker-title {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
.cdp__assign-picker-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-radius: 0.45rem;
  cursor: pointer;
  font-size: 0.8125rem;
  color: #333;
  transition: background 0.12s;
}
[data-theme='dark'] .cdp__assign-picker-row {
  color: #ccc;
}
.cdp__assign-picker-row:hover {
  background: #f5f5f7;
}
[data-theme='dark'] .cdp__assign-picker-row:hover {
  background: #2a2d34;
}
.cdp__assign-picker-row input[type='checkbox'] {
  accent-color: var(--nxt-primary);
  width: 0.9rem;
  height: 0.9rem;
  cursor: pointer;
  flex-shrink: 0;
}

/* Colonne droite */
.cdp__assign-right-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.cdp__assign-right {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: visible;
  padding: 0.25rem 0.25rem 0.25rem 0;
}
.cdp__assign-right > .cdp__assign-col-card {
  flex-shrink: 0;
  margin-bottom: 0.25rem;
}
.cdp__assign-right > .cdp__assign-col-card:last-child {
  margin-bottom: 0;
}
.cdp__assign-col-card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
[data-theme='dark'] .cdp__assign-col-card {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
.cdp__assign-col-card--over {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12);
}
.cdp__assign-col-card--focus {
  animation: cdp-focus-pulse 1.8s ease forwards;
}
@keyframes cdp-focus-pulse {
  0% {
    border-color: var(--nxt-primary);
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.25);
  }
  60% {
    border-color: var(--nxt-primary);
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.25);
  }
  100% {
    border-color: #ebebeb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
}
.cdp__assign-col-hd {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.125rem 0.875rem;
  border-bottom: 1px solid #f5f5f5;
}
[data-theme='dark'] .cdp__assign-col-hd {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}
.cdp__assign-col-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-primary);
  font-size: 0.875rem;
  flex-shrink: 0;
}
.cdp__assign-col-ico--none {
  background: rgba(100, 100, 100, 0.07);
  border-color: rgba(100, 100, 100, 0.18);
  color: #888;
}
[data-theme='dark'] .cdp__assign-col-ico {
  background: rgba(255, 214, 10, 0.15);
  border-color: rgba(255, 214, 10, 0.35);
  color: #ffd60a;
}
[data-theme='dark'] .cdp__assign-col-ico--none {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  color: #aaa;
}
.cdp__assign-col-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.cdp__assign-col-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
}
[data-theme='dark'] .cdp__assign-col-name {
  color: #f0f0f0;
}
.cdp__assign-col-count {
  font-size: 0.75rem;
  color: #aaa;
}

/* Drop zone */
.cdp__assign-dropzone {
  min-height: 4rem;
  padding: 0.75rem;
  margin: 0.75rem;
  border: 2px dashed #e5e7eb;
  border-radius: 0.75rem;
  transition:
    border-color 0.15s,
    background 0.15s;
}
[data-theme='dark'] .cdp__assign-dropzone {
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__assign-dropzone--over {
  border-color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.04);
}
.cdp__assign-dropzone-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 3.5rem;
  color: #ccc;
  font-size: 0.8125rem;
}
.cdp__assign-dropzone-empty .pi {
  font-size: 1.1rem;
}
[data-theme='dark'] .cdp__assign-dropzone-empty {
  color: #6b7280;
}

/* Chips assignées */
.cdp__assign-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.cdp__assign-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  background: #f5f5f7;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #333;
}
[data-theme='dark'] .cdp__assign-chip {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
  color: #ccc;
}
.cdp__assign-chip-rm {
  background: none;
  border: none;
  cursor: pointer;
  color: #bbb;
  display: flex;
  align-items: center;
  padding: 0;
  font-size: 0.65rem;
  transition: color 0.12s;
}
.cdp__assign-chip-rm:hover {
  color: #ef4444;
}

/* No categories */
.cdp__assign-no-cats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem;
  text-align: center;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  color: #aaa;
}
[data-theme='dark'] .cdp__assign-no-cats {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
.cdp__assign-no-cats .pi {
  font-size: 2rem;
}

/* Modale confirmation */
.cdp__confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cdp__confirm-modal {
  background: #fff;
  border-radius: 1.125rem;
  padding: 2rem 1.75rem 1.5rem;
  max-width: 380px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.875rem;
  text-align: center;
}
[data-theme='dark'] .cdp__confirm-modal {
  background: #1e2025;
}
.cdp__confirm-ico {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.375rem;
}
.cdp__confirm-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #111;
}
[data-theme='dark'] .cdp__confirm-title {
  color: #f0f0f0;
}
.cdp__confirm-desc {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  line-height: 1.5;
}
[data-theme='dark'] .cdp__confirm-desc {
  color: #aaa;
}
.cdp__confirm-actions {
  display: flex;
  gap: 0.625rem;
  width: 100%;
  justify-content: center;
  margin-top: 0.25rem;
}
.cdp__btn--danger {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}
.cdp__btn--danger:hover {
  background: #dc2626;
  border-color: #dc2626;
}

/* Footer */
.cdp__assign-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
}
[data-theme='dark'] .cdp__assign-footer {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
.cdp__assign-footer-info {
  font-size: 0.8125rem;
  color: #aaa;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.cdp__assign-footer-actions {
  display: flex;
  gap: 0.625rem;
}

/* ── Collections tab ──────────────────────────────────────── */
.cdp__cols {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 0.75rem;
}

.cdp__cols-toolbar {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cdp__cols-toolbar .cdp__search-wrap {
  flex: 1;
  min-width: 12rem;
}

.cdp__cols-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  white-space: nowrap;
  transition: all 0.15s;
}
.cdp__cols-new-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px -3px rgba(0, 0, 0, 0.4);
}
.cdp__cols-new-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__cols-new-btn--toolbar {
  margin-left: auto;
}

.cdp__ncol {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  margin: 0 1.25rem 1rem;
  border-radius: 1rem;
  background: linear-gradient(
    180deg,
    rgba(248, 147, 3, 0.05) 0%,
    rgba(0, 0, 0, 0.015) 100%
  );
  border: 1px solid rgba(248, 147, 3, 0.18);
  animation: cdpNcolIn 0.2s ease;
}
@keyframes cdpNcolIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
[data-theme='dark'] .cdp__ncol {
  background: linear-gradient(
    180deg,
    rgba(248, 147, 3, 0.08) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border-color: rgba(248, 147, 3, 0.25);
}

.cdp__ncol-hd {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.cdp__ncol-hd-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
  color: #0b1320;
  font-size: 1rem;
  flex-shrink: 0;
}
[data-theme='dark'] .cdp__ncol-hd-ico {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.cdp__ncol-title {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-on-surface, #171310);
  letter-spacing: -0.01em;
}
[data-theme='dark'] .cdp__ncol-title {
  color: #f5f2ee;
}

.cdp__ncol-lang-toggle {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  padding: 0.1875rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .cdp__ncol-lang-toggle {
  background: rgba(255, 255, 255, 0.06);
}
.cdp__ncol-lang-btn {
  padding: 0.3125rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-muted, #938282);
  cursor: pointer;
  transition: all 0.15s;
}
.cdp__ncol-lang-btn--active {
  background: #fff;
  color: #0b1320;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
[data-theme='dark'] .cdp__ncol-lang-btn--active {
  background: #2a2d33;
  color: #fff;
}

.cdp__ncol-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.cdp__ncol-field {
  flex: 1;
  min-width: 12rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cdp__ncol-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted, #938282);
}
.cdp__ncol-req {
  color: #ef4444;
}
.cdp__ncol-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.25rem;
  padding: 0 0.3rem;
  border-radius: 0.3125rem;
  font-size: 0.625rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-muted, #938282);
}
[data-theme='dark'] .cdp__ncol-badge {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__ncol-badge--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .cdp__ncol-badge--active {
  background: #fff;
  color: #0b1320;
}
.cdp__ncol-spin {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #938282);
  margin-left: auto;
}
.cdp__ncol-input {
  height: 2.5rem;
  padding: 0 0.875rem;
  border-radius: 0.625rem;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface, #171310);
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.cdp__ncol-input::placeholder {
  color: #b7b2ab;
}
.cdp__ncol-input:focus {
  border-color: #0b1320;
  box-shadow: 0 0 0 3px rgba(11, 19, 32, 0.1);
}
[data-theme='dark'] .cdp__ncol-input:focus {
  border-color: #fff;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}
.cdp__ncol-input--active {
  border-color: rgba(11, 19, 32, 0.4);
}
[data-theme='dark'] .cdp__ncol-input {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f2ee;
}
.cdp__ncol-textarea {
  height: auto;
  min-height: 3.5rem;
  padding: 0.5rem 0.875rem;
  resize: vertical;
}

.cdp__ncol-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 500;
}

.cdp__ncol-modules {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cdp__ncol-module-chip {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.cdp__ncol-module-chip--on {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
  color: #0b1320;
}
[data-theme='dark'] .cdp__ncol-module-chip--on {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}

.cdp__ncol-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}
.cdp__ncol-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5625rem 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.cdp__ncol-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__ncol-btn--ghost {
  background: transparent;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  color: var(--nxt-on-surface-muted, #6b6b6b);
}
.cdp__ncol-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .cdp__ncol-btn--ghost {
  border-color: rgba(255, 255, 255, 0.12);
  color: #cfcac2;
}
.cdp__ncol-btn--primary {
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
}
.cdp__ncol-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px -3px rgba(0, 0, 0, 0.4);
}

.cdp__cols-thead {
  display: grid;
  grid-template-columns: 24px 1.8fr 0.9fr 1fr 70px 190px;
  gap: 1rem;
  padding: 0.375rem 1.25rem;
}
.cdp__cols-th {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-muted, #938282);
}
.cdp__cols-th--r {
  text-align: right;
}

/* Card row — blanc pur avec ombre légère */
.cdp__cols-row {
  display: grid;
  grid-template-columns: 24px 1.8fr 0.9fr 1fr 70px 190px;
  gap: 1rem;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 0.875rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow:
    0 1px 4px rgba(0, 0, 0, 0.06),
    0 0 0 0 rgba(0, 0, 0, 0);
  transition:
    box-shadow 0.18s,
    border-color 0.18s,
    transform 0.14s;
}
[data-theme='dark'] .cdp__cols-row {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__cols-row:hover {
  border-color: rgba(0, 0, 0, 0.25);
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.1),
    0 1px 4px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}
[data-theme='dark'] .cdp__cols-row:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

.cdp__cols-cell {
  display: flex;
  align-items: center;
}
.cdp__cols-cell--name {
  gap: 0.875rem;
}
.cdp__cols-cell--clickable {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.15s;
}
.cdp__cols-cell--clickable:hover {
  background: rgba(0, 0, 0, 0.035);
}
[data-theme='dark'] .cdp__cols-cell--clickable:hover {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__cols-cell--clickable:hover .cdp__cols-edit-hint {
  opacity: 1;
}
.cdp__cols-edit-hint {
  margin-left: 0.375rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #938282);
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.cdp__cols-cell--channels {
  gap: 0.375rem;
  flex-wrap: wrap;
}
.cdp__cols-cell--actions {
  justify-content: flex-end;
  gap: 0.5rem;
}

.cdp__cols-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  color: var(--nxt-on-surface-muted, #938282);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
[data-theme='dark'] .cdp__cols-icon-btn {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.cdp__cols-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__cols-icon-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

/* Icône catégorie — noir */
.cdp__cols-ico {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  flex-shrink: 0;
  background: #111;
  border: 1px solid #111;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.9rem;
}
[data-theme='dark'] .cdp__cols-ico {
  background: #fff;
  color: #111;
  border-color: #fff;
}
.cdp__cols-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.cdp__cols-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
[data-theme='dark'] .cdp__cols-name {
  color: var(--nxt-on-surface, #e2e4e5);
}
.cdp__cols-id {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #938282);
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

/* Handle drag collections */
.cdp__cols-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  flex-shrink: 0;
  cursor: grab;
  color: var(--nxt-on-surface-muted);
  opacity: 0.35;
  transition: opacity 0.15s;
}
.cdp__cols-row:hover .cdp__cols-handle {
  opacity: 0.7;
}
.cdp__cols-handle:active {
  cursor: grabbing;
}
.cdp__cols-handle .pi {
  font-size: 0.875rem;
}

/* Drag-over indicator */
.cdp__cols-row--drag-over {
  border-top: 2px solid var(--nxt-primary) !important;
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .cdp__cols-row--drag-over {
  background: rgba(255, 255, 255, 0.04);
}

/* Badge "Standard" — gris neutre */
.cdp__cols-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.cdp__cols-badge--std {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
[data-theme='dark'] .cdp__cols-badge--std {
  background: rgba(255, 255, 255, 0.07);
  color: #aaa;
  border-color: rgba(255, 255, 255, 0.1);
}

/* Badge canal C&C — gris neutre */
.cdp__cols-channel {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.65rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}
[data-theme='dark'] .cdp__cols-channel {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c0c0;
  border-color: rgba(255, 255, 255, 0.12);
}
.cdp__cols-channel--all {
  background: rgba(255, 214, 10, 0.12);
  color: #8a6d00;
  border-color: rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cdp__cols-channel--all {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}

/* Badge nb produits — noir */
.cdp__cols-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.5rem;
  border-radius: 9999px;
  background: #111;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
}
[data-theme='dark'] .cdp__cols-count-badge {
  background: #fff;
  color: #111;
}
.cdp__cols-count-badge--empty {
  gap: 0.3rem;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  background: rgba(245, 158, 11, 0.14);
  color: #b45309;
  font-size: 0.6875rem;
  font-weight: 700;
}
[data-theme='dark'] .cdp__cols-count-badge--empty {
  background: rgba(245, 158, 11, 0.16);
  color: #fbbf24;
}

/* Bouton "Gérer les produits" — blanc avec bordure noire */
.cdp__cols-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.cdp__cols-btn--assign {
  background: #fff;
  color: #111;
  border: 1px solid rgba(0, 0, 0, 0.18);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .cdp__cols-btn--assign {
  background: var(--nxt-surface-mid, #222527);
  color: #e0e0e0;
  border-color: rgba(255, 255, 255, 0.15);
}
.cdp__cols-btn--assign:hover {
  background: #111;
  color: #fff;
  border-color: #111;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.cdp__cols-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: var(--nxt-on-surface-muted, #938282);
  font-size: 0.875rem;
}
.cdp__cols-empty .pi {
  font-size: 2rem;
}

/* Stats bas */
.cdp__cols-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin-top: 0.5rem;
}
.cdp__cols-stat {
  position: relative;
  overflow: hidden;
  border-radius: 0.875rem;
  padding: 1.25rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition:
    box-shadow 0.15s,
    transform 0.14s;
}
[data-theme='dark'] .cdp__cols-stat {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__cols-stat:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}
.cdp__cols-stat--cta {
  background: #111;
  border: none;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
[data-theme='dark'] .cdp__cols-stat--cta {
  background: #fff;
}
.cdp__cols-stat--cta:hover {
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.35);
}

.cdp__cols-stat-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.cdp__cols-stat-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}
.cdp__cols-stat-ico--orange {
  background: rgba(0, 0, 0, 0.06);
  color: #111;
}
[data-theme='dark'] .cdp__cols-stat-ico--orange {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0e0;
}
.cdp__cols-stat-ico--blue {
  background: rgba(0, 99, 152, 0.1);
  color: #006398;
}
.cdp__cols-stat-ico--white {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}
[data-theme='dark'] .cdp__cols-stat--cta .cdp__cols-stat-ico--white {
  background: rgba(0, 0, 0, 0.1);
  color: #111;
}
[data-theme='dark'] .cdp__cols-stat--cta .cdp__cols-stat-label--light {
  color: rgba(0, 0, 0, 0.6);
}
[data-theme='dark'] .cdp__cols-stat--cta .cdp__cols-stat-val--light {
  color: #111;
}
[data-theme='dark'] .cdp__cols-stat--cta .cdp__cols-stat-desc--light {
  color: rgba(0, 0, 0, 0.7);
}
[data-theme='dark'] .cdp__cols-stat-arrow {
  color: #111;
}
[data-theme='dark'] .cdp__cols-stat-ico--blue {
  background: rgba(0, 99, 152, 0.2);
  color: #6ab8e8;
}

.cdp__cols-stat-badge {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
}
.cdp__cols-stat-badge--green {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}
[data-theme='dark'] .cdp__cols-stat-badge--green {
  background: rgba(255, 255, 255, 0.08);
  color: #c0c0c0;
  border-color: rgba(255, 255, 255, 0.12);
}

.cdp__cols-stat-sync {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
}
[data-theme='dark'] .cdp__cols-stat-sync {
  color: #c0c0c0;
}
.cdp__cols-stat-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #374151;
}
[data-theme='dark'] .cdp__cols-stat-dot {
  background: #c0c0c0;
}

.cdp__cols-stat-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-muted, #938282);
}
.cdp__cols-stat-label--light {
  color: rgba(255, 255, 255, 0.8);
}
.cdp__cols-stat-val {
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface, #191c1d);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
[data-theme='dark'] .cdp__cols-stat-val {
  color: var(--nxt-on-surface, #e2e4e5);
}
.cdp__cols-stat-val--light {
  color: #fff;
}
.cdp__cols-stat-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #938282);
  line-height: 1.4;
  margin-top: 0.1rem;
}
.cdp__cols-stat-desc--light {
  color: rgba(255, 255, 255, 0.88);
}

.cdp__cols-stat-arrow {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  color: #fff;
  font-size: 1.125rem;
  opacity: 0.9;
}

/* ══ Groupes d'options ══════════════════════════════════════════════════════ */
.cdp__opts-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: 0.875rem;
  margin-bottom: 1.25rem;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
[data-theme='dark'] .cdp__opts-banner {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__opts-banner-ico {
  font-size: 1rem;
  color: var(--nxt-on-surface-variant);
  margin-top: 0.125rem;
  flex-shrink: 0;
}
.cdp__opts-banner-title {
  margin: 0 0 0.2rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__opts-banner-sub {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}

.cdp__opts-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.cdp__opts-toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cdp__opts-search-wrap {
  position: relative;
  flex: 1;
  max-width: 20rem;
}
.cdp__opts-search-ico {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cdp__opts-search {
  width: 100%;
  height: 2.375rem;
  padding: 0 1rem 0 2.25rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg, #fff);
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  box-sizing: border-box;
}
.cdp__opts-search:focus {
  border-color: #111;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.07);
}
[data-theme='dark'] .cdp__opts-search {
  background: var(--nxt-surface-mid, #222527);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__opts-search:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.15);
}
.cdp__btn--sm {
  height: 2.375rem;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
}
.cdp__btn--icon-left {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.cdp__opts-table-wrap {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  overflow: hidden;
}
[data-theme='dark'] .cdp__opts-table-wrap {
  border-color: rgba(255, 255, 255, 0.08);
}

.cdp__opts-table {
  width: 100%;
  border-collapse: collapse;
}
.cdp__opts-table thead th {
  padding: 0.875rem 1.25rem;
  text-align: left;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-variant);
  border-bottom: 1px solid var(--nxt-outline-variant);
  white-space: nowrap;
}
.cdp__opts-th--center {
  text-align: center;
}
[data-theme='dark'] .cdp__opts-table thead th {
  border-color: rgba(255, 255, 255, 0.07);
}

.cdp__opts-row {
  transition: background 0.12s;
}
.cdp__opts-row:not(:last-child) td {
  border-bottom: 1px solid var(--nxt-outline-variant);
}
[data-theme='dark'] .cdp__opts-row:not(:last-child) td {
  border-color: rgba(255, 255, 255, 0.06);
}
.cdp__opts-row:hover {
  background: var(--nxt-surface-low, #f8f9fa);
}
[data-theme='dark'] .cdp__opts-row:hover {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__opts-row td {
  padding: 0.875rem 1.25rem;
  vertical-align: middle;
}
.cdp__opts-td--center {
  text-align: center;
}
.cdp__opts-td--right {
  text-align: right;
}

.cdp__opts-name-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.cdp__opts-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__opts-id {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-muted);
  font-family: monospace;
}

.cdp__opts-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}
.cdp__opts-chip {
  padding: 0.2rem 0.5rem;
  border-radius: 0.375rem;
  background: var(--nxt-surface-mid, #f1f3f4);
  color: var(--nxt-on-surface-variant);
  font-size: 0.6875rem;
  font-weight: 500;
}
[data-theme='dark'] .cdp__opts-chip {
  background: rgba(255, 255, 255, 0.08);
  color: #bbb;
}
.cdp__opts-chip-more {
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--nxt-primary);
  font-style: italic;
  align-self: center;
}
[data-theme='dark'] .cdp__opts-chip-more {
  color: #ffd60a;
}

.cdp__opts-badge {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}
.cdp__opts-badge--neutral {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__opts-badge--primary {
  background: rgba(0, 0, 0, 0.08);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.cdp__opts-badge--muted {
  background: var(--nxt-surface-high, #e7e8e9);
  color: var(--nxt-on-surface-muted);
  border: 1px solid var(--nxt-outline-variant);
}
.cdp__opts-badge--success {
  background: rgba(16, 181, 16, 0.08);
  color: #0a7a0a;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
[data-theme='dark'] .cdp__opts-badge--primary {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
  border-color: rgba(255, 214, 10, 0.4);
}
[data-theme='dark'] .cdp__opts-badge--neutral {
  background: rgba(255, 255, 255, 0.07);
  color: #aaa;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__opts-badge--success {
  background: rgba(16, 181, 16, 0.16);
  color: #4ade80;
  border-color: rgba(16, 181, 16, 0.35);
}

.cdp__opts-price-free {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}

.cdp__opts-products {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cdp__opts-product {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__opts-product--link {
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.cdp__opts-product--link:hover {
  color: var(--nxt-on-surface);
}

.cdp__opts-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.875rem;
  border-radius: 0.5rem;
  background: none;
  border: 1px solid var(--nxt-outline-variant);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s,
    background 0.12s;
}
.cdp__opts-row:hover .cdp__opts-edit-btn {
  opacity: 1;
}
.cdp__opts-edit-btn:hover {
  background: var(--nxt-surface-mid);
}

.cdp__opts-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low, #f8f9fa);
}
[data-theme='dark'] .cdp__opts-pagination {
  border-color: rgba(255, 255, 255, 0.07);
  background: transparent;
}
.cdp__opts-pag-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cdp__opts-pag-info {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__opts-pag-size {
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
}
[data-theme='dark'] .cdp__opts-pag-size {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.1);
}
.cdp__opts-pag-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.cdp__opts-pag-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface, #fff);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s;
  padding: 0 0.375rem;
}
[data-theme='dark'] .cdp__opts-pag-btn {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.1);
}
.cdp__opts-pag-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
}
[data-theme='dark'] .cdp__opts-pag-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__opts-pag-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.cdp__opts-pag-btn--active {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .cdp__opts-pag-btn--active {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
  border-color: var(--nxt-on-surface, #e2e4e5);
}
.cdp__opts-pag-ellipsis {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  padding: 0 0.25rem;
}

@keyframes cdp-shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}
.cdp__skel {
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 800px 100%;
  animation: cdp-shimmer 1.4s infinite linear;
}
[data-theme='dark'] .cdp__skel {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 800px 100%;
}
.cdp__skel--name {
  height: 2.25rem;
  width: 70%;
}
.cdp__skel--chips {
  height: 1.5rem;
  width: 85%;
}
.cdp__skel--badge {
  height: 1.5rem;
  width: 5rem;
  margin: 0 auto;
}
.cdp__skel--prod {
  height: 1.5rem;
  width: 60%;
}
.cdp__opts-row--skel {
  pointer-events: none;
}

.cdp__opts-empty-row {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
}
.cdp__opts-empty-row i {
  font-size: 2rem;
  opacity: 0.4;
  display: block;
  margin-bottom: 0.75rem;
}

/* ── Promotions tab ──────────────────────────────────────────────────────── */
.promo__wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(240, 243, 255, 0.35);
  border-radius: 2rem;
}
[data-theme='dark'] .promo__wrap {
  background: rgba(20, 22, 30, 0.35);
}

.promo__kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.875rem;
}
.promo__kpi {
  background: var(--nxt-surface, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
[data-theme='dark'] .promo__kpi {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.08);
}
.promo__kpi--green {
}
.promo__kpi-val {
  font-size: 2rem;
  font-weight: 900;
  color: var(--nxt-on-surface);
  line-height: 1;
}
.promo__kpi-lbl {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}

.promo__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.promo__filters {
  display: flex;
  gap: 0.375rem;
}
.promo__filter {
  padding: 0.375rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
.promo__filter:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .promo__filter:hover {
  background: rgba(255, 255, 255, 0.06);
}
.promo__filter--on {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .promo__filter--on {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
  border-color: transparent;
}
.promo__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1.125rem;
  border-radius: 0.625rem;
  border: none;
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
}
.promo__add-btn:hover {
  opacity: 0.82;
}
[data-theme='dark'] .promo__add-btn {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}

.promo__section-title {
  font-size: 0.6875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--nxt-on-surface-variant);
  margin: 0.25rem 0 0.125rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.promo__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.promo__card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.875rem;
  background: var(--nxt-surface, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.15s;
}
[data-theme='dark'] .promo__card {
  background: var(--nxt-surface-low, #1a1c1e);
  border-color: rgba(255, 255, 255, 0.07);
}
.promo__card:hover {
  border-color: rgba(0, 0, 0, 0.18);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
[data-theme='dark'] .promo__card:hover {
  border-color: rgba(255, 255, 255, 0.18);
}
.promo__card--inactive {
  opacity: 0.55;
}
.promo__card--skel {
  height: 5rem;
  pointer-events: none;
  padding: 1.25rem;
}

.promo__card-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
  min-width: 0;
}
.promo__card-ico {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}
.promo__card-ico--green {
  background: #d1fae5;
  color: #059669;
}
.promo__card-ico--purple {
  background: #ede9fe;
  color: #7c3aed;
}
.promo__card-ico--grey {
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .promo__card-ico--green {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}
[data-theme='dark'] .promo__card-ico--purple {
  background: rgba(124, 58, 237, 0.15);
  color: #a78bfa;
}
[data-theme='dark'] .promo__card-ico--grey {
  background: rgba(255, 255, 255, 0.07);
}

.promo__card-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.promo__card-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.promo__card-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.promo__card-desc {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.promo__card-tags {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.promo__tag {
  padding: 0.2rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
  border: 1px solid rgba(0, 0, 0, 0.07);
}
[data-theme='dark'] .promo__tag {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.1);
}
.promo__tag--val {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  border-color: transparent;
}
[data-theme='dark'] .promo__tag--val {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}

.promo__code-chip {
  padding: 0.2rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 800;
  background: #111;
  color: #fff;
  font-family: monospace;
  letter-spacing: 0.08em;
}
[data-theme='dark'] .promo__code-chip {
  background: #e2e4e5;
  color: #111;
}

.promo__status {
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.promo__status--active {
  background: #d1fae5;
  color: #065f46;
}
.promo__status--draft {
  background: #f3f4f6;
  color: #6b7280;
}
.promo__status--paused {
  background: #fef3c7;
  color: #92400e;
}
.promo__status--muted {
  background: #f3f4f6;
  color: #9ca3af;
}
[data-theme='dark'] .promo__status--active {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}
[data-theme='dark'] .promo__status--draft {
  background: rgba(255, 255, 255, 0.07);
  color: #aaa;
}
[data-theme='dark'] .promo__status--paused {
  background: rgba(251, 191, 36, 0.12);
  color: #fbbf24;
}
[data-theme='dark'] .promo__status--muted {
  background: rgba(255, 255, 255, 0.07);
  color: #9ca3af;
}

.promo__card-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
}

.promo__toggle-wrap {
  cursor: pointer;
}
.promo__toggle {
  appearance: none;
  width: 36px;
  height: 20px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 9999px;
  position: relative;
  cursor: pointer;
  outline: none;
  transition: background 0.2s;
  flex-shrink: 0;
}
.promo__toggle:checked {
  background: #10b981;
}
.promo__toggle::before {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  background: #fff;
  transition: left 0.2s;
}
.promo__toggle:checked::before {
  left: 19px;
}

.promo__icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.promo__icon-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.promo__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-variant);
}
.promo__empty-ico-wrap {
  width: 4rem;
  height: 4rem;
  border-radius: 1.25rem;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-on-surface-variant);
  margin-bottom: 0.25rem;
}
[data-theme='dark'] .promo__empty-ico-wrap {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
.promo__empty-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.promo__empty-sub {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}

/* Modal */
.promo__overlay {
  position: fixed;
  inset: 0;
  z-index: 9100;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.promo__modal {
  width: 100%;
  max-width: 520px;
  border-radius: 1.25rem;
  background: var(--nxt-surface, #fff);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow: hidden;
}
[data-theme='dark'] .promo__modal {
  background: var(--nxt-surface-low, #141618);
}
.promo__modal-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
.promo__modal-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.promo__modal-close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.promo__modal-close:hover {
  background: rgba(0, 0, 0, 0.06);
}
.promo__modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.promo__mfield {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.promo__mlabel {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.promo__minput {
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
}
.promo__minput:focus {
  outline: none;
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .promo__minput {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.promo__mselect {
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
  cursor: pointer;
}
[data-theme='dark'] .promo__mselect {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.promo__mrow2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.promo__merror {
  font-size: 0.8125rem;
  color: #dc2626;
  background: #fee2e2;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  white-space: pre-line;
}
.promo__modal-ft {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
.promo__mbtn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
.promo__mbtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.promo__mbtn--ghost {
  background: transparent;
  border-color: var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  color: var(--nxt-on-surface);
}
.promo__mbtn--primary {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
.promo__mbtn--primary:hover:not(:disabled) {
  opacity: 0.82;
}
[data-theme='dark'] .promo__mbtn--primary {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}

/* ── Recommandations ─────────────────────────────────────────────────── */
.rec__wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem;
}
.rec__toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.rec__search-wrap {
  flex: 1;
  min-width: 200px;
  position: relative;
}
.rec__search-ico {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.rec__search {
  width: 100%;
  padding: 0.625rem 0.875rem 0.625rem 2.25rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
}
.rec__search:focus {
  outline: none;
  border-color: var(--nxt-on-surface, #111);
}
[data-theme='dark'] .rec__search {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.rec__kpi {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.625rem;
  background: var(--nxt-primary-container, #dbeafe);
  color: var(--nxt-on-primary-container, #1e40af);
  font-size: 0.8125rem;
  font-weight: 700;
  white-space: nowrap;
}
[data-theme='dark'] .rec__kpi {
  background: rgba(59, 130, 246, 0.15);
  color: #93c5fd;
}

.rec__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rec__row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  border-radius: 0.875rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  background: rgba(0, 0, 0, 0.015);
  transition:
    background 0.15s,
    border-color 0.15s;
}
.rec__row--on {
  border-color: rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.04);
}
[data-theme='dark'] .rec__row {
  background: rgba(255, 255, 255, 0.02);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .rec__row--on {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.07);
}

.rec__img-wrap {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--nxt-surface-variant, rgba(0, 0, 0, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;
}
.rec__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rec__img-ico {
  font-size: 1.125rem;
  color: var(--nxt-on-surface-variant);
}

.rec__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}
.rec__name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rec__price {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}

.rec__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}
[data-theme='dark'] .rec__badge {
  background: rgba(59, 130, 246, 0.18);
  color: #93c5fd;
}

.rec__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}
.rec__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.rec__btn--on {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
}
.rec__btn--on:hover:not(:disabled) {
  opacity: 0.8;
}
.rec__btn--off {
  background: transparent;
  border-color: var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  color: var(--nxt-on-surface);
}
.rec__btn--off:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .rec__btn--on {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}
[data-theme='dark'] .rec__btn--off {
  border-color: rgba(255, 255, 255, 0.12);
}

.rec__row--skel {
  pointer-events: none;
}

.rec__pagesize {
  padding: 0.5rem 0.75rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
  cursor: pointer;
}
[data-theme='dark'] .rec__pagesize {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.rec__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
}
.rec__empty i {
  font-size: 2rem;
}
.rec__empty p {
  font-size: 0.875rem;
}

.rec__pag {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  padding: 0.25rem 0;
}
.rec__pag-btn {
  min-width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.5rem;
  transition: all 0.15s;
  font-family: inherit;
}
[data-theme='dark'] .rec__pag-btn {
  background: rgba(255, 255, 255, 0.04);
}
.rec__pag-btn:hover:not(:disabled) {
  background: var(--nxt-surface-mid, rgba(0, 0, 0, 0.06));
}
[data-theme='dark'] .rec__pag-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
.rec__pag-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.rec__pag-btn--active {
  background: var(--nxt-on-surface, #111);
  color: var(--nxt-surface, #fff);
  border-color: transparent;
}
[data-theme='dark'] .rec__pag-btn--active {
  background: var(--nxt-on-surface, #e2e4e5);
  color: #141618;
}
.rec__pag-ellipsis {
  padding: 0 0.25rem;
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
}
.rec__pag-info {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}

/* ── Tab Modules ──────────────────────────────────────────────────────────── */
.cdp__mod {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.cdp__mod-banner {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.125rem 1.25rem;
  background: rgba(146, 76, 0, 0.06);
  border: 1px solid rgba(146, 76, 0, 0.18);
  border-radius: 1rem;
}
.cdp__mod-banner-ico {
  font-size: 1.125rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
  margin-top: 0.1rem;
}
.cdp__mod-banner-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-primary);
  margin-bottom: 0.25rem;
}
.cdp__mod-banner-sub {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
[data-theme='dark'] .cdp__mod-banner {
  background: rgba(255, 214, 10, 0.1);
  border-color: rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cdp__mod-banner-ico,
[data-theme='dark'] .cdp__mod-banner-title {
  color: #ffd60a;
}

.cdp__mod-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.cdp__mod-card {
  border-radius: 1.125rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: opacity 0.2s;
}
.cdp__mod-card--on {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: var(--nxt-shadow-sm);
}
.cdp__mod-card--off {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: var(--nxt-shadow-sm);
}
.cdp__mod-card--disabled {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  opacity: 0.65;
  filter: grayscale(0.4);
}
[data-theme='dark'] .cdp__mod-card--on,
[data-theme='dark'] .cdp__mod-card--off {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__mod-card--disabled {
  background: rgba(255, 255, 255, 0.02);
}

.cdp__mod-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}
.cdp__mod-settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  transition: border-color 0.15s;
}
.cdp__mod-settings-btn:hover {
  border-color: #ffd60a;
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .cdp__mod-settings-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.cdp__mod-settings-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cdp__mod-back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  width: fit-content;
}
.cdp__mod-back:hover {
  text-decoration: underline;
}
.cdp__mod-settings-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
  letter-spacing: -0.02em;
}
.cdp__mod-card-ico {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .cdp__mod-card-ico {
  background: rgba(255, 255, 255, 0.06);
}
.cdp__mod-card-ico--on {
  background: rgba(146, 76, 0, 0.12);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__mod-card-ico--on {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}

.cdp__mod-badge {
  font-size: 0.5625rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
}
.cdp__mod-badge--on {
  background: rgba(16, 181, 16, 0.1);
  color: #10b510;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cdp__mod-badge--off {
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface-variant);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.cdp__mod-card-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__mod-card-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.45;
  flex: 1;
}
.cdp__mod-card-ft {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.25rem;
}
.cdp__mod-link {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.cdp__mod-link:hover {
  text-decoration: underline;
}
.cdp__mod-link--dim {
  color: var(--nxt-on-surface-variant);
  font-weight: 400;
}

.cdp__mod-toggle {
  width: 2.75rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.18));
  display: flex;
  align-items: center;
  padding: 0.1875rem;
  transition: background 0.2s;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
}
.cdp__mod-toggle--on {
  background: #10b510;
}
.cdp__mod-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.cdp__mod-toggle--pending {
  opacity: 0.7;
}
.cdp__mod-toggle-thumb {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s;
  transform: translateX(0);
}
.cdp__mod-toggle--on .cdp__mod-toggle-thumb {
  transform: translateX(1.375rem);
}

.cdp__mod-section {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.125rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: var(--nxt-shadow-sm);
}
.cdp__mod-section--row {
  flex-direction: row;
  align-items: flex-start;
  gap: 1.25rem;
}
.cdp__mod-section--highlight {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.35);
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;
}
.cdp__mod-section-hd {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.cdp__mod-section-ico {
  font-size: 1.0625rem;
  color: var(--nxt-primary);
}
.cdp__mod-section-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__mod-section-desc {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.55;
}

.cdp__mod-slug-row {
  display: flex;
  align-items: center;
  height: 3rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  border-radius: 0.625rem;
  overflow: hidden;
}
.cdp__mod-slug-prefix,
.cdp__mod-slug-suffix {
  padding: 0 0.875rem;
  background: rgba(0, 0, 0, 0.03);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: monospace;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: 100%;
  border-right: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
}
.cdp__mod-slug-suffix {
  border-right: none;
  border-left: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
}
.cdp__mod-slug-input {
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-primary);
  font-family: inherit;
}
.cdp__mod-slug-input:focus {
  outline: none;
}
.cdp__mod-slug-btn {
  height: 100%;
  padding: 0 1.25rem;
  background: var(
    --nxt-gradient-primary,
    linear-gradient(92deg, #fbaf01 0%, #f89303 100%)
  );
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.cdp__mod-slug-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cdp__mod-slug-feedback {
  min-height: 1.5rem;
}
.cdp__mod-slug-ok {
  font-size: 0.8125rem;
  color: #10b510;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.cdp__mod-slug-err {
  font-size: 0.8125rem;
  color: var(--nxt-error, #dc2626);
}

.cdp__mod-num-ico {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.375rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
}
.cdp__mod-num-body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}
.cdp__mod-num-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
}
.cdp__mod-num-desc {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}

.cdp__mod-slug-row--ok {
  border-color: #10b510 !important;
}
.cdp__mod-slug-row--err {
  border-color: #dc2626 !important;
}
.cdp__mod-slug-btn--ok {
  background: #10b510 !important;
}
.cdp__mod-slug-btn--err {
  background: #dc2626 !important;
}

.cdp__mod-slug-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cdp__mod-slug-chip {
  font-size: 0.8125rem;
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  cursor: pointer;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: rgba(0, 0, 0, 0.02);
  color: var(--nxt-on-surface);
  transition:
    border-color 0.15s,
    color 0.15s;
}
.cdp__mod-slug-chip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.cdp__mod-slug-hint {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .cdp__mod-section {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__mod-section-ico,
[data-theme='dark'] .cdp__mod-slug-input,
[data-theme='dark'] .cdp__icode,
[data-theme='dark'] .cdp__mod-num-ico {
  color: #ffd60a;
}
[data-theme='dark'] .cdp__mod-slug-prefix,
[data-theme='dark'] .cdp__mod-slug-suffix,
[data-theme='dark'] .cdp__mod-num-ico {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__mod-slug-row {
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__icode {
  background: rgba(255, 214, 10, 0.12);
  border-color: rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .cdp__mod-slug-btn {
  background: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cdp__mod-slug-btn--ok {
  background: #10b510 !important;
  color: #fff !important;
}
[data-theme='dark'] .cdp__mod-slug-btn--err {
  background: #dc2626 !important;
  color: #fff !important;
}
[data-theme='dark'] .cdp__mod-slug-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .cdp__mod-slug-chip:hover {
  border-color: #ffd60a;
  color: #ffd60a;
}

/* ─── Tab Médias ──────────────────────────────────────────────────────────── */
.cdp__med {
  padding: 1.5rem 0;
}
.cdp__med-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.cdp__med-section {
  background: #fff;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 1rem;
  padding: 1.375rem 1.5rem;
}
[data-theme='dark'] .cdp__med-section {
  background: rgba(255, 255, 255, 0.04);
}

.cdp__med-section-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  gap: 1rem;
}
.cdp__med-section-hd-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.cdp__med-section-hd-left i {
  font-size: 1.125rem;
  color: var(--nxt-on-surface-variant);
}
.cdp__med-section-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__med-count {
  font-weight: 400;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
}

.cdp__med-add-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: rgba(0, 0, 0, 0.04);
  color: var(--nxt-on-surface);
  transition: all 0.15s;
}
[data-theme='dark'] .cdp__med-add-btn {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__med-add-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__med-add-btn:hover:not(:disabled) {
  border-color: #ffd60a;
  color: #ffd60a;
}
.cdp__med-add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__med-add-btn i {
  font-size: 0.8rem;
}

/* Liste items */
.cdp__med-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cdp__med-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  background: #fff;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.cdp__med-item:hover {
  border-color: var(--nxt-primary);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}
.cdp__med-item--editing {
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__med-item {
  background: rgba(255, 255, 255, 0.03);
}

/* Preview */
.cdp__med-item-preview {
  width: 5rem;
  height: 5rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
[data-theme='dark'] .cdp__med-item-preview {
  background: rgba(255, 255, 255, 0.06);
}
.cdp__med-item-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cdp__med-item-preview i {
  font-size: 1.5rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.3;
}

.cdp__med-item-preview--logo {
  width: 4.5rem;
  height: 4.5rem;
  padding: 0.5rem;
}
.cdp__med-item-preview--logo img {
  object-fit: contain;
}
.cdp__med-item-preview--dark {
  background: #0c1421;
}
.cdp__med-item-preview--light {
  background: #f4f4f6;
}

.cdp__med-item-preview--banner {
  width: 8rem;
  height: 4rem;
}
.cdp__med-item-preview--banner img {
  object-fit: cover;
}

/* Info */
.cdp__med-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}
.cdp__med-item-order {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-primary);
}
.cdp__med-item-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdp__med-item-desc {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cdp__med-item-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
}
.cdp__med-item-badge--active {
  background: rgba(16, 181, 16, 0.1);
  color: #15803d;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cdp__med-item-badge--draft {
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
[data-theme='dark'] .cdp__med-item-badge--active {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
}

.cdp__med-item-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* Actions */
.cdp__med-item-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.cdp__med-icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.cdp__med-icon-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__med-icon-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}

.cdp__med-config-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.45rem 0.875rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-primary);
  color: var(--nxt-primary);
  background: transparent;
  transition: all 0.15s;
}
.cdp__med-config-btn:hover {
  background: var(--nxt-primary);
  color: #fff;
}
.cdp__med-config-btn--active {
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__med-config-btn {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .cdp__med-config-btn:hover,
[data-theme='dark'] .cdp__med-config-btn--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}

.cdp__med-del-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  transition:
    background 0.15s,
    color 0.15s;
}
.cdp__med-del-btn:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}
.cdp__med-del-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Vide */
.cdp__med-empty {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1.5px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  font-style: italic;
}
.cdp__med-empty i {
  font-size: 1.25rem;
  opacity: 0.4;
}

/* Confirmation suppression inline */
.cdp__inline-confirm {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  background: rgba(220, 38, 38, 0.06);
  border: 1px solid rgba(220, 38, 38, 0.2);
  margin-top: -0.375rem;
}
.cdp__inline-confirm-icon {
  font-size: 1rem;
  color: #dc2626;
  flex-shrink: 0;
}
.cdp__inline-confirm span {
  flex: 1;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  font-weight: 500;
}
.cdp__inline-confirm-btns {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.cdp__inline-confirm-btn-sm {
  padding: 0.35rem 0.875rem;
  font-size: 0.8125rem;
}
.cdp__inline-confirm-btn-del {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.875rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border: none;
  background: #dc2626;
  color: #fff;
  transition: opacity 0.15s;
}
.cdp__inline-confirm-btn-del:hover:not(:disabled) {
  opacity: 0.85;
}
.cdp__inline-confirm-btn-del:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Formulaire ajout inline */
.cdp__add-form {
  margin-bottom: 1.25rem;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
[data-theme='dark'] .cdp__add-form {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__add-form-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__add-form-row {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
}
.cdp__add-form-preview {
  width: 12rem;
  min-width: 12rem;
  height: 7rem;
  border-radius: 0.75rem;
  flex-shrink: 0;
  border: 1.5px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s;
}
[data-theme='dark'] .cdp__add-form-preview {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__add-form-preview:hover {
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__add-form-preview:hover {
  border-color: #ffd60a;
}
.cdp__add-form-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cdp__add-form-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem;
  text-align: center;
}
.cdp__add-form-preview-empty i {
  font-size: 1.5rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.3;
}
.cdp__add-form-preview-empty span {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.cdp__add-form-preview-hint {
  font-size: 0.625rem !important;
  font-weight: 400 !important;
  opacity: 0.6;
}
.cdp__add-form-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
.cdp__add-form-footer {
  display: flex;
  gap: 0.625rem;
  justify-content: flex-end;
}
@media (max-width: 640px) {
  .cdp__add-form-row {
    flex-direction: column;
  }
  .cdp__add-form-preview {
    width: 100%;
    min-width: unset;
  }
}

/* Boutons réordonnement */
.cdp__med-item-order-btns {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;
}
.cdp__med-order-btn {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.25rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
  font-size: 0.5rem;
  transition:
    background 0.15s,
    color 0.15s;
}
.cdp__med-order-btn:hover:not(:disabled) {
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__med-order-btn:hover:not(:disabled) {
  background: #ffd60a;
  color: #0b1320;
}
.cdp__med-order-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.cdp__med-item-order-num {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--nxt-primary);
  min-width: 1rem;
  text-align: center;
}

/* Copié feedback */
.cdp__med-icon-btn--copied {
  color: #16a34a !important;
  background: rgba(22, 163, 74, 0.1) !important;
}

/* Fichiers grid */
.cdp__med-files-hd-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
}
.cdp__med-files-search {
  position: relative;
  display: flex;
  align-items: center;
}
.cdp__med-files-search-icon {
  position: absolute;
  left: 0.625rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.5;
  pointer-events: none;
}
.cdp__med-files-search-input {
  padding: 0.4rem 0.75rem 0.4rem 1.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: rgba(0, 0, 0, 0.04);
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  width: 14rem;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.cdp__med-files-search-input:focus {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12);
}
[data-theme='dark'] .cdp__med-files-search-input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.15);
}
[data-theme='dark'] .cdp__med-files-search-input {
  background: rgba(255, 255, 255, 0.05);
}

.cdp__med-files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.75rem;
}
.cdp__med-file-card {
  border-radius: 0.875rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: default;
  transition:
    border-color 0.18s,
    box-shadow 0.18s,
    transform 0.18s;
}
.cdp__med-file-card:hover {
  border-color: var(--nxt-primary);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}
[data-theme='dark'] .cdp__med-file-card {
  background: rgba(255, 255, 255, 0.04);
}

/* Preview zone */
.cdp__med-file-preview {
  position: relative;
  height: 8.5rem;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}
[data-theme='dark'] .cdp__med-file-preview {
  background: rgba(255, 255, 255, 0.06);
}

/* File type icon */
.cdp__med-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.cdp__med-file-icon i {
  font-size: 2.5rem;
  color: var(--nxt-primary);
  opacity: 0.25;
}

/* Extension badge (bottom-left corner) */
.cdp__med-file-ext {
  position: absolute;
  bottom: 0.4rem;
  left: 0.4rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  border-radius: 0.3rem;
  padding: 0.15rem 0.4rem;
  line-height: 1.4;
  backdrop-filter: blur(4px);
}

/* Actions overlay (appears on hover) */
.cdp__med-file-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(2px);
  opacity: 0;
  transition: opacity 0.18s;
}
.cdp__med-file-card:hover .cdp__med-file-overlay {
  opacity: 1;
}
.cdp__med-overlay-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: #111;
  font-size: 0.8rem;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
  text-decoration: none;
  flex-shrink: 0;
}
.cdp__med-overlay-btn:hover {
  background: #fff;
  transform: scale(1.08);
}
.cdp__med-overlay-btn--ok {
  background: #dcfce7;
  color: #16a34a;
}
.cdp__med-overlay-btn--del {
  background: rgba(255, 230, 230, 0.95);
  color: #dc2626;
}
.cdp__med-overlay-btn--del:hover {
  background: #fee2e2;
}

/* Info zone */
.cdp__med-file-info {
  padding: 0.55rem 0.65rem 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}
.cdp__med-file-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.35;
}
.cdp__med-file-date {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
}
.cdp__med-file-date i {
  font-size: 0.6rem;
}

/* Pagination fichiers */
.cdp__med-files-pager {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1rem;
  flex-wrap: wrap;
}
.cdp__med-pager-sizes {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.5rem;
}
.cdp__med-pager-size-btn {
  padding: 0.25rem 0.55rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  background: transparent;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.cdp__med-pager-size-btn:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.cdp__med-pager-size-btn--active {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__med-pager-size-btn--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
.cdp__med-pager-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.15s;
}
[data-theme='dark'] .cdp__med-pager-btn {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__med-pager-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__med-pager-btn:hover:not(:disabled) {
  border-color: #ffd60a;
  color: #ffd60a;
}
.cdp__med-pager-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.cdp__med-pager-info {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}

/* Modal édition */
.cdp__modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 800;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cdp__modal {
  width: 100%;
  max-width: 30rem;
  border-radius: 1rem;
  background: #fff;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
[data-theme='dark'] .cdp__modal {
  background: var(--nxt-surface-mid, #1a1c1e);
}

.cdp__modal-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.375rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
.cdp__modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cdp__modal-close {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  transition: background 0.15s;
}
.cdp__modal-close:hover {
  background: rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .cdp__modal-close:hover {
  background: rgba(255, 255, 255, 0.08);
}

.cdp__modal-body {
  padding: 1.25rem 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__modal-ft {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 0.875rem 1.375rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}

/* Champs formulaire */
.cdp__med-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cdp__med-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.cdp__med-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.cdp__med-input {
  padding: 0.55rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: rgba(0, 0, 0, 0.03);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.cdp__med-input:focus {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
[data-theme='dark'] .cdp__med-input {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__med-textarea {
  resize: vertical;
  min-height: 5rem;
}

/* Toggle */
.cdp__med-toggle-label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  user-select: none;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
}
.cdp__med-toggle {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.2));
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}
.cdp__med-toggle--on {
  background: var(--nxt-primary, #6366f1);
}
[data-theme='dark'] .cdp__med-toggle--on {
  background: #ffd60a;
}
.cdp__med-toggle-thumb {
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}
.cdp__med-toggle--on .cdp__med-toggle-thumb {
  transform: translateX(1.125rem);
}

/* Boutons modal */
.cdp__med-btn-ghost {
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant);
  background: transparent;
  color: var(--nxt-on-surface-variant);
  transition: all 0.15s;
}
.cdp__med-btn-ghost:hover {
  border-color: var(--nxt-on-surface-variant);
  color: var(--nxt-on-surface);
}
.cdp__med-btn-primary {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  border: none;
  background: var(--nxt-primary, #6366f1);
  color: #fff;
  transition: opacity 0.15s;
}
.cdp__med-btn-primary:hover:not(:disabled) {
  opacity: 0.88;
}
.cdp__med-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Erreur formulaire */
.cdp__med-form-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(220, 38, 38, 0.07);
  border: 1px solid rgba(220, 38, 38, 0.18);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 500;
}

/* ─── Tab Horaires ────────────────────────────────────────────────────────── */
.cdp__hor {
  padding: 1.5rem 0;
}

.cdp__hor-layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.5rem;
  align-items: start;
}
@media (max-width: 900px) {
  .cdp__hor-layout {
    grid-template-columns: 1fr;
  }
}

.cdp__hor-col-main,
.cdp__hor-col-side {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Section card */
.cdp__hor-section {
  background: #fff;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 1rem;
  padding: 1.375rem 1.5rem;
}
[data-theme='dark'] .cdp__hor-section {
  background: rgba(255, 255, 255, 0.04);
}

.cdp__hor-section-hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.cdp__hor-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0 0 0.2rem;
}
.cdp__hor-section-sub {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
}

/* Lignes jours */
.cdp__hor-days {
  display: flex;
  flex-direction: column;
}

.cdp__hor-day-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.8125rem 1rem;
  border-radius: 0.625rem;
  transition: background 0.12s;
}
.cdp__hor-day-row:hover {
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .cdp__hor-day-row:hover {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__hor-day-row--off {
  opacity: 0.6;
}

.cdp__hor-day-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  min-width: 5.5rem;
  flex-shrink: 0;
}

.cdp__hor-time {
  flex: 1;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}

.cdp__hor-day-empty {
  flex: 1;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  font-style: italic;
}

/* Pills ouvert/fermé */
.cdp__hor-pill {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
  text-transform: uppercase;
}
.cdp__hor-pill--open {
  background: rgba(16, 181, 16, 0.1);
  color: #15803d;
  border: 1px solid rgba(16, 181, 16, 0.2);
}
.cdp__hor-pill--closed {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.15);
}
.cdp__hor-pill--special {
  background: rgba(99, 102, 241, 0.1);
  color: #4f46e5;
  border: 1px solid rgba(99, 102, 241, 0.2);
}
[data-theme='dark'] .cdp__hor-pill--open {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
  border-color: rgba(74, 222, 128, 0.2);
}
[data-theme='dark'] .cdp__hor-pill--closed {
  color: #f87171;
  background: rgba(248, 113, 113, 0.1);
  border-color: rgba(248, 113, 113, 0.2);
}

/* État vide exceptionnel */
.cdp__hor-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem 1.5rem;
  text-align: center;
  border: 2px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.02);
}
[data-theme='dark'] .cdp__hor-empty-state {
  background: rgba(255, 255, 255, 0.03);
}
.cdp__hor-empty-ico {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  font-size: 1.5rem;
  opacity: 0.5;
}
[data-theme='dark'] .cdp__hor-empty-ico {
  background: rgba(255, 255, 255, 0.07);
}
.cdp__hor-empty-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
.cdp__hor-empty-desc {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.7;
}

/* Carte pause */
.cdp__hor-pause--on {
}
.cdp__hor-pause--off {
}

.cdp__hor-pause-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
}
.cdp__hor-pause-hd-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface);
}
.cdp__hor-pause-hd-left i {
  font-size: 1.125rem;
  color: var(--nxt-primary);
}

.cdp__hor-pause-desc {
  margin: 0 0 0.875rem;
  font-size: 0.8375rem;
  line-height: 1.6;
  color: var(--nxt-on-surface-variant);
}

.cdp__hor-pause-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8375rem;
  font-weight: 600;
  padding: 0.6rem 0.875rem;
  border-radius: 0.625rem;
}
.cdp__hor-pause-status--on {
  background: rgba(16, 181, 16, 0.1);
  color: #15803d;
}
.cdp__hor-pause-status--off {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
}
[data-theme='dark'] .cdp__hor-pause-status--on {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.08);
}
[data-theme='dark'] .cdp__hor-pause-status--off {
  color: #f87171;
  background: rgba(248, 113, 113, 0.08);
}

.cdp__hor-pause-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* Tips */
.cdp__hor-tips-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface);
  margin-bottom: 1rem;
}
.cdp__hor-tips-hd i {
  color: var(--nxt-primary);
  font-size: 1.125rem;
}

.cdp__hor-tips-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.cdp__hor-tip {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}
.cdp__hor-tip p {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.55;
}

.cdp__hor-tip-num {
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--nxt-primary-container, #ffdcc4);
  color: var(--nxt-primary);
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Bouton "Ajouter" dans le header */
.cdp__hor-add-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.45rem 0.9rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: rgba(0, 0, 0, 0.04);
  color: var(--nxt-on-surface);
  transition: all 0.15s;
  flex-shrink: 0;
}
[data-theme='dark'] .cdp__hor-add-btn {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__hor-add-btn:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(146, 76, 0, 0.06);
}
[data-theme='dark'] .cdp__hor-add-btn:hover {
  border-color: #ffd60a;
  color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
}
.cdp__hor-add-btn i {
  font-size: 0.7rem;
}

/* Formulaire ajout exceptionnel */
.cdp__hor-special-form {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  border-radius: 0.75rem;
}
[data-theme='dark'] .cdp__hor-special-form {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__hor-sf-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.cdp__hor-sf-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 8rem;
}
.cdp__hor-sf-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.cdp__hor-sf-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  border-radius: 0.4rem;
  font-size: 0.8125rem;
  font-family: inherit;
  background: #fff;
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.cdp__hor-sf-input:focus {
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__hor-sf-input {
  background: rgba(255, 255, 255, 0.06);
}

/* Créneaux multiples dans le formulaire exceptionnel */
.cdp__hor-sf-slots {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cdp__hor-sf-slots-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.cdp__hor-sf-add-slot {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.2));
  background: transparent;
  color: var(--nxt-primary);
  border-radius: 0.375rem;
  padding: 0.25rem 0.6rem;
  transition: all 0.15s;
}
.cdp__hor-sf-add-slot:hover {
  background: rgba(146, 76, 0, 0.06);
}
[data-theme='dark'] .cdp__hor-sf-add-slot:hover {
  background: rgba(255, 214, 10, 0.1);
}
.cdp__hor-sf-add-slot i {
  font-size: 0.65rem;
}

.cdp__hor-sf-slot-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
}
[data-theme='dark'] .cdp__hor-sf-slot-row {
  background: rgba(255, 255, 255, 0.04);
}

.cdp__hor-sf-slot-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.cdp__hor-sf-input--time {
  width: 7rem;
  flex-shrink: 0;
}

.cdp__hor-sf-closed-msg {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(239, 68, 68, 0.06);
  color: #dc2626;
  font-size: 0.8125rem;
  border: 1px solid rgba(239, 68, 68, 0.15);
}
.cdp__hor-sf-closed-msg i {
  flex-shrink: 0;
}
[data-theme='dark'] .cdp__hor-sf-closed-msg {
  color: #f87171;
  background: rgba(248, 113, 113, 0.08);
}

.cdp__hor-sf-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1px solid rgba(239, 68, 68, 0.2);
}
.cdp__hor-sf-error i {
  flex-shrink: 0;
}
[data-theme='dark'] .cdp__hor-sf-error {
  color: #f87171;
  background: rgba(248, 113, 113, 0.08);
  border-color: rgba(248, 113, 113, 0.2);
}

.cdp__hor-sf-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.cdp__hor-sf-cancel {
  padding: 0.4rem 0.875rem;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: transparent;
  color: var(--nxt-on-surface-variant);
  transition: all 0.15s;
}
.cdp__hor-sf-cancel:hover {
  background: rgba(0, 0, 0, 0.04);
}
.cdp__hor-sf-save {
  padding: 0.4rem 1rem;
  border-radius: 0.4rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  border: none;
  background: var(--nxt-primary);
  color: #fff;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
[data-theme='dark'] .cdp__hor-sf-save {
  background: #ffd60a;
  color: #0b1320;
}
.cdp__hor-sf-save:hover:not(:disabled) {
  filter: brightness(1.1);
}
.cdp__hor-sf-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bouton supprimer exceptionnel */
.cdp__hor-day-row--special {
  position: relative;
}
.cdp__hor-del-btn {
  flex-shrink: 0;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  opacity: 0;
  transition:
    opacity 0.15s,
    background 0.15s,
    color 0.15s;
}
.cdp__hor-day-row--special:hover .cdp__hor-del-btn {
  opacity: 1;
}
.cdp__hor-del-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}
.cdp__hor-del-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Édition inline */
.cdp__hor-day-row--clickable {
  cursor: pointer;
}
.cdp__hor-day-row--clickable:hover .cdp__hor-edit-ico {
  opacity: 1;
}
.cdp__hor-day-row--editing {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 0.625rem;
  cursor: default;
}

.cdp__hor-edit-ico {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
  margin-left: auto;
}

.cdp__hor-day-name--editing {
  color: var(--nxt-primary);
}

.cdp__hor-edit-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex: 1;
  flex-wrap: wrap;
}

.cdp__hor-edit-toggle {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
}
.cdp__hor-edit-toggle input {
  accent-color: var(--nxt-primary);
  width: 0.9rem;
  height: 0.9rem;
  cursor: pointer;
}

.cdp__hor-edit-time {
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  border-radius: 0.4rem;
  font-size: 0.8125rem;
  font-family: inherit;
  background: #fff;
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
  width: 7rem;
}
.cdp__hor-edit-time:focus {
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__hor-edit-time {
  background: rgba(255, 255, 255, 0.06);
}

.cdp__hor-edit-slot-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cdp__hor-time-sep {
  margin: 0 0.4rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
}

.cdp__hor-edit-sep {
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}

.cdp__hor-edit-save,
.cdp__hor-edit-cancel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.4rem;
  border: none;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cdp__hor-edit-save {
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__hor-edit-save {
  background: #ffd60a;
  color: #0b1320;
}
.cdp__hor-edit-save:hover:not(:disabled) {
  filter: brightness(1.1);
}
.cdp__hor-edit-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.cdp__hor-edit-cancel {
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .cdp__hor-edit-cancel {
  background: rgba(255, 255, 255, 0.07);
}
.cdp__hor-edit-cancel:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}
[data-theme='dark'] .cdp__hor-edit-cancel:hover {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
}

/* Résumé stats */
.cdp__hor-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  margin-top: 0.25rem;
}
.cdp__hor-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.75rem 0.5rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.04);
  text-align: center;
}
[data-theme='dark'] .cdp__hor-stat {
  background: rgba(255, 255, 255, 0.05);
}
.cdp__hor-stat-val {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--nxt-primary);
  line-height: 1;
}
[data-theme='dark'] .cdp__hor-stat-val {
  color: #ffd60a;
}
.cdp__hor-stat-lbl {
  font-size: 0.7rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.3;
}

/* Journal d'activité */
.cdp__hor-journal-list {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  margin-top: 0.25rem;
}

.cdp__hor-journal-row {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
}
.cdp__hor-journal-row:last-child {
  border-bottom: none;
}

.cdp__hor-journal-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 0.3rem;
}
.cdp__hor-journal-dot--blue {
  background: #3b82f6;
}
.cdp__hor-journal-dot--red {
  background: #ef4444;
}
.cdp__hor-journal-dot--purple {
  background: #8b5cf6;
}

.cdp__hor-journal-body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}
.cdp__hor-journal-label {
  font-size: 0.7875rem;
  font-weight: 500;
  color: var(--nxt-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cdp__hor-journal-time {
  font-size: 0.7rem;
  color: var(--nxt-on-surface-variant);
}

.cdp__hor-journal-empty {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  font-style: italic;
  padding: 0.5rem 0.25rem;
  text-align: center;
}

/* Skeleton inside hor */
.cdp__hor .cdp__skel {
  display: inline-block;
  border-radius: 0.25rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
[data-theme='dark'] .cdp__hor .cdp__skel {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.06) 75%
  );
  background-size: 200% 100%;
}

/* ── Formulaire "Nouveau gestionnaire" ─────────────────────────────────── */
.cdp__uf {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.09));
  border-radius: 1rem;
  overflow: hidden;
  margin-bottom: 0.25rem;
}
[data-theme='dark'] .cdp__uf {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Header */
.cdp__uf-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.125rem 1.25rem;
  background: var(--nxt-surface-low, #f7f7f8);
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
}
[data-theme='dark'] .cdp__uf-header {
  background: rgba(255, 255, 255, 0.04);
}
.cdp__uf-avatar-preview {
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.3s ease;
}
.cdp__uf-avatar-preview--img {
  object-fit: cover;
}
.cdp__uf-photo-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.cdp__uf-photo-preview {
  width: 3.5rem;
  height: 3.5rem;
  min-width: 3.5rem;
  border-radius: 50%;
  object-fit: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
}
.cdp__uf-photo-preview--empty {
  letter-spacing: 0.02em;
}
.cdp__uf-photo-input {
  display: none;
}
.cdp__uf-header-text {
  flex: 1;
  min-width: 0;
}
.cdp__uf-header-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0 0 0.15rem;
  letter-spacing: -0.01em;
}
.cdp__uf-header-sub {
  font-size: 0.775rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
  line-height: 1.4;
}
.cdp__uf-close {
  width: 2rem;
  height: 2rem;
  border-radius: 0.45rem;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  transition:
    background 0.14s,
    color 0.14s;
  flex-shrink: 0;
}
.cdp__uf-close:hover {
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .cdp__uf-close:hover {
  background: rgba(255, 255, 255, 0.08);
}

/* Corps */
.cdp__uf-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cdp__uf-section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.cdp__uf-section-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
  margin: 0;
}
.cdp__uf-section-label .pi {
  font-size: 0.75rem;
}

.cdp__uf-sep {
  height: 1px;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  margin: 1rem 0;
}

/* Champs */
.cdp__uf-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
@media (max-width: 520px) {
  .cdp__uf-grid2 {
    grid-template-columns: 1fr;
  }
}

.cdp__uf-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.cdp__uf-label {
  font-size: 0.775rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  letter-spacing: 0.01em;
}
.cdp__uf-required {
  color: var(--nxt-error, #ba1a1a);
  margin-left: 0.1rem;
}

.cdp__uf-input {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  background: var(--nxt-card-bg, #fff);
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  box-sizing: border-box;
}
.cdp__uf-input:focus {
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .cdp__uf-input:focus {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06);
}
.cdp__uf-input::placeholder {
  color: var(--nxt-on-surface-variant);
  opacity: 0.45;
}

/* Sélecteur de rôle — radio visuels */
.cdp__uf-role-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.cdp__uf-role-opt {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.625rem;
  cursor: pointer;
  transition:
    border-color 0.14s,
    background 0.14s;
  user-select: none;
}
.cdp__uf-role-opt:hover {
  border-color: var(--nxt-primary);
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.03));
}
.cdp__uf-role-opt--sel {
  border-color: var(--nxt-primary);
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.03));
}
.cdp__uf-role-opt--none {
  opacity: 0.7;
}
.cdp__uf-role-opt--none:hover {
  opacity: 1;
}

.cdp__uf-role-radio {
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  border-radius: 50%;
  border: 1.75px solid var(--nxt-outline-variant, #ccc);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.14s,
    background 0.14s;
  flex-shrink: 0;
  position: relative;
}
.cdp__uf-role-radio--on {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
}
[data-theme='dark'] .cdp__uf-role-radio--on {
  border-color: #ffd60a;
  background: #ffd60a;
}
.cdp__uf-role-radio--on::after {
  content: '';
  width: 0.35rem;
  height: 0.35rem;
  border-radius: 50%;
  background: #fff;
  position: absolute;
}
[data-theme='dark'] .cdp__uf-role-radio--on::after {
  background: #0b1320;
}

.cdp__uf-role-checkbox {
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  border-radius: 0.25rem;
  border: 1.75px solid var(--nxt-outline-variant, #ccc);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.14s,
    background 0.14s;
  flex-shrink: 0;
  font-size: 0.625rem;
  color: #fff;
}
.cdp__uf-role-checkbox--on {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
}
[data-theme='dark'] .cdp__uf-role-checkbox--on {
  border-color: #ffd60a;
  background: #ffd60a;
  color: #0b1320;
}

.cdp__uf-role-name {
  font-size: 0.8375rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  flex: 1;
}
.cdp__uf-role-code {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
  padding: 0.15rem 0.4rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.05));
  border-radius: 0.25rem;
}
.cdp__uf-role-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
}

/* Erreur */
.cdp__uf-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 1.25rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(186, 26, 26, 0.07);
  color: var(--nxt-error, #ba1a1a);
  font-size: 0.8125rem;
  font-weight: 600;
}
.cdp__uf-error .pi {
  font-size: 0.875rem;
}

/* Footer */
.cdp__uf-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.625rem;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
  margin-top: 1rem;
}
[data-theme='dark'] .cdp__uf-footer {
  background: rgba(255, 255, 255, 0.025);
}

.cdp__uf-submit {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.cdp__uf-submit .pi {
  font-size: 0.875rem;
}
.cdp__uf-submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Variante édition — sans marge de séparation hors grille */
.cdp__uf--edit {
  margin-top: 0.25rem;
}

/* États chargement / vide des rôles dans le formulaire */
.cdp__uf-roles-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.03));
  border-radius: 0.5rem;
}
.cdp__uf-roles-empty {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  font-size: 0.8rem;
  color: var(--nxt-on-surface-variant);
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.03));
  border-radius: 0.5rem;
  line-height: 1.45;
}
.cdp__uf-roles-empty .pi {
  margin-top: 0.075rem;
  flex-shrink: 0;
  opacity: 0.6;
}

/* ── Formulaire rôle ──────────────────────────────────────────────────── */
/* Icône dans le header (à la place de l'avatar rond) */
.cdp__uf-header--role {
  align-items: center;
}
.cdp__uf-role-icon {
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: 0.625rem;
  background: var(--nxt-primary, #111);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.cdp__uf-header-sub--mono {
  font-family: ui-monospace, 'Cascadia Code', 'Fira Code', monospace;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  color: var(--nxt-primary, #555);
  opacity: 0.85;
}

/* Input monospace pour le code */
.cdp__uf-input--mono {
  font-family: ui-monospace, 'Cascadia Code', 'Fira Code', monospace;
  font-size: 0.8375rem;
  letter-spacing: 0.03em;
}

/* Hint sous les inputs */
.cdp__uf-hint {
  font-size: 0.7rem;
  color: var(--nxt-on-surface-variant);
  opacity: 0.6;
  line-height: 1.3;
  margin-top: 0.15rem;
}

/* Ligne statut (toggle + texte) */
.cdp__uf-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.875rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.03));
  border-radius: 0.625rem;
}
.cdp__uf-status-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

/* Toggle repensé avec label intégré */
.cdp__uf-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.35rem 0.35rem;
  border: 1.5px solid var(--nxt-outline-variant, #ddd);
  border-radius: 9999px;
  cursor: pointer;
  background: none;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
  transition:
    border-color 0.18s,
    background 0.18s,
    color 0.18s;
  white-space: nowrap;
}
.cdp__uf-toggle:hover {
  border-color: var(--nxt-primary);
}
.cdp__uf-toggle--on {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__uf-toggle--on {
  border-color: #ffd60a;
  background: #ffd60a;
  color: #0b1320;
}
.cdp__uf-toggle-knob {
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: var(--nxt-outline-variant, #ccc);
  display: block;
  flex-shrink: 0;
  transition: background 0.18s;
}
.cdp__uf-toggle--on .cdp__uf-toggle-knob {
  background: rgba(255, 255, 255, 0.9);
}
[data-theme='dark'] .cdp__uf-toggle--on .cdp__uf-toggle-knob {
  background: rgba(11, 19, 32, 0.85);
}
.cdp__uf-toggle-label {
  padding-right: 0.125rem;
}

/* Header permissions */
.cdp__uf-perm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.cdp__uf-perm-count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
}
[data-theme='dark'] .cdp__uf-perm-count {
  background: rgba(255, 255, 255, 0.1);
}

/* Chips permissions */
.cdp__uf-perm-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.cdp__uf-perm-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem 0.3rem 0.45rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 9999px;
  cursor: pointer;
  user-select: none;
  font-size: 0.775rem;
  font-weight: 500;
  color: var(--nxt-on-surface-variant);
  background: var(--nxt-card-bg, #fff);
  transition:
    border-color 0.13s,
    background 0.13s,
    color 0.13s;
}
.cdp__uf-perm-chip:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-on-surface);
}
.cdp__uf-perm-chip--on {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__uf-perm-chip--on {
  border-color: #ffd60a;
  background: #ffd60a;
  color: #0b1320;
}
.cdp__uf-perm-check {
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
}

/* ── Champ mot de passe avec œil + générateur ─────────────────────────── */
.cdp__uf-pwd-wrap {
  display: flex;
  align-items: stretch;
  gap: 0.375rem;
}
.cdp__uf-input--pwd {
  flex: 1;
  min-width: 0;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  letter-spacing: 0.06em;
}
.cdp__uf-pwd-eye {
  flex-shrink: 0;
  width: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.875rem;
  transition:
    border-color 0.14s,
    color 0.14s;
}
.cdp__uf-pwd-eye:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-on-surface);
}

.cdp__uf-pwd-gen {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  white-space: nowrap;
  transition:
    border-color 0.14s,
    background 0.14s,
    color 0.14s;
}
.cdp__uf-pwd-gen:hover {
  border-color: var(--nxt-primary);
  background: var(--nxt-primary);
  color: #fff;
}
[data-theme='dark'] .cdp__uf-pwd-gen:hover {
  border-color: #ffd60a;
  background: #ffd60a;
  color: #0b1320;
}
.cdp__uf-pwd-gen .pi {
  font-size: 0.75rem;
}

.cdp__uf-pwd-preview {
  display: block;
  margin-top: 0.3rem;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 0.7375rem;
  letter-spacing: 0.05em;
  color: var(--nxt-primary);
  font-weight: 600;
  word-break: break-all;
}

/* ── Toast gestionnaires ───────────────────────────────────────────────── */
.cdp__usr-toast {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8375rem;
  font-weight: 600;
  border: 1px solid transparent;
}
.cdp__usr-toast--success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border-color: rgba(16, 185, 129, 0.2);
}
.cdp__usr-toast--error {
  background: rgba(186, 26, 26, 0.08);
  color: var(--nxt-error, #ba1a1a);
  border-color: rgba(186, 26, 26, 0.15);
}
.cdp__usr-toast .pi {
  font-size: 1rem;
  flex-shrink: 0;
}
.cdp__usr-toast span {
  flex: 1;
}
.cdp__usr-toast-close {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.375rem;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  opacity: 0.6;
  font-size: 0.75rem;
  transition: opacity 0.14s;
}
.cdp__usr-toast-close:hover {
  opacity: 1;
}

/* Animation toast */
.cdp__usr-toast-anim-enter-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.cdp__usr-toast-anim-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.2s ease;
}
.cdp__usr-toast-anim-enter-from {
  opacity: 0;
  transform: translateY(-0.5rem);
}
.cdp__usr-toast-anim-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

/* ── Tab Pages légales ─────────────────────────────────────────────────── */
.cdp__legal {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

/* ── Tab Paiement ──────────────────────────────────────────────────────── */
.cdp__paiement {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

/* ── Tab Utilisateurs & Rôles ──────────────────────────────────────────── */
.cdp__usr {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cdp__usr-subtabs {
  display: flex;
  gap: 0.375rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  padding-bottom: 0.75rem;
}
.cdp__usr-subtab {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  transition: all 0.15s;
}
.cdp__usr-subtab:hover {
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.04));
}
.cdp__usr-subtab--active {
  background: var(--nxt-primary, #111);
  color: #fff;
}

.cdp__usr-toolbar {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.cdp__usr-search-wrap {
  position: relative;
  flex: 1;
  min-width: 160px;
  max-width: 320px;
}
.cdp__usr-search-ico {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.cdp__usr-search {
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid var(--nxt-outline-variant, #e5e7eb);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-family: inherit;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
}
.cdp__usr-search:focus {
  border-color: var(--nxt-primary);
}
/* Select de rôle personnalisé (recherchable) — filtre utilisateurs */
.cdp__usr-role-dropdown-wrap {
  position: relative;
}
.cdp__usr-role-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.375rem;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.cdp__usr-role-dropdown-btn span {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cdp__usr-role-dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.cdp__usr-role-dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.cdp__usr-role-dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cdp__usr-role-dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.cdp__usr-role-dropdown-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
}
.cdp__usr-role-dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.cdp__usr-role-dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.cdp__usr-role-dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}
.cdp__usr-drop-enter-active,
.cdp__usr-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.cdp__usr-drop-enter-from,
.cdp__usr-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.cdp__usr-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  white-space: nowrap;
}
.cdp__usr-add-btn {
  margin-left: auto;
}
.cdp__usr-sort-select {
  padding: 0.45rem 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.cdp__usr-sort-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.cdp__usr-sort-btn:hover {
  background: var(--nxt-surface-mid);
}
[data-theme='dark'] .cdp__usr-sort-select,
[data-theme='dark'] .cdp__usr-sort-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Panneau détail inline (gestionnaire / CNC) */
.cdp__usr-detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cdp__usr-detail-back {
  align-self: flex-start;
}
.cdp__usr-detail-card {
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 0.875rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
[data-theme='dark'] .cdp__usr-detail-card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
.cdp__usr-detail-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.cdp__usr-detail-header-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.cdp__usr-detail-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0;
}
.cdp__usr-detail-email {
  font-size: 0.85rem;
  color: var(--nxt-on-surface-variant);
  margin: 0;
}
.cdp__usr-detail-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
[data-theme='dark'] .cdp__usr-detail-info-grid {
  border-top-color: rgba(255, 255, 255, 0.08);
}
.cdp__usr-detail-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cdp__usr-detail-info-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--nxt-on-surface-variant);
}
.cdp__usr-detail-info-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}

/* Grille gestionnaires */
.cdp__usr-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.cdp__usr-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 0.875rem;
  transition: border-color 0.15s;
  cursor: pointer;
}
[data-theme='dark'] .cdp__usr-card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cdp__usr-role-dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cdp__usr-role-dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cdp__usr-role-dropdown-item:hover,
[data-theme='dark'] .cdp__usr-role-dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}
.cdp__usr-card--editing {
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .cdp__usr-card--editing {
  border-color: #ffd60a;
}
.cdp__usr-card--sk {
  animation: cdp-pulse 1.4s infinite;
  opacity: 0.6;
}
.cdp__usr-avatar {
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}
.cdp__usr-avatar--img {
  object-fit: cover;
  flex-shrink: 0;
}
.cdp__usr-avatar--lg {
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  font-size: 1.25rem;
}
.cdp__usr-avatar--anon {
  color: #fff;
}
.cdp__usr-info {
  flex: 1;
  min-width: 0;
}
.cdp__usr-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}
.cdp__usr-email {
  font-size: 0.7875rem;
  color: var(--nxt-on-surface-variant);
  margin: 0.1rem 0 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cdp__usr-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.cdp__usr-role-badge {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.175rem 0.5rem;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
[data-theme='dark'] .cdp__usr-role-badge {
  background: rgba(255, 255, 255, 0.08);
}
.cdp__usr-status {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.175rem 0.5rem;
  border-radius: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.cdp__usr-status::before {
  content: '';
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
}
.cdp__usr-status--on {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}
.cdp__usr-status--on::before {
  background: #10b981;
}
.cdp__usr-status--off {
  background: rgba(0, 0, 0, 0.05);
  color: var(--nxt-on-surface-variant);
}
.cdp__usr-status--off::before {
  background: #9ca3af;
}
.cdp__usr-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

/* Pagination users */
.cdp__usr-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
}

/* Liste des rôles */
.cdp__usr-roles-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cdp__usr-role-row {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
  padding: 0.875rem 1rem;
  background: var(--nxt-card-bg, #fff);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  border-radius: 0.875rem;
  transition: border-color 0.15s;
}
.cdp__usr-role-row--editing {
  border-color: var(--nxt-primary);
}
.cdp__usr-role-row--sk {
  animation: cdp-pulse 1.4s infinite;
  opacity: 0.6;
}
.cdp__usr-role-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cdp__usr-role-code {
  font-size: 0.7125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}
.cdp__usr-role-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cdp__usr-role-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cdp__usr-perm-count {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.04));
}

/* Formulaires inline users */
.cdp__usr-form {
  margin: 0.25rem 0 0.5rem;
}
.cdp__usr-edit-form {
  margin: 0.25rem 0 0.5rem;
}
.cdp__add-form-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
@media (max-width: 600px) {
  .cdp__add-form-grid2 {
    grid-template-columns: 1fr;
  }
}

/* Grille permissions */
.cdp__perm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.375rem;
  margin-top: 0.375rem;
}
.cdp__perm-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.7875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.35rem 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  color: var(--nxt-on-surface-variant);
  transition: all 0.12s;
  user-select: none;
}
.cdp__perm-item:hover {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.cdp__perm-item--checked {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
}
[data-theme='dark'] .cdp__perm-item:hover {
  border-color: #ffd60a;
  color: #ffd60a;
}
[data-theme='dark'] .cdp__perm-item--checked {
  border-color: #ffd60a;
  color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
}
.cdp__perm-item .pi {
  font-size: 0.875rem;
}

/* Toggle (réutilisé) */
.cdp__add-form-label--toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cdp__toggle-btn {
  position: relative;
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: var(--nxt-outline-variant, #ccc);
  transition: background 0.2s;
  padding: 0;
  flex-shrink: 0;
}
.cdp__toggle-btn--on {
  background: var(--nxt-primary, #111);
}
[data-theme='dark'] .cdp__toggle-btn--on {
  background: #ffd60a;
}
.cdp__toggle-knob {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  display: block;
}
.cdp__toggle-btn--on .cdp__toggle-knob {
  transform: translateX(1.125rem);
}
</style>

<style>
/* Modale teleportée — non scoped pour fonctionner dans <body> */
.cdp__confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.cdp__confirm-modal {
  background: #fff;
  border-radius: 1.125rem;
  padding: 2rem 1.75rem 1.5rem;
  max-width: 380px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.875rem;
  text-align: center;
}
[data-theme='dark'] .cdp__confirm-modal {
  background: #1e2025;
}
.cdp__confirm-ico {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.375rem;
}
.cdp__confirm-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #111;
}
[data-theme='dark'] .cdp__confirm-title {
  color: #f0f0f0;
}
.cdp__confirm-desc {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  line-height: 1.5;
}
[data-theme='dark'] .cdp__confirm-desc {
  color: #aaa;
}
.cdp__confirm-actions {
  display: flex;
  gap: 0.625rem;
  width: 100%;
  justify-content: center;
  margin-top: 0.25rem;
}
.cdp__confirm-actions .cdp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.1rem;
  border-radius: 0.55rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.cdp__confirm-actions .cdp__btn--ghost {
  background: #f5f5f7;
  color: #555;
  border-color: #e5e7eb;
}
.cdp__confirm-actions .cdp__btn--ghost:hover {
  background: #eee;
}
.cdp__confirm-actions .cdp__btn--danger {
  background: #ef4444;
  color: #fff;
  border-color: #ef4444;
}
.cdp__confirm-actions .cdp__btn--danger:hover {
  background: #dc2626;
  border-color: #dc2626;
}
</style>
